import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { CsPage, NavController, CanLeaveResult } from 'providers/nav-controller';
import { ExerciseStat, FitnessState, DeclaredState } from '@cf-shared/data-model/user-state';
import { isEquipmentValid, adjustWeight, getDisplayWeightError_kg, WeightSetup, EquipmentType, Equipment } from '@cf-shared/data-model/equipment';
import { isE2ETest, hasClientSmallScreen } from 'environments/environment-helpers';
import { ExerciseDbService, exerciseUsesRMTesting, getMyoAwareNumSets, getRestSecondsForExercise } from 'services/exercise-db.service';
import { RestTimerService } from 'services/rest-timer.service';
import { ConfigDbService } from 'services/config-db.service';
import { ConfigKey } from 'services/config-keys';
import { UserDb } from 'services/user-db.service';
import { addPostStatusOnce, applyPlateauResolution, clearPlateauDetectedStatuses, clearProgressionOutcomeStatuses, clearRepTestSuccessStatuses, clearRepTestWeightAnswerStatuses, clearStrengthTestVerdictStatuses, combinedEntryForExercise, fillMissingEarlierSetsFromScreen, hasSetsToKeepBeforeSwitch, isSplitSubSlotId, isStrengthTestSetType, isStrengthTestWorkout, Log_Workout, Log_WorkoutExercise, Log_WorkoutSet, Log_WorkoutSetPostStatus, Log_WorkoutSetPreStatus, mergeScreenExercisesOverStored, PlateauResolutionOutcome, recordedRepTestWeightAnswerStatus, selectWorkoutCompletionMessage, shouldShowTestWorkoutExplainer, splitLogExerciseAtSwitch, WorkoutCompletionMessage } from '@cf-shared/data-model/log-workout';
import { buildWorkoutSetStatesOnScreen, exerciseHoldsConcludedSet, findConcludedSetsMissingFromDocument, isExerciseConcluded, resolveConcludedWorkoutState, screenHoldsEveryStoredExercise, WorkoutSetStatesOnScreen } from '@cf-shared/data-model/workout-conclusion';
import { LogRecordState, LogRecordType, getEntryLocalTimeInDesiredTimeZone } from '@cf-shared/data-model/user-log';
import { formatRestDuration, resolveRestTimerStart, RestTimerStart } from '@cf-shared/data-model/rest-timer';
import { SetType, SetSuccess, PostSetStatus, PreSetStatus, remapOldPreSetStatusNumberToEnum, remapOldPostSetStatusNumberToEnum } from '@cf-shared/enums';
import { olderRuleLayoffWeight_kg, otherIntensityLayoffWeight_kg } from 'services/layoff-prescription';
import { PostSetStatusTable } from '@cf-shared/post-set-status-text';
import { TrainingLevel, minTwoTrainingLevels } from '@cf-shared/data-model/training-level';
import { DatabaseWrapper, DocWatcher, TransactionId } from 'services/database-wrapper';
import { AuthService } from 'services/auth.service';
import { SettingsService } from 'services/settings.service';
import { DebugService } from 'services/debug.service';
import { estimate1RM, SetStats } from '@cf-shared/data-model/set-stats';
import { afterSessionSnapshotFor, clearAutoregulationFields, resolveAutoregulationOnExercise, storedVolumeAdjustmentForAnswer } from './autoregulation-resolve';
import { alertTextForRepTestWeightAnswer, decideRepTestWeightQuestion, isRepTestWeightOffTarget, RepTestWeightAnswer, repTestWeightAnswerForPostStatus, resolveRepTestWeight } from './rep-test-weight-answer';
import { decideEquipmentFloorHint, EquipmentFloorHint, EquipmentFloorKind, EquipmentFloorReason, roundWeightForDisplay_kg } from './equipment-floor-hint';
import { SessionRecency, sessionRecencyForLoggedExercise, snapshotConcludedDate } from 'util/historical-subslot';
import { PlanSaveCaller, PlanSaveContext } from 'util/orphan-subslot-at-save';
import { advisoryRecommendedWeight_kg, benchmarkInputForSet, benchmarkTargetWeight_kg, bestStrengthOfSession_net_kg, clampSnappedBenchmarkWeight_kg, classifyFollowupProgression, classifyRegular1stProgression, classifyStrengthTest, FollowupProgression, computeUnbrowsedCustomWeightIncrement_kg, concludeEditedSetWithoutPlanUpdate, decideMaxedOutEquipment, maxedOutAtAfterProgression_kg, BeatTargetMove, decideBeatTargetProgression, decideTargetHitProgression, deriveDeloadWeight_net_kg, MAX_TARGET_REPS, RepCeilingAction, deriveRmTestPathTarget, isFollowupAtStrengthTestWeight, isLoggedWeightBelowRecommended, isStrengthGainOverPreviousSession, previousSessionBestAttempt, isSetImplausiblyStrong, isSetJudgedAgainstLayoffHeldWeight, isSetLoggable, nextStoredStrength_net_kg, NON_RM_TEST_REP_BAND, prescribedImplied1RM_net_kg, PREVIOUS_SESSION_WAIT_MS, PreviousSessionWait, previousSessionBaselineSet, recordOtherIntensityRetestDemands, refreshStrengthTestFollowupNotes, Regular1stOutcome, restoreOtherIntensityRetestDemands, restorePrescribedTargets, RM_TEST_REP_BAND, decideSetTapRoute, SetTapRoute, sessionSetsForExercise, strengthGuardAnchor_net_kg, StrengthTestOutcome, waitForPreviousSessionReadAsync } from './workout-progression-decision';
import { applyPendingCustomIncrementHeals, defaultCustomWeightIncrement_kg, PendingCustomIncrementHeal } from 'util/custom-weight-increment';
import { RepProgressBarTone } from 'components/cs-rep-progress-bar/rep-progress-bar-tone';
import { FinishedExerciseSummary, getFinishedExerciseSummary, RepChangeChip, RepChangeDirection } from './finished-exercise-display';
import { buildWorkoutSummary, WorkoutSummary } from './workout-summary';
import { exercisesWithRecordCandidates, personalRecordReport } from '@cf-shared/review-prompt-personal-record';
import { PERSONAL_RECORD_LOOKUP_TIMEOUT_MS } from 'services/review-prompt.service';
import { awaitSignalOrTimeoutAsync, FirstProbeGateOutcome } from 'services/first-probe-gate';
import { errorReportFields } from 'services/logging.service';
import { sleep } from '@cf-shared/shared-util';
import { formatLocaleFloat, LocaleSettings } from '@cf-shared/util/locale-number';
import { CompactSetRowDisplay, getCompactSetRowDisplay, getHiddenStatusMessageCount, getRestTimerButtonState, getSetDetailDisplay, getSetLoggedTime, getSetProgressionResult, getSetRepRangeDisplay, readWeightAndReps, RepRangeDisplay, SetDetailDisplay, SetOutcomeMark, SetProgressionHeadline, SetProgressionInput, SetProgressionResult, shouldOfferRestTimerStart, WeightAndReps } from './set-progress-display';
import { compareWithLastSessionSet, getLastSessionSet, LaterSetComparison, readLastSessionSetStats } from './last-session-set';
import { restoreSetEditSnapshot, takeSetEditSnapshot } from './set-edit-snapshot';
import { clearPendingVolumeAutoregulationStatuses, computeVolumeAutoregulation, resolveAcceptedVolumeAdjustment } from '@cf-shared/data-model/volume-autoregulation';
import {
  Plan_ExercisePath,
  Plan_SubSlot,
  FitnessPlan,
  IntensityLevel,
  getMinTargetRepsForIntensity,
  getSlotPathIntensity,
  parseSubslotIdAndOffset,
  getRepTargetForIntensity,
  resolveEstablishedSetType,
  coachChangedSinceSnapshot,
} from '@cf-shared/data-model/fitness-plan';
import { DateTime } from 'luxon';
import { ExerciseChangeDoc, ExerciseChangeField } from '@cf-shared/data-model/exercise-change';
import { CHANGES_READ_PER_EXERCISE, WORKOUTS_READ_FOR_NOTE, coachAnsweredQuestion, coachNoteChanges, overwrittenChange, previousWorkoutWithSubSlot, volumeQuestionOpen } from './coach-change-client';
import { describeChangeDoc } from 'pages/exercise-configuration/coach-exercise-view';
import { deepClone, deepCompareEQ } from '@cf-shared/deep-helpers';
import { AlertController, AlertButton, AlertButtonRole } from 'providers/alert-controller';
import { PopoverController } from 'providers/popover-controller';
import { PopupMenuProvider, PopupMenuButton } from 'providers/popup-menu/popup-menu';
import { NotePageResult } from 'pages/note-page/note-page-interface';
import { CsViewNotesPopover, CsViewNotesPopoverParams } from 'components/cs-view-notes-popover/cs-view-notes-popover';
import { CsLogDetailPopover, CsLogDetailPopoverParams } from 'components/cs-log-detail-popover/cs-log-detail-popover';
import { SvPlanHandlerService } from 'services/server/sv-plan-handler.service';
import { TelemetryService } from 'services/telemetry.service';
import { ReviewPromptService, ReviewPromptTrigger } from 'services/review-prompt.service';
import { LoggingService } from 'services/logging.service';
import { SessionHealth } from 'services/session-health';
import { ExerciseStatusBadgeType } from 'components/cs-exercise-status-badge/cs-exercise-status-badge';
import { BadgeColor } from 'components/cs-badge/cs-badge';
import { canonValueToTexts_global, findBestUnitConfigForType, formatExerciseSets, getMeasureUnitConversion, formatOneExerciseSet, formatMeasure, Formatting, formatRelativeDay, formatShortDate, formatTime, formatWorkoutName } from 'util/formatting';
import { groupBy, findLast, upperFirst } from 'lodash-es';
import { EquipmentUIItem, getBestEquipmentIdBasedOnTemplate, getSourceEquipments } from 'services/equipment-ui-items';
import { copyEquipmentToOtherUnit, decideEquipmentForUnitSwitch, UnitSwitchEquipmentSource } from 'services/equipment-helpers';
import {
  clearEquipmentForUnit, clearLegacyEquipmentId, declaresTheOtherUnit, ensureEquipmentSlots, equipmentIdForUnit, equipmentIdForUnitOrLegacy,
  LegacyEquipmentMigration, legacyMigrationUnit, migrateLegacyEquipmentId, recordEquipmentForUnit,
} from 'services/exercise-stat-equipment';
import { ToastController } from 'providers/toast-controller';
import { IEquipmentSearchPage } from 'pages/equipment/equipment-search-interface';
import { IExerciseStatsPage } from 'pages/exercise-stats/exercise-stats-interface';
import { INotePage } from 'pages/note-page/note-page-interface';
import { IHomePage } from 'pages/home/home-interface';
import { IExerciseSwitchPage, ExerciseSwitchTempResult } from 'pages/exercise-switch/exercise-switch-interface';
import { IChangeWorkoutPage } from 'pages/change-workout-page/change-workout-interface';
import { daysSinceDate } from 'util/time';
import { Schedule_ItemType } from '@cf-shared/data-model/schedule';
import { ScheduleRuleService } from 'services/schedule-rule.service';
import { isoTimeToHours } from '@cf-shared/time';
import { UiExercise, UiSet, generatePossibleWeightValues, AUTO_CUSTOM_INCREMENT_EQUIPMENT_ID_PREFIX } from 'models/workout-ui-models';
import { PlanMerge, planMergeForMode, WorkoutLoaderService, WorkoutLoadMode, WorkoutLoadingContext } from 'services/workout-loader.service';
import { namesOfMissingWorkoutLoadingDocuments } from 'services/workout-loading-context-check';
import { mayRepairStuckWorkoutInMode } from 'services/workout-load-mode-resolve';
import { ExerciseChosenBy, rotateExerciseInSubSlotByApp } from 'services/exercise-switching';
import { WorkoutDetailPageParams, WorkoutDetailPageResult } from './workout-detail-interface';
import { CoachNotifyService } from 'services/coach-notify.service';
import { CoachNotifyWorkoutParams } from '@cf-shared/coach-notify-interface';
import { NotificationService } from 'services/notification.service';
import { ReferralService } from 'services/referral.service';
import { isDatabaseUnavailable } from 'services/database-unavailable-error';
import { describeError } from '@cf-shared/error-describe';
import { parseHintsSeen } from 'util/hints-seen';
import { ProblemKind } from 'services/problem-kind';
import { StatusIconSize, StatusIconTone } from 'components/cs-status-icon/cs-status-icon';

type StatusMessageFn = (params?: { [key: string]: string }) => string;

/**
 * How one call of `reassertConcludedWorkoutState` reports the repair it just
 * made. The name and the severity travel together in one object on purpose:
 * they answer the same question — "what did this repair mean?" — and the two
 * callers answer it differently. Two separate parameters could drift apart.
 */
interface ReassertReport {
  /** The report name. Also the Sentry issue title, after the tier's prefix. */
  readonly name: string;
  /** How bad it is. See `client/src/services/problem-kind.ts`. */
  readonly tier: ProblemKind.Info | ProblemKind.Problem;
}

/**
 * The save path's report: the workout's state was put back to unfinished
 * between the moment the page decided the workout was finished and the moment
 * it was saved, and the line above has just put it right again.
 *
 * "Unfinished" is either of the two repairable states — in progress, or never
 * started (#4840). The state it was put back TO is in this report's `context`
 * as `previousState`, so the two are told apart there rather than by having two
 * more report names.
 *
 * INFO tier, not problem tier, and the reason is worth stating because #4802
 * originally filed it as a problem. Two facts settle it:
 *
 * 1. **The repair has already run by the time this reports.** That is the exact
 *    test `design-docs/telemetry.md` gives for the info tier, and the rule
 *    written on `trackSilentProblem` itself — "if the thing being reported is
 *    an automatic repair that already succeeded, it belongs in
 *    `trackSilentInfo` instead (#4382)".
 * 2. **It happens routinely, by a mechanism we have deliberately not fixed.**
 *    Every canonicalizing read copies the stored body over the one shared
 *    object the page is holding, and the workout page already carried a
 *    defence against exactly that for the exercises array long before #4802
 *    (see the re-link in `saveWorkoutAsync`). On CI this fires two to four
 *    times in a single workout scenario. A condition that common, and already
 *    repaired, filed at problem tier is precisely the noise that made real
 *    problems unfindable — the thing #4382 exists to remove.
 *
 * Nothing is lost by the move: the report still reaches Sentry (as
 * `INFO: <name>`) and Mixpanel (as `sentry_error` with `kind: info`), which is
 * where the always-on canaries live — `UserDbConflictsPresent` is the same
 * shape and moved to this tier for the same reason.
 *
 * Two consequences to know. The Sentry issue identity CHANGES with the tier
 * (`design-docs/telemetry.md`, "Two traps when changing a report's tier"):
 * `PROBLEM: workout-state-reverted-before-save` goes quiet and
 * `INFO: workout-state-reverted-before-save` starts fresh. Nothing real is
 * split by that here — #4802 never reached `stable`, so it only ever ran on
 * dev, and the old issue holds a few hours of CI traffic and nothing else.
 * The second consequence is the one that mattered: at problem tier this set
 * the per-session "something went wrong" flag that suppresses the in-app
 * review prompt (#4625), so a user finishing a workout silenced their own
 * prompt for the rest of that session. At info tier it no longer does.
 */
const REVERTED_BEFORE_SAVE: ReassertReport = {
  name: 'workout-state-reverted-before-save',
  tier: ProblemKind.Info,
};

/**
 * The load path's report: the workout was ALREADY stored as unfinished — in
 * progress, or never started (#4840) — with every set finished with, before
 * this page ever opened it. Which of the two is in `context.previousState`.
 *
 * The NAME still says `in-progress`, and deliberately so: renaming it would
 * start a fresh Sentry issue and split this report's history in two, which
 * costs more than the wording buys. See `design-docs/telemetry.md`, "Two traps
 * when changing a report's tier" — the identity trap is the same one.
 *
 * This one stays at PROBLEM tier, and the difference from its sibling above is
 * the whole point of having two names. A document found stored wrong means the
 * in-page defence did not hold on whatever device wrote it — that condition is
 * not repaired anywhere until somebody opens the workout again, and until then
 * the Home page goes on offering a session the user finished. Somebody does
 * eventually need to look at those.
 */
const FOUND_STUCK_ON_LOAD: ReassertReport = {
  name: 'workout-found-stuck-in-progress',
  tier: ProblemKind.Problem,
};

interface PostSetStatusMessage {
  status: PostSetStatus;
  getMessage: StatusMessageFn;
  // App Store 1.4.1: optional scientific-citation help doc for this status message
  helpId?: string;
}

interface PreSetStatusMessage {
  status: PreSetStatus;
  getMessage: StatusMessageFn;
  // App Store 1.4.1: optional scientific-citation help doc for this status message
  helpId?: string;
}

// Keyed by status rather than written as an array, so that TypeScript refuses a
// table with a status missing from it. The coach notification email has a second
// table over the same statuses, in the server's own translation catalog, and the
// two used to be free to drift apart in silence — a status added here and
// forgotten there printed its raw identifier into a coach's inbox (#4916).
// `PostSetStatusTable` is the shared statement of that rule.
//
// Declaration ORDER is load-bearing and is why the array below still exists:
// getSetPostStatusTextLines walks the table and picks out the statuses a set
// carries, so this order — not the set's own — decides the order the lines are
// shown in. Object keys that are not numeric strings keep insertion order, and
// every one of these is a word, so deriving the array preserves it exactly.
//
// A status whose name ends in `LastSet` is the one chosen when NO set follows
// this one — see the pushes in concludeSet, which pick it in the `else` of
// `e.sets.length > iSet + 1`. So a message that talks about later sets belongs
// on the OTHER member of the pair, never on the `LastSet` one.
const postSetStatusMessages: PostSetStatusTable<Omit<PostSetStatusMessage, 'status'>> = {
  [PostSetStatus.Weakness]: { getMessage: () => LL.workouts.status.exerciseNotOptimal() },
  [PostSetStatus.NonRMStrengthTest]: { getMessage: () => LL.workouts.status.goodJobProgression() },
  [PostSetStatus.StrengthTestSuccess]: { getMessage: () => LL.workouts.status.strengthCalculatedLighterSets() },
  [PostSetStatus.StrengthTestSuccessLastSet]: { getMessage: () => LL.workouts.status.strengthLevelCalculated() },
  [PostSetStatus.StrengthTestFailToManyReps]: { getMessage: () => LL.workouts.status.tooManyRepsRetry() },
  [PostSetStatus.StrengthTestFailToManyRepsLastSet]: { getMessage: () => LL.workouts.status.tooManyRepsNextWorkout() },
  [PostSetStatus.StrengthTestFailToFewReps]: { getMessage: () => LL.workouts.status.tooFewRepsRetry() },
  [PostSetStatus.StrengthTestFailToFewRepsLastSet]: { getMessage: () => LL.workouts.status.tooFewRepsNextWorkout() },
  [PostSetStatus.RepTestSuccess]: { getMessage: () => LL.workouts.status.repRangeEstablishedContinue() },
  [PostSetStatus.RepTestSuccessLastSet]: { getMessage: () => LL.workouts.status.repRangeEstablished() },
  [PostSetStatus.RepTestFailToHeavy]: { getMessage: () => LL.workouts.status.strengthRetestNextTooHeavy() },
  [PostSetStatus.RepTestFailToLight]: { getMessage: () => LL.workouts.status.strengthRetestNextTooLight() },
  [PostSetStatus.RepTestBadEquipment]: { getMessage: () => LL.workouts.status.differentWeightEquipment() },
  [PostSetStatus.ProgressionDeload]: { getMessage: () => LL.workouts.status.progressionDeload(), helpId: '@help:references.deloads-recovery' },
  [PostSetStatus.ProgressionDeloadLastSet]: { getMessage: () => LL.workouts.status.progressionDeloadLastSet(), helpId: '@help:references.deloads-recovery' },
  [PostSetStatus.UpdatedRepTargetRegularSession]: { getMessage: () => LL.workouts.status.repTargetUpdatedRegular() },
  [PostSetStatus.UpdatedRepTargetHighRepSession]: { getMessage: () => LL.workouts.status.repTargetUpdatedHighRep() },
  [PostSetStatus.UpdatedRepTargetHeavySession]: { getMessage: () => LL.workouts.status.repTargetUpdatedHeavy() },
  [PostSetStatus.SkippedSet]: { getMessage: () => LL.workouts.status.lastSetNotNeeded() },
  [PostSetStatus.RegularSetLowerWeight]: { getMessage: (p) => LL.workouts.status.lowerThanRecommended({ plannedWeight: p?.plannedWeight ?? '' }) },
  [PostSetStatus.ImmediateProgression]: { getMessage: () => LL.workouts.status.immediateProgression() },
  [PostSetStatus.ImmediateProgressionReps]: { getMessage: () => LL.workouts.status.immediateProgressionReps() },
  [PostSetStatus.MaxedOutEquipment]: { getMessage: () => LL.workouts.status.maxedOutEquipment() },
  [PostSetStatus.PlateauResolved]: { getMessage: (p) => LL.workouts.status.plateauResolved({ message: p?.message ?? '' }) },
  [PostSetStatus.PlateauDetected]: { getMessage: () => LL.workouts.status.plateauDetected() },
  [PostSetStatus.PlateauDismissed]: { getMessage: () => LL.workouts.status.plateauDismissed() },
  [PostSetStatus.VolumeAutoregulationIncrease]: { getMessage: () => LL.workouts.status.volumeAutoregulationIncrease(), helpId: '@help:references.training-volume' },
  [PostSetStatus.VolumeAutoregulationDecrease]: { getMessage: () => LL.workouts.status.volumeAutoregulationDecrease(), helpId: '@help:references.training-volume' },
  [PostSetStatus.VolumeAutoregulationPending]: { getMessage: () => LL.workouts.status.volumeAutoregulationPending(), helpId: '@help:references.training-volume' },
  [PostSetStatus.StrengthTestFollowupDone]: { getMessage: () => LL.workouts.status.strengthTestFollowupDone() },
  [PostSetStatus.RepCeilingSwitch]: {
    getMessage: (p) => p?.sameEquipment ? LL.workouts.status.repCeilingSwitchSameEquipment() : LL.workouts.status.repCeilingSwitch(),
  },
  [PostSetStatus.RepCeilingHeld]: { getMessage: () => LL.workouts.status.repCeilingHeld() },
};

const messageForPostSetStatus: PostSetStatusMessage[] =
  Object.entries(postSetStatusMessages).map(([status, message]) => ({ status: status as PostSetStatus, ...message }));

const messageForPreSetStatus: PreSetStatusMessage[] = [
  { status: PreSetStatus.DropSet, getMessage: () => LL.workouts.status.dropSet() },
  { status: PreSetStatus.CompleteProgressionReset, getMessage: () => LL.workouts.status.completeProgressionReset() },
  { status: PreSetStatus.DidNotPerformDeload, getMessage: () => LL.workouts.status.adjustedTargetWeight(), helpId: '@help:references.deloads-recovery' },
  { status: PreSetStatus.DidNotPerformDeloadReps, getMessage: () => LL.workouts.status.adjustedTargetReps(), helpId: '@help:references.deloads-recovery' },
  { status: PreSetStatus.PrescribedWeightCapped, getMessage: () => LL.workouts.status.cappedToRecentPerformance() },
  { status: PreSetStatus.DeloadedTargetWeight, getMessage: () => LL.workouts.status.deloadedTargetWeight(), helpId: '@help:references.deloads-recovery' },
  { status: PreSetStatus.DidNotPerformRetest, getMessage: () => LL.workouts.status.retestRequired() },
  { status: PreSetStatus.PlateauResolved, getMessage: (p) => LL.workouts.status.plateauResolved({ message: p?.message ?? '' }) },
];

interface StatusLineInfo {
  text: string;
  status: PostSetStatus | PreSetStatus;
  statusObject: Log_WorkoutSetPostStatus | Log_WorkoutSetPreStatus;
  // App Store 1.4.1: scientific-citation help doc, propagated from the status-message table
  helpId?: string;
}

/**
 * What `onClickSetDone` learned by asking the user why a rep test ran at a weight far from the
 * prescribed one, handed to `concludeSet` so the two agree on both halves of it (#4558).
 */
interface RepTestWeightQuestionOutcome {
  // whether the caller judged the logged weight far enough off the prescribed one to ask at all
  weightWasOffTarget: boolean;
  // what the user answered; only set when the question was actually asked
  answer?: RepTestWeightAnswer;
}

/** What a tap of the Log button did to the set itself. */
export enum SetRecordAction {
  /** a set that had not been completed before */
  Logged = 'logged',
  /** an already-completed set whose values the user changed and saved */
  Edited = 'edited',
}

/** Why a tap left the user's program alone. Only ever set when it did. */
export enum SetRecordUnchangedReason {
  /** a correction the app does not carry into the program */
  EditWithoutPlanUpdate = 'edit_without_plan_update',
  /** the exercise was swapped for this session only */
  TempSwap = 'temp_swap',
  /** a later session of this exercise has already been performed */
  SupersededSession = 'superseded_session',
  /** the program holds no entry for this exercise any more */
  NoLiveEntry = 'no_live_entry',
}

/** Why the app swaps an exercise out of the user's program on its own. */
enum ProgramSwapReason {
  /** a strength test showed the user is too weak for it */
  Weakness = 'weakness',
  /** the user accepted switching after a plateau */
  Plateau = 'plateau',
  /** the rep target reached its ceiling and the next weight step was too big a jump (#5979) */
  RepCeiling = 'rep-ceiling',
}

/** What a rep-target push onto a neighbouring DUP/TUP intensity does to that intensity's weight. */
enum NeighbourPushWeight {
  /** the neighbour's target weight is backed off to its established weight */
  BackOff = 'back_off',
  /** the neighbour's target weight is left as it is */
  Keep = 'keep',
}

/**
 * #4939: what one tap of the Log button did, gathered by whichever branch concluded something
 * and reported once at the end of `onClickSetDone`. `reason` is set only when the program was
 * left alone, and says which of the several reasons applied.
 */
interface SetRecordOutcome {
  action: SetRecordAction;
  updatedProgram: boolean;
  reason?: SetRecordUnchangedReason;
}

// #4142 compact feedback: an exercise's DONE sets as one string, without the planned-weight brackets.
// formatExerciseSets prints every set that has numbers, and a skipped set keeps the numbers it was
// prefilled with, so the skipped ones are left out first
function formatDoneSets(exercise: Log_WorkoutExercise, locale: LocaleSettings): string {
  const doneSets = (exercise?.sets ?? []).filter(set => set?.state === LogRecordState.Done);
  return doneSets.length ? formatExerciseSets({ ...exercise, sets: doneSets }, locale, false, false) : '';
}

/** #6428 R9: the overwritten coach changes already reported in this run of the app, so opening the
 *  same workout again does not send the same report again */
const reportedOverwrittenChangeIds = new Set<string>();

@Component({
  selector: 'page-workout-detail',
  templateUrl: 'workout-detail.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkoutDetailPage extends CsPage<WorkoutDetailPageParams, WorkoutDetailPageResult> {
  protected LL = LL;
  pageTitle: string;
  pageTitleRight: string;
  mode: WorkoutLoadMode = WorkoutLoadMode.Unknown;
  showViewOnlyInfo: boolean = true;
  viewOnlyInfoMessage: TranslatedString = untranslatedString('');
  isCoachManaging = false;
  /** #6428: the exercise editor's change documents for each sub-slot on the page, newest first.
   *  Read once per page load by `loadExerciseChangesAsync`; empty until that read finishes. */
  private exerciseChangesBySubSlot = new Map<string, ExerciseChangeDoc[]>();
  /** #6428 R8: per sub-slot, the coach changes the client's note is about, oldest first */
  private coachNoteBySubSlot = new Map<string, ExerciseChangeDoc[]>();
  private isLoggingMessage: boolean = false;
  showPendingWorkoutWarning: boolean = false;
  pendingWorkoutDateLabel: string = '';
  pendingWorkoutDateISO: string = '';
  showWorkoutChangedInfo: boolean = false;
  // shows the "Test workout to customize your program" header card the first time
  // the user does each workout of the split -- A, B, C get it once each (#4060)
  showTestWorkoutExplainer: boolean = false;
  WorkoutLoadMode = WorkoutLoadMode;
  hasClientSmallScreen = hasClientSmallScreen;
  // all the previous workouts that are still pending
  previousWorkouts: Log_Workout[];

  complete = false; // marker used to show the "Done" button at the end
  // Message shown on the end-of-workout completion card. Defaults to the generic
  // "Great work!" text; the onboarding payoff copy (#4061) replaces it for the
  // first two strength-test workouts. Resolved in onExerciseDone before `complete`
  // flips true, so the card never flashes generic → payoff.
  completionMessage: TranslatedString = LL.workouts.detail.workoutComplete();
  // which completion message was chosen; the summary shows only a test-workout one
  private completionMessageKind = WorkoutCompletionMessage.Generic;
  exercises: UiExercise[];

  fitnessState: FitnessState;
  declaredState: DeclaredState;
  plan: FitnessPlan;
  // df-704db86556f7: the plan's revision when the exercises were last built, so the plan-save
  // orphan canary can say whether the program moved under the page after that
  private planRevAtExerciseLoad: string | undefined;
  loading = true;
  // #4242: hold the heavy exercise list off the DOM until the page-enter slide
  // finishes. Building + rendering the exercise cards is ~100 ms of main-thread
  // work; run mid-slide it drops frames and the slide jams. ionViewDidEnter fires
  // AFTER the slide, so the build runs there while the page is stationary (where
  // the same cost is imperceptible). A fresh instance starts false and shows the
  // loading shell; it flips true once and stays true, so return-from-child
  // re-entry renders immediately.
  protected contentEntered = false;
  // #4242: start of the first-entry load, captured in ionViewWillEnter so the
  // workout_detail_load_time telemetry can be emitted after the deferred build in
  // ionViewDidEnter. Now spans the enter slide (time until the list is visible).
  private loadStartMs = 0;
  // #4242: the in-flight exercise-list build, or null when none is running. Used
  // to serialise concurrent builds: the deferred first-entry build and a reactive
  // docWatcher reload can both call buildWorkoutExercisesAsync, and the
  // preselectEquipmentIfNeeded loop inside mutates shared fitnessState.allEquipment
  // and must run serially (#3162). A second caller awaits this promise instead of
  // starting an interleaving build.
  private exerciseBuildInFlight: Promise<void> | null = null;
  // used to temporarily hide the Done button while showing the RM confirmation alert - prevents confusion in E2E tests
  duringSetDoneAlert = false;

  logWorkout: Log_Workout;
  // Track if this is an in-memory workout (created from "Start Workout" on rest day)
  isInMemoryWorkout: boolean = false;
  // Track if workout was modified (for returning result to caller)
  workoutModified: boolean = false;
  /** which workout documents this page instance has already reported a carried-over
   *  exercise for — see `reportExercisesCarriedOverAtSave`, which saves after every
   *  concluded set and must not file the same report each time. */
  private readonly reportedCarriedOverExercisesForDocIds = new Set<string>();

  LogRecordState = LogRecordState; // this is needed to make enum visible in the template!
  SetProgressionHeadline = SetProgressionHeadline;
  SetOutcomeMark = SetOutcomeMark;
  StatusIconTone = StatusIconTone;
  StatusIconSize = StatusIconSize;
  RepChangeDirection = RepChangeDirection;
  RepProgressBarTone = RepProgressBarTone;

  private nextAutofocusElementId: string;
  /** when the card to scroll to is an exercise card, its sub-slot id: the card's DOM id is the
   *  exercise id, and an exercise changed after sets were logged and then changed back (#5507)
   *  shows two cards with the same one */
  private nextAutofocusSubSlotId: string | undefined;
  private docWatcher: DocWatcher;
  /** `ConfigKey.CompactWorkoutFeedback`: true only for an exact `true` (fails closed) */
  compactFeedback = false;
  /** #4142 compact feedback: the summary in the completion card; set when the workout finishes */
  workoutSummary?: WorkoutSummary;
  // the exercises whose personal record this workout beat, once the lookup has answered
  private summaryRecordExerciseIds?: Set<string>;
  // counts the record lookups started; an answer is used only when no newer lookup has started since,
  // so an edit after finishing cannot be overwritten by the slower answer for the older sets
  private summaryLookupGeneration = 0;
  // set once, on the first entry, so re-entering the page never subscribes a second time
  private unwatchCompactFeedback?: () => void;

  // Rep ranges from 0 - 30
  possibleRepValues: number[] = Array.from({ length: 31 }, (_v, k) => k);

  // Read user's stated rep-target preference for an exercise+intensity (Phase 2 model:
  // ExerciseStat.preferRepTargetByIntensity). Undefined when the user never typed a value;
  // callers fall through to the system default (getRepTargetForIntensity) or skip the
  // preference-only branch (Case-2 +1 bump in targetHit).
  private getPreferRepTarget(e: UiExercise, intensityLevel: IntensityLevel): number | undefined {
    return this.fitnessState.exerciseStats[e.exercise._id]?.preferRepTargetByIntensity?.[intensityLevel];
  }

  possibleMyoRepsValues: number[] = Array.from({ length: 7 }, (_v, k) => k);

  constructor(
    public navCtrl: NavController,
    private exerciseDb: ExerciseDbService,
    private planService: SvPlanHandlerService,
    private userDb: UserDb,
    protected settings: SettingsService,
    private alertCtrl: AlertController,
    private popoverCtrl: PopoverController,
    private popupCtrl: PopupMenuProvider,
    protected auth: AuthService,
    protected debug: DebugService,
    private telemetry: TelemetryService,
    private reviewPrompt: ReviewPromptService,
    public logging: LoggingService,
    private cdRef: ChangeDetectorRef,
    private workoutLoader: WorkoutLoaderService,
    private scheduleRuleService: ScheduleRuleService,
    private coachNotify: CoachNotifyService,
    private restTimer: RestTimerService,
    private notificationService: NotificationService,
    private referral: ReferralService,
    private toastCtrl: ToastController,
    private configDb: ConfigDbService,
  ) {
    super();
  }

  // state for the expanded rest-timer overlay; the pill is driven directly
  // by RestTimerService signals, but the expanded view is toggled locally
  restTimerExpanded = false;

  // the pill sits in the header's cs-header-content slot, which is only rendered while a timer
  // shows; reading the service's signals here keeps the template reactive (zoneless)
  get restTimerPillShown(): boolean {
    return this.restTimer.isRunning() && this.restTimer.isAllowedOnThisPlatform();
  }

  // pill was tapped → open the full-screen rest-timer overlay
  onRestTimerExpand() {
    this.restTimerExpanded = true;
  }

  // overlay closed itself (backdrop tap or Skip) → collapse back to pill
  onRestTimerCollapse() {
    this.restTimerExpanded = false;
  }

  isTouchDevice() {
    return ('ontouchstart' in document.documentElement);
  }

  useInputScroller() {
    return this.isTouchDevice();
  }

  getSuggestedPreselectedRep(set: UiSet) {
    let exercise = set.uiExercise;
    let setIndex = exercise.sets.findIndex(s => s === set);
    if (setIndex === 0) {
      return undefined;
    }

    let previousSet = exercise.sets[setIndex - 1];
    if (previousSet.logSet && previousSet.logSet.state === LogRecordState.Done) {
      return previousSet.logSet.stats.reps;
    }

    return undefined;
  }

  // #4142: what an open set shows about its rep target — the range, the last-session line, the Reps
  // placeholder and where the picker lands. The rules: design-docs/working-set-screen.md
  getRepRangeDisplay(set: UiSet): RepRangeDisplay {
    return getSetRepRangeDisplay({
      setType: set.logSet.setType,
      // a reopened, already concluded set reads the path it was prescribed from, not the one its
      // own conclusion wrote
      path: set.logSet.planPathBackup ?? set.uiExercise.planPath,
      // #4142 review fixes: with the switch on, the range runs from the display floor (1 after a
      // planned weight increase), and the tick shows only when last time was at the given weight
      compactFeedback: this.compactFeedback,
      givenWeight_kg: set.logSet.plannedWeight_kg,
    });
  }

  /**
   * #4142 review fixes: the "Last session <weight × reps>" line above an open set's inputs. A
   * benchmark set 1 reads it from its plan path (`getRepRangeDisplay`); with the switch on, every other
   * set reads last session's same set, only in today's or a soft-future workout (on a past one the
   * read behind it can find a LATER workout)
   */
  getOpenSetLastSession(set: UiSet): WeightAndReps | undefined {
    const fromPath = this.getRepRangeDisplay(set).lastSession;
    if (fromPath || !this.compactFeedback || set.logSet.setType === SetType.Regular1st) {
      return fromPath;
    }
    return this.getLastSessionSameSet(set);
  }

  // last session's set at this set's index, in today's or a soft-future workout only
  private getLastSessionSameSet(set: UiSet): WeightAndReps | undefined {
    if (!this.isEditableMode()) {
      return undefined;
    }
    return getLastSessionSet(set.uiExercise.lastWorkoutSetStats(), set.uiExercise.sets.indexOf(set));
  }

  // undefined keeps the field's own placeholder
  getRepsPlaceholder(set: UiSet): TranslatedString | undefined {
    const placeholder = this.getRepRangeDisplay(set).placeholder;
    if (!placeholder) {
      return undefined;
    }
    if (placeholder.floor !== undefined) {
      return LL.workouts.detail.setProgress.repsRangePlaceholder({ min: placeholder.floor, max: placeholder.target });
    }
    return untranslatedString(String(placeholder.target));
  }

  // the range helper decides the landing value on a benchmark set; every other set keeps today's
  getRepsLandingValue(set: UiSet): number | undefined {
    return this.getRepRangeDisplay(set).suggestedReps ?? this.getSuggestedPreselectedRep(set);
  }

  // #4142: the confirmation a completed benchmark set shows, from its own stored data only
  getSetProgression(set: UiSet): SetProgressionResult | undefined {
    if (set.logSet.state !== LogRecordState.Done) {
      return undefined;
    }
    return getSetProgressionResult(this.getSetProgressionInput(set));
  }

  // the stored fields every display rule of a logged set reads
  private getSetProgressionInput(set: UiSet): SetProgressionInput {
    return {
      setType: set.logSet.setType,
      setSuccess: set.logSet.setSuccess,
      stats: set.logSet.stats,
      plannedWeight_kg: set.logSet.plannedWeight_kg,
      planPathBackup: set.logSet.planPathBackup,
      deloadDeclined: set.logSet.deloadDeclined,
      noGainOverLastSession: set.logSet.noGainOverLastSession,
    };
  }

  // #6014: whether this completed benchmark set's confirmation offers Start Rest Timer
  isRestTimerStartOffered(set: UiSet): boolean {
    const allSets: Log_WorkoutSet[] = [];
    for (const uie of this.exercises ?? []) {
      for (const s of uie.sets) {
        allSets.push(s.logSet);
      }
    }
    return shouldOfferRestTimerStart({
      platformAllowed: this.restTimer.isAllowedOnThisPlatform(),
      isCurrentWorkout: this.mode === WorkoutLoadMode.Current,
      isCoachManaging: this.isCoachManaging,
      set: set.logSet,
      allSets,
    });
  }

  /** the button label with the exercise's rest time, e.g. "Start Rest Timer · 2:00" */
  getStartRestTimerText(set: UiSet): TranslatedString {
    const seconds = getRestSecondsForExercise(set.uiExercise.exercise, this.declaredState, this.fitnessState);
    return LL.workouts.detail.setProgress.startRestTimer({ duration: formatRestDuration(seconds) });
  }

  // #6014: start the rest timer from full, replacing one already running. the
  // click must not reach the completed-set row, whose own click reopens the set
  onClickStartRestTimer(event: Event, set: UiSet): void {
    event.stopPropagation();
    // #6575: the compact button takes no taps while a rest counts down (it ignores pointer events, and a
    // key press still reaches here), so it must not restart the rest
    if (this.compactFeedback && this.getRestTimerButtonState(set).counting) {
      return;
    }
    const exercise = set.uiExercise.exercise;
    const seconds = getRestSecondsForExercise(exercise, this.declaredState, this.fitnessState);
    void this.restTimer.start(seconds, { exerciseName: exercise.name, workoutId: this.logWorkout._id });
    // #6575: a finger or mouse tap leaves no focus ring over the countdown (owner's mock-up A); a key
    // press (detail 0) keeps focus where a keyboard user expects it
    if (this.compactFeedback && event instanceof MouseEvent && event.detail > 0 && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  /** the translated headline of a completed benchmark set's confirmation, e.g. "You progressed!" */
  getProgressionHeadlineText(progression: SetProgressionResult): TranslatedString {
    switch (progression.headline) {
      case SetProgressionHeadline.TargetHit:
        return LL.workouts.detail.setProgress.targetHitTitle();
      case SetProgressionHeadline.Progressed:
        return LL.workouts.detail.setProgress.progressedTitle();
      default:
        return LL.workouts.detail.setProgress.setLoggedTitle();
    }
  }

  /** the confirmation's "9 reps at 50 kg" line, with the weight in the user's unit */
  getRepsAtWeightText(done: WeightAndReps): TranslatedString {
    return LL.workouts.detail.setProgress.repsAtWeight({ reps: done.reps, weight: this.formatSetWeight(done.weight_kg, done.weight_unit) });
  }

  // #4142 compact feedback: true when set 1 shows the folded row and unfolded detail instead of the
  // v1.34 panel. the set must carry a progression result, and the switch must be on
  isCompactSetRow(set: UiSet): boolean {
    return this.compactFeedback && !!this.getSetProgression(set);
  }

  /** a done set without set 1's result (set 2, 3…) is drawn like set 1's folded "Set logged" row while the switch is on */
  isCompactFollowupRow(set: UiSet): boolean {
    return this.compactFeedback && set.logSet.state === LogRecordState.Done && !this.getSetProgression(set);
  }

  /** #4142 review fixes: with the switch on, every done set is one line that unfolds its details under it */
  isCompactDoneRow(set: UiSet): boolean {
    return this.compactFeedback && set.logSet.state === LogRecordState.Done;
  }

  /** set 1's folded row, from its stored data; undefined when it has no progression result */
  getCompactSetRow(set: UiSet): CompactSetRowDisplay | undefined {
    return this.isCompactSetRow(set) ? getCompactSetRowDisplay(this.getSetProgressionInput(set)) : undefined;
  }

  /** a done set's mark: set 1's from its stored data (#4142 review fixes, R5.1); none on every other set */
  getSetMark(set: UiSet): SetOutcomeMark {
    return this.getCompactSetRow(set)?.mark ?? SetOutcomeMark.None;
  }

  /** set 1's unfolded details, from its stored data; undefined when it has no progression result */
  getSetDetail(set: UiSet): SetDetailDisplay | undefined {
    return this.isCompactSetRow(set) ? getSetDetailDisplay(this.getSetProgressionInput(set), set.logSet.completedTime_utc) : undefined;
  }

  /** a later set's unfolded details: last session's same set, the "+N" against it, and when it was logged */
  getLaterSetDetail(set: UiSet): LaterSetComparison & { loggedAt?: DateTime } {
    const done = readWeightAndReps(set.logSet.stats);
    const detail: LaterSetComparison & { loggedAt?: DateTime } = compareWithLastSessionSet(done, this.getLastSessionSameSet(set));
    const loggedAt = getSetLoggedTime(set.logSet.completedTime_utc);
    if (loggedAt) {
      detail.loggedAt = loggedAt;
    }
    return detail;
  }

  /** a reopened done set's mark in its open header, the same as on its folded row (#6694); undefined while the set is not logged */
  getOpenSetOutcome(set: UiSet): SetOutcomeMark | undefined {
    return set.logSet.state === LogRecordState.Done ? this.getSetMark(set) : undefined;
  }

  /** the badge colour of a mark: green, orange, red, or the neutral grey */
  getMarkTone(mark: SetOutcomeMark): StatusIconTone {
    switch (mark) {
      case SetOutcomeMark.Success:
        return StatusIconTone.Success;
      case SetOutcomeMark.Same:
        return StatusIconTone.Warning;
      case SetOutcomeMark.Fewer:
        return StatusIconTone.Assertive;
      default:
        return StatusIconTone.Neutral;
    }
  }

  /** the colour of today's dot on set 1's details bar: green when the set reached the target range, grey otherwise */
  getMarkBarTone(mark: SetOutcomeMark): RepProgressBarTone {
    return mark === SetOutcomeMark.Success ? RepProgressBarTone.Progress : RepProgressBarTone.Neutral;
  }

  /** the badge icon of a mark: a minus for the same reps, a down arrow for fewer, a check otherwise */
  getMarkIcon(mark: SetOutcomeMark): string {
    switch (mark) {
      case SetOutcomeMark.Same:
        return 'remove';
      case SetOutcomeMark.Fewer:
        return 'arrow-down';
      default:
        return 'checkmark';
    }
  }

  /** the mark's chip text: "Target hit", "Same as last time" or "Fewer reps"; undefined without a mark */
  getMarkChipText(mark: SetOutcomeMark): TranslatedString | undefined {
    switch (mark) {
      case SetOutcomeMark.Success:
        return LL.workouts.detail.setProgress.outcomeTargetHit();
      case SetOutcomeMark.Same:
        return LL.workouts.detail.setProgress.outcomeSame();
      case SetOutcomeMark.Fewer:
        return LL.workouts.detail.setProgress.outcomeFewer();
      default:
        return undefined;
    }
  }

  /** "Target 7–12 reps", or "Target 12 reps" without a range */
  getTargetRangeText(target: { floor?: number; target: number }): TranslatedString {
    return target.floor !== undefined
      ? LL.workouts.detail.setProgress.targetRangeReps({ floor: target.floor, target: target.target })
      : LL.workouts.detail.setProgress.targetReps({ target: target.target });
  }

  /** the log time in the user's 12/24-hour choice */
  formatLoggedTime(completedAt: DateTime): string {
    return formatTime(completedAt, this.settings);
  }

  /** what a screen reader reads for a folded row's toggle: the set, its mark and its weight × reps */
  getCompactRowAccessibleName(set: UiSet, setNumber: number): string {
    const parts: string[] = [LL.workouts.detail.setOrdinal({ number: setNumber })];
    const chip = this.getMarkChipText(this.getSetMark(set));
    if (chip) {
      parts.push(chip);
    }
    parts.push(this.getActualSetText(set));
    return parts.join(', ');
  }

  /** folds or unfolds a done set's details; page memory only */
  toggleSetProgressFold(set: UiSet) {
    set.progressUnfolded.update(unfolded => !unfolded);
  }

  /** the dismissed-message dot after "Set N" on a folded row (#4142 review fixes, R1.3) */
  hasDismissedStatusMessage(set: UiSet): boolean {
    return getHiddenStatusMessageCount(set.logSet) > 0;
  }

  // "Edit Set" shows wherever reopenSet acts: today's and past workouts, not while a coach manages it
  canEditSetFromDetail(): boolean {
    return this.mode <= WorkoutLoadMode.Current && !this.isCoachManaging;
  }

  // "Edit Set" reopens the set; the click must not also fold the row
  onClickEditSet(event: Event, e: UiExercise, setIndex: number) {
    event.stopPropagation();
    this.reopenSet(e, setIndex);
  }

  // the bell on a compact row must not reach the row's toggle
  onClickCompactBell(event: Event, set: UiSet) {
    event.stopPropagation();
    this.onNotificationBellClick(set);
  }

  /** the tinted Rest Timer button's label: "Rest Timer · 2:00", or the time left while a rest counts down (#6575) */
  getRestTimerButtonText(set: UiSet): TranslatedString {
    return LL.workouts.detail.setProgress.restTimer({ duration: formatRestDuration(this.getRestTimerButtonState(set).shownSeconds) });
  }

  /** whether the tinted Rest Timer button sits directly below this done set's block, folded or unfolded
   * (#4142 review fixes; #6575 for later sets), and only on the latest logged set */
  isCompactRestTimerBelowRow(set: UiSet): boolean {
    return this.isCompactDoneRow(set) && this.isRestTimerStartOffered(set);
  }

  /** whether the tinted Rest Timer button counts down, so it takes no taps (#6575) */
  isRestTimerCounting(set: UiSet): boolean {
    return this.getRestTimerButtonState(set).counting;
  }

  /** the countdown fill's width on the tinted Rest Timer button, in percent (#6575) */
  getRestTimerFillPercent(set: UiSet): number {
    return this.getRestTimerButtonState(set).fillFraction * 100;
  }

  // reads the timer's signals, so the button re-renders on every tick
  private getRestTimerButtonState(set: UiSet) {
    return getRestTimerButtonState({
      restSeconds: getRestSecondsForExercise(set.uiExercise.exercise, this.declaredState, this.fitnessState),
      remainingSeconds: this.restTimer.remainingSeconds(),
      totalSeconds: this.restTimer.totalSeconds(),
      isRunning: this.restTimer.isRunning(),
      isExpired: this.restTimer.isExpired(),
    });
  }

  /** prints a weight and a rep count as "50 kg × 7", for the "Last session" line */
  formatWeightAndReps(value: WeightAndReps): string {
    return `${this.formatSetWeight(value.weight_kg, value.weight_unit)} × ${value.reps}`;
  }

  async loadWorkoutForDate(date: string) {
    const dayDate = DateTime.fromISO(date);

    // find next workout in next 7 days
    const futureEntries = await this.userDb.getLogEntriesInRangeAsync(
      dayDate.plus({ days: 1 }).startOf('day'),
      dayDate.plus({ days: 7 }).endOf('day'),
      this.docWatcher,
      { inLogEntriesTimeZone: true }
    );
    const nextWorkout = futureEntries.find(entry =>
      entry.type === LogRecordType.Workout && entry.state !== LogRecordState.Skipped
    ) as Log_Workout;

    if (!nextWorkout) {
      return this.auth.failPageGuardAsync(`No next workout found to use as template!`);
    }

    // create in-memory workout
    this.plan = await this.userDb.loadPlanAsync(this.docWatcher);
    if (this.plan.workouts.length === 0) {
      return this.auth.failPageGuardAsync(`No workouts defined in plan`);
    }

    // create workout at the same time as next workout but on the specified date
    const nextWorkoutTimeLocal = getEntryLocalTimeInDesiredTimeZone(nextWorkout, this.debug.getNowLocal());
    const workoutTime = dayDate.set({
      hour: nextWorkoutTimeLocal.hour,
      minute: nextWorkoutTimeLocal.minute,
      second: nextWorkoutTimeLocal.second
    });

    this.logWorkout = this.planService.createWorkoutEntry(workoutTime.toLocal());
    this.isInMemoryWorkout = true;
    // in-memory workouts created for today are always in Current mode
    this.mode = WorkoutLoadMode.Current;
    this.updateViewOnlyInfoMessage();
    this.cdRef.markForCheck();
  }


  async ionViewCanEnter() {
    /* When returning from child pages, don't do anything. Reloading the Log_Workout would break the UI, because we want to keep the UI as it was, and it refers to the old Log_Workout object. */
    if (this.exercises) {
      return;
    }

    // if workout or plan doc changed via sync, rebuild UI exercise list from the updated canonical.
    // Workout: deepOverwriteCouchSafe replaces nested arrays (exercises[]), orphaning
    // UiExercise.logExercise references. reactiveRefreshAsync re-links them.
    // Plan: subslot exercise_id or structure may change on another device; without rebuild,
    // UiExercise clones go stale and exercise switch / set conclusion can conflict.
    // Note: DatabaseWrapper's realtime-changes handler short-circuits on
    // `doc._rev !== cachedEntry.expectedRev` (line ~1738) for local writes — expectedRev is
    // updated to the saved rev synchronously, so the change feed event never reaches
    // notifyDocWatchers. This callback fires only for genuine cross-device changes pulled in
    // via replication. See design-docs/reactive-sync.md 'When ionViewWillEnter is required as
    // a resync point' for the local-edit propagation pattern used by training / edit-workouts.
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((changes) => {
        if (this.viewDestroyed) return undefined;
        const workoutChanged = this.logWorkout?._id && changes.some(c => c.id === this.logWorkout._id);
        const planChanged = this.plan?._id && changes.some(c => c.id === this.plan._id);
        // Also reload when any exercise doc registered with this watcher changes —
        // primarily catches cross-device exercise edits (e.g. a coach-channel UDE
        // flipped `virtuallyDeleted` on the coach side: the pulled change reaches the
        // local DB via replication and `notifyDocWatchers` fires from the pulled-change
        // handler in `database-wrapper.ts`). On reload `reactiveRefreshAsync` re-runs
        // `skipAvoidedExercises` so the subSlot rebinds to a live ancestor via the
        // lineage walker. Same-session local edits (picker → Delete) don't actually
        // reach this watcher — after a local save, `cachedEntry.expectedRev` already
        // matches the saved `_rev`, so the realtime-changes handler short-circuits
        // before `notifyDocWatchers`. For same-session updates we rely on the picker's
        // `onExerciseSwitched` returnCallback path, not the docWatcher.
        const exerciseChanged = changes.some(c => c.id.startsWith('exercise/'));
        if (workoutChanged || planChanged || exerciseChanged) {
          // #4358 / df-e3e98841308e: RETURN the promise so this page is on the
          // DocWatcher single-flight + delivery contract. It used to be the one
          // page kept off it, because the reactive rebuild raised the loading
          // shell and single-flight only made that blanking land later, mid-edit.
          // `reactiveRefreshAsync` no longer touches `loading` — it swaps the
          // rebuilt data in underneath — so joining the contract is now safe.
          return this.reactiveRefreshAsync();
        }
        // no relevant doc changed: repaint (menu/state) but start no async work,
        // so return nothing — the watcher treats a non-promise result as finished.
        this.cdRef.markForCheck();
        return undefined;
      }, { label: 'workout-detail' });
    }

    this.isCoachManaging = !!this.userDb.getCoachManagingClient();

    // this page accepts workout id either as log entry id (for scheduled/done workouts), or as a number (then it just displays the workout template from the fitness plan)
    let id = this.navParams.data.id;
    let date = this.navParams.data.date;

    if (id) {
      try {
        if (id.startsWith('log/')) {
          this.logWorkout = await this.userDb.getOneLogEntryAsync<Log_Workout>(id, this.docWatcher);
          // if the entry does not exist (deleted, or replaced by a database restore), refuse the
          // page here through the catch below, the same way an unknown plan workout is refused.
          // The read answers `undefined` rather than throwing, and letting that reach
          // getWorkoutModeAsync used to open an alert inside this hook — which cs-nav waits on
          // with no time limit, so a boot restoring this page stayed wedged until the nav-restore
          // watchdog fired (df-fa1a97e1bc02, #5486).
          if (!this.logWorkout) {
            throw Error(`Workout log entry not found`);
          }
          // determine the mode if it wasn't determined already
          if (this.mode === WorkoutLoadMode.Unknown) {
            this.mode = await this.getWorkoutModeAsync();
            this.updateViewOnlyInfoMessage();
            this.cdRef.markForCheck();
          }
          // Coach Dashboard write site (#3141): when a coach in manage mode opens a
          // strength-test workout, stamp `coachReviewedAtUtc` once. "Opening = reviewing"
          // — the strength-test action item on the dashboard clears on the next refresh.
          // Separate single-field write, straight to the database rather than through
          // `saveWorkoutAsync` — the method's own comment says why, and it is the reason
          // the save-sites tripwire carries an exception for it. Failures are silent —
          // the dashboard will simply keep the item visible until the next coach visit
          // retries.
          await this.stampCoachReviewedIfApplicableAsync();
        } else {
          let planReference = parseInt(id);
          let plan = this.navParams.data.local?.plan;
          if (plan) {
            this.plan = plan;
          } else {
            this.plan = await this.userDb.loadPlanAsync(this.docWatcher);
          }
          if (!this.plan.workouts[planReference]) {
            throw Error(`Unknown plan workout #${planReference}`);
          }
          // #5013: pass the LOCAL time, never `.toUTC()` — the creator takes the entry's offset
          // from this argument. See design-docs/sync-integrity.md, "A Log Entry's Two Time
          // Fields Must Always Move Together".
          this.logWorkout = this.planService.createWorkoutEntry(this.debug.getNowLocal());
          // Template mode: explicitly set planReference since user requested specific workout
          this.workoutLoader.setWorkoutPlanReference(this.logWorkout, planReference);
          this.mode = WorkoutLoadMode.Template;
          this.updateViewOnlyInfoMessage();
          this.cdRef.markForCheck();
        }
      } catch (err) {
        return this.auth.failPageGuardAsync(`Unknown workout id: '${id}'`, { cause: err });
      }
    } else if (date) {
      // date-only parameter - find next workout and create in-memory workout
      try {
        await this.loadWorkoutForDate(date);
      } catch (err) {
        return this.auth.failPageGuardAsync(`Failed to create workout: ${err.message}`);
      }
    } else {
      return this.auth.failPageGuardAsync(`Manually created workouts are not supported yet!`);
    }
  }

  async ionViewWillEnter() {
    // measure full page-load time for the workout_detail_load_time telemetry event.
    // #4242: the heavy exercise build is deferred to ionViewDidEnter, so store the
    // start on the instance and emit the event there, after the build.
    this.loadStartMs = performance.now();

    // Initialize translated strings
    this.setInstructionsTextInputReps = LL.workouts.instructions.fillInReps();
    this.setInstructionsTextInputStrength = LL.workouts.instructions.fillInWeight();
    this.setInstructionsTextInputRepsAndStrength = LL.workouts.instructions.fillInWeightAndReps();

    // auto-hide basic set instructions for experienced users (>= 10 completed workouts)
    // default to experienced (hide help text) and update if needed — avoids blocking page load
    // on cold start when PouchDB view indexes aren't built yet
    this.showSetInstructions = false;
    this.showBwHelpText = false;
    void Promise.all([
      this.userDb.hasCompletedNWorkoutsAsync(10),
      this.userDb.hasCompletedNBwWorkoutsAsync(10),
    ]).then(([isExperienced, isBwExperienced]) => {
      this.showSetInstructions = !isExperienced;
      this.showBwHelpText = !isBwExperienced;
      this.cdRef.markForCheck();
    }).catch(err => {
      // The two fields keep the "experienced" default set above, which is the
      // conservative outcome: an unavailable read must not decide that someone
      // is a beginner and show them the basic-set help text they had already
      // grown out of. Previously an unavailable read returned an empty result,
      // which made both checks answer `false` and did exactly that, silently —
      // this catch only ever saw a genuine throw. Report it rather than
      // console-logging it, per CLAUDE.md's must-surface rule.
      this.logging.trackSilentProblem('workout-detail: experienced check failed', {
        databaseUnavailable: isDatabaseUnavailable(err),
        error: describeError(err),
      });
    });

    this.watchCompactFeedbackSwitch();

    this.fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);
    this.declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
    // when returning from  child pages
    if (!this.exercises) {
      // determine the mode if it wasn't determined already
      if (this.mode === WorkoutLoadMode.Unknown) {
        this.mode = await this.getWorkoutModeAsync();
        this.updateViewOnlyInfoMessage();
        this.cdRef.markForCheck();
      }

      // if logWorkout is still undefined after getWorkoutModeAsync (which handles the alert), return early
      if (!this.logWorkout) {
        return;
      }

      // #4242: load only the LIGHT metadata here, DURING the slide, so the header
      // (title + options menu) shows immediately. The HEAVY exercise-list build
      // (buildWorkoutExercisesAsync) froze the enter animation when it ran
      // mid-slide, so it is deferred to ionViewDidEnter, after the slide. `loading`
      // stays true (its default) so the content area holds its shell until then.
      // The first-entry follow-ups (test-workout explainer, orphaned-autoregulation
      // heal, load-time telemetry) move there too: the heal and the telemetry read
      // this.exercises, which does not exist yet, and the explainer belongs beside
      // them because it is part of the same once-per-entry block.
      await this.loadWorkoutMetaAsync();

      // if this is the user's current/active workout
      if (this.mode === WorkoutLoadMode.Current) {
        this.telemetry.trackEvent('active_workout_opened', {});
      }
      return;
    }
  }

  // #4242: builds the deferred exercise list on first entry, after the page-enter
  // slide, then reveals the page. Called once from ionViewDidEnter (gated by
  // contentEntered). Hardened so a build failure can never strand the page on a
  // permanent spinner — the finally always reveals.
  private async buildContentOnFirstEntryAsync() {
    // if getWorkoutModeAsync dismissed the page via an alert, logWorkout is
    // undefined and the page is being popped — leave the loading shell up rather
    // than revealing an empty exercise list behind the alert.
    if (!this.logWorkout) {
      return;
    }

    // this method runs even when ionViewWillEnter — which loads all three of these —
    // never finished, so check they are really here before building anything.
    // See design-docs/navigation.md, "Check the light half's data actually arrived".
    const missingEntryData = namesOfMissingWorkoutLoadingDocuments({
      plan: this.plan,
      fitnessState: this.fitnessState,
      declaredState: this.declaredState,
    });
    if (missingEntryData.length > 0) {
      // if the page has already been replaced, do nothing at all: cs-nav reported this
      // same event moments ago, nobody is looking, and popAsync would act on whichever
      // page replaced this one. Reasoning in design-docs/navigation.md, same section.
      if (this.viewDestroyed) {
        return;
      }

      // the page is still on screen, so the user is waiting on a workout that cannot
      // be built. Report it, then tell them in their own language and take them back.
      this.logging.trackSilentProblem('workout-detail-entry-data-missing', {
        location: 'buildContentOnFirstEntryAsync',
        missing: missingEntryData.join(','),
        mode: this.mode || 'none',
        isInMemoryWorkout: this.isInMemoryWorkout,
      });
      await this.alertCtrl.showAlertAsync({
        title: LL.workouts.errors.unableToLoad(),
        message: LL.workouts.errors.unableToLoadMessage(),
        buttons: [{
          text: LL.common.buttons.ok(),
          role: AlertButtonRole.Primary,
        }]
      });
      // pop after the alert closes HOWEVER it closed — the OK button, a tap on the
      // backdrop, or browser Back. Popping from the button's own result callback
      // instead would cover only the OK press: a backdrop dismiss counts as a cancel
      // and runs no callback, which would strand the user on the loading shell this
      // branch deliberately leaves up. Skipped if the page went away while the alert
      // was open, for the same reason as the viewDestroyed return above.
      if (!this.viewDestroyed) {
        void this.navCtrl.popAsync();
      }
      // returned BEFORE the try on purpose: the finally below would otherwise clear
      // the loading shell and reveal an empty exercise list behind the alert, the
      // same reason the !this.logWorkout return above sits where it does.
      return;
    }

    try {
      // build the list now, unless a reactive docWatcher reload already built it
      // during the slide. buildWorkoutExercisesAsync serialises concurrent builds,
      // so if a reload is still mid-flight this awaits it instead of interleaving
      // the serial equipment loop (#3162); either way this.exercises is populated.
      if (!this.exercises) {
        await this.buildWorkoutExercisesAsync();
      }

      // first-entry follow-ups, run once now that the exercise list exists —
      // whether we built it or a reactive reload did.
      // #4060: on the first outing of each workout of the split, show the "Test
      // workout to customize your program" header card so the reduced calibration
      // volume doesn't read as a broken program.
      void this.updateTestWorkoutExplainerAsync();
      // heal stuck Resolve cards left behind by earlier app versions —
      // volume autoregulation (#4052) and plateau (#4360)
      this.healOrphanedAutoregulationPending();
      this.healOrphanedPlateauDetected();
      // #6428: the exercise editor's changes, for the client's note and the overwrite report
      void this.loadExerciseChangesAsync();
      // #6295: save equipment steps the exercise build replaced in memory
      void this.persistHealedCustomIncrementsAsync();

      // surface page-load time in analytics so slow first-time loads (e.g. while
      // the post-login backfill is still draining the SQLite connection) are
      // visible in the field without needing a local repro. the build may throw
      // before assigning this.exercises; the field's declared (non-optional) type
      // doesn't reflect that, so read the count defensively.
      const exerciseCount = (this.exercises as UiExercise[] | undefined)?.length ?? 0;
      this.telemetry.trackEvent('workout_detail_load_time', {
        total_ms: Math.round(performance.now() - this.loadStartMs),
        exercise_count: exerciseCount,
      });
    } catch (err) {
      // a build failure reports and pops back (a workout with no exercise list is
      // useless). popAsync is fire-and-forget, so viewDestroyed is NOT set here —
      // the finally still reveals the page for the instant before the pop lands,
      // which is harmless. This pop-on-failure is FIRST-ENTRY only: a reactive
      // refresh (reactiveRefreshAsync) already has a rendered page, so it reports
      // and keeps it rather than throwing the user out mid-workout.
      this.logging.trackAbnormalException(err, untranslatedString('Something went wrong! Please, try again later or report the issue.'));
      void this.navCtrl.popAsync();
    } finally {
      // always reveal the page (clear the loading shell, show the list) so a build
      // failure can never strand it on a permanent spinner. guarded so we don't
      // touch a torn-down view.
      if (!this.viewDestroyed) {
        this.contentEntered = true;
        this.loading = false;
        this.safeMarkForCheck(this.cdRef);
      }
    }
  }

  async ionViewDidEnter() {

    // #4242: on first entry, build the deferred exercise list now — ionViewDidEnter
    // fires AFTER the page-enter slide, so the heavy build runs while the page is
    // stationary instead of jamming the animation (ionViewWillEnter loaded only the
    // light header). Kept in its own method to flatten this hook and separate the
    // first-entry build from the return-from-child reconcile below.
    if (!this.contentEntered) {
      await this.buildContentOnFirstEntryAsync();
    }
    // #4242: the first-entry build above is awaited, so the view may have been
    // torn down (fast back-nav during the build) before the reconcile block runs.
    // Bail out here rather than let its raw markForCheck / signal writes fire on a
    // dead view (NG0911). On re-entry there is no preceding await, so this is a
    // cheap no-op.
    if (this.viewDestroyed) return;

    // when returning from  child pages
    if (this.exercises) {
      // check for changed plate configs
      for (let ie = 0; ie < this.exercises.length; ie++) {
        let uie = this.exercises[ie];
        uie.checkEquipment(this.fitnessState, this.userDb, this.settings.locale, this.logging);
        this.cdRef.markForCheck();
      }

      // check for changed myorep configs...

      // for each exercise
      for (let ie = 0; ie < this.exercises.length; ie++) {
        let uie = this.exercises[ie];
        // check if myoreps setting has changed since sets were built
        let currentUseMyoReps = this.workoutLoader.willUseMyoReps(uie, this.declaredState, this.fitnessState);

        let needsRebuild = false;
        // if myoreps setting changed
        if (uie.builtWithMyoReps !== currentUseMyoReps) {
          // we need to rebuild the sets if possible
          if (uie.sets[0] && uie.sets[0].logSet && uie.sets[0].logSet.state !== LogRecordState.Done) {
            needsRebuild = true;
          }
        }
        let usesRMTesting = exerciseUsesRMTesting(uie.exercise, this.declaredState, this.fitnessState);
        if (uie.sets[0] && uie.sets[0].logSet && uie.sets[0].logSet.state !== LogRecordState.Done) {
          if ((uie.sets[0].logSet.setType === SetType.RMTest && !usesRMTesting) ||
            (uie.sets[0].logSet.setType === SetType.NonRMTest && usesRMTesting && uie.planSubSlot.exercisePaths.every(p => !p.nextWorkoutNonRMTestRequired))) {
            uie.resetProgress = true;
            this.rebuildPathsIfNeeded(uie);
            delete uie.resetProgress;
            needsRebuild = true;
          }
        }
        if (needsRebuild) {
          await this.rebuildSets(uie);
          // a rebuild keeps the logged sets, so the first set can still be a skipped one here;
          // `openSet` refuses a concluded set, so do not ask it to
          if (uie.expanded() && !(uie.sets[0] && this.isSetCompleted(uie.sets[0]))) {
            await this.startFirstSet(uie);
          }
        }
      }

      if (!this.logWorkout) {
        return;
      }

      if (this.isEditableMode()) {

        // try to restore the saved UI state from before (if present)
        let previousStateRestored = this.restoreUIState();

        // if the saved state is not present, or not applicable
        if (!previousStateRestored) {
          // skip if a set is already open (returning from child page)
          const hasOpenSet = this.exercises.some(e => e.openSetIndex() >= 0);
          if (!hasOpenSet) {
            // find the first exercise that has _something_ to open
            for (let ie = 0; ie < this.exercises.length; ie++) {
              let uie = this.exercises[ie];
              let setToDoIndex = uie.sets.findIndex(s => s.logSet.state !== LogRecordState.Done && s.logSet.state !== LogRecordState.Skipped);
              // if any set is not done and not skipped
              if (setToDoIndex > -1) {
                await this.openSet(uie, setToDoIndex, 'open');
                break;
              }
            }
          }
        }
      }
    }
  }


  // check in which mode the page should be displayed
  async getWorkoutModeAsync(): Promise<WorkoutLoadMode> {
    // defensive check in case logWorkout is not initialized
    if (!this.logWorkout) {
      // try one more time to load the workout
      try {
        if (this.navParams?.data?.id) {
          const id = this.navParams.data.id;
          if (id.startsWith('log/')) {
            this.logWorkout = await this.userDb.getOneLogEntryAsync<Log_Workout>(id, this.docWatcher);
          }
        } else if (this.navParams?.data?.date) {
          await this.loadWorkoutForDate(this.navParams.data.date);
        }
      } catch (err) {
        // report the cause here: the report below only says the workout is still missing
        this.logging.trackSilentProblem('WorkoutDetailRetryLoadFailed', { detail: describeError(err) });
      }

      // if still no workout after retry, log and show alert
      if (!this.logWorkout) {
        this.logging.trackSilentProblem('workout-detail-undefined-logworkout', {
          location: 'getWorkoutModeAsync',
          hasExercises: !!this.exercises,
          navParamsId: this.navParams?.data?.id || 'none',
          navParamsDate: this.navParams?.data?.date || 'none',
          isInMemoryWorkout: this.isInMemoryWorkout,
          mode: this.mode || 'none',
          retriedLoading: true
        });

        // show the alert WITHOUT awaiting it: this method runs inside the page's entry hooks,
        // and the alert answers only when a person closes it, so awaiting it held the whole
        // navigation open until then (df-fa1a97e1bc02). pop after it closes HOWEVER it closed —
        // OK, backdrop or browser Back — unless the page has gone away meanwhile, in which case
        // a pop would act on whichever page replaced this one. Same shape as
        // buildContentOnFirstEntryAsync's missing-entry-data alert.
        void this.alertCtrl.showAlertAsync({
          title: LL.workouts.errors.unableToLoad(),
          message: LL.workouts.errors.unableToLoadMessage(),
          buttons: [{
            text: LL.common.buttons.ok(),
            role: AlertButtonRole.Primary,
          }]
        }).then(() => {
          if (!this.viewDestroyed) {
            void this.navCtrl.popAsync();
          }
        }).catch(err => {
          // nobody awaits this chain, so a failure to show the alert must be reported here
          this.logging.trackAbnormalException(err);
        });

        // default to past mode to avoid further errors
        return WorkoutLoadMode.Past;
      }
    }

    // use shared service for core mode calculation with context
    const nowLocal = this.debug.getNowLocal();
    const result = await this.workoutLoader.getWorkoutModeWithContextAsync(
      this.logWorkout,
      this.userDb,
      nowLocal,
      this.docWatcher
    );

    // if SoftFuture with in-progress workout, set up pending workout warning using context from the result
    // only show warning if the pending workout was actually started (Incomplete), not just scheduled
    if (result.mode === WorkoutLoadMode.SoftFuture && result.currentWorkout && result.currentWorkout.state === LogRecordState.Incomplete) {
      const todayDay = nowLocal.startOf('day');
      const currentWorkoutDay = getEntryLocalTimeInDesiredTimeZone(result.currentWorkout, nowLocal).startOf('day');

      this.previousWorkouts = result.workoutsBetween;
      this.pendingWorkoutDateLabel = formatRelativeDay(todayDay, currentWorkoutDay);
      this.pendingWorkoutDateISO = currentWorkoutDay.toISODate();
      this.showPendingWorkoutWarning = true;
      this.cdRef.markForCheck();
    }

    return result.mode;
  }

  private autofocusTimeoutId: ReturnType<typeof setTimeout> = null;
  
  ngAfterContentChecked() {
    if (this.nextAutofocusElementId && !this.autofocusTimeoutId) {
      if (isE2ETest()) {
        //return;
      }
      // delay execution of next block by half second
      this.autofocusTimeoutId = setTimeout(() => {
        let el = this.nextAutofocusSubSlotId
          ? document.querySelector(`cs-card.exercise-card[data-sub-slot-id="${CSS.escape(this.nextAutofocusSubSlotId)}"]`)
          : null;
        el = el ?? document.getElementById(this.nextAutofocusElementId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          this.nextAutofocusElementId = undefined;
          this.nextAutofocusSubSlotId = undefined;
        }
        this.autofocusTimeoutId = null;
      }, 500);
    }
  }

  async ionViewDidLeave() {
    this.debug.setPageDebug(null);
  }

  async onExerciseStartClicked(uie: UiExercise) {
    void this.onHeaderClicked(uie);
  }

  async onHeaderClicked(uie: UiExercise) {
    if (uie.expanded() && this.isEditableMode() && uie.sets[0] && uie.sets[0].logSet.state === LogRecordState.Scheduled) {
      await this.startFirstSet(uie);
    }
  }

  onCardExpandedChange(e: UiExercise, expanded: boolean) {
    // if card was expanded, scroll it into view
    if (expanded) {
      this.nextAutofocusElementId = this.getCardElementId(e);
      this.nextAutofocusSubSlotId = e.logExercise?.subSlotId;
    }
  }

  // cs-exercise-list event handlers
  onExerciseListExpandedChange(event: { exercise: UiExercise, expanded: boolean }) {
    event.exercise.expanded.set(event.expanded);
    this.onCardExpandedChange(event.exercise, event.expanded);
    this.cdRef.markForCheck();
  }

  onExerciseListHighlightedChange(event: { exercise: UiExercise, highlighted: boolean }) {
    event.exercise.highlighted.set(event.highlighted);
    this.cdRef.markForCheck();
  }

  onExerciseListMoreOptionsClick(event: { event: Event, exercise: UiExercise }) {
    void this.showExerciseOptionsMenuCollapsed(event.exercise, event.event);
  }

  // #4142: the expanded card's menu button, top right, opens the full exercise menu that the
  // button bar's More button used to open
  onExerciseExpandedMoreClick(event: { event: Event, exercise: UiExercise }) {
    const target = (event.event.target as HTMLElement)?.closest('cs-icon-button') as HTMLElement;
    void this.popupCtrl.createMenu({
      buttons: this.getExerciseMoreMenuButtons(event.exercise),
      targetFromElement: target ?? (event.event.target as HTMLElement),
    });
  }

  onExerciseListHeaderClick(exercise: UiExercise) {
    void this.onHeaderClicked(exercise);
  }

  isExerciseScheduled(e: UiExercise) {
    return e.sets.every(s => s.logSet && (s.logSet.state === LogRecordState.Scheduled));
  }

  isExerciseCompleted(e: UiExercise) {
    return e.sets.every(s => s.logSet && (s.logSet.state === LogRecordState.Done || s.logSet.state === LogRecordState.Skipped));
  }

  isExerciseIncomplete(e: UiExercise) {
    return e.sets.some(s => s.logSet && (s.logSet.state === LogRecordState.Done || s.logSet.state === LogRecordState.Skipped)) &&
      e.sets.some(s => s.logSet && (s.logSet.state === LogRecordState.Incomplete || s.logSet.state === LogRecordState.Scheduled));
  }

  isExerciseSkipped(e: UiExercise) {
    return e.sets.some(s => s.logSet && s.logSet.state === LogRecordState.Skipped);
  }

  // Exercise status badge methods
  ExerciseStatusBadgeType = ExerciseStatusBadgeType;
  BadgeColor = BadgeColor;

  getExerciseStatusBadgeType(e: UiExercise): ExerciseStatusBadgeType {
    if (this.isExerciseCompleted(e)) {
      return ExerciseStatusBadgeType.Complete;
    } else if (this.isExerciseIncomplete(e)) {
      return ExerciseStatusBadgeType.InProgress;
    }
    return ExerciseStatusBadgeType.Number;
  }

  shouldShowMoreOptionsInCollapsed(): boolean {
    // More options button in collapsed view for Past and HardFuture modes
    return this.mode === WorkoutLoadMode.Past ||
           this.mode === WorkoutLoadMode.HardFuture;
  }

  shouldShowExpandArrow(): boolean {
    // Expand arrow for Past and editable modes (Current and SoftFuture)
    return this.mode === WorkoutLoadMode.Past || this.isEditableMode();
  }

  async showExerciseOptionsMenuCollapsed(e: UiExercise, event: Event) {
    event.stopPropagation();

    const buttons: PopupMenuButton[] = [];

    // Always available options
    buttons.push({
      text: LL.workouts.buttons.description(),
      icon: 'logo-youtube',
      handler: () => this.handleExerciseTutorialButton(e)
    });
    buttons.push({
      text: LL.workouts.buttons.stats(),
      icon: 'trending-up',
      handler: () => this.handleExerciseStatsOptionClick(e)
    });
    buttons.push({
      text: LL.workouts.buttons.settings(),
      icon: 'settings',
      handler: () => this.handleExerciseSettingsOptionClick(e)
    });

    // Conditional options
    if (this.shouldShowSkipButtonOption(e)) {
      buttons.push({
        text: LL.workouts.buttons.skipToday(),
        icon: 'redo',
        handler: () => void this.askToSkipExerciseAsync(e)
      });
    }
    if (this.shouldShowSwitchExerciseOption(e)) {
      buttons.push({
        text: LL.workouts.buttons.changeExercise(),
        icon: 'swap-horizontal',
        handler: () => this.handleSwitchExerciseButton(e)
      });
    }
    if (this.shouldShowResetProgressOption(e)) {
      buttons.push({
        text: LL.workouts.buttons.resetProgress(),
        icon: 'refresh',
        handler: () => void this.askToResetProgress(e)
      });
    }

    await this.popupCtrl.createMenu({
      buttons: buttons,
      targetFromElement: event.target as HTMLElement,
    });
  }

  getExerciseMoreMenuButtons(e: UiExercise): PopupMenuButton[] {
    const buttons: PopupMenuButton[] = [];

    // if there's an open set, show set notes option
    if (e.openSetIndex() !== -1) {
      buttons.push({
        text: LL.workouts.buttons.setNote(),
        icon: 'add-notes',
        handler: () => void this.openSetNotePopover(null, e.sets[e.openSetIndex()])
      });
    }

    buttons.push({
      text: LL.workouts.buttons.settings(),
      icon: 'settings',
      handler: () => this.handleExerciseSettingsOptionClick(e)
    });

    // if conditional options apply
    if (this.shouldShowSkipButtonOption(e)) {
      buttons.push({
        text: LL.workouts.buttons.skipToday(),
        icon: 'redo',
        handler: () => void this.askToSkipExerciseAsync(e)
      });
    }
    if (this.shouldShowSwitchExerciseOption(e)) {
      buttons.push({
        text: LL.workouts.buttons.changeExercise(),
        icon: 'swap-horizontal',
        handler: () => this.handleSwitchExerciseButton(e)
      });
    }
    if (this.shouldShowResetProgressOption(e)) {
      buttons.push({
        text: LL.workouts.buttons.resetProgress(),
        icon: 'refresh',
        handler: () => void this.askToResetProgress(e)
      });
    }

    return buttons;
  }

  adjustWeight(
    initialWeight_kg: number,
    mode: 'step_down' | 'step_up' | 'round_down' | 'round' | 'manual',
    config: Equipment,
  ): { weight_kg: number; setup: WeightSetup } {
    return adjustWeight(initialWeight_kg, mode, config);
  }

  async rebuildSets(uie: UiExercise) {
    uie.loading.set(true);
    this.cdRef.markForCheck();

    try {
      this.logging.checkWorkoutExerciseValidity("rebuildSets start", uie.logExercise)
      uie.openSetIndex.set(-1);
      uie.checkEquipment(this.fitnessState, this.userDb, this.settings.locale, this.logging);

      uie.equipmentPreselected = await this.preselectEquipmentIfNeeded(uie);
      this.cdRef.markForCheck();

      // synchronize units from the config
      this.workoutLoader.syncStatUnitFromEquipment(uie, uie.stat);

      // this doesn't change the saved value of tutorialSeen, so we still have the info on who's seen it if we change our mind later
      uie.tutorialSeen = uie.stat.tutorialSeen;

      // Build sets, description, and badges via service
      const context: WorkoutLoadingContext = {
        plan: this.plan,
        fitnessState: this.fitnessState,
        declaredState: this.declaredState,
        nowLocal: this.debug.getNowLocal(),
        locale: this.settings.locale,
        watcher: this.docWatcher,
        currentWorkoutId: this.logWorkout._id,
      };
      this.workoutLoader.rebuildSetsData(uie, context, this.mode as unknown as WorkoutLoadMode);

      this.logging.checkWorkoutExerciseValidity("rebuildSets end", uie.logExercise)
    } finally {
      // always clear the loading flag — a throw between set(true) and set(false)
      // would otherwise leave the in-card spinner stuck (#720 follow-up)
      uie.loading.set(false);
      this.cdRef.markForCheck();
    }
  }

  async reloadUIExercise(uie: UiExercise, context?: WorkoutLoadingContext) {
    // this is just a patch-up for the case when a user opens some broken exercise log
    // (see issue #238)
    if (!uie.planSubSlot) {
      let { parsedSlotId, initialExerciseIndex } = parseSubslotIdAndOffset(uie.logExercise.subSlotId);
      let slotId = 'slot/' + parsedSlotId;
      let subSlot: Plan_SubSlot = {
        slotId,
        startIndex: initialExerciseIndex,
        exercise_id: (await this.exerciseDb.slotForIdAsync(slotId, this.docWatcher)).exercises[initialExerciseIndex].exerciseId,
        exercisePaths: [{ intensityLevel: IntensityLevel.Initial, nextWorkoutSetType: SetType.RMTest }],
      } as Plan_SubSlot;
      uie.planSubSlot = subSlot;
    }

    // build context from page state if not provided
    const ctx: WorkoutLoadingContext = context || {
      plan: this.plan,
      fitnessState: this.fitnessState,
      declaredState: this.declaredState,
      nowLocal: this.debug.getNowLocal(),
      locale: this.settings.locale,
      watcher: this.docWatcher,
      currentWorkoutId: this.logWorkout._id,
    };

    // load exercise data from service - mutates uie directly including set backlinks
    await this.workoutLoader.loadExerciseAsync(uie, ctx, this.mode as unknown as WorkoutLoadMode);

    // UI-only operations that need page context (DB saves, async UI updates)
    uie.loading.set(true);
    this.cdRef.markForCheck();

    try {
      uie.equipmentPreselected = await this.preselectEquipmentIfNeeded(uie);
    } finally {
      uie.loading.set(false);
      this.cdRef.markForCheck();
    }
  }

  // Load last workout info for an exercise (same pattern as exercise-stats.ts:155-167).
  // Fired off the critical path; toggles e.lastWorkoutLoading so the card shows a
  // spinner in the last-workout section until the (potentially slow) query resolves.
  async loadLastWorkoutInfoAsync(e: UiExercise): Promise<void> {
    if (!e.exercise?._id) return;
    e.lastWorkoutLoading.set(true);
    this.cdRef.markForCheck();
    const read = this.loadLastWorkoutInfoImplAsync(e);
    // kept so a first set's Done can wait for the baseline this fills (df-3a4a75620292). The kept
    // copy never rejects: a failure still reaches this method's caller, which reports it.
    const kept = read.then(() => undefined, () => undefined);
    e.lastWorkoutRead = kept;
    try {
      await read;
    } finally {
      // if a newer read started on this card meanwhile (an exercise switch), its spinner stays
      // until that one finishes
      if (e.lastWorkoutRead === kept) {
        e.lastWorkoutLoading.set(false);
      }
      this.cdRef.markForCheck();
    }
  }

  // starts `loadLastWorkoutInfoAsync` off the critical path. A failure is reported, except when the
  // page was torn down meanwhile (back nav, warm-resume): the user can no longer see the "last
  // workout" line either way, so reporting it is just noise.
  private startLastWorkoutRead(e: UiExercise): void {
    void this.loadLastWorkoutInfoAsync(e).catch(err => {
      if (this.viewDestroyed) return;
      this.logging.trackAbnormalException(err, untranslatedString(`loadLastWorkoutInfoAsync failed for ${e.exercise?._id}`));
    });
  }

  // implementation body of loadLastWorkoutInfoAsync — populates the last-workout
  // signals + previousFirstSetStats from the most recent matching session
  private async loadLastWorkoutInfoImplAsync(e: UiExercise): Promise<void> {
    // get all workouts containing this exercise
    // Deliberately its OWN full-history read, not the narrow one the #5176 prescription cap uses.
    // This card walks back for the newest session matching today's intensity, which can be older
    // than the handful of workouts the cap looks at — and it runs off the critical path, where the
    // full read is affordable. Sharing the cap's short list would blank this card for DUP/TUP users.
    // #6447: the card may hold another exercise's attempt (a temporary swap reuses it), and this read
    // only sets the field when it finds one, so start from "nothing to beat"
    delete e.previousAttemptStats;
    // df-f4964c2d2d3c: the same goes for the baseline set 1 is judged against and the "Last workout"
    // row. A permanent switch also reuses the card, and a new exercise with no history would
    // otherwise keep showing, and be judged against, the exercise switched away from.
    delete e.previousFirstSetStats;
    e.lastWorkoutInfo.set(undefined);
    e.lastWorkoutSetsText.set(undefined);
    e.lastWorkoutSets.set([]);
    e.lastWorkoutSetStats.set([]);
    e.lastWorkoutHasNotes.set(false);
    e.lastWorkoutDate.set(undefined);
    const readExerciseId = e.exercise._id;

    const workouts = await this.userDb.getWorkoutsForExerciseAsync(readExerciseId);
    if (workouts.length === 0) return;

    // if using DUP/TUP periodization, filter by matching intensity level
    const currentIntensity = e.planPath?.intensityLevel;
    const useIntensityFilter = currentIntensity && (
      currentIntensity === IntensityLevel.DUPlight ||
      currentIntensity === IntensityLevel.DUPheavy ||
      currentIntensity === IntensityLevel.TUPlight ||
      currentIntensity === IntensityLevel.TUPmedium ||
      currentIntensity === IntensityLevel.TUPheavy
    );

    // find the last workout, optionally filtered by intensity
    let lastWorkout: Log_Workout | undefined;
    let exercise: Log_WorkoutExercise | undefined;
    // #6447: the attempt a first set must beat to be praised, from the newest session where the
    // exercise was really done, so a skipped session is looked past
    let previousAttempt: Log_WorkoutSet | undefined;

    for (let i = workouts.length - 1; i >= 0; i--) {
      const workout = workouts[i];

      // skip the current workout being edited
      if (workout._id === this.logWorkout._id) continue;

      const exercises = this.workoutLoader.getEffectiveExercises(workout, this.plan, PlanMerge.AsStored);
      // every entry of this exercise in that workout, not just the first (#5507)
      const ex = combinedEntryForExercise(exercises, e.exercise._id);
      if (!ex || ex.sets.length === 0) continue;

      // if filtering by intensity, check if it matches
      if (useIntensityFilter && ex.pathLevel !== currentIntensity) continue;

      // the newest matching workout is the "last workout", whatever happened in it
      if (!lastWorkout) {
        lastWorkout = workout;
        exercise = ex;
      }
      // if the exercise was really done here, this is the attempt to beat; otherwise look further back
      previousAttempt = previousSessionBestAttempt(ex.sets);
      if (previousAttempt) break;
    }

    // if the card was switched to another exercise while this read ran, the attempt is not its own
    if (previousAttempt && e.exercise?._id === readExerciseId) {
      e.previousAttemptStats = previousAttempt.stats;
    }

    if (!lastWorkout || !exercise) return;
    // the same for everything below: a newer read for the card's current exercise fills it instead
    if (e.exercise?._id !== readExerciseId) return;

    // the previous workout's strongest successful set is what this session's first set is judged
    // against (df-808f4b93dc06: its LAST one let a lighter closing set hide a collapse)
    const highWaterSet = previousSessionBaselineSet(exercise.sets);
    if (highWaterSet) {
      e.previousFirstSetStats = highWaterSet.stats;
    }

    const now_local = this.debug.getNowLocal();
    const workoutDate = DateTime.fromISO(lastWorkout.time_utc).toLocal();
    const dateLabel = upperFirst(formatRelativeDay(now_local, workoutDate));
    const setsText = formatExerciseSets(exercise, this.settings.locale, false);

    // store the summary string
    e.lastWorkoutInfo.set(`${dateLabel} : ${setsText}`);
    e.lastWorkoutSetsText.set(formatDoneSets(exercise, this.settings.locale));

    // store detailed sets data for popover/expanded view
    const locale = this.settings.locale;
    const sets = exercise.sets.map((s, i) => ({
      setText: LL.exerciseStats.setNumber({ number: i + 1 }),
      dataText: s.state === LogRecordState.Skipped
        ? LL.workouts.detail.exerciseHeader.skipped() as string
        : formatOneExerciseSet(s, locale),
      userNotes: s?.userNotes,
    }));
    e.lastWorkoutSets.set(sets);
    e.lastWorkoutSetStats.set(readLastSessionSetStats(exercise.sets));
    e.lastWorkoutHasNotes.set(sets.some(s => s.userNotes));
    e.lastWorkoutDate.set(dateLabel);

    this.cdRef.markForCheck();
  }

  // Check if exercise uses DUP/TUP periodization
  hasPeriodization(e: UiExercise): boolean {
    const intensity = e.planPath?.intensityLevel;
    return intensity && (
      intensity === IntensityLevel.DUPlight ||
      intensity === IntensityLevel.DUPheavy ||
      intensity === IntensityLevel.TUPlight ||
      intensity === IntensityLevel.TUPmedium ||
      intensity === IntensityLevel.TUPheavy
    );
  }

  // handles clicking on last workout row
  handleLastWorkoutClick(e: UiExercise, event: Event): void {
    if (!e.lastWorkoutInfo()) return;

    if (hasClientSmallScreen()) {
      // on mobile, open bottom sheet with detailed log info
      const title = LL.workouts.detail.lastWorkout();
      const params: CsLogDetailPopoverParams = {
        dateTitle: `${title} - ${e.lastWorkoutDate()}`,
        subtitle: this.hasPeriodization(e)
          ? LL.workouts.detail.lastWorkoutSameIntensity() as string
          : undefined,
        sets: e.lastWorkoutSets(),
      };
      lockClick(event, CsLogDetailPopover.present(this.popoverCtrl, event.target as HTMLElement, params));
    } else {
      // on desktop, toggle expand/collapse
      e.lastWorkoutExpanded.set(!e.lastWorkoutExpanded());
      this.cdRef.markForCheck();
    }
  }

  // Reactive refresh from the DocWatcher: the workout doc, the plan doc, or a
  // registered `exercise/` doc changed via sync. This runs while the page is
  // ALREADY on screen and the user may be mid-edit, so — unlike first entry — it
  // must NOT raise the loading shell and must NOT pop the page on failure.
  //
  // #4358 / df-e3e98841308e: an earlier version set `this.loading = true` here,
  // which swaps the whole set list for a loading shell while it rebuilds. On an
  // ordinary sync that blanked the page out from under whoever was looking at it,
  // and removed any weight input a set had open mid-edit. So this rebuilds the
  // light metadata and the heavy exercise list and lets the new data swap in
  // underneath, without ever touching `loading`. The heavy build reassigns
  // `this.exercises` and re-opens the in-progress set as part of its normal
  // open-state fix-up, so the set the user is working stays open.
  //
  // It returns its promise (rather than `void`-ing it) so this page is on the
  // DocWatcher single-flight + delivery contract like every other page. That
  // contract owns error handling: a genuine failure propagates to the watcher's
  // `onCallbackError`, which reports it via `trackAbnormalException` (and logs,
  // not reports, our own sign-out teardown). So there is no pop and no
  // user-facing alert on a background refresh — a stale view is better than
  // throwing the user out of their workout. The FIRST page entry uses the split
  // `loadWorkoutMetaAsync` / `buildContentOnFirstEntryAsync` path instead, which
  // owns the loading shell (#4242).
  private async reactiveRefreshAsync(): Promise<void> {
    await this.loadWorkoutMetaAsync();
    await this.buildWorkoutExercisesAsync();
    this.cdRef.markForCheck();
  }

  // Loads only the LIGHT workout metadata: the plan (if needed) and the header
  // title. This is everything the header (title + options menu) needs to render.
  // #4242: on first entry ionViewWillEnter calls only this — DURING the slide —
  // so the header shows immediately, and the heavy exercise-list build is
  // deferred to ionViewDidEnter (after the slide). Sets no loading flag; the
  // caller owns the loading shell.
  private async loadWorkoutMetaAsync() {
    // load plan first (needed for getEffectiveWorkoutName and other operations)
    if ((this.mode === WorkoutLoadMode.Current) || !this.plan) {
      this.plan = await this.userDb.loadPlanAsync(this.docWatcher);
    }

    this.pageTitle = formatWorkoutName(this.workoutLoader.getEffectiveWorkoutName(this.logWorkout, this.plan));
    this.pageTitleRight = undefined;
    // add relative day label to the title if needed
    let workoutDay = DateTime.fromISO(this.logWorkout.time_utc).toLocal().startOf('day');
    let todayDay = this.debug.getNowLocal().startOf('day');
    if (workoutDay !== todayDay) {
      this.pageTitleRight = formatRelativeDay(todayDay, workoutDay);
    }
    this.cdRef.markForCheck();
  }

  // Builds the HEAVY exercise list. Serialises concurrent builds: the deferred
  // first-entry build and a reactive docWatcher reload can both reach this, and
  // buildWorkoutExercisesInternalAsync runs preselectEquipmentIfNeeded serially
  // (it mutates shared fitnessState.allEquipment and each step must observe the
  // previous, #3162). So if a build is already in flight, a second caller awaits
  // that same build instead of starting an interleaving one — on return
  // this.exercises is populated either way. Does NOT set the loading flag or catch
  // its own errors; both callers (the reactiveRefreshAsync rebuild, the first-entry
  // build in ionViewDidEnter) own that.
  private async buildWorkoutExercisesAsync(): Promise<void> {
    // if a build is already running, wait for it rather than start a second.
    // conscious trade-off: a reactive docWatcher reload that fires during a build
    // JOINS that in-flight build (which used the pre-change snapshot) instead of
    // rebuilding against the changed doc, so its change is dropped until the next
    // reload. This is only reachable in the narrow first-entry-slide window, it
    // self-heals on the next change, and it is strictly safer than the alternative
    // (two concurrent builds interleaving the serial equipment loop, #3162).
    if (this.exerciseBuildInFlight) {
      return this.exerciseBuildInFlight;
    }
    // otherwise run the build, holding its promise so a concurrent caller can join
    const build = this.buildWorkoutExercisesInternalAsync();
    this.exerciseBuildInFlight = build;
    try {
      await build;
    } finally {
      this.exerciseBuildInFlight = null;
    }
  }

  // The actual heavy exercise-list build: the exercise pipeline plus its three
  // per-exercise loops. This is the ~100 ms of main-thread work (its cache-served
  // awaits resolve as microtasks that never yield to rendering) that froze the
  // enter animation when it ran mid-slide, so on first entry it runs from
  // ionViewDidEnter, after the slide (#4242). Assumes loadWorkoutMetaAsync
  // already set this.plan / pageTitle. Call via buildWorkoutExercisesAsync, never
  // directly — that wrapper serialises concurrent builds (#3162).
  private async buildWorkoutExercisesInternalAsync() {
    // build context once for all exercises
    const context: WorkoutLoadingContext = {
      plan: this.plan,
      fitnessState: this.fitnessState,
      declaredState: this.declaredState,
      nowLocal: this.debug.getNowLocal(),
      locale: this.settings.locale,
      watcher: this.docWatcher,
      currentWorkoutId: this.logWorkout._id,
    };

    // load all exercises via service
    const { exercises, skippedExerciseIds, exercisesNeedingSwitch } = await this.workoutLoader.loadWorkoutExercisesAsync(
      this.logWorkout,
      context,
      this.mode as unknown as WorkoutLoadMode
    );

    // fire read-only lastWorkout queries OFF the critical path — they hit the
    // workoutByExerciseId view which can take seconds on large DBs / during the
    // post-login backfill. Each call toggles uie.lastWorkoutLoading so the card
    // renders a spinner in the last-workout section and fills in when ready;
    // the page becomes interactive immediately instead of blocking on them.
    for (const uie of exercises) {
      this.startLastWorkoutRead(uie);
    }
    // run preselect serially because duplicateEquipment mutates
    // fitnessState.allEquipment and each iteration must observe the previous
    // one (#3162).
    for (const uie of exercises) {
      uie.equipmentPreselected = await this.preselectEquipmentIfNeeded(uie);
    }

    // for exercises that need switching due to max weight reached, switch them
    for (const uie of exercisesNeedingSwitch) {
      const newExerciseId = await this.workoutLoader.getReplacementExerciseAsync(uie, 'switched', this.fitnessState, this.plan, this.debug.getNowLocal(), this.docWatcher);
      await this.switchExercise(uie, newExerciseId, { automatically: true });
    }

    if (skippedExerciseIds.length > 0) {
      await this.alertCtrl.showAlertAsync({
        message: LL.workouts.alerts.exercisesSkipped({ exercises: skippedExerciseIds.join('\n') }),
      });
    }
    this.exercises = exercises;
    this.planRevAtExerciseLoad = this.plan?._rev;

    let someHighlighted = false;
    // for each exercise, fix up its open/highlight state
    for (let ie = 0; ie < this.exercises.length; ie++) {
      let uie = this.exercises[ie];
      // if the exercise is already completed, or we are not in Current (edit) mode
      if (uie.sets.every(s => s.logSet.state === LogRecordState.Done || s.logSet.state === LogRecordState.Skipped) || (this.mode !== WorkoutLoadMode.Current)) {
        // just show all sets
        uie.openSetIndex.set(-1);
        continue;
      }
      if (this.isExerciseScheduled(uie) || this.isExerciseIncomplete(uie)) {
        if (!someHighlighted) {
          uie.highlighted.set(true);
          someHighlighted = true;
        }
      }
      // find the last first set that is not done
      let setNotDone = uie.sets.find(s => s.logSet.state !== LogRecordState.Done && s.logSet.state !== LogRecordState.Skipped);
      let setNotDoneIndex = uie.sets.indexOf(setNotDone);
      // if this exercise was open before
      if (setNotDoneIndex != null && setNotDoneIndex > 0 && setNotDoneIndex < uie.sets.length) {
        await this.openSet(uie, setNotDoneIndex, 'open');
      }
    }

    this.debug.setPageDebug({
      exercises: this.exercises.map(e => ({
        id: e.exercise._id,
        planSubSlot: e.planSubSlot,
        planPath: e.planPath,
        stat: e.stat,
        sets: e.sets.map(s => s.logSet),
      })),
      fitnessState: this.fitnessState,
    });

    await this.repairWorkoutLeftOpenAsync();

    // #6536: if this workout was finished on an earlier visit, draw its summary again from the stored
    // sets. After the repair above, which can itself mark the workout finished
    if (this.isWorkoutFinished()) {
      this.showWorkoutSummary();
    }
  }

  /**
   * Repair a workout that is stored as unfinished even though every one of its
   * sets is finished with. #4802: something can revert the workout's own state
   * field between the moment the app decides the workout is finished and the
   * moment it is saved — see `reassertConcludedWorkoutState`, which stops new
   * ones happening. This repairs the ones already stored that way, of which the
   * customer who reported the ticket has one; without it their Home page goes on
   * offering a workout they finished, for ever.
   *
   * "Unfinished" covers a workout stored as never started as well as one stored
   * as in progress (#4840) — undoing a skip used to write the first of those. It
   * is `resolveConcludedWorkoutState` that decides which states are repairable,
   * and this method deliberately does not ask the same question again; the two
   * copies disagreeing is how #4840 stayed unreachable.
   *
   * It runs here, after the exercises have been loaded, because the loaded
   * exercises are the only place the full set list exists. A set is written to
   * the document only once it is CONCLUDED (see `concludeSet`), so the stored
   * document holds just the sets the user got through — and judging from that
   * alone cannot tell "finished the exercise" apart from "stopped part-way
   * through it". The loader fills in the sets the user has not reached yet, so
   * the answer here reads the same set list `isWorkoutDone` reads during a
   * workout — but it is stricter on the empty cases, treating a workout with no
   * exercises, or an exercise with no sets, as unfinished rather than finished.
   *
   * Because the screen is the authority, it must be showing the WHOLE workout,
   * and the loader does not always manage that — `screenHoldsEveryStoredExercise`
   * is the check, and its own comment says what a dropped exercise would cost.
   * Note that the save keeping such an exercise's sets is a SEPARATE mechanism
   * (`mergeScreenExercisesOverStored`) and does not make this check redundant:
   * keeping the data does not make the screen a trustworthy judge of whether the
   * workout is over.
   *
   * Which modes it may write in is `mayRepairStuckWorkoutInMode`, whose own
   * comment explains why a workout that has fallen into the PAST is included
   * (#4960) and why the two sibling load-time repairs
   * (`healOrphanedAutoregulationPending`, `healOrphanedPlateauDetected`) keep the
   * narrower rule. On top of the mode there are two guards a mode cannot express,
   * and both are kept here: never while a coach is managing a client — which
   * `isEditableMode()` alone does not cover, because a coach looking at a
   * client's workout satisfies it while the page is view-only for them and the
   * document being written would be the client's — and never before the exercise
   * list has loaded, since the screen is what the answer is read from.
   */
  private async repairWorkoutLeftOpenAsync(): Promise<void> {
    // never write a document in a mode this repair does not cover, nor a client's
    // document while a coach is managing them
    if (!mayRepairStuckWorkoutInMode(this.mode) || this.isCoachManaging || !this.exercises) return;

    // the screen is what this repair reads, so it must be showing the whole
    // workout. The loader drops an exercise it cannot display — its entry in the
    // program is gone and the workout carries no snapshot of it, loading it
    // threw, or the database connection died part-way through the load — and
    // such an exercise cannot stop the workout being judged finished, so a
    // session the user really did abandon would be closed here (#4960).
    //
    // It no longer costs the user their logged sets as well: the save merges the
    // stored list rather than replacing it (`mergeScreenExercisesOverStored`).
    // That is why this guard is still needed and must not be removed along with
    // the loss it used to prevent — the two answer different questions, and this
    // one is about whether the screen may be trusted to declare a workout over.
    const storedExercises = this.workoutLoader.getEffectiveExercises(this.logWorkout, this.plan, planMergeForMode(this.mode));
    if (!screenHoldsEveryStoredExercise({
      storedSubSlotIds: storedExercises.map(e => e?.subSlotId),
      onScreenSubSlotIds: this.exercises.map(e => e.logExercise?.subSlotId),
    })) {
      // problem tier: the repair itself was refused safely, but the reason it
      // was refused is an exercise this account cannot display at all, which
      // nothing else reports and somebody eventually needs to look at
      this.logging.trackSilentProblem('workout-repair-skipped-exercise-missing-from-screen', {
        docId: this.logWorkout._id,
        rev: this.logWorkout._rev,
        mode: this.mode,
        storedCount: storedExercises.length,
        onScreenCount: this.exercises.length,
      });
      return;
    }

    // Decide and report under THIS path's name, and save only if that changed
    // something. Whether this workout is repairable at all is asked ONCE, inside
    // `resolveConcludedWorkoutState` — this used to ask again here, and the two
    // copies disagreeing is exactly how #4840 escaped: the shared rule was
    // widened to cover a workout stored as never started, while a second copy of
    // the state list sat here and did not know about it.
    //
    // The save below runs the same re-assert every save runs, but by then the
    // state already agrees with the sets, so it finds nothing to do and stays
    // quiet — which is what keeps the two reports meaning different things.
    // `workout-state-reverted-before-save` has to go on meaning "a workout was
    // reverted while the user had it open", or the number stops answering the
    // question it exists to answer.
    if (!this.reassertConcludedWorkoutState(FOUND_STUCK_ON_LOAD)) return;

    try {
      await this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
    } catch (err) {
      // this repair is optional — it can simply run again the next time the
      // workout is opened. It is awaited inside the first-entry build, whose
      // catch reports "Something went wrong" and pops the user back out
      // (deliberately, for a workout with no exercise list). Letting a failed
      // repair write reach that catch would throw the user out of a workout
      // whose exercise list loaded perfectly well. Report it here instead —
      // never swallow it.
      this.logging.trackAbnormalException(err);
    }
  }

  initializeSetType(s: UiSet) {
    // if already established, nothing to do
    if (s.logSet.setType != null) {
      return;
    }
    const e = s.uiExercise;
    const i = e.sets.indexOf(s);

    // if first set
    if (i === 0) {
      if (e.isTempSwap) {
        // temp swap sets already have their type assigned
        s.logSet.setType = SetType.Regular1st;
      } else if (e.planPath) {
        // take the type from the plan
        s.logSet.setType = e.planPath.nextWorkoutSetType;
      } else {
        s.logSet.setType = SetType.Dummy;
      }
      return;
    }

    // not first set in workout - special continuation logic...

    const prevSet = e.sets[i - 1].logSet;
    const prevType = prevSet.setType;
    const prevSucceeded = prevSet.setSuccess;

    switch (prevType) {
      case SetType.NonRMTest:
        s.logSet.setType = prevSucceeded !== SetSuccess.Fail ? SetType.Followup : SetType.NonRMTest;
        break;
      case SetType.RMTest:
        s.logSet.setType = prevSucceeded !== SetSuccess.Fail ? SetType.RMTestFollow : SetType.RMTest;
        break;
      case SetType.RMTestFollow:
        s.logSet.setType = SetType.RMTestFollow;
        break;
      case SetType.RepTest:
        s.logSet.setType = SetType.Followup;
        break;
      case SetType.Regular1st:
        s.logSet.setType = prevSucceeded !== SetSuccess.Fail ? SetType.Followup : SetType.Deload;
        break;
      case SetType.Deload:
        s.logSet.setType = SetType.Deload;
        break;
      case SetType.Dropset:
        s.logSet.setType = SetType.Dropset;
        break;
      case SetType.Followup:
        s.logSet.setType = SetType.Followup;
        break;
      default:
        console.assert(false, `unknown previous set type ${prevType}`);
        break;
    }

    if (
      // dropsets are possible after all, except a few set types
      // NOTE: doing 3 reps on a dropset _does_ cause another dropset!
      ![SetType.Deload, SetType.RMTest].some(t => t === prevSet.setType) &&
      // ...but they must _not_ replace deloads!
      s.logSet.setType !== SetType.Deload
    ) {
      if (prevSet.planPathBackup && prevSet.planPathBackup.target_reps > 3 ? prevSet.stats.reps <= 3 : prevSet.stats.reps <= 2) {
        s.logSet.setType = SetType.Dropset;
        s.logSet.preStatus.push({ status: PreSetStatus.DropSet, textInserts: undefined });
      }
    }

    if (s.logSet.setType == null) {
      console.assert(false, `No followup set type defined for ${prevType}. Defaulting to regular.`);
      s.logSet.setType = SetType.Followup;
    }
  }

  /**
   * Promote a path to a working set after a test/establishing set has set its target weight.
   * The caller is responsible for having written `e.planPath.target_weight_kg` (from the
   * concluded set) before calling — this method only decides the resulting set type from it.
   * Guards the invariant that a `Regular1st` path must carry a finite `target_weight_kg`. If
   * the weight is somehow missing here (Sentry 2YK / mh-physique #3752), keep the path testable
   * (`resolveEstablishedSetType`) and surface it at the source — with the conclusion stack and
   * the establishing set's stats — instead of baking an invalid "target reps but no target
   * weight" path that later trips the loader-side `fixupInvalidPathState` canary on every view.
   */
  private establishWorkingPath(e: UiExercise, s: UiSet): void {
    const usesRMTesting = exerciseUsesRMTesting(e.exercise, this.declaredState, this.fitnessState);
    const resolved = resolveEstablishedSetType(e.planPath, usesRMTesting);
    // #4890: the set type the next workout prescribes is decided here, so it goes into every
    // container this path lives in — see `livePathsForCurrentIntensity()`.
    for (const path of e.livePathsForCurrentIntensity()) {
      path.nextWorkoutSetType = resolved;
    }
    // if the target weight was missing the path stayed a test type; surface it at the source
    if (resolved !== SetType.Regular1st) {
      this.logging.trackSilentProblem('Working set concluded without target weight (#3752)', {
        exerciseId: e.exercise._id,
        exerciseName: e.exercise.name,
        setType: s.logSet.setType,
        loggedWeight_kg: s.logSet.stats.weight_kg,
        loggedReps: s.logSet.stats.reps,
        weight_unit: s.logSet.stats.weight_unit,
        equipmentId: e.equipment?.id,
        intensityLevel: e.planPath.intensityLevel,
      });
    }
  }

  /**
   * #5617: after a break, write each of the exercise's OTHER intensity paths its own layoff weight.
   * Which paths get which weight is decided by `otherIntensityLayoffWeight_kg`; this only walks the
   * page's copy and the plan's own copy of each other intensity and applies the answer.
   */
  private writeLayoffWeightToOtherIntensities(e: UiExercise): void {
    const activeIntensity = e.planPath?.intensityLevel;
    const reduction_pc = e.reducedTargetWeight_pc && e.reducedTargetWeight_pc !== -1 ? e.reducedTargetWeight_pc : undefined;
    for (const pagePath of e.planSubSlot.exercisePaths) {
      if (pagePath.intensityLevel === activeIntensity) continue;
      for (const path of e.pageAndPlanPathsForIntensity(pagePath.intensityLevel)) {
        const weight_kg = otherIntensityLayoffWeight_kg(path, {
          cut1RM_net_kg: e.layoffCut1RM_net_kg,
          reduction_pc,
          bodyWeight_kg: this.fitnessState.weight_kg,
          exerciseBw_pc: e.exercise?.bw_pc || 0,
          equipment: e.equipment,
        });
        if (weight_kg !== undefined) path.target_weight_kg = weight_kg;
      }
    }
  }

  /**
   * #4614: write an exercise's edited sub-slot back into the user's program.
   *
   * Skips the write when the program no longer holds an entry under that id. That happens
   * when the user removed the exercise in Edit Workouts and this page is showing a finished
   * session that contained it, restored from the snapshot the workout log carries. Putting
   * the sub-slot back would resurrect an entry the program deliberately dropped: the plan's
   * own garbage collector (gcOrphanSubslots, called from savePlanAsync) deletes it again on
   * the next save and raises the 'plan.orphan-subslot-at-save' alarm while doing it, and the
   * historical values would meanwhile have overwritten the live program.
   *
   * #4939 added the second reason to skip: the copy being held is a finished session's own
   * historical snapshot. The program does still hold an entry there, so the #4614 test alone
   * would let this through — and it would replace the user's current sub-slot with the settings
   * that session was performed under. `canWritePlanSubSlotBackToPlan` covers both.
   *
   * Every exercise that is still in the program, on a workout that is not finished, writes back
   * exactly as before.
   */
  private writeSubSlotBackToPlan(e: UiExercise): void {
    // #6428 R9: a coach change seen here for the first time (it arrived before set 1) must also
    // drop the live pointer `refreshLivePlanSubSlot` set at the top of the conclusion, or a
    // direct write through it (the next-intensity rotation) still reaches the coach's entry
    if (this.refreshCoachChangeGuard(e)) {
      e.livePlanSubSlot = undefined;
    }
    if (!e.canWritePlanSubSlotBackToPlan()) return;
    this.plan.subSlots[e.logExercise.subSlotId] = e.planSubSlot;
    // the two are now the same object; keep the pointer honest so nothing writes an entry the
    // plan has stopped holding
    e.livePlanSubSlot = e.planSubSlot;
  }

  /**
   * df-704db86556f7: what this page tells `savePlanAsync`, so that if the save finds a
   * plan.subSlots entry no workout lists, the `plan.orphan-subslot-at-save` report can say
   * which save asked, whether this page shows that exercise and how it was flagged, and
   * whether the program moved under the page after it built its exercises. Diagnosis only;
   * the save is unchanged.
   */
  private planSaveContext(caller: PlanSaveCaller): PlanSaveContext {
    return {
      caller,
      pagePlan: this.plan,
      pageLoadPlanRev: this.planRevAtExerciseLoad,
      describeSubSlot: (subSlotId: string) => {
        const shown = this.exercises?.find(e => e.logExercise?.subSlotId === subSlotId);
        if (!shown) return { shownOnPage: false };
        return {
          shownOnPage: true,
          missingFromPlan: shown.planSubSlotMissingFromPlan,
          historical: shown.planSubSlotIsHistorical,
        };
      },
    };
  }

  /**
   * df-8d1620a9593c: point `e.livePlanSubSlot` at the fitness plan's OWN entry for this exercise,
   * so a progression write reaches the plan even while `e.planSubSlot` is still the loader's
   * detached copy. Called at the moments a progression write begins rather than once at load,
   * because `this.plan` is itself reassigned in several places and a reference taken at load
   * would silently start writing an orphaned object. Full rules:
   * `design-docs/volume-autoregulation.md`, "The containers a path lives in".
   *
   * It leaves the pointer unset in TWO situations, and they are not the same one. The program no
   * longer holds this exercise (`planSubSlotMissingFromPlan`) is the #4614 case that
   * `writeSubSlotBackToPlan` also skips. The other is an exercise whose sub-slot id the plan has
   * no entry for at all while that flag is false — `reloadUIExercise` fabricates a sub-slot for a
   * broken workout log that way (#238). There, unlike `writeSubSlotBackToPlan`, this deliberately
   * writes NOTHING rather than creating the entry: a fabricated sub-slot put into the program is
   * an entry the user never had, which `gcOrphanSubslots` deletes again at the next save while
   * raising an alarm.
   */
  private refreshLivePlanSubSlot(e: UiExercise): void {
    // #6428 R9: a coach change made during this session leaves the pointer unset, so the
    // progression helpers write only the page and snapshot copies
    if (this.refreshCoachChangeGuard(e)) {
      e.livePlanSubSlot = undefined;
      return;
    }
    e.livePlanSubSlot = e.planSubSlotMissingFromPlan ? undefined : this.plan.subSlots[e.logExercise.subSlotId];
  }

  /**
   * #6428 R9: look whether the program entry for this exercise now carries a different coach stamp
   * than this session's own snapshot, and remember the answer on the row for the rest of the page's
   * life. Called where a write into the program begins (`refreshLivePlanSubSlot`,
   * `writeSubSlotBackToPlan`) and before the page raises a question, because `this.plan` is
   * reloaded when the coach's change syncs in. Answers whether the guard is on.
   */
  private refreshCoachChangeGuard(e: UiExercise): boolean {
    if (!e.coachChangedDuringSession && !e.planSubSlotMissingFromPlan
      && coachChangedSinceSnapshot(this.plan?.subSlots?.[e.logExercise.subSlotId], e.logExercise.planSubslotBackup)) {
      e.coachChangedDuringSession = true;
    }
    return e.coachChangedDuringSession;
  }

  /**
   * #4939: the sub-slot in the user's PROGRAM that an open question is read from and answered
   * into — as opposed to `e.planSubSlot`, which is what the screen draws with.
   *
   * On every workout that is not finished the two are the same object and this changes nothing.
   * On a finished one they are not: the page draws with that session's own snapshot, and a
   * question is not history. `plateauWorkoutLog_id` says "a plateau question is open right
   * now", and it is answered by clearing it — clearing it on a snapshot would change nothing
   * the user can see and leave the card open for ever. The same goes for what accepting a
   * plateau offer does: rotating the intensity, raising the training level or swapping the
   * exercise are all statements about what the user will train NEXT.
   *
   * Every read, write and delete of the plateau pointer goes through this one method, so a new
   * site cannot reintroduce the bug by reaching for `planSubSlot` the way all of them used to.
   * `workout-detail-answer-target.test.ts` fails the build if one does.
   *
   * It answers undefined when the program holds no entry for this exercise. That mirrors
   * `refreshLivePlanSubSlot`'s own rule: there is nowhere to put the pointer, and nothing here
   * may create an entry the user does not have — `gcOrphanSubslots` would delete it again at
   * the next save while raising an alarm. Callers treat undefined as "no question is open".
   */
  private programSubSlotForAnswer(e: UiExercise): Plan_SubSlot | undefined {
    // #6428 R9: after a coach change made during this session the page copy is out of date too, so
    // the question lives in the program entry, exactly as on a finished workout
    if (!e.planSubSlotIsHistorical && !e.coachChangedDuringSession) {
      return e.planSubSlot;
    }
    return e.planSubSlotMissingFromPlan ? undefined : this.plan.subSlots[e.logExercise.subSlotId];
  }

  /**
   * #4939: the same target as `programSubSlotForAnswer`, but for the callers that must have an
   * object to work on rather than a decision to make.
   *
   * The fallback is only ever taken when the program holds no entry for this exercise, and the
   * page's own copy is then the only sub-slot in existence. Reads off it are correct; writes
   * reach a copy nothing saves, which is the right outcome — there is no program entry to
   * change, and creating one would resurrect an entry `gcOrphanSubslots` deletes again at the
   * next save while raising an alarm.
   *
   * It exists so that meaning is stated once. Three call sites used to spell out
   * `programSubSlotForAnswer(e) ?? e.planSubSlot` and each argued the point again in its own
   * comment, which left a reader comparing three passages by hand to check they agreed.
   */
  private programSubSlotForAnswerOrDisplayCopy(e: UiExercise): Plan_SubSlot {
    return this.programSubSlotForAnswer(e) ?? e.planSubSlot;
  }

  /**
   * Swaps an exercise the user should stop training — a strength test showed it is a poor fit
   * (`'weakness'`), or progress on it stalled (`'plateau'`) — out of the user's program. This is
   * the one place that does it, so a change to what a swap does is made once.
   *
   * #4939: the swap is about what the user trains from now on, so it changes the entry their
   * program holds today rather than the copy the screen is drawn with. Those are the same object
   * on any workout that is not finished, so nothing changes there. On a finished one the copy is
   * that session's own snapshot, and rewriting it would change which exercise the history claims
   * was performed while leaving the program alone — exactly backwards.
   *
   * `programSubSlot` is for a caller that already resolved the entry before asking for a
   * replacement (the plateau path). Without it, the entry is resolved after the replacement is
   * chosen, and nothing is written when the program holds no entry for this exercise.
   *
   * #6063: a swap at the rep ceiling prefers a replacement on other equipment, and reports when it
   * could not find one, so the user can be told.
   */
  private async swapExerciseOutOfProgramAsync(
    e: UiExercise,
    reason: ProgramSwapReason,
    programSubSlot?: Plan_SubSlot,
  ): Promise<{ mayUseSameEquipment: boolean }> {
    let newExerciseId: string;
    let mayUseSameEquipment = false;
    if (reason === ProgramSwapReason.RepCeiling) {
      ({ exerciseId: newExerciseId, mayUseSameEquipment } = await this.workoutLoader.getRepCeilingReplacementAsync(e, this.fitnessState, this.plan, this.debug.getNowLocal(), this.docWatcher));
    } else {
      const mode = reason === ProgramSwapReason.Weakness ? 'weakness' : 'plateau';
      newExerciseId = await this.workoutLoader.getReplacementExerciseAsync(e, mode, this.fitnessState, this.plan, this.debug.getNowLocal(), this.docWatcher);
    }
    const target = programSubSlot ?? this.programSubSlotForAnswer(e);
    // suggestAvailableExercise can fall back to the current id when no viable alternative
    // exists; only run the canonical reset when we're actually swapping.
    if (target && newExerciseId !== target.exercise_id) {
      const swappedOutId = target.exercise_id;
      // the app chose this replacement, so it must not read as the user customizing the program
      rotateExerciseInSubSlotByApp(target, newExerciseId);
      // #6428: record what a plateau or weakness result set aside, so a coach can see it and bring
      // it back. A rep-ceiling swap sets nothing aside: the exercise is not quarantined
      if (reason === ProgramSwapReason.Plateau || reason === ProgramSwapReason.Weakness) {
        target.setAsideExerciseId = swappedOutId;
      }
    }
    return { mayUseSameEquipment };
  }

  /**
   * #4939: report what one tap of the Log button did to the set and to the user's program.
   *
   * Why this event exists. Nothing fired when a set was logged or edited, and no workout event
   * recorded whether the workout was already finished — so how often anybody touches a finished
   * session, and what the app did about it, could not be answered at all. That gap is why the
   * rules around finished sessions had to be decided from reading the code rather than from
   * evidence.
   *
   * It is reported from ONE place, at the end of the tap, from a value the concluding branches
   * set. Reporting inside those branches would fire twice on the RM-test reset path, which
   * rebuilds the sets and then falls through into the fresh-set branch. An early return leaves
   * the value unset and reports nothing, which is right: a dismissed dialog concluded nothing.
   *
   * `reason` matters as much as `updatedProgram`. Without it a dashboard cannot tell a write
   * that the session-recency rule refused from one that never had a program entry to write to,
   * and those call for opposite responses.
   */
  private reportSetRecorded(
    e: UiExercise,
    set: UiSet,
    iSet: number,
    outcome: SetRecordOutcome | undefined,
    sessionRecency: SessionRecency,
    workoutModeAtTap: WorkoutLoadMode,
    rmTestResetRan: boolean,
  ): void {
    if (!outcome) {
      return;
    }

    this.telemetry.trackEvent('workout_set_recorded', {
      action: outcome.action,
      updated_program: outcome.updatedProgram,
      reason: outcome.reason ?? 'none',
      // the mode as it was when the tap began — the SoftFuture branch reassigns `this.mode`
      // mid-tap. Sent as the enum member's NAME, because the values are numbers (and negative
      // for Past), which no dashboard filter can be read against. `unrecognized` rather than
      // `unknown` keeps it distinct from the `Unknown` member's own name.
      workout_mode: WorkoutLoadMode[workoutModeAtTap] ?? 'unrecognized',
      session_recency: sessionRecency,
      rm_test_reset: rmTestResetRan,
      exercise: e.exercise?._id,
      set_index: iSet,
      set_type: set.logSet?.setType,
      intensity: e.planPath?.intensityLevel,
    });
  }

  /**
   * #4939: can a conclusion on this tap reach the user's program at all, and if not, why not?
   *
   * The question it answers is whether the user's program DIFFERS after this tap's save from
   * before it — the durable outcome, not whether some write touched the plan object in between.
   * That is the only definition a baseline for the later pushes can use, because what those
   * pushes change is whether the program moves, and a write the same tap undoes did not move it.
   *
   * A temporary swap is the first case where it cannot move: `concludeSet` records the swap
   * history and returns before any progression write.
   *
   * The second is a program with no live entry for this exercise. That is two conditions, and
   * they take different routes to the same outcome. When the loader flagged the exercise as
   * removed (`planSubSlotMissingFromPlan`, #4614), `writeSubSlotBackToPlan` skips it and
   * `refreshLivePlanSubSlot` leaves the live pointer unset, so nothing reaches the plan at all.
   * When that flag is false but `this.plan.subSlots` holds no entry under this id,
   * `refreshLivePlanSubSlot` still leaves the pointer unset — but `writeSubSlotBackToPlan`
   * gates on the flag ALONE, so on a first-set conclusion it creates the entry and the
   * progression writes land in it. That entry does not survive the tap: `concludeSet` ends by
   * saving the plan, and `gcOrphanSubslots` deletes any entry no workout references and raises
   * the `plan.orphan-subslot-at-save` canary. So the program is unchanged once the tap
   * finishes, whichever set was concluded, and the canary is what records that a transient
   * write happened.
   *
   * The second condition deliberately mirrors `refreshLivePlanSubSlot`'s own rule, so the
   * telemetry and the code that decides where progression writes go share one model. No route
   * into it has been confirmed from the code — a cross-device removal rebuilds the exercises
   * and sets the flag, and the #238 fabricated sub-slot is unreachable from its only caller —
   * so it is kept as a guard against reporting a lie, not as a case anyone expects to measure.
   *
   * This does NOT yet answer for a superseded session — nothing gates on recency until the push
   * that starts using it, and reporting a refusal that never happens would be its own lie.
   */
  private programWriteOutcomeForTap(
    e: UiExercise,
  ): { updatedProgram: boolean; reason?: SetRecordUnchangedReason } {
    if (e.isTempSwap) {
      return { updatedProgram: false, reason: SetRecordUnchangedReason.TempSwap };
    }
    if (e.planSubSlotMissingFromPlan || !this.plan.subSlots[e.logExercise.subSlotId]) {
      return { updatedProgram: false, reason: SetRecordUnchangedReason.NoLiveEntry };
    }
    return { updatedProgram: true };
  }

  /**
   * #4939: does the session on screen still own this exercise's progression?
   *
   * The rule is that the exercise's most recent concluded session owns it, until the first set
   * of the next session of that exercise is concluded. `sessionRecencyForLoggedExercise` holds
   * the rule and the choice of which dates to compare, and is unit-tested; this only reaches
   * into the page for the program entry and the workout being shown.
   *
   * Call this BEFORE anything is concluded. Concluding the first set writes both dates the
   * answer is computed from — it stamps the program and then takes this session's snapshot — so
   * asking afterwards would be asking about the state the conclusion just created.
   *
   * `this.plan` is read unguarded, exactly as `refreshLivePlanSubSlot` reads it: the page cannot
   * draw a workout without a plan, so an absent one is a bug worth throwing on rather than
   * quietly answering "this session may write".
   */
  private classifySessionRecencyForExercise(e: UiExercise): SessionRecency {
    return sessionRecencyForLoggedExercise(
      e.logExercise,
      this.plan.subSlots[e.logExercise.subSlotId],
      this.logWorkout?.time_utc,
    );
  }

  /**
   * The weight a rep test is judged against — the ONE derivation, read by the question the user is
   * asked and by the conclusion that follows it, so the two can never disagree (df-393f9706fb12).
   *
   * It comes off the SET, because `openSet` already wrote the prescribed weight there when it
   * prefilled the row: `plannedWeight_kg` is `e.targetWeight_kg` snapped onto the equipment, which
   * is the weight the user was shown. It is not rewritten when the user types a different weight,
   * and it survives into the document, so a correction months later still reads what THAT session
   * prescribed rather than what the plan has climbed to since.
   *
   * The fallback covers a set carrying no recorded prescribed weight — one logged before the field
   * existed, or one `openSet` could not prefill. It is the expression the caller used before this
   * method existed. `??`, not `||`, because a prescribed weight of exactly 0 is legitimate on a
   * bodyweight exercise.
   */
  private prescribedRepTestWeight_kg(e: UiExercise, s: UiSet): number | undefined {
    if (typeof s.logSet.plannedWeight_kg === 'number' && isFinite(s.logSet.plannedWeight_kg)) {
      return s.logSet.plannedWeight_kg;
    }
    const { weight_kg } = this.adjustWeight(e.targetWeight_kg ?? e.planPath?.target_weight_kg, 'round', e.equipment);
    return weight_kg;
  }

  /**
   * Asks why a rep test ran at a weight far from the prescribed one, and returns the user's answer
   * — or `undefined` when they dismissed the question with the X or the backdrop, which cancels the
   * whole Done action (#4558). It is asked HERE, before `concludeSet`, precisely so that dismissing
   * can cancel: once `concludeSet` starts, the set is marked done, the workout leaves its scheduled
   * state and plan progression is written, and there is nothing clean to undo.
   *
   * Only one direction is offered, the one the user's own weight choice implies: reaching for less
   * weight than prescribed means "that is too heavy for me", reaching for more means "too light".
   * The caller works that direction out with the rest of the question, so this method only shows it.
   */
  private async askRepTestWeightAnswerAsync(direction: RepTestWeightAnswer): Promise<RepTestWeightAnswer | undefined> {
    this.duringSetDoneAlert = true;
    try {
      return await this.alertCtrl.showAlertAsync<RepTestWeightAnswer>({
        title: LL.workouts.alerts.youChangedWeight(),
        message: LL.workouts.alerts.weightEstimateWrong(),
        buttons: [
          {
            // the Cancel role keeps the alert valid for `validateButtonRoles` (exactly one Primary
            // and one Cancel); dismissing with the X returns undefined either way, which cancels
            text: alertTextForRepTestWeightAnswer(direction),
            role: AlertButtonRole.Cancel,
            result: direction,
          },
          {
            text: alertTextForRepTestWeightAnswer(RepTestWeightAnswer.ClosestAvailable),
            role: AlertButtonRole.Primary,
            result: RepTestWeightAnswer.ClosestAvailable,
          },
        ],
      });
    } finally {
      // a rejected alert must not leave the Done button hidden for the rest of the workout
      this.duringSetDoneAlert = false;
    }
  }

  /**
   * `repTestWeightQuestion` carries the outcome of the "you changed the weight?" question, which the
   * CALLER asks before this method runs — `askRepTestWeightAnswerAsync` says why it lives out there.
   * It carries whether the caller judged the weight off target as well as the answer, so that this
   * method decides the same way the question did rather than working the weight out again.
   */
  async concludeSet(e: UiExercise, iSet: number, repTestWeightQuestion?: RepTestWeightQuestionOutcome) {
    this.logging.checkWorkoutExerciseValidity("concludeSet start", e.logExercise)
    // df-8d1620a9593c: every progression write below goes through `livePathsForCurrentIntensity()`,
    // which reaches the fitness plan's own path only while this pointer is current. Refresh it here,
    // at the top of the one method that writes progression, so the plan is written on a later set
    // too — the `iSet === 0` branch further down is the ONLY thing that attaches the page's copy,
    // and a page rebuilt mid-workout or a corrected later set never runs it.
    this.refreshLivePlanSubSlot(e);
    let s = e.sets[iSet];
    // #5979: only the conclusion that reached the ceiling may ask about it
    s.pendingRepCeiling = undefined;

    // verbose logging for debugging equipment and unit issues
    const exerciseStat = this.fitnessState.exerciseStats[e.exercise._id];
    console.log('[Set Complete]', {
      exercise: e.exercise._id,
      exerciseName: e.exercise.name,
      setIndex: iSet,
      setType: s.logSet.setType,
      stats: {
        weight_kg: s.logSet.stats.weight_kg,
        reps: s.logSet.stats.reps,
        weight_unit: s.logSet.stats.weight_unit,
      },
      equipment: e.equipment ? {
        id: e.equipment.id,
        name: e.equipment.name,
        type: e.equipment.type,
        weight_unit: e.equipment.weight_unit,
        isAutomatic: e.equipment.id?.startsWith('automatic_'),
      } : null,
      stat: {
        weight_unit: e.stat?.weight_unit,
        equipmentId: exerciseStat?.equipmentId,
        equipmentIdByUnit: exerciseStat?.equipmentIdByUnit,
        customWeightIncrementByUnit: exerciseStat?.customWeightIncrementByUnit,
      },
      planPath: {
        target_weight_kg: e.planPath?.target_weight_kg,
        target_reps: e.planPath?.target_reps,
      },
      setupError: s.setupErrorWeight,
    });
    s.logSet.state = LogRecordState.Done;
    // #6151: the hint explains a suggested weight, and a logged set no longer carries a suggestion
    s.equipmentFloorHint = undefined;

    // store values for edit mode tracking (but don't mark as edit mode yet - do that at the end)
    s.weightBeforeEdit = s.logSet.stats.weight_kg;
    s.repsBeforeEdit = s.logSet.stats.reps;

    if (this.logWorkout.state === LogRecordState.Scheduled) {
      this.logWorkout.state = LogRecordState.Incomplete;
      this.telemetry.trackEvent('workout_started', {
        exercise: e.exercise._id,
        exercise_count: this.exercises.length,
      });
      // user started the workout — suppress today's workout reminder. only the Scheduled→Incomplete
      // transition reaches this branch, so this fires once per workout, not on every set.
      void this.notificationService.suppressForEntryAsync(this.logWorkout, this.debug.getNowLocal());
    }

    s.logSet.completedTime_utc = this.debug.getNowLocal().toUTC().toISO();

    // issue #3924: remember whether this session took the rep-fallback layoff branch (weight HELD at
    // the last lifted weight, only the rep target cut) before the marker is cleared below. The deload
    // yardstick further down must then measure against the held weight, not the higher climbing target.
    // df-78bb051f6709: the loader's marker only exists while the page is the one that loaded the
    // session, and the first conclusion deletes it a few lines below. So a correction to an
    // already-finished set reads the fact back off the set itself, and a later set in the same
    // session (#5685) reads it off the first set until the weight moves. The helper holds all three
    // routes. Without them a set is measured against the climbing target and can be refused credit,
    // or offered a back-off, for lifting exactly what was asked.
    // #5617: under the newer layoff rule the path's target is SET to the weight the user was asked
    // for when this first set concludes (below), so the set is judged against its own target and the
    // held-weight yardstick is only for the older rule. The set records that instead
    // (`layoffPrescribed`), so a correction keeps the "never judged above what was asked" clamp.
    const layoffPrescribedThisSet = iSet === 0 && !e.isTempSwap && e.layoffWeight_kg !== undefined;
    const wasLayoffRepFallback = isSetJudgedAgainstLayoffHeldWeight({
      hasLoaderMarker: !!e.reducedTargetReps_delta && e.layoffWeight_kg === undefined,
      set: s.logSet,
      iSet,
      earlierSets: e.sets.slice(0, iSet).map(earlier => earlier.logSet),
      currentTargetWeight_kg: e.planPath?.target_weight_kg,
    });

    // when the first set is concluded, update plan state (skip for temp swaps — plan is not modified)
    if (iSet === 0 && !e.isTempSwap) {
      if ((!s.inEditMode() && !e.startedInFuture)) {
        e.planSubSlot.lastTimeConcluded_utc = this.debug.getNowLocal().toUTC().toISO();
      } else if (!e.planSubSlot.lastTimeConcluded_utc) {
        e.planSubSlot.lastTimeConcluded_utc = this.logWorkout.time_utc;
      }

      // #4614: never store a sub-slot the program does not hold as this session's snapshot.
      // For an exercise still in the program this is the ordinary case and writes as before.
      // For one the program has dropped, `e.planSubSlot` is not a copy of a program entry at
      // all — it is either the snapshot already stored here (so this branch is not taken) or
      // one `rebuildDisplaySubSlotFromLog` built for DISPLAY out of the log's own sets. Storing
      // the rebuilt one would freeze today's reconstruction into the document for good: the
      // exercise would draw from it for ever, a later improvement to the rebuild rule could
      // never reach it, and the two repairs would stop being tellable apart in telemetry.
      // Worse, the stored copy carries the `lastTimeConcluded_utc` stamped just above and a
      // finite target weight, which is exactly what `isUsableLayoffBackup` looks for — so a
      // fabricated progression could later be lifted into a freshly created program sub-slot
      // by `seedProgressionFromBackup`. See `design-docs/exercise-replacement.md`.
      if (!e.logExercise.planSubslotBackup && !e.planSubSlotMissingFromPlan) {
        const snapshot = deepClone(e.planSubSlot);
        // #4939: the snapshot is THIS session's own record, so it carries this session's own
        // date. On a finished workout the copy above can be a clone of the live program entry,
        // which already holds whichever session concluded most recently — freezing that in
        // would make an older session read as the exercise's latest for ever. The date is
        // decided by `snapshotConcludedDate`, which is where the rule and its tests live, and
        // it is applied to the SNAPSHOT alone: `e.planSubSlot` is what gets attached to the
        // program a few lines below, so writing an old workout's date onto it would roll the
        // program's own progression clock backwards.
        snapshot.lastTimeConcluded_utc = snapshotConcludedDate(
          e.planSubSlot.lastTimeConcluded_utc, this.logWorkout.time_utc, !!e.startedInFuture);
        e.logExercise.planSubslotBackup = snapshot;
      }
      // Put edited plan subslot to plan to plan data after first set is concluded as it is now safe to store any data modified before any set is concluded.
      this.writeSubSlotBackToPlan(e);
      //e.planSubSlot = this.plan.subSlots[e.logExercise.subSlotId];
      // #6428 R4: a hand-set weight skips the prescription cap for one session only. The snapshot
      // taken above keeps the mark, so a reload of THIS session still shows the weight that was set.
      if (e.planPath) {
        for (const path of e.pageAndPlanPathsForIntensity(e.planPath.intensityLevel)) {
          delete path.coachSetWeight;
        }
      }

      if (e.layoffWeight_kg !== undefined) {
        // #5617: store the weight the layoff asked for as the path's target, in both the weight-cut
        // and the rep-cut branch, so what the user was shown and what the plan remembers agree and
        // an exact hit cannot write a lighter weight over a heavier stored one. It is this workout's
        // own progression, so it goes into the workout's own copy of the plan as well as into the
        // plan itself. The other intensities get the same cut one-rep max run back to their own reps.
        for (const path of e.livePathsForCurrentIntensity()) {
          path.target_weight_kg = e.layoffWeight_kg;
        }
        this.writeLayoffWeightToOtherIntensities(e);
      } else if (e.reducedTargetWeight_pc && e.reducedTargetWeight_pc !== -1) {
        // the older rule (the #5617 rule could not judge this path, or had no set to work from): a
        // percentage off the last lifted weight, on every intensity — see `sweepPathsForEveryIntensity()`
        for (let exercisePath of e.sweepPathsForEveryIntensity()) {
          if (exercisePath.target_weight_kg && exercisePath.lastStats && exercisePath.lastStats.weight_kg) {  // just in case something is not initialized yet (workaround for #916)
            exercisePath.target_weight_kg = olderRuleLayoffWeight_kg(exercisePath.lastStats.weight_kg, e.reducedTargetWeight_pc);
          }
        }
      }
      delete e.reducedTargetWeight_pc;
      delete e.layoffWeight_kg;
      delete e.layoffCut1RM_net_kg;
      // issue #3339: the loader already mutated `planPath.target_reps` and `min_reps`
      // in place on `uie.planSubSlot` when populating `reducedTargetReps_delta`. The
      // `writeSubSlotBackToPlan(e)` call above has now swapped that mutated path into
      // the canonical plan, so persistence is automatic — just clear the marker. The
      // one case where that write is skipped is an exercise the program no longer
      // holds (#4614), and there is nothing to persist into then. (Rep targets are
      // per-path and the loader only touches the active one.)
      delete e.reducedTargetReps_delta;
    }

    // df-4127ee779713: record what this set was PRESCRIBED — what the correction rewind
    // (`restorePrescribedTargets`) restores from. Taken HERE, after the block above, because the
    // layoff weight cut in that block is part of the prescription: it is the reduced weight the
    // loader already showed the user, and cloning before it recorded a target nobody was asked to
    // lift. Do not move this earlier, and do not add a progression write above it.
    // See `design-docs/volume-autoregulation.md`, "Correcting an already-finished set".
    if (e.planPath) {
      s.logSet.planPathBackup = deepClone(e.planPath);
      // df-72de01d6fc68: and what the OTHER intensities hold for the strength-retest demand, which
      // this conclusion may write onto every intensity. Taken at the same point for the same reason.
      // Read off the page's copy, which is what `sweepPathsForEveryIntensity()` walks.
      s.logSet.otherIntensityRetestDemandBackup = recordOtherIntensityRetestDemands(
        e.planSubSlot.exercisePaths, e.planPath.intensityLevel);
    }

    // df-78bb051f6709: the record above says what the path held; this one says how to read it. In
    // the rep-fallback layoff the path keeps its higher climbing `target_weight_kg` and the weight
    // the user was actually asked for is `lastStats.weight_kg`, which `restorePrescribedTargets`
    // puts back from the clone above — so the flag is all a correction needs, and storing the
    // number as well would only give it a second copy that can disagree. Written and never
    // cleared: what a set was prescribed does not change because the user corrects what they typed.
    if (wasLayoffRepFallback) {
      s.logSet.layoffHeldWeightWithReducedReps = true;
    }
    if (layoffPrescribedThisSet) {
      s.logSet.layoffPrescribed = true;
    }

    // #5176: same idea, for the prescription cap. The path keeps its higher climbing
    // `target_weight_kg` and the weight the user was actually asked for is `plannedWeight_kg`, which
    // `openSet` already recorded and which survives a correction — so the flag is all that is
    // needed here, and storing the number again would only give it a second copy that can disagree.
    if (e.prescriptionCap) {
      s.logSet.prescribedWeightCapped = true;
    }

    // df-148d4d4ced87: put back any earlier set the stored list lost but the screen still shows
    // finished, so it is not stored as a blank skip. The report carries the state as found.
    this.fillMissingEarlierSetsBeforeConclude(e, iSet);
    // this ends the transient "UI-only" detached state that this set and exercise might have had
    e.logExercise.sets[iSet] = s.logSet;
    e.logExercise.exerciseId = e.exercise._id;
    if (e.planPath) {
      e.logExercise.pathLevel = e.planPath.intensityLevel;
    }

    // continue with same units the next time
    e.stat.weight_unit = s.logSet.stats.weight_unit;

    // we need to use the target as rounded to current config in _some_ places below
    let roundedTargetWeight_kg: number | undefined;
    if (e.planPath) {
      const benchmarkInput = benchmarkInputForSet({
        iSet,
        set: s.logSet,
        firstSet: e.sets[0]?.logSet,
        planPath: e.planPath,
        layoffHeld: wasLayoffRepFallback,
      });
      const benchmarkWeight_kg = benchmarkTargetWeight_kg(benchmarkInput);
      // a benchmark the user was ASKED to lift (capped, or layoff-held) is never judged above it:
      // the snap goes to the equipment attached NOW, which can raise it. See the helper's comment.
      const { weight_kg: snappedBenchmark_kg } = this.adjustWeight(benchmarkWeight_kg, 'round', e.equipment);
      roundedTargetWeight_kg = clampSnappedBenchmarkWeight_kg(benchmarkInput, snappedBenchmark_kg);
    }

    // keep track of statistics
    estimate1RM(s.logSet.stats, this.fitnessState.weight_kg);
    this.rememberDerivedStatsSource(s);
    // #5174: what we remember for this exercise is THIS SESSION's best, not the best ever. The
    // rule this replaced only ever wrote a value at or above the stored one, which made the number
    // a personal record that could never come down — one corrupt reading pinned it for good. The
    // just-concluded set is already Done and already in `e.logExercise.sets` by here (it is marked
    // above and written into the array a few lines up), so it is included.
    //
    // The sets come from every screen row for this exercise, not just this one: a workout can hold
    // the same exercise in two sub-slots and both share one `ExerciseStat` - see
    // `sessionSetsForExercise`. `screenLogExercises()` says why the screen rather than the document.
    //
    // The timestamp moves only when the number itself moves, which is what it claims to record --
    // the date this estimate was arrived at. Re-confirming the same best does not restate it.
    const storedStrength_net_kg = nextStoredStrength_net_kg(
      sessionSetsForExercise(this.screenLogExercises(), e.exercise._id),
      e.stat.estimated1RM_net_kg,
    );
    if (storedStrength_net_kg !== e.stat.estimated1RM_net_kg) {
      e.stat.estimated1RM_net_kg = storedStrength_net_kg;
      e.stat.estimated1RM_timestamp = this.logWorkout.time_utc;
    }

    // for temp swaps: skip all plan progression, save history and return
    if (e.isTempSwap) {
      if (!this.fitnessState.tempSwapHistory) {
        this.fitnessState.tempSwapHistory = {};
      }
      this.fitnessState.tempSwapHistory[e.exercise._id] = {
        lastSets: e.logExercise.sets
          .filter(ls => ls.state === LogRecordState.Done)
          .map(ls => ({ weight_kg: ls.stats.weight_kg, reps: ls.stats.reps, weight_unit: ls.stats.weight_unit })),
        lastPerformed_utc: this.debug.getNowLocal().toUTC().toISO(),
      };

      let transaction = DatabaseWrapper.getTransactionId();
      await this.saveWorkoutAsync(transaction);
      await this.userDb.saveFitnessStateAsync(transaction);
      s.inEditMode.set(true);
      this.logging.checkWorkoutExerciseValidity("concludeSet end (temp swap)", e.logExercise);
      return;
    }

    // if was testing RM
    if (s.logSet.setType == SetType.NonRMTest) {
      let exerciseNotOptimal = false;
      let handleNotOptimal = async (exerciseNotOptimal: boolean) => {
        // handle the case where user would work with negative weight
        if (exerciseNotOptimal) {
          // #6428 R9: no swap while a coach change made during this session guards the exercise —
          // the swap would act on a copy the program no longer holds
          if (!e.coachChangedDuringSession) {
            await this.swapExerciseOutOfProgramAsync(e, ProgramSwapReason.Weakness);
            s.logSet.postStatus.push({ status: PostSetStatus.Weakness, textInserts: undefined });
          }
        } else {
          s.logSet.postStatus.push({ status: PostSetStatus.NonRMStrengthTest, textInserts: undefined });
          ExerciseStat.unmarkQuarantined(e.stat);
          ExerciseStat.unmarkSwitched(e.stat);
        }
      };

      let numPaths = e.planSubSlot.exercisePaths.length;

      // did this test land in the rep band a non-RM test trusts? The same question is asked for an
      // RM test below, against a different band — see classifyStrengthTest.
      const nonRmOutcome = classifyStrengthTest(s.logSet.stats.reps, NON_RM_TEST_REP_BAND);

      // if tested correctly
      if (nonRmOutcome === StrengthTestOutcome.Passed) {
        if (s.logSet.stats.reps === s.logSet.plannedReps) {
          // read the established weight off the active path BEFORE writing, so every container
          // records the same number even if one of them had drifted
          const establishedWeight_kg = e.planPath.target_weight_kg;
          for (const path of e.livePathsForCurrentIntensity()) {
            path.orgTarget_weight_kg = establishedWeight_kg;
          }
        } else {
          for (const path of e.livePathsForCurrentIntensity()) {
            path.orgTarget_weight_kg = path.target_weight_kg = s.logSet.stats.weight_kg;
            path.min_reps = s.logSet.stats.reps + 1;
          }
        }

        await handleNotOptimal(exerciseNotOptimal);
        if (!exerciseNotOptimal) {
          // promote user's stated nominal target back into target_reps for the upcoming Regular1st
          // workouts. plannedReps (the nonrmtest's test target) is only the fallback when the user
          // never typed a preference. Phase 2 model reads preferRepTargetByIntensity from ExerciseStat.
          this.setTargetReps(s, this.getPreferRepTarget(e, e.planPath.intensityLevel) ?? s.logSet.plannedReps);

          // #4165: a strength test that ran PAST its rep target has proved the weight the user
          // picked is too light for that target, so carrying it forward as the working weight
          // prescribes a load we already know is wrong. Replace it with the load the rep target
          // actually implies. This runs after setTargetReps so it reads the final target.
          //
          // Note this is a different code path from the same rule inside targetHit: an overshot
          // test set never reaches targetHit (that only fires when the reps land exactly on the
          // target), so without this the fix would only apply from the second set onwards.
          this.applyBeatTargetWeightAfterStrengthTest(e, s);
        }

        if (s.logSet.stats.reps === s.logSet.plannedReps) {
          this.targetHit(s, e);
        }

        s.logSet.setSuccess = SetSuccess.Ok;

        this.establishWorkingPath(e, s);
        // df-ad72c95954fc: the demand for another strength test is answered here, so it is
        // cleared in every container this path lives in — exactly like the set type written
        // by `establishWorkingPath` above. Clearing only `planPath` left the other container
        // holding `true`, and a later correction of this same set then wrote that stale `true`
        // back into the fitness plan. See `livePathsForCurrentIntensity()`.
        for (const path of e.livePathsForCurrentIntensity()) {
          delete path.nextWorkoutNonRMTestRequired;
        }
        if (numPaths > 1) {
          // df-72de01d6fc68: each other intensity still owes its own test. Written through the same
          // containers the correction rewind restores (`pageAndPlanPathsForIntensity`), so a
          // correction that still passes puts this back everywhere the rewind took it from.
          for (const pagePath of e.planSubSlot.exercisePaths) {
            if (pagePath.intensityLevel !== e.planPath.intensityLevel && pagePath.nextWorkoutSetType !== SetType.Regular1st) {
              for (const path of e.pageAndPlanPathsForIntensity(pagePath.intensityLevel)) {
                path.nextWorkoutSetType = SetType.NonRMTest;
              }
            }
          }
        }
        // if the set ran so far past the target that its estimated 1RM cannot be trusted (#4165), the
        // load finding was misperformed — ask for a heavier attempt instead of prescribing a weight
        // derived from an unusable estimate. This mirrors what the RM test already does above its own
        // rep ceiling and what the branch below does under the band's lower bound.
      } else if (nonRmOutcome === StrengthTestOutcome.FailedTooManyReps) {
        s.logSet.setSuccess = SetSuccess.Fail;
        // if this is the last set, we can only retry in a future workout
        if (e.sets.length - 1 == iSet) {
          s.logSet.postStatus.push({ status: PostSetStatus.StrengthTestFailToManyRepsLastSet, textInserts: undefined });
          // if sets are left, the next one becomes another NonRMTest to try again with more weight
        } else {
          s.logSet.postStatus.push({ status: PostSetStatus.StrengthTestFailToManyReps, textInserts: undefined });
        }
      } else {
        // mark it
        s.logSet.setSuccess = SetSuccess.Fail;
        // if this is the last set
        if (e.sets.length - 1 == iSet) {
          // warn the user that this was wrong
          s.logSet.postStatus.push({ status: PostSetStatus.StrengthTestFailToFewRepsLastSet, textInserts: undefined });
          // if we still have sets left to try and correct
        } else {
          // warn the user that this was wrong
          s.logSet.postStatus.push({ status: PostSetStatus.StrengthTestFailToFewReps, textInserts: undefined });
        }
      }
    } else if (s.logSet.setType == SetType.RMTest) {

      let exerciseNotOptimal = false;
      let handleNotOptimal = async (exerciseNotOptimal: boolean) => {
        // handle the case where user would work with negative weight
        if (exerciseNotOptimal) {
          // #6428 R9: no swap while a coach change made during this session guards the exercise —
          // the swap would act on a copy the program no longer holds
          if (!e.coachChangedDuringSession) {
            await this.swapExerciseOutOfProgramAsync(e, ProgramSwapReason.Weakness);
            s.logSet.postStatus.push({ status: PostSetStatus.Weakness, textInserts: undefined });
          }
        } else {
          // congratulate the user
          if (e.sets.length > iSet + 1) {
            s.logSet.postStatus.push({ status: PostSetStatus.StrengthTestSuccess, textInserts: undefined });
          } else {
            s.logSet.postStatus.push({ status: PostSetStatus.StrengthTestSuccessLastSet, textInserts: undefined });
          }
          ExerciseStat.unmarkQuarantined(e.stat);
          ExerciseStat.unmarkSwitched(e.stat);
        }
      };

      let handlePath = (path: Plan_ExercisePath) => {
        // the arithmetic is pure and lives in workout-progression-decision.ts so it can be
        // unit-tested; snapping onto the equipment and writing into the plan stay here, because
        // both need this page's equipment configuration.
        const derived = deriveRmTestPathTarget({
          estimated1rm_net_kg: s.logSet.stats.estimated1rm_net_kg,
          intensity_pc: getSlotPathIntensity(e.planSubSlot, path),
          bodyWeight_kg: this.fitnessState.weight_kg,
          bw_pc: s.logSet.stats.bw_pc,
        });

        // read BEFORE the snap below: the equipment grid has no negative rungs, so snapping first
        // would hide "this exercise is too hard for this user" as an ordinary light prescription
        if (derived.isBelowZero) {
          exerciseNotOptimal = true;
        }
        ({ weight_kg: path.target_weight_kg } = this.adjustWeight(
          derived.target_weight_kg,
          derived.rounding,
          e.equipment,
        ));

        path.orgTarget_weight_kg = path.target_weight_kg;
      }

      // if tested correctly
      if (classifyStrengthTest(s.logSet.stats.reps, RM_TEST_REP_BAND) === StrengthTestOutcome.Passed) {
        s.logSet.setSuccess = SetSuccess.Ok;

        // update the expected weight for all paths in the plan (each with different intensity),
        // and for the intensity being performed also in the workout's own copy of the plan —
        // see `sweepPathsForEveryIntensity()`. `handlePath` derives its weight from the path's
        // own intensityLevel, so running it once per container gives both the same number.
        for (let path of e.sweepPathsForEveryIntensity()) {
          handlePath(path);
          path.nextWorkoutSetType = SetType.RepTest;
        }

        await handleNotOptimal(exerciseNotOptimal);

        // if entered too many reps
      } else {
        // mark it
        s.logSet.setSuccess = SetSuccess.Fail;
        // if this is the last set
        if (e.sets.length - 1 == iSet) {
          // warn the user that this was wrong
          s.logSet.postStatus.push({ status: PostSetStatus.StrengthTestFailToManyRepsLastSet, textInserts: undefined });
          // the demand for another strength test is this workout's own progression, so for the
          // intensity being performed it goes into the workout's own copy of the plan too
          for (let path of e.sweepPathsForEveryIntensity()) {
            path.nextWorkoutSetType = SetType.NonRMTest;
            path.nextWorkoutNonRMTestRequired = true;
          }
          // if we still have sets left to try and correct
        } else {
          // warn the user that this was wrong
          s.logSet.postStatus.push({ status: PostSetStatus.StrengthTestFailToManyReps, textInserts: undefined });
        }
      }
      // if was testing reps
    } else if (s.logSet.setType == SetType.RepTest) {
      // whether the weight was off target is decided ONCE, by the caller, and travels here with the
      // answer — deciding it again here off a weight worked out differently is how the user ends up
      // asked about a weight they never changed, or recorded as failing a set nobody questioned.
      // Only an edit-mode conclusion has no question behind it, and it never asks; it re-derives
      // from `prescribedRepTestWeight_kg`, which is the SAME number the question used because it is
      // read off the set rather than rebuilt from the plan (df-393f9706fb12).
      const prescribedRepTestWeight_kg = this.prescribedRepTestWeight_kg(e, s);
      const weightWasOffTarget = repTestWeightQuestion === undefined
        ? isRepTestWeightOffTarget(s.logSet.stats.weight_kg, prescribedRepTestWeight_kg)
        : repTestWeightQuestion.weightWasOffTarget;
      // the deciding is pure and lives in rep-test-weight-answer.ts so it can be unit-tested;
      // carrying the verdict out — the status, the retest demand — stays here, because those are
      // writes into this page's own set and plan paths.
      const repTestWeightVerdict = resolveRepTestWeight({
        weightWasOffTarget,
        answer: repTestWeightQuestion?.answer,
        inEditMode: s.inEditMode(),
        // df-6c87ae2df081: a correction asks the user nothing, so the status the first conclusion
        // recorded is the only surviving account of what they answered. Read it back HERE, before
        // the withdrawal below can take it off, so a corrected set is judged the way the user's own
        // answer judged it rather than by its weight alone.
        recordedAnswer: repTestWeightAnswerForPostStatus(recordedRepTestWeightAnswerStatus(s.logSet)),
      });
      const repWeightCorrect = repTestWeightVerdict.weightCorrect;
      const configureWeight = repTestWeightVerdict.configureWeight;
      if (repTestWeightVerdict.answerMissing) {
        this.logging.trackSilentProblem('RepTestWeightAnswerMissing', {
          exerciseId: e.exercise._id,
          loggedWeight_kg: s.logSet.stats.weight_kg,
          // the field keeps its old name so a saved Sentry search still finds it. What it holds is
          // now the ONE prescribed weight both the question and this conclusion judged against,
          // rather than the plan-derived `roundedTargetWeight_kg` it used to carry.
          roundedTargetWeight_kg: prescribedRepTestWeight_kg,
        });
      }
      // df-09c86ce422e8: a correction that puts the weight back ON target withdraws the answer the
      // user gave about the old weight. That answer — "too heavy", "too light", "closest I could
      // get" — was about a weight this set no longer records, and the success verdict pushed below
      // would otherwise sit next to it saying the opposite. It is withdrawn HERE rather than in
      // `onClickSetDone` beside the other correction clears, because `weightWasOffTarget` is
      // decided here, from the one prescribed weight both the question and this conclusion judge
      // against; working it out again in the caller is exactly what the comment above warns about.
      // Only on this condition: a correction records no answer of its own, so while the corrected
      // weight is STILL off target the old answer remains the only account of it — and the only
      // thing `resolveRepTestWeight` above can judge that weight by (df-6c87ae2df081) — so it stays.
      if (s.inEditMode() && !weightWasOffTarget) {
        clearRepTestWeightAnswerStatuses([s.logSet]);
      }
      if (repTestWeightVerdict.postStatus !== undefined) {
        s.logSet.postStatus.push({ status: repTestWeightVerdict.postStatus, textInserts: undefined });
      }
      // for the intensity being performed the retest demand goes into the workout's own copy of the
      // plan as well, or re-opening the exercise shows it as never having been made
      if (repTestWeightVerdict.requireStrengthRetest) {
        for (let path of e.sweepPathsForEveryIntensity()) {
          path.nextWorkoutSetType = SetType.NonRMTest;
          path.nextWorkoutNonRMTestRequired = true;
        }
      }

      // if rep test used correct weight (or user confirmed that better weight is not possible), then
      if (repWeightCorrect) {
        this.setTargetReps(s, s.logSet.stats.reps);

        // next WO will use increased weight _only_ if this reps is not clamped by the rep minimum rules!
        if (e.planPath.target_reps <= s.logSet.stats.reps) {
          this.targetHit(s, e);
        } else {
          s.logSet.setSuccess = SetSuccess.MinReps;
          for (const path of e.livePathsForCurrentIntensity()) {
            path.min_reps = s.logSet.stats.reps + 1;
          }
        }

        this.establishWorkingPath(e, s);
        if (e.sets.length > iSet + 1) {
          s.logSet.postStatus.push({ status: PostSetStatus.RepTestSuccess, textInserts: undefined });
        } else {
          s.logSet.postStatus.push({ status: PostSetStatus.RepTestSuccessLastSet, textInserts: undefined });
        }

        // if the rep test failed, try again next time
      } else {
        s.logSet.setSuccess = SetSuccess.Fail;
      }

      if (configureWeight) {
        void this.configurePlates(e, s);
      }

      // if ordinary first set
    } else if (s.logSet.setType == SetType.Regular1st) {
      // The Regular1st progression decision (target hit / min-reps success / reactive deload) is
      // pure logic, extracted to workout-progression-decision.ts so it can be unit-tested in
      // isolation from concludeSet's side effects (the workout loader normalizes seeded plan state,
      // which makes an end-to-end repro non-deterministic — see #3720).
      const decision = classifyRegular1stProgression({
        setStats: s.logSet.stats,
        roundedTargetWeight_kg,
        targetReps: e.planPath.target_reps,
        minReps: e.planPath.min_reps,
        schemeMinReps: getMinTargetRepsForIntensity(e.planPath.intensityLevel),
        previousFirstSetStats: e.previousFirstSetStats,
        lastStats: e.planPath.lastStats,
        bodyWeight_kg: this.fitnessState.weight_kg,
      });

      // the "lower than recommended weight" hint belongs to the effective-1RM-improved branch. Compare
      // on the equipment's loadable grid (isLoggedWeightBelowRecommended), not raw kg, so a pounds-
      // configured barbell logging the same nominal weight as the recommendation does not trip a false
      // "lower than recommended" note from lb→kg float noise (#3824).
      if (decision.isViaWeightImproved && roundedTargetWeight_kg != null && isLoggedWeightBelowRecommended(s.logSet.stats.weight_kg, roundedTargetWeight_kg, e.equipment)) {
        // name the weight the set itself stores, not the target snapped onto whatever equipment is
        // attached now — see advisoryRecommendedWeight_kg for why they can disagree (#5325)
        const recommendedWeight_kg = advisoryRecommendedWeight_kg(s.logSet.plannedWeight_kg, roundedTargetWeight_kg, s.logSet.stats.weight_kg);
        let plannedWeight_txt = formatMeasure(recommendedWeight_kg, 'addedweight_kg', this.settings.locale, s.logSet.stats.weight_unit);
        s.logSet.postStatus.push({ status: PostSetStatus.RegularSetLowerWeight, textInserts: { ["plannedWeight"]: plannedWeight_txt } });
      }

      // if hit the target weight and target reps → linear progression
      if (decision.outcome === Regular1stOutcome.TargetHit) {
        this.targetHit(s, e);

        // if progress without hitting target reps (heavier than prescribed above the scheme min — #3720 —
        // or cleared the raw-reps floor) → just increase reps next time
      } else if (decision.outcome === Regular1stOutcome.MinReps) {
        s.logSet.setSuccess = SetSuccess.MinReps;
        for (const path of e.livePathsForCurrentIntensity()) {
          path.min_reps = s.logSet.stats.reps + 1;
        }
        // #6447: the result stands, but the confirmation only says "You progressed!" when the set is
        // stronger than the best attempt of the last session in which the exercise was really done
        if (!isStrengthGainOverPreviousSession(s.logSet.stats, e.previousAttemptStats)) {
          s.logSet.noGainOverLastSession = true;
        }

        // if none of the conditions above detected any progress - deload
      } else {
        s.logSet.setSuccess = SetSuccess.Fail;

        // #5717: always ask, even when this failed set is the exercise's only set. The deload
        // outlives the session — it lowers the NEXT workout's target and stores the plateau
        // yardstick — so the user must be able to decline it, e.g. after a deliberately light
        // day. It used to be applied without asking on the last set, and the benchmark set is
        // the last set of every one-set exercise, which includes every Myo-Reps exercise
        // prescribed 2 to 5 sets (they collapse into one).
        const handleDeload = await this.alertCtrl.showAlertAsync({
          title: LL.workouts.alerts.agreeWithDeload(),
          message: LL.workouts.alerts.recommendDeload(),
          enableBackdropDismiss: false,
          buttons: [
            {
              text: LL.workouts.buttons.yesAgree(),
              role: AlertButtonRole.Primary,
              result: true,
            },
            {
              text: LL.workouts.buttons.noDisagree(),
              role: AlertButtonRole.Cancel,
              result: false,
            },
          ],
        });

        this.telemetry.trackEvent('workout_deload_response', {
          accepted: handleDeload,
          exercise: e.exercise._id,
          intensity: e.planPath.intensityLevel,
          weight_kg: s.logSet.stats.weight_kg,
          reps: s.logSet.stats.reps,
          estimated_1rm_kg: s.logSet.stats.estimated1rm_net_kg,
          is_plateau: !!(e.planPath.lastDeload1rm_net_kg && s.logSet.stats.estimated1rm_net_kg < e.planPath.lastDeload1rm_net_kg * 1.05),
        });

        // #4625 — a deload the person agreed to when asked is a qualifying moment
        if (handleDeload) {
          void this.reviewPrompt.considerPromptingAsync(ReviewPromptTrigger.DeloadAccepted);
        }

        if (handleDeload) {
          // if already hit this same limit once before - then this is a plateau
          // (for it not to be "same", the new 1RM must be at least 5% better)
          const isPlateau = !!e.planPath.lastDeload1rm_net_kg && s.logSet.stats.estimated1rm_net_kg < e.planPath.lastDeload1rm_net_kg * 1.05;
          // #4822: when no set follows, there are no light sets for "Deloading..." to announce — the
          // whole deload is the next workout's lower target, so say that. A plateau lowers nothing,
          // and keeps the old wording beside its own card.
          const deloadStatus = isPlateau || e.sets.length > iSet + 1 ? PostSetStatus.ProgressionDeload : PostSetStatus.ProgressionDeloadLastSet;
          s.logSet.postStatus.push({ status: deloadStatus, textInserts: undefined });
          if (isPlateau && e.coachChangedDuringSession) {
            // #6428 R9: raise no plateau question while a coach change made during this session
            // guards the exercise; the next session judges the coach's version
          } else if (isPlateau) {
            // #4939: onto the entry the program holds today, never onto a finished session's
            // own snapshot — a question the user has to answer is about their program now.
            const plateauPointerHome = this.programSubSlotForAnswer(e);
            if (plateauPointerHome) {
              plateauPointerHome.plateauWorkoutLog_id = this.logWorkout._id;
            }
            s.logSet.postStatus.push({ status: PostSetStatus.PlateauDetected, textInserts: undefined });
            // if new limit, back up one step in the next WO, and mark this event for future plateau detection
          } else {
            const { weight_kg: deloadedWeight_kg } = this.adjustWeight(s.logSet.stats.weight_kg, 'step_down', e.equipment);
            // restore the user's stated rep-target preference (Phase 2 model) on deload; if they never
            // typed one, leave target_reps at whatever it was (system default from rebuild).
            const preferReps = this.getPreferRepTarget(e, e.planPath.intensityLevel);
            // #4890: the reduced weight and the strength estimate the deload happened at both decide
            // the NEXT workout, so both go into every container this path lives in. Written through
            // the active path alone they reached only one of the two, and which one varies — so a
            // deload taken while correcting a finished set never reached the fitness plan, and the
            // next workout prescribed the weight the user had just failed at.
            // `design-docs/volume-autoregulation.md`, "The containers a path lives in".
            for (const path of e.livePathsForCurrentIntensity()) {
              path.target_weight_kg = deloadedWeight_kg;
              path.target_reps = preferReps ?? path.target_reps;
              path.lastDeload1rm_net_kg = s.logSet.stats.estimated1rm_net_kg;
            }
          }
        } else {
          s.logSet.setSuccess = SetSuccess.MinReps;
          // #6309: stored apart from real progress, so the confirmation does not say "You progressed!"
          s.logSet.deloadDeclined = true;
        }
      }

      // if ordinary non-first set
    } else if (s.logSet.setType === SetType.Followup && iSet > 0) {
      // (the is>0 is just a precaution)
      // handle eventual autoprogression - if hit target reps twice in a row, immediately increase target weight.
      // the scoring rules are in classifyFollowupProgression
      const followupProgression = classifyFollowupProgression({
        setStats: s.logSet.stats,
        earlierSets: e.sets.slice(0, iSet).map(earlier => earlier.logSet),
        roundedTargetWeight_kg,
        targetReps: e.planPath.target_reps,
        anchorReps: this.getPreferRepTarget(e, e.planPath.intensityLevel),
        minReps: e.planPath.min_reps,
      });

      if (followupProgression === FollowupProgression.TargetHitAgain) {
        const progressionType = this.targetHit(s, e);

        // only notify the user if there's more sets to do - otherwise the increase is only visible in the next WO
        if (e.sets.length > iSet + 1 && !s.inEditMode()) {
          s.logSet.postStatus.push({ status: progressionType, textInserts: undefined });
        }

        // if previous sets didn't reach the rep target but this one did (unusual)
      } else if (followupProgression === FollowupProgression.TargetHit) {
        this.targetHit(s, e);

        /* If we didn't yet hit target reps, but did hit min reps - it needs to update min reps and stats used for further deloads */
      } else if (followupProgression === FollowupProgression.MinReps) {
        s.logSet.setSuccess = SetSuccess.MinReps;
        for (const path of e.livePathsForCurrentIntensity()) {
          path.min_reps = s.logSet.stats.reps + 1;
        }
      }

      // if managed to do more reps than target on a followup set _on the rep test day_ - increase the rep target!
      if (
        s.logSet.stats.weight_kg >= roundedTargetWeight_kg &&
        s.logSet.stats.reps >= e.planPath.target_reps &&
        e.sets[0].logSet.setType === SetType.RepTest
      ) {
        for (const path of e.livePathsForCurrentIntensity()) {
          // #5979: never past the rep ceiling
          path.target_reps = Math.min(s.logSet.stats.reps, MAX_TARGET_REPS);
        }
      }

      // #5620: after a strength test that beat its target, the stored weight has moved up but the
      // session keeps offering the tested weight, so a follow-up that did what it was asked lands
      // in none of the arms above. tell the user it counted — as a note only, never as a success,
      // because a success would let openSet prefill the raised weight on the next set.
      if (isFollowupAtStrengthTestWeight({
        setStats: s.logSet.stats,
        setSuccess: s.logSet.setSuccess,
        earlierSets: e.sets.slice(0, iSet).map(earlier => earlier.logSet),
        targetReps: e.planPath.target_reps,
        storedTargetWeight_kg: roundedTargetWeight_kg,
        equipment: e.equipment,
      })) {
        s.logSet.postStatus.push({ status: PostSetStatus.StrengthTestFollowupDone, textInserts: undefined });
      }
    }

    // #5620: the note on a strength test's follow-ups says the test moved the progression. a
    // correction re-runs concludeSet for the test alone, so if this is a strength test that left a
    // path (a weakness swap leaves none), re-decide the note on the follow-ups already concluded
    // after it. they are judged against the stored target snapped onto the equipment, which is what
    // the follow-up branch above uses in a chain that starts at a strength test: the layoff hold and
    // the prescription cap only ever apply to a Regular1st first set.
    if (s.logSet.setType === SetType.NonRMTest && e.planPath) {
      refreshStrengthTestFollowupNotes(e.sets.map(set => set.logSet), iSet, {
        targetReps: e.planPath.target_reps,
        storedTargetWeight_kg: this.adjustWeight(e.planPath.target_weight_kg, 'round', e.equipment).weight_kg,
        equipment: e.equipment,
      });
    }

    // store new progression level if target or rep progression was hit, plus...
    // ...check for weird cases where it is obvious the stats have improved but the math above doesn't
    // catch it (this happens for RegularFollow sets, usually)
    if (
      s.logSet.setSuccess === SetSuccess.TargetReps ||
      s.logSet.setSuccess === SetSuccess.MinReps ||
      (e.planPath.lastStats &&
        s.logSet.stats.reps >= e.planPath.lastStats.reps &&
        s.logSet.stats.weight_kg >= e.planPath.lastStats.weight_kg)
    ) {
      // #4890: this benchmark decides the next workout's progression when there is no previous
      // session to compare against, so it belongs in every container. Each gets its OWN clone —
      // sharing one object between the two would let a later edit of one silently change the other.
      for (const path of e.livePathsForCurrentIntensity()) {
        path.lastStats = deepClone(s.logSet.stats);
      }
    }

    this.checkVolumeAutoregulation(s);

    await this.askRepCeilingAsync(e, s);

    // if the workout doesn't need to be repeated the next time
    if (
      ((s.logSet.setType === SetType.RMTest || s.logSet.setType === SetType.NonRMTest) && s.logSet.setSuccess) ||
      s.logSet.setType === SetType.RepTest ||
      s.logSet.setType === SetType.Regular1st
    ) {
      // progress the exercise to next path in the next WO (if there are multiple paths)
      if (e.planSubSlot.exercisePaths && e.planSubSlot.exercisePaths.length > 1 && e.planPath) {
        let intensity = e.planPath.intensityLevel;
        let pathIndex = e.planSubSlot.exercisePaths.findIndex(p => p.intensityLevel === intensity);
        if (pathIndex >= 0) {
          pathIndex = (pathIndex + 1) % e.planSubSlot.exercisePaths.length;
        }
        const nextWorkoutIntensity = e.planSubSlot.exercisePaths[pathIndex].intensityLevel;
        e.planSubSlot.nextWorkoutIntensity = nextWorkoutIntensity;
        // df-8d1620a9593c: this one lives on the SUB-SLOT rather than on a path, so
        // `livePathsForCurrentIntensity()` cannot express it — write the plan's own entry here.
        // When the page's copy is already attached the two are the same object and this is a
        // no-op, which is exactly what happens today; when it is detached the plan would
        // otherwise keep prescribing the intensity the exercise has just finished.
        if (e.livePlanSubSlot && !e.coachChangedDuringSession) {
          e.livePlanSubSlot.nextWorkoutIntensity = nextWorkoutIntensity;
        }
      }
    }

    // df-5dbef4889250: every progression write for this set is done, so record where the session
    // has now left the exercise. This is what a later swap back, or a re-add, restores from once
    // the in-session backup is gone (it is wiped at every app start). The first set's
    // `planSubslotBackup` cannot serve: for a strength test it is the bare test.
    this.recordAfterSessionSnapshot(e);

    let transaction = DatabaseWrapper.getTransactionId();
    await this.userDb.savePlanAsync(transaction, undefined, this.planSaveContext(PlanSaveCaller.WorkoutConcludeSet));
    await this.userDb.saveFitnessStateAsync(transaction);

    // Mark set as in edit mode now that all alerts have been handled
    // This prevents re-showing alerts when user re-edits the set
    s.inEditMode.set(true);

    this.logging.checkWorkoutExerciseValidity("concludeSet end", e.logExercise)
  }

  /**
   * df-5dbef4889250: store the exercise's program entry as this session has now left it, in
   * `logExercise.planSubslotAfterSession`. Called at the end of `concludeSet`, so each set
   * overwrites the copy and the last one wins. Which entry may be copied, and when nothing is, is
   * decided by `afterSessionSnapshotFor`. A temp swap never gets here: `concludeSet` returns early.
   */
  private recordAfterSessionSnapshot(e: UiExercise): void {
    const snapshot = afterSessionSnapshotFor(e, this.logWorkout.time_utc);
    if (snapshot) {
      e.logExercise.planSubslotAfterSession = snapshot;
    }
  }

  /**
   * After a strength test that beat its rep target, move the working weight up to the load that
   * rep target actually implies (#4165).
   *
   * The test asks the user to find the weight they can lift for the target reps. When they manage
   * MORE reps than that, the weight they chose is provably too light — but the branch above still
   * records it as `target_weight_kg`, so without this the app would prescribe a load it already
   * knows is wrong. The decision comes from `decideBeatTargetProgression`, the same helper the
   * working-set path uses, so the two places this rule fires cannot drift apart. When one more rep
   * is the smaller jump than the heavier load (#5927), the tested weight stays and the rep target
   * moves to the reps done plus one instead.
   *
   * Does nothing when the reps did not beat the target (a test that fell SHORT means the weight was
   * too heavy, which the existing branch already handles by keeping what was lifted), or when the
   * equipment cannot produce anything heavier.
   */
  private applyBeatTargetWeightAfterStrengthTest(e: UiExercise, s: UiSet): void {
    const determinedTargetReps = e.planPath.target_reps;
    const loggedWeight_kg = s.logSet.stats.weight_kg;
    // if the set did not beat its target, the weight it used stands
    if (!Number.isFinite(determinedTargetReps) || !(s.logSet.stats.reps > determinedTargetReps)) {
      return;
    }

    const anchorReps = this.getPreferRepTarget(e, e.planPath.intensityLevel);
    const decision = decideBeatTargetProgression({
      loggedWeight_kg,
      loggedReps: s.logSet.stats.reps,
      loggedEstimated1rm_net_kg: s.logSet.stats.estimated1rm_net_kg,
      determinedTargetReps,
      // the weight branch below writes no rep target, so a raise here is prescribed at the target
      // `setTargetReps` already wrote — which its neighbour rules may have moved off the preferred one
      raisedTargetReps: determinedTargetReps,
      anchorReps,
      currentPlusOneStep_kg: this.adjustWeight(loggedWeight_kg, 'step_up', e.equipment).weight_kg,
      bodyWeight_kg: this.fitnessState.weight_kg,
      bw_pc: s.logSet.stats.bw_pc || 0,
      equipment: e.equipment,
    });
    // undefined means the equipment cannot go heavier — leave the tested weight in place
    if (decision === undefined) {
      return;
    }

    // #5927: one more rep was the smaller jump, so the tested weight stays and the rep target moves
    if (decision.move === BeatTargetMove.Reps) {
      if (decision.anchorRepsToSeed !== undefined) {
        const stat = this.fitnessState.exerciseStats[e.exercise._id] || {} as ExerciseStat;
        stat.preferRepTargetByIntensity ||= {};
        stat.preferRepTargetByIntensity[e.planPath.intensityLevel] = decision.anchorRepsToSeed;
        this.fitnessState.exerciseStats[e.exercise._id] = stat;
      }
      // written straight onto every container, as targetHit does. Not through setTargetReps: the
      // branch above already ran it, and a second run would push the neighbour path again (which a
      // correction cannot withdraw), repeat its badge, and could clamp reps + 1 back down to the
      // reps just done.
      for (const path of e.livePathsForCurrentIntensity()) {
        path.target_reps = decision.newTargetReps;
        // the branch above armed min_reps at reps + 1, which now equals the new rep target. Left
        // there, repeating the tested set next session would score below the minimum and offer a
        // deload. Clear it back to the post-progression grace, as every progression does.
        path.min_reps = 1;
      }
      return;
    }

    for (const path of e.livePathsForCurrentIntensity()) {
      path.orgTarget_weight_kg = path.target_weight_kg = decision.newTargetWeight_kg;
      // the branch above armed min_reps from the reps done at the OLD, lighter weight. That floor is
      // meaningless at the new weight — left in place it would demand more reps than the rep target
      // itself and read the next session as a failure. Clear it back to the post-progression grace.
      path.min_reps = 1;
    }
  }

  private targetHit(s: UiSet, e: UiExercise): PostSetStatus {
    s.logSet.setSuccess = SetSuccess.TargetReps;

    // Get current session data
    const currentWeight = s.logSet.stats.weight_kg;
    const targetReps = s.logSet.stats.reps;  // Use actual performed reps
    const bw_pc = s.logSet.stats.bw_pc || 0;

    let { weight_kg: currentPlusOneStep } = this.adjustWeight(currentWeight, 'step_up', e.equipment);
    let { weight_kg: target } = this.adjustWeight(s.uiExercise.planPath.target_weight_kg, 'round', e.equipment);
    let { weight_kg: targetPlusOneStep } = this.adjustWeight(target, 'step_up', e.equipment);

    // Check if we've maxed out the equipment
    // #4890: the loader reads this off whichever container it finds on the NEXT workout, to decide
    // whether to suggest switching exercise — so it goes into every container, like the target
    // fields written further down.
    // unreadable equipment is never called maxed out (#4337, #5860) — see decideMaxedOutEquipment
    const { maxedOutAt_kg: maxedOutAtStoredTarget_kg, skippedBrokenEquipment } = decideMaxedOutEquipment(target, targetPlusOneStep, e.equipment);
    if (skippedBrokenEquipment) {
      this.logging.trackSilentProblem('maxed-out-check-skipped-invalid-equipment', {
        exerciseId: e.exercise._id,
        equipmentType: e.equipment?.type,
      });
    }

    // The weight/rep decision itself is pure, and lives in workout-progression-decision.ts so it can
    // be unit-tested away from this method's side effects (same reason as classifyRegular1stProgression).
    const decision = decideTargetHitProgression({
      loggedReps: targetReps,
      determinedTargetReps: e.planPath.target_reps,
      anchorReps: this.getPreferRepTarget(e, e.planPath.intensityLevel),
      // only the exercise's first set of the session is the benchmark the decision is based on
      isBenchmarkSet: e.sets.indexOf(s) === 0,
      // read BEFORE the write loop below replaces it, so a follow-up cannot write it downwards (#5338)
      storedTargetWeight_kg: e.planPath.target_weight_kg,
      currentWeight_kg: currentWeight,
      currentPlusOneStep_kg: currentPlusOneStep,
      loggedEstimated1rm_net_kg: s.logSet.stats.estimated1rm_net_kg,
      bodyWeight_kg: this.fitnessState.weight_kg,
      bw_pc,
      equipment: e.equipment,
      minTargetReps: getMinTargetRepsForIntensity(e.planPath.intensityLevel),
    });

    // a set can only reach here by hitting a rep target, so a missing one means some earlier step
    // left the path without the target it is supposed to carry — surface it rather than guessing
    if (decision.hasNoDeterminedTarget) {
      this.logging.trackSilentProblem('targetHit without a determined rep target (#4165)', {
        exerciseId: e.exercise._id,
        exerciseName: e.exercise.name,
        setType: s.logSet.setType,
        intensityLevel: e.planPath.intensityLevel,
        loggedWeight_kg: currentWeight,
        loggedReps: targetReps,
      });
    }

    // Auto-record the achieved reps as the user's anchor preference if they haven't typed one in
    // Edit Workouts. Pre-Phase-2 this lived on path.originalTargetReps; Phase 2 stores it on
    // ExerciseStat.preferRepTargetByIntensity so the anchor survives plan rebuilds, intensity
    // changes, and exercise swaps. Only the FIRST successful targetHit seeds it; later hits never
    // overwrite, so the Case-2 +1 bumps oscillate around the original anchor instead of chasing it.
    if (decision.anchorRepsToSeed !== undefined) {
      const stat = this.fitnessState.exerciseStats[e.exercise._id] || {} as ExerciseStat;
      stat.preferRepTargetByIntensity ||= {};
      stat.preferRepTargetByIntensity[e.planPath.intensityLevel] = decision.anchorRepsToSeed;
      this.fitnessState.exerciseStats[e.exercise._id] = stat;
    }

    // the marker stands only if the new target is still at the maxed-out weight: a strength test can
    // leave an old, heavier target stored and then move the target well below it (df-ecb462a04f94)
    const { weight_kg: newTargetRounded_kg } = this.adjustWeight(decision.newTargetWeight_kg, 'round', e.equipment);
    const maxedOutAt_kg = maxedOutAtAfterProgression_kg(maxedOutAtStoredTarget_kg, newTargetRounded_kg);
    if (maxedOutAt_kg !== undefined) {
      s.logSet.postStatus.push({ status: PostSetStatus.MaxedOutEquipment, textInserts: undefined });
    }

    // write the new targets into every container this path lives in, so they survive a save
    for (const path of e.livePathsForCurrentIntensity()) {
      path.target_weight_kg = decision.newTargetWeight_kg;
      path.target_reps = decision.newTargetReps;
      path.min_reps = 1;
      path.reachedMaxWeightConfiguration = maxedOutAt_kg;
    }

    // #5979: the target above is already held at the ceiling, which is what a declined offer leaves.
    // concludeSet asks the user once the whole progression is decided.
    s.pendingRepCeiling = decision.repCeiling;

    const progressionStatus: PostSetStatus = decision.progression;
    return progressionStatus;
  }

  /**
   * #5979: the set just concluded would have pushed the rep target past `MAX_TARGET_REPS`, and
   * `targetHit` has already written what happens instead. A raise (one weight step, back at the
   * preferred rep target) is applied silently: no question and no note (owner decision). When one
   * step up allows too few reps, the target was held at 30 and the user is asked whether to switch
   * the exercise — every time the ceiling is reached, the way the deload question is asked (#5717),
   * so a user who declines is asked again next time. A set that already accepted a switch is not
   * asked again when it is corrected: the program already holds the replacement.
   *
   * Why the strength is read as if the set had stopped at 20 reps: `decideRepCeiling`, and
   * `design-docs/reactive-deload.md`, #4165 part 6.
   */
  private async askRepCeilingAsync(e: UiExercise, s: UiSet): Promise<void> {
    const proposal = s.pendingRepCeiling;
    s.pendingRepCeiling = undefined;
    if (!proposal) {
      return;
    }

    // a raise is applied without telling the user (owner decision): the next session just shows it
    if (proposal.action === RepCeilingAction.Raise) {
      return;
    }
    // if a correction re-concluded a set that already accepted the switch, the program already holds
    // the replacement: asking again would swap a second time, or record a contradictory "held at 30"
    if (s.logSet.postStatus.some(p => p.status === PostSetStatus.RepCeilingSwitch)) {
      return;
    }
    // #6428 R9: no switch offer while a coach change made during this session guards the exercise
    if (e.coachChangedDuringSession) {
      return;
    }

    const accepted = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.workouts.alerts.repCeiling.title(),
      message: LL.workouts.alerts.repCeiling.switchMessage(),
      enableBackdropDismiss: false,
      buttons: [
        { text: LL.workouts.alerts.repCeiling.yes(), role: AlertButtonRole.Primary, result: true },
        { text: LL.workouts.alerts.repCeiling.noKeep(), role: AlertButtonRole.Cancel, result: false },
      ],
    });

    this.telemetry.trackEvent('workout_rep_ceiling_response', {
      accepted: !!accepted,
      exercise: e.exercise._id,
      intensity: e.planPath.intensityLevel,
      weight_kg: s.logSet.stats.weight_kg,
      reps: s.logSet.stats.reps,
    });

    // if declined, targetHit already left the weight and a rep target of 30 on every path
    if (!accepted) {
      s.logSet.postStatus.push({ status: PostSetStatus.RepCeilingHeld, textInserts: undefined });
      return;
    }

    // the swap takes effect from the next workout; this session keeps the exercise it is logging
    const { mayUseSameEquipment } = await this.swapExerciseOutOfProgramAsync(e, ProgramSwapReason.RepCeiling);
    s.logSet.postStatus.push({
      status: PostSetStatus.RepCeilingSwitch,
      // #6063: when every exercise that could replace it trains on the same equipment
      textInserts: mayUseSameEquipment ? { sameEquipment: 'true' } : undefined,
    });
  }

  /** after RepTest set succeeds, this properly adjusts rep target */
  setTargetReps(s: UiSet, targetReps: number) {
    // minimum target reps depends on path type
    const minReps = getMinTargetRepsForIntensity(s.uiExercise.planPath.intensityLevel);
    let newTargetReps = Math.max(targetReps, minReps);

    // when one path pushes some _other_ path away from itself, it moves that path's rep target and
    // backs off its target weight. df-e5bc0aa664f1: both go into every live copy of the other
    // intensity's path, so a page rebuilt mid-workout (a detached copy) still saves them into the
    // plan — see `pageAndPlanPathsForIntensity()`. the numbers are worked out once, off the page's
    // copy the push was decided from, so every container records the same values.
    //
    // df-09c86ce422e8: the badge that reports such a push goes on with `addPostStatusOnce`, not
    // `postStatus.push`. Correcting a rep test upwards re-runs this method and can push the other
    // path FURTHER, which is a genuine second push — so the badge cannot be withdrawn beforehand
    // the way the other correction badges are (`clearProgressionOutcomeStatuses` says why), and
    // without the guard the user is told the same thing twice on one set.
    const pushOtherPath = (pagePath: Plan_ExercisePath, newOtherTargetReps: number, weight: NeighbourPushWeight) => {
      const backedOffWeight_kg = weight === NeighbourPushWeight.BackOff
        ? this.adjustWeight(pagePath.orgTarget_weight_kg, 'round', s.uiExercise.equipment).weight_kg
        : undefined;
      // #5979: a push never moves the other path past the ceiling either
      newOtherTargetReps = Math.min(newOtherTargetReps, MAX_TARGET_REPS);
      for (const path of s.uiExercise.pageAndPlanPathsForIntensity(pagePath.intensityLevel)) {
        path.target_reps = newOtherTargetReps;
        if (weight === NeighbourPushWeight.BackOff) {
          path.target_weight_kg = backedOffWeight_kg;
        }
      }
    };

    // DUPlight pushes itself away from DUPheavy
    if (s.uiExercise.planPath.intensityLevel === IntensityLevel.DUPlight) {
      let heavyPath = s.uiExercise.planSubSlot.exercisePaths.find(p => p.intensityLevel === IntensityLevel.DUPheavy);
      if (heavyPath && heavyPath.target_reps) {
        newTargetReps = Math.max(newTargetReps, heavyPath.target_reps + 4);
      }

      // DUPheavy pushes DUPlight away from itself
    } else if (s.uiExercise.planPath.intensityLevel === IntensityLevel.DUPheavy) {
      let lightPath = s.uiExercise.planSubSlot.exercisePaths.find(p => p.intensityLevel === IntensityLevel.DUPlight);
      if (lightPath && lightPath.target_reps) {
        let minLightTargetReps = Math.max(newTargetReps + 4, lightPath.target_reps);
        if (minLightTargetReps > lightPath.target_reps) {
          addPostStatusOnce(s.logSet, PostSetStatus.UpdatedRepTargetHighRepSession);
          pushOtherPath(lightPath, minLightTargetReps, NeighbourPushWeight.BackOff);
        }
      }

      // TUPlight pushes itself away from TUPmedium
    } else if (s.uiExercise.planPath.intensityLevel === IntensityLevel.TUPlight) {
      let mediumPath = s.uiExercise.planSubSlot.exercisePaths.find(p => p.intensityLevel === IntensityLevel.TUPmedium);
      if (mediumPath && mediumPath.target_reps) {
        newTargetReps = Math.max(newTargetReps, mediumPath.target_reps + 3);
      }

      // TUPheavy pushes itself away from TUPmedium
    } else if (s.uiExercise.planPath.intensityLevel === IntensityLevel.TUPheavy) {
      let mediumPath = s.uiExercise.planSubSlot.exercisePaths.find(p => p.intensityLevel === IntensityLevel.TUPmedium);
      if (mediumPath && mediumPath.target_reps) {
        newTargetReps = Math.min(newTargetReps, mediumPath.target_reps - 3);
        newTargetReps = Math.max(newTargetReps, 3); // but no less than 3 !
      }

      // TUPmedium pushes TUPlight and TUheavy away from itself
    } else if (s.uiExercise.planPath.intensityLevel === IntensityLevel.TUPmedium) {
      let lightPath = s.uiExercise.planSubSlot.exercisePaths.find(p => p.intensityLevel === IntensityLevel.TUPlight);
      if (lightPath && lightPath.target_reps) {
        let minLightTargetReps = Math.max(newTargetReps + 3, lightPath.target_reps);
        if (minLightTargetReps > lightPath.target_reps) {
          addPostStatusOnce(s.logSet, PostSetStatus.UpdatedRepTargetHighRepSession);
          pushOtherPath(lightPath, minLightTargetReps, NeighbourPushWeight.BackOff);
        }
      }

      let heavyPath = s.uiExercise.planSubSlot.exercisePaths.find(p => p.intensityLevel === IntensityLevel.TUPheavy);
      if (heavyPath && heavyPath.target_reps) {
        let maxHeavyTargetReps = Math.min(newTargetReps - 3, heavyPath.target_reps);
        if (maxHeavyTargetReps < heavyPath.target_reps) {
          addPostStatusOnce(s.logSet, PostSetStatus.UpdatedRepTargetHeavySession);
          pushOtherPath(heavyPath, maxHeavyTargetReps, NeighbourPushWeight.Keep);
        }
      }
    }

    // #5979: no rep target goes past the ceiling. On a light path near 30 this can close the DUP/TUP
    // gap to its heavier sibling; that is accepted — a target the user is never allowed to reach
    // would be worse.
    newTargetReps = Math.min(newTargetReps, MAX_TARGET_REPS);

    if (targetReps !== newTargetReps) {
      s.logSet.postStatus.push({ status: PostSetStatus.UpdatedRepTargetRegularSession, textInserts: undefined });
    }

    for (const path of s.uiExercise.livePathsForCurrentIntensity()) {
      path.target_reps = newTargetReps;
    }
  }

  /** when a set is done, this tunes volumeAdjustment value if needed */
  checkVolumeAutoregulation(set: UiSet) {
    const exercise = set.uiExercise;
    const setIndex = exercise.sets.findIndex(s => s === set);
    // only followup sets after the first set are candidates — bail early to
    // skip the myo-rep lookup on every set completion
    if (setIndex < 1 || set.logSet.setType !== SetType.Followup) {
      return;
    }
    // #6428 R9: no volume question while a coach change made during this session guards the exercise
    if (exercise.coachChangedDuringSession) {
      return;
    }
    // if the user already answered for this exercise today, do not ask again (#4714)
    const alreadyAnsweredThisWorkout = !!exercise.logExercise.volumeAutoregulationAnswered;
    const firstSet = exercise.sets[0];

    // baseline must reflect myo-rep translation — defaultVolume_sets is a
    // combined classic-equivalent volume for myo-rep exercises (#3201)
    //
    // #4939: the baseline is the volume the user's PROGRAM prescribes now, because the question
    // this raises asks whether to change that. On a finished workout the copy the screen draws
    // with is that session's own snapshot, which holds the volume prescribed back then — asking
    // against it would compare today's performance with a number that is no longer in force.
    const baselineNumSets = getMyoAwareNumSets(
      this.programSubSlotForAnswerOrDisplayCopy(exercise),
      exercise.exercise, this.declaredState, this.fitnessState,
    );

    const decision = computeVolumeAutoregulation({
      setIndex,
      setType: set.logSet.setType,
      numSets: exercise.sets.length,
      baselineNumSets,
      volumeAdjustment: exercise.planPath.volumeAdjustment,
      firstSetReps: firstSet.logSet.stats.reps,
      firstSetWeightKg: firstSet.logSet.stats.weight_kg,
      firstSetPlannedWeightKg: firstSet.logSet.plannedWeight_kg,
      currentSetReps: set.logSet.stats.reps,
      currentSetWeightKg: set.logSet.stats.weight_kg,
      alreadyAnsweredThisWorkout,
    });

    if (!decision) {
      return;
    }

    const autoregulationType = decision.direction;
    const wasAlreadyTriggered = !!exercise.planPath.autoregulationWorkoutLog_id;

    // set the flags on every container this path lives in — the page reads them off planPath, the
    // fitness plan saves them off planSubSlot, and the workout log saves them off the backup
    for (const path of exercise.livePathsForCurrentIntensity()) {
      path.autoregulationTriggerSetIndex = setIndex;
      path.autoregulationWorkoutLog_id = this.logWorkout._id;
      path.autoregulationType = autoregulationType;
      // the value the user is being offered, stored so that accepting can use the
      // number this rule decided rather than working one out again later — the
      // answer can arrive in a later workout, where the rule's inputs are gone
      path.autoregulationNewVolumeAdjustment = decision.newVolumeAdjustment;
      // the baseline that value is measured against, so accepting can tell whether
      // it still means what the dialog says. an explicit set-count write or a
      // program rebuild can move the baseline before the user answers, and the
      // stored position would then be applied to a number the rule never saw
      path.autoregulationBaselineSets = baselineNumSets;
    }

    // add post-set status so user sees the pending autoregulation notification
    // (only if not already triggered by a previous set)
    if (!wasAlreadyTriggered) {
      set.logSet.postStatus.push({ status: PostSetStatus.VolumeAutoregulationPending });
    }

    // save both plan and workout
    this.writeSubSlotBackToPlan(exercise);
    const transaction = DatabaseWrapper.getTransactionId();
    void this.userDb.savePlanAsync(transaction, undefined, this.planSaveContext(PlanSaveCaller.WorkoutVolumeAutoregulationOffer));
    void this.saveWorkoutOrOtherAsync(transaction, this.logWorkout);
  }

  /**
   * Shows an alert while temporarily clearing the exercise loading state
   * to prevent spinner from showing during user interaction
   */
  private async showAlertInsideLoading<T>(
    exercise: UiExercise, 
    alertConfig: any
  ): Promise<T> {
    // Store current loading state and temporarily clear it
    const wasLoading = exercise.loading();
    exercise.loading.set(false);
    this.cdRef.markForCheck();

    try {
      // Show the alert without spinner
      return await this.alertCtrl.showAlertAsync<T>(alertConfig);
    } finally {
      // Always restore original loading state
      exercise.loading.set(wasLoading);
      this.cdRef.markForCheck();
    }
  }

  async handlePlateauAsync(e: UiExercise, bMustDecide: boolean): Promise<string | false | null> {
    // #4939: everything below reads and changes the user's program going FORWARD — which
    // intensity to rotate to, whether to raise the training level, whether to swap the exercise
    // out. So it works on the entry the program holds today, not on the copy the screen is
    // drawn with, which on a finished workout is that session's own snapshot. On every other
    // workout the two are the same object and nothing changes.
    //
    // No plateau question can be open when the program holds no entry for this exercise, so
    // the accessor's fallback is unreachable from here — it is used for its type, which saves
    // every read below an optional-chain that could never answer undefined.
    const programSubSlot = this.programSubSlotForAnswerOrDisplayCopy(e);
    const markForExerciseSwitchAsync = async () => {
      let oldExerciseName = e.exercise.name;
      await this.swapExerciseOutOfProgramAsync(e, ProgramSwapReason.Plateau, programSubSlot);
      if (bMustDecide) {
        await this.reloadUIExercise(e);
        return LL.workouts.alerts.plateau.exerciseChanged({ name: oldExerciseName });
      }
      return LL.workouts.alerts.plateau.exerciseWillChange();
    };

    const handleExerciseIntensityChangeAlert = async () => {
      let message = LL.workouts.alerts.plateau.intensityChangeMessage();
      let buttons: any[] = [
        {
          text: LL.workouts.detail.buttons.yesAgree(),
          role: AlertButtonRole.Primary,
          result: true,
        },
        {
          text: LL.workouts.detail.buttons.noKeepIntensity(),
          role: bMustDecide ? AlertButtonRole.Cancel : undefined,
          result: false,
        },
      ];
      if (!bMustDecide) {
        buttons.push({
          text: LL.workouts.detail.buttons.decideLater(),
          result: null,
          role: AlertButtonRole.Cancel,
        });
      }
      let result = await this.showAlertInsideLoading<boolean | null>(e, {
        title: LL.workouts.alerts.plateau.intensityChangeTitle({ name: e.exercise.name }),
        message: message,
        enableBackdropDismiss: !bMustDecide,
        buttons: buttons,
      });

      this.telemetry.trackEvent('workout_plateau_response', {
        type: 'intensity_change',
        response: result === true ? 'accepted' : result === false ? 'declined' : 'decide_later',
        exercise: e.exercise._id,
        intensity: e.planPath.intensityLevel,
        training_level: programSubSlot.trainingLevel,
      });

      return result;
    }

    const handleExerciseChangeAlert = async () => {
      let message = LL.workouts.alerts.plateau.exerciseChangeMessage();
      let buttons: any[] = [
        {
          text: LL.workouts.detail.buttons.yesAgree(),
          role: AlertButtonRole.Primary,
          result: true,
        },
        {
          text: LL.workouts.detail.buttons.noKeepExercise(),
          role: bMustDecide ? AlertButtonRole.Cancel : undefined,
          result: false,
        },
      ];
      if (!bMustDecide) {
        buttons.push({
          text: LL.workouts.detail.buttons.decideLater(),
          result: null,
          role: AlertButtonRole.Cancel,
        });
      }
      let result = await this.showAlertInsideLoading<boolean | null>(e, {
        title: LL.workouts.alerts.plateau.exerciseChangeTitle({ name: e.exercise.name }),
        message: message,
        enableBackdropDismiss: !bMustDecide,
        buttons: buttons,
      });

      this.telemetry.trackEvent('workout_plateau_response', {
        type: 'exercise_change',
        response: result === true ? 'accepted' : result === false ? 'declined' : 'decide_later',
        exercise: e.exercise._id,
        intensity: e.planPath.intensityLevel,
        training_level: programSubSlot.trainingLevel,
      });

      return result;
    }

    const handlePlanChangeAlert = async () => {
      let message = LL.workouts.alerts.plateau.planUpgradeMessage();
      let buttons: any[] = [
        {
          text: LL.workouts.detail.buttons.yesAgree(),
          role: AlertButtonRole.Primary,
          result: true,
        },
        {
          text: LL.workouts.detail.buttons.noKeepProgram(),
          role: AlertButtonRole.Cancel,
          result: false,
        },
      ];
      if (!bMustDecide) {
        buttons.push({
          text: LL.workouts.detail.buttons.decideLater(),
          result: null,
        });
      }
      let result = await this.alertCtrl.showAlertAsync<boolean | null>({
        title: LL.workouts.alerts.agreeWithUpgrade(),
        message: message,
        enableBackdropDismiss: !bMustDecide,
        buttons: buttons,
      });

      this.telemetry.trackEvent('workout_plateau_response', {
        type: 'plan_upgrade',
        response: result === true ? 'accepted' : result === false ? 'declined' : 'decide_later',
        exercise: e.exercise._id,
        intensity: e.planPath.intensityLevel,
        training_level: programSubSlot.trainingLevel,
      });

      return result;
    }

    const intensityUpdateText = bMustDecide
      ? LL.workouts.alerts.plateau.retestThis()
      : LL.workouts.alerts.plateau.retestNext();

    // for novices, use intensification if possible
    if (programSubSlot.trainingLevel === TrainingLevel.Novice) {
      if (
        e.planPath.intensityLevel === IntensityLevel.Initial &&
        programSubSlot.intensities[programSubSlot.trainingLevel][IntensityLevel.Intensified]
      ) {
        let result = await handleExerciseIntensityChangeAlert();
        if (result === true) {
          programSubSlot.rebuildPathsForIntensity = IntensityLevel.Intensified;
          return intensityUpdateText;
        }
        return result;
      }
      // others, use periodization
    } else {
      let numPaths = programSubSlot.exercisePaths.length;
      // DUP -> TUP
      if (numPaths === 2 && programSubSlot.intensities[programSubSlot.trainingLevel][IntensityLevel.TUPheavy]) {
        let result = await handleExerciseIntensityChangeAlert();
        if (result === true) {
          programSubSlot.rebuildPathsForIntensity = IntensityLevel.TUPheavy;
          return bMustDecide
            ? LL.workouts.alerts.plateau.switchToTUPThis()
            : LL.workouts.alerts.plateau.switchToTUPNext();
        }
        return result;
        // none -> DUP
      } else if (numPaths === 1 && programSubSlot.intensities[programSubSlot.trainingLevel][IntensityLevel.DUPheavy]) {
        let result = await handleExerciseIntensityChangeAlert();
        if (result === true) {
          programSubSlot.rebuildPathsForIntensity = IntensityLevel.DUPheavy;
          return bMustDecide
            ? LL.workouts.alerts.plateau.switchToDUPThis()
            : LL.workouts.alerts.plateau.switchToDUPNext();
        }
        return result;
      }
    }

    // if undulation or intensification is not possible... try levelup

    const mainSlots: { slot: string; half: 'upper' | 'lower' }[] = [
      { slot: '$bench_press', half: 'upper' },
      { slot: '$chin_up', half: 'upper' },
      { slot: '$ohp', half: 'upper' },
      { slot: '$rdl', half: 'lower' },
      { slot: '$squat', half: 'lower' },
    ];

    // (need to parse slot id to remove the "slot/" prefix that is used in the leveling tables...)
    let shortSlotId = programSubSlot.slotId.replace(/^slot\//, '');

    let mainSlot = mainSlots.find(s => s.slot === shortSlotId);

    // if not main lift, then just change the exercise, no levelup
    // NOTE: subslots that have an id of main lift, but don't start at first exercise, they are not main slots
    if (!mainSlot || programSubSlot.startIndex > 0) {
      let result = await handleExerciseChangeAlert();
      if (result === true) {
        return markForExerciseSwitchAsync();
      }
      return result;
    }

    let bodyHalf = mainSlot.half;

    let getSlotLevel = (slotId: string) => {
      return this.plan.trainingLevels[slotId] || TrainingLevel.Novice;
    };

    // averages the body half; pass overrideSlot/overrideLevel to see what the
    // average WOULD be if one slot were raised, without writing that raise into
    // the plan first — the raise is only stored once the user accepts (below).
    let calcAverageForHalf = (half: string, overrideSlot?: string, overrideLevel?: TrainingLevel) =>
      minTwoTrainingLevels(mainSlots.filter(s => s.half === half).map(s => s.slot === overrideSlot ? overrideLevel! : getSlotLevel(s.slot)));

    let oldAverage = calcAverageForHalf(bodyHalf);

    /** While `trainingLevel` in slot is used to calculate intensity, these levels are used just to track progress
     * and calculate new average levels to determine when to revamp the program.
     */
    let trainingLevel = getSlotLevel(shortSlotId);

    // for main lifts, increase level if possible
    if (trainingLevel === TrainingLevel.Novice) {
      trainingLevel = TrainingLevel.Intermediate;
    } else if (trainingLevel === TrainingLevel.Intermediate) {
      trainingLevel = TrainingLevel.Advanced;
      // if already at max level, then just change the exercise
    } else if (trainingLevel === TrainingLevel.Advanced) {
      // nothing here
    }

    // compute the would-be average with this slot raised, but do NOT write the
    // raise into the plan yet — that only happens if the user accepts the upgrade
    // below. Writing it here left the stored level permanently raised even when
    // the user declined, or when the raise did not improve the average and the
    // code fell through to a plain exercise swap (df-8b0236bde1a6).
    let newAverage = calcAverageForHalf(bodyHalf, shortSlotId, trainingLevel);

    // ...if average for that bodyhalf has improved
    if (newAverage > oldAverage) {
      let result = await handlePlanChangeAlert();
      if (result === true) {
        // rebuild entire program
        this.plan.trainingLevels[shortSlotId] = trainingLevel;
        this.plan.needsReplan = true;
        programSubSlot.trainingLevel = trainingLevel;
        programSubSlot.rebuildPathsForIntensity = IntensityLevel.Initial;
        return LL.workouts.alerts.plateau.planUpgradeConfirm();
      }
      return result;
    }

    // otherwise, just switch the exercise
    let result = await handleExerciseChangeAlert();
    if (result === true) {
      return markForExerciseSwitchAsync();
    }
    return result;
  }

  someExerciseHighlighted(): boolean {
    return this.exercises && this.exercises.some(uie => uie.highlighted());
  }

  // if the exercise has no equipment assigned for the current or alternate unit,
  // pick a default from the user's existing equipment (preferring the most
  // recently used) or create a new one from the template. mutates
  // fitnessState.allEquipment + exerciseStats.equipmentIdByUnit; caller must
  // run this serially across exercises so each sees the previous mutations.
  async preselectEquipmentIfNeeded(uie: UiExercise): Promise<boolean> {
    // if we already have equipment for this exercise, don't do anything
    // This includes automatic equipment created for custom increments
    if (uie.equipment !== undefined) {
      return false;
    }

    // if the exercise doesn't have an equipment template, we can't do anything
    if (!uie.exercise.equipmentTemplateId) {
      return false;
    }

    // make sure we have an exercise to save into
    let exerciseStats = this.fitnessState.exerciseStats[uie.exercise._id];
    if (!exerciseStats) {
      exerciseStats = {} as ExerciseStat;
      this.fitnessState.exerciseStats[uie.exercise._id] = exerciseStats;
    }

    // Initialize equipmentIdByUnit if not present
    ensureEquipmentSlots(exerciseStats);

    // Preselect equipment for both kg and lb units
    let preselectDone = false;

    // if equipment unit doesn't match the key it's stored under, clear the entry (#2876)
    for (const unit of ['kg', 'lb'] as const) {
      const unitEquipmentId = equipmentIdForUnit(exerciseStats, unit);
      if (unitEquipmentId && declaresTheOtherUnit(this.fitnessState.allEquipment?.[unitEquipmentId], unit)) {
        clearEquipmentForUnit(exerciseStats, unit);
      }
    }

    // Check if existing equipment IDs actually point to valid equipment (fix dangling references)
    const recordedKgId = equipmentIdForUnit(exerciseStats, 'kg');
    const kgEquipmentExists = recordedKgId && this.fitnessState.allEquipment?.[recordedKgId];
    const recordedLbId = equipmentIdForUnit(exerciseStats, 'lb');
    const lbEquipmentExists = recordedLbId && this.fitnessState.allEquipment?.[recordedLbId];

    // if kg slot is empty or dangling, preselect for kg
    if (!kgEquipmentExists) {
      const sourceEquipmentKg = await getSourceEquipments(this.fitnessState, this.exerciseDb, this.userDb, this.settings.locale, false, 'kg', this.docWatcher);
      const kgEquipmentId = getBestEquipmentIdBasedOnTemplate(sourceEquipmentKg, this.fitnessState, this.plan, uie.exercise.equipmentTemplateId, 'kg');
      if (kgEquipmentId) {
        recordEquipmentForUnit(exerciseStats, 'kg', kgEquipmentId);
        preselectDone = true;
      }
    }

    // if lb slot is empty, preselect for lb; recompute source AFTER kg so that
    // alternate-unit naming and the basedOnTemplate filter see any equipment
    // the kg path just created.
    if (!lbEquipmentExists) {
      const sourceEquipmentLb = await getSourceEquipments(this.fitnessState, this.exerciseDb, this.userDb, this.settings.locale, false, 'lb', this.docWatcher);
      const lbEquipmentId = getBestEquipmentIdBasedOnTemplate(sourceEquipmentLb, this.fitnessState, this.plan, uie.exercise.equipmentTemplateId, 'lb');
      if (lbEquipmentId) {
        recordEquipmentForUnit(exerciseStats, 'lb', lbEquipmentId);
        preselectDone = true;
      }
    }

    // Migrate legacy equipmentId if present and unit-specific ones are not set, then clear the
    // legacy field. getEquipmentForExercise migrates differently and keeps it; see
    // LegacyEquipmentMigration for both. A legacy machine of the other unit goes to its own unit's
    // slot, never the shown unit's (#2876, df-0caf8c4d43d4)
    const currentUnit = exerciseStats.weight_unit === 'lb' ? 'lb' : 'kg';
    const migrationUnit = legacyMigrationUnit(exerciseStats, currentUnit, this.fitnessState.allEquipment);
    if (migrateLegacyEquipmentId(exerciseStats, migrationUnit, LegacyEquipmentMigration.MoveIntoEmptySlotDroppingLegacy)) {
      preselectDone = true;
    }

    if (preselectDone) {
      await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
    }

    // Always check equipment after preselection attempt
    uie.checkEquipment(this.fitnessState, this.userDb, this.settings.locale, this.logging);
    // #5176: the load capped this exercise's weight before it had equipment, so the cap was snapped
    // onto no grid at all — snap it again now that there is one
    this.workoutLoader.resnapPrescriptionCapOntoEquipment(
      uie, this.declaredState, this.fitnessState, this.mode as unknown as WorkoutLoadMode, this.settings.locale,
    );
    // the load built the header with no equipment, so its weight was not rounded onto the
    // equipment's steps — if equipment was attached just now, build the header again
    if (uie.equipment) {
      this.workoutLoader.refreshBadgesAndDescription(
        uie, this.declaredState, this.fitnessState, this.mode as unknown as WorkoutLoadMode, this.settings.locale,
      );
    }
    this.cdRef.markForCheck();

    return true;
  }

  /**
   * The ONE place a pending plateau suggestion is put to the user (#4625).
   *
   * There are two places a person can answer one: tapping the "Resolve" card
   * (`handlePlateauResolveButton`) and the prompt raised when they open the
   * exercise for their first set (`openExercise`). Those two persist the answer
   * differently on purpose — the card tap records the answer on the workout the
   * plateau was detected in, while the pre-set prompt records it on the NEW
   * workout and only takes the card off the old one — so their persistence
   * bodies stay separate.
   *
   * What is NOT different is the moment the answer arrives, and that used to be
   * hand-copied to both sites. A missed copy is silent: the symptom is the
   * behaviour simply happening less often, which looks exactly like a small
   * eligible audience. Both sites now ask the question through here, so a
   * behaviour attached to "the person answered a plateau suggestion" is written
   * once.
   */
  private async askPlateauAnswerAsync(e: UiExercise, bMustDecide: boolean): Promise<string | false | null> {
    const message = await this.handlePlateauAsync(e, bMustDecide);

    // #4625 — acceptance is a TRUTHY MESSAGE, not the word "accepted" and not a
    // boolean. `false` is a decline and `null`/absent is decide-later. Comparing
    // against a string would build a trigger that can never fire.
    if (message) {
      void this.reviewPrompt.considerPromptingAsync(
        ReviewPromptTrigger.PlateauResolutionAccepted);
    }

    return message;
  }

  /**
   * Applies the user's decisive answer to a plateau suggestion (#4360).
   *
   * `plateauWorkoutLog_id` must be read by the CALLER and handed in here, not
   * read off `e.planSubSlot` inside this method. Accepting "change the
   * exercise" runs `replaceExerciseInSubSlot`, which wipes that field as part
   * of resetting exercise-tied state — so by the time this method runs the
   * field can already be gone, and reading it here found no workout to clean.
   *
   * `message` is a string when the user accepted (the text describing what the
   * app will do), `false` when they turned the suggestion down, and `null` when
   * they chose "decide later" — which deliberately keeps the card, so this
   * method does nothing at all for it.
   */
  async handlePlateauMessage(e: UiExercise, message: string | false | null, plateauWorkoutLog_id: string | undefined) {
    // "decide later" (and dismissing the dialog) keeps the card on purpose
    if (message === null || message === undefined) {
      return;
    }

    const outcome = message ? PlateauResolutionOutcome.Resolved : PlateauResolutionOutcome.Dismissed;
    const resolvedMessage = message || undefined;

    // clear the plan-side pointer first — this half always has to happen, even
    // when the workout below cannot be found, or the suggestion becomes
    // unanswerable for good
    delete this.programSubSlotForAnswer(e)?.plateauWorkoutLog_id;
    this.writeSubSlotBackToPlan(e);
    let transaction = DatabaseWrapper.getTransactionId();
    await this.userDb.savePlanAsync(transaction, undefined, this.planSaveContext(PlanSaveCaller.WorkoutPlateauAnswer));

    // clean the workout the plateau was actually detected in, which can be an
    // earlier one than the one on screen. This runs BEFORE the viewed sweep
    // below, because it needs to see which set was carrying the card in order
    // to put the record of the user's answer on that same set.
    //
    // reading or saving that workout can fail (a read throws
    // DatabaseUnavailableError when the database could not be read at all), and
    // by this point the plan half is already committed — so the failure has to
    // be reported rather than allowed to escape as a bare rejection. The card
    // itself is not left stuck by it: the load-time and tap-time heals below
    // clear an orphaned status.
    let cleanedWorkoutId: string | undefined;
    if (plateauWorkoutLog_id) {
      try {
        // nothing found self-heals: the plan field is already cleared above, which
        // is what #3291 does for a dangling pointer too
        const found = await this.loadExerciseInReferencedWorkoutAsync(
          plateauWorkoutLog_id, e.logExercise.subSlotId);
        if (found) {
          applyPlateauResolution(found.logExercise.sets, outcome, resolvedMessage);
          await this.saveWorkoutOrOtherAsync(transaction, found.workout);
          cleanedWorkoutId = found.workout._id;
        }
      } catch (err) {
        this.logging.trackAbnormalException(err);
      }
    }

    // now take the card off the exercise as it is drawn on THIS page. The sets
    // the page draws are not always the same objects as the ones in the workout
    // document (a staged set is only linked into the document when it is
    // concluded), so cleaning the document alone can leave the card on
    // screen (#4360).
    const removedFromViewed = this.sweepViewedPlateauDetected(e);

    // if the workout on screen lost statuses and was not the one just saved,
    // persist it too, so the card cannot come back when the page is re-opened.
    // same transaction as the plan save above — the two documents are the two
    // halves of one user action, and the conflict resolver reads db__trans to
    // tell which documents were written together
    if (removedFromViewed > 0 && cleanedWorkoutId !== this.logWorkout._id) {
      void this.saveWorkoutAsync(transaction);
    }

    // the app is zoneless, so a model change made after the click handler's
    // first change-detection pass does not redraw on its own
    this.cdRef.markForCheck();
  }

  /** #4360: removes every `PlateauDetected` status from the exercise as currently
   *  viewed on this page. Sweeps the union of the staged UI sets and the persisted
   *  log sets, because staged UiSet objects may not be assigned into
   *  logExercise.sets yet. Does NOT save — callers decide whether the viewed
   *  workout still needs persisting. Returns the number removed. */
  private sweepViewedPlateauDetected(e: UiExercise): number {
    return clearPlateauDetectedStatuses([
      ...e.logExercise.sets,
      ...e.sets.map(s => s.logSet),
    ]);
  }

  /**
   * df-8ab3d2fbc794: takes back the plateau that THIS set raised, so that correcting the set
   * re-decides it from the corrected numbers instead of leaving the old answer standing.
   *
   * Called from the correction branch of `onClickSetDone`, right beside
   * `restorePrescribedTargets` and before the set is concluded again. A plateau is an OUTCOME
   * of concluding a set, in the same way the new target weight is, so it is rewound before the
   * re-run for the same reason the prescription is: whatever the corrected numbers deserve, the
   * re-run writes it, and whatever they no longer deserve is simply not written again.
   *
   * Both halves go together and neither is any use alone: the `PlateauDetected` status draws the
   * card, and `plateauWorkoutLog_id` is what makes it answerable and what forces the question at
   * the start of the next session. Clearing them together means no orphan of either kind is
   * created, so `healOrphanedPlateauDetected` has nothing to repair.
   *
   * Why the two guards are what they are, what #5049 changed, and which run demonstrated the
   * defect: `design-docs/volume-autoregulation.md`, "Correcting an already-finished set".
   *
   * Does NOT save. The `concludeSet` call that follows saves the plan, and `onClickSetDone`
   * saves the workout at the end — the same writes that persist everything else this branch does.
   */
  private withdrawPlateauRaisedBySet(e: UiExercise, iSet: number, set: UiSet): void {
    // if this set is not carrying the card, it did not raise the plateau
    if (!set.logSet.postStatus?.some(status => status.status === PostSetStatus.PlateauDetected)) {
      return;
    }

    // if the pending question was raised in some other workout, leave it for the user to answer
    if (this.programSubSlotForAnswer(e)?.plateauWorkoutLog_id !== this.logWorkout._id) {
      return;
    }

    // #6428 R9: under a coach change the re-run cannot raise the question again, so the
    // program's pointer stays and the question is asked at the next workout; only the card goes
    if (!e.coachChangedDuringSession) {
      delete this.programSubSlotForAnswer(e)?.plateauWorkoutLog_id;
    }
    // the staged UI set and the stored log set are the same object once a set has been
    // concluded, but both are passed for the same reason `sweepViewedPlateauDetected` sweeps the
    // union: nothing here guarantees it, and clearing one while the other keeps the status would
    // leave the card on screen or bring it back on reload
    clearPlateauDetectedStatuses([set.logSet, e.logExercise.sets[iSet]]);
  }

  /**
   * #4360: takes the "Resolve" card off the exercise in an EARLIER workout.
   *
   * Used when a pending plateau is answered from the pre-set prompt of a later
   * workout (`openExercise`). That answer clears the plan-side pointer, and the
   * record of it is written as a pre-set status on the NEW workout — so the old
   * workout only needs the card removing, not a second badge.
   */
  private async clearPlateauCardInEarlierWorkoutAsync(uie: UiExercise, plateauWorkoutLog_id: string): Promise<void> {
    // nothing found needs nothing done — there is no card anywhere to take away,
    // which is also what a dangling pointer means here (#3291)
    const found = await this.loadExerciseInReferencedWorkoutAsync(
      plateauWorkoutLog_id, uie.logExercise.subSlotId);
    if (!found) {
      return;
    }

    if (clearPlateauDetectedStatuses(found.logExercise.sets) > 0) {
      await this.saveWorkoutOrOtherAsync(DatabaseWrapper.getTransactionId(), found.workout);
      this.cdRef.markForCheck();
    }
  }

  async handleAutoregulationAsync(e: UiExercise, bMustDecide: boolean): Promise<boolean | null> {
    const direction = e.planPath.autoregulationType === 'increase'
      ? LL.workoutDetail.volumeAutoregulation.directionIncreasing()
      : LL.workoutDetail.volumeAutoregulation.directionDecreasing();

    // Get intensity name for DUP/TUP paths only
    let intensityInfo = '';
    switch (e.planPath.intensityLevel) {
      case IntensityLevel.DUPlight:
      case IntensityLevel.TUPlight:
        intensityInfo = LL.workoutDetail.volumeAutoregulation.intensityHighRep();
        break;
      case IntensityLevel.TUPmedium:
        intensityInfo = LL.workoutDetail.volumeAutoregulation.intensityMediumRep();
        break;
      case IntensityLevel.DUPheavy:
      case IntensityLevel.TUPheavy:
        intensityInfo = LL.workoutDetail.volumeAutoregulation.intensityLowRep();
        break;
      default:
        intensityInfo = ''; // No intensity name for Initial/Intensified
    }

    const message = LL.workoutDetail.volumeAutoregulation.message({
      intensityInfo,
      direction,
      exerciseName: e.exercise.name
    });

    let buttons: AlertButton<boolean | null>[] = [
      {
        text: LL.workoutDetail.volumeAutoregulation.buttons.accept(),
        result: true,
        role: AlertButtonRole.Primary
      },
      {
        text: LL.workoutDetail.volumeAutoregulation.buttons.decline(),
        role: bMustDecide ? AlertButtonRole.Cancel : undefined,
        result: false,
      },
    ];

    if (!bMustDecide) {
      buttons.push({
        text: LL.workoutDetail.volumeAutoregulation.buttons.decideLater(),
        result: null,
        role: AlertButtonRole.Cancel,
      });
    }

    let result = await this.showAlertInsideLoading<boolean | null>(e, {
      title: LL.workoutDetail.volumeAutoregulation.title(),
      message: message,
      enableBackdropDismiss: !bMustDecide,
      buttons: buttons,
    });

    this.telemetry.trackEvent('workout_volume_autoregulation_response', {
      response: result === true ? 'accepted' : result === false ? 'declined' : 'decide_later',
      direction: e.planPath.autoregulationType,
      exercise: e.exercise._id,
      intensity: e.planPath.intensityLevel,
      current_volume_adjustment: e.planPath.volumeAdjustment || 0,
    });

    return result;
  }

  async handleAutoregulationMessage(e: UiExercise, accepted: boolean | null) {
    // #4939: the answer has to reach the fitness plan's own entry, and
    // `resolveAutoregulationOnExercise` reaches it only through `livePlanSubSlot`. That pointer
    // is refreshed at the top of `concludeSet`, which is not on this path at all — a card can be
    // answered from `openExercise` or from the Resolve button without concluding anything. So
    // refresh it here too, for the same reason and in the same way.
    this.refreshLivePlanSubSlot(e);

    // decide_later (null) keeps the card on purpose — only a decisive answer
    // sweeps the pending statuses.
    // sweep the visible card's source first: the card renders from the sets
    // of the workout being viewed, which may differ from the workout
    // referenced by autoregulationWorkoutLog_id — a single-set cleanup there
    // must not leave a stuck card here (#4052)
    // #4625 — only an acceptance is a qualifying moment. `null` is decide-later
    // and `false` is a decline; neither is a win worth asking about.
    if (accepted === true) {
      void this.reviewPrompt.considerPromptingAsync(
        ReviewPromptTrigger.VolumeAutoregulationAccepted);
    }

    const isDecisiveAnswer = accepted === true || accepted === false;
    const removedCountFromViewed = isDecisiveAnswer ? this.sweepViewedAutoregulationPending(e) : 0;
    let savedAutoregulationWorkoutId: string | undefined;
    // the resolve below can clear the answered question out of the copy of the plan THIS workout
    // carries, and that copy is part of the workout document on screen — so it has to be saved
    let clearedViewedBackup = false;
    // the workout that RAISED the question, once the block below has updated it. Its save is
    // deliberately held back until after the resolve: when that workout is the one on screen, the
    // resolve writes into this very document, and saving first would race its own input.
    let autoregulationWorkoutToSave: Log_Workout | undefined;

    // #4714 — remember that this exercise has been decided, so no later set of it
    // raises the card again today. Answering deletes the autoregulation fields
    // below, and those fields were the only thing stopping a second card.
    // The pointer is read BEFORE those deletions, and the marker is set only when the
    // card came from the workout on screen: this method is also reached from
    // openExercise, where the user answers a card left over from an EARLIER workout,
    // and marking there would stop today's workout being judged at all.
    const wasRaisedByThisWorkout = e.planPath?.autoregulationWorkoutLog_id === this.logWorkout._id;
    let markedAnsweredOnThisWorkout = false;
    if (isDecisiveAnswer && wasRaisedByThisWorkout && !e.logExercise.volumeAutoregulationAnswered) {
      e.logExercise.volumeAutoregulationAnswered = true;
      markedAnsweredOnThisWorkout = true;
    }

    if (accepted === true) {

      // store the number the rule decided when it raised this suggestion, which
      // travelled with it. It is NOT the same as stepping the stored adjustment
      // one up or down: the rule compares against a current adjustment it infers
      // from the set count when nothing is stored, so the two disagree, and a
      // step cannot express a two-step move at all.
      // The baseline comes off the sub-slot this answer is about to be written back
      // into, so the helper can tell whether the decided value still means what the
      // dialog said. See design-docs/volume-autoregulation.md, "The baseline can
      // move while a suggestion is unanswered".
      // #4939: "the sub-slot this answer is written back into" is the program's own entry, and
      // on a finished workout that is no longer the copy the screen draws with.
      // df-06e16b428c66: the stored adjustment the fallback steps from comes off that same
      // sub-slot too — on a finished workout `planPath` is the session's snapshot, which can
      // hold an adjustment the program has since dropped.
      const answerTarget = this.programSubSlotForAnswerOrDisplayCopy(e);
      const baselineSetsNow = getMyoAwareNumSets(
        answerTarget,
        e.exercise, this.declaredState, this.fitnessState,
      );
      e.planPath.volumeAdjustment = resolveAcceptedVolumeAdjustment(
        e.planPath.autoregulationNewVolumeAdjustment,
        storedVolumeAdjustmentForAnswer(e, answerTarget),
        e.planPath.autoregulationType,
        e.planPath.autoregulationBaselineSets,
        baselineSetsNow,
      );

      // User accepted the autoregulation
      // Add PostSetStatus to the set that triggered the autoregulation in the original workout
      const triggerSetIndex = e.planPath.autoregulationTriggerSetIndex;
      if (triggerSetIndex !== undefined && e.planPath.autoregulationWorkoutLog_id) {
        // Load the workout where autoregulation was detected (might be a previous
        // workout). Nothing found — including a dangling pointer, e.g. a
        // bleed-corrupted plan referencing a workout the client never logged —
        // skips the post-status update and lets the unconditional cleanup below
        // clear the autoregulation* fields (#3291).
        const found = await this.loadExerciseInReferencedWorkoutAsync(
          e.planPath.autoregulationWorkoutLog_id, e.logExercise.subSlotId);
        if (found) {
          const { workout: autoregulationWorkout, logExercise } = found;

          // remove the pending status from ALL sets — the stored trigger
          // index can drift away from the set that carries it (#4052)
          clearPendingVolumeAutoregulationStatuses(logExercise.sets);

          if (logExercise.sets[triggerSetIndex]) {
            const statusType = e.planPath.autoregulationType === 'increase'
              ? PostSetStatus.VolumeAutoregulationIncrease
              : PostSetStatus.VolumeAutoregulationDecrease;
            logExercise.sets[triggerSetIndex].postStatus.push({ status: statusType, textInserts: undefined });
          } else {
            // The set that raised this suggestion is not there any more, so the
            // user's answer is recorded nowhere on the workout. This used to
            // pass in silence, and that silence is why #4832 took a customer
            // writing in to find: in the reported session the set had been
            // logged eighteen seconds earlier and then dropped out of the
            // document. Nothing repairs a badge that was never written, so this
            // is problem tier.
            // the counts answer different questions and can disagree: the list
            // the badge was written to (the screen's, for this page's own
            // workout), the screen's own set list, and what the document holds
            this.logging.trackSilentProblem('volume-autoregulation-trigger-set-missing', {
              docId: autoregulationWorkout._id,
              rev: autoregulationWorkout._rev,
              subSlotId: e.logExercise.subSlotId,
              triggerSetIndex,
              actedOnSetCount: logExercise.sets.length,
              onScreenSetCount: e.sets?.length ?? 0,
              inDocumentSetCount: autoregulationWorkout.exercises
                ?.find(x => x.subSlotId === e.logExercise.subSlotId)?.sets?.length,
              direction: e.planPath.autoregulationType,
              isWorkoutShownOnThisPage: this.isWorkoutShownOnThisPage(autoregulationWorkout),
            });
          }

          if (logExercise.planSubslotBackup) {
            const backupPath = logExercise.planSubslotBackup.exercisePaths.find(p => p.intensityLevel === e.planPath.intensityLevel);
            if (backupPath) {
              clearAutoregulationFields(backupPath);
            }
          }

          // saved after the resolve below, not here — see the declaration
          autoregulationWorkoutToSave = autoregulationWorkout;
        }
      }

      // the accepted adjustment is already on planPath — carry it, and the answer, to every
      // other container this same path lives in
      clearedViewedBackup = resolveAutoregulationOnExercise(e, true);

      if (autoregulationWorkoutToSave) {
        void this.saveWorkoutOrOtherAsync(DatabaseWrapper.getTransactionId(), autoregulationWorkoutToSave);
        savedAutoregulationWorkoutId = autoregulationWorkoutToSave._id;
      }

      this.writeSubSlotBackToPlan(e);
      let transaction = DatabaseWrapper.getTransactionId();
      await this.userDb.savePlanAsync(transaction, undefined, this.planSaveContext(PlanSaveCaller.WorkoutAutoregulationAccepted));
    } else if (accepted === false) {
      // User declined the autoregulation
      // Remove the pending status from the original workout (must read values before deleting)
      const triggerSetIndex = e.planPath.autoregulationTriggerSetIndex;
      if (triggerSetIndex !== undefined && e.planPath.autoregulationWorkoutLog_id) {
        // see Accept branch above — nothing found, a dangling pointer included,
        // self-heals via the unconditional cleanup below, and no post-status
        // update is needed (#3291).
        const found = await this.loadExerciseInReferencedWorkoutAsync(
          e.planPath.autoregulationWorkoutLog_id, e.logExercise.subSlotId);
        if (found) {
          const { workout: autoregulationWorkout, logExercise } = found;

          // remove the pending status from ALL sets — the stored trigger
          // index can drift away from the set that carries it (#4052)
          clearPendingVolumeAutoregulationStatuses(logExercise.sets);

          if (logExercise.planSubslotBackup) {
            const backupPath = logExercise.planSubslotBackup.exercisePaths.find(p => p.intensityLevel === e.planPath.intensityLevel);
            if (backupPath) {
              clearAutoregulationFields(backupPath);
            }
          }

          // saved after the resolve below, not here — see the declaration
          autoregulationWorkoutToSave = autoregulationWorkout;
        }
      }

      // declining changes no volume, so only the answer itself is carried across
      clearedViewedBackup = resolveAutoregulationOnExercise(e, false);

      if (autoregulationWorkoutToSave) {
        void this.saveWorkoutOrOtherAsync(DatabaseWrapper.getTransactionId(), autoregulationWorkoutToSave);
        savedAutoregulationWorkoutId = autoregulationWorkoutToSave._id;
      }

      this.writeSubSlotBackToPlan(e);
      let transaction = DatabaseWrapper.getTransactionId();
      await this.userDb.savePlanAsync(transaction, undefined, this.planSaveContext(PlanSaveCaller.WorkoutAutoregulationDeclined));
    }

    // if the viewed workout lost pending statuses, or just gained the answered
    // marker, and was not the doc saved inside the branch above, persist it so the
    // card cannot come back on re-entry (#4052, #4714). The branch above usually
    // saves it already — the marker is only set for a card THIS workout raised, and
    // that is the doc it loads — but the dangling-pointer and missing-exercise cases
    // (#3291) leave without saving any workout, and an unsaved marker would be lost
    // the moment the user leaves the page.
    if ((removedCountFromViewed > 0 || markedAnsweredOnThisWorkout || clearedViewedBackup)
        && savedAutoregulationWorkoutId !== this.logWorkout._id) {
      void this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
    }

    this.cdRef.markForCheck();
  }

  /** #4052: removes every pending autoregulation status from the exercise as
   *  currently viewed on this page. Sweeps the union of the staged UI sets and
   *  the persisted log sets, because staged UiSet objects may not be assigned
   *  into logExercise.sets yet. Does NOT save — callers decide whether the
   *  viewed workout still needs persisting. Returns the number removed. */
  private sweepViewedAutoregulationPending(e: UiExercise): number {
    return clearPendingVolumeAutoregulationStatuses([
      ...e.logExercise.sets,
      ...e.sets.map(s => s.logSet),
    ]);
  }

  /** #4052 self-heal on page load: a pending autoregulation status whose
   *  plan-side autoregulation fields are gone can never be resolved or
   *  dismissed — the card is stuck and its Resolve button silently does
   *  nothing. Detect and clear such orphans so already-affected users heal
   *  the next time they open the workout. */
  private healOrphanedAutoregulationPending() {
    // never write docs in view-only or coach-managing mode
    if (!this.isEditableMode() || this.isCoachManaging || !this.exercises) {
      return;
    }

    let totalRemoved = 0;
    for (const uie of this.exercises) {
      // if any plan path still references an autoregulation workout, the
      // pending status is legitimately resolvable (possibly via another
      // DUP/TUP intensity path) — leave it alone
      const paths = [
        uie.planPath,
        ...(uie.planSubSlot?.exercisePaths ?? []),
        ...(uie.logExercise.planSubslotBackup?.exercisePaths ?? []),
      ];
      // #6428 R6: after a coach change the program's own paths decide (see `volumeQuestionOpen`)
      const guardOn = this.refreshCoachChangeGuard(uie);
      if (volumeQuestionOpen(guardOn, this.plan?.subSlots?.[uie.logExercise.subSlotId], paths, this.logWorkout._id)) {
        continue;
      }
      if (guardOn) {
        this.clearVolumeQuestionFromSnapshot(uie);
      }

      const removed = this.sweepViewedAutoregulationPending(uie);
      if (removed > 0) {
        totalRemoved += removed;
        this.logging.trackSilentInfo('workout-detail.orphaned-autoregulation-pending-healed', {
          exercise_id: uie.exercise?._id,
          cleared_count: removed,
        });
      }
    }

    if (totalRemoved > 0) {
      void this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
      this.cdRef.markForCheck();
    }
  }

  /** #4360 self-heal on page load, the plateau twin of
   *  `healOrphanedAutoregulationPending`: a `PlateauDetected` status whose
   *  plan-side `plateauWorkoutLog_id` is gone can never be resolved or
   *  dismissed — the card is stuck and its Resolve button silently does
   *  nothing. Detect and clear such orphans so already-affected users heal the
   *  next time they open the workout. */
  private healOrphanedPlateauDetected() {
    // never write docs in view-only or coach-managing mode
    if (!this.isEditableMode() || this.isCoachManaging || !this.exercises) {
      return;
    }

    let totalRemoved = 0;
    for (const uie of this.exercises) {
      // if the live subslot still names a workout, the card is legitimately
      // answerable — leave it alone.
      //
      // deliberately NOT checking `logExercise.planSubslotBackup` here, unlike
      // the volume twin above. That twin checks the backup because its resolve
      // paths write through to the backup's exercise paths, so a pointer parked
      // there is answerable where it sits. A plateau pointer is not: both
      // answer sites read the pointer through `programSubSlotForAnswer` and
      // nothing else, so one sitting only in the snapshot cannot be answered as
      // it stands.
      // Other code CAN promote it back into the live subslot later
      // (`seedProgressionFromBackup` copies the field across), but at that moment
      // it is in `planSubSlot` and this same check sees it. So the live subslot
      // alone is both sufficient and correct, and counting the snapshot would
      // leave a user stuck with a card nothing can answer.
      // Correcting a concluded set used to be a second such route, back when it
      // restored the whole subslot from the backup; #5049 removed that restore.
      if (this.programSubSlotForAnswer(uie)?.plateauWorkoutLog_id) {
        continue;
      }

      const removed = this.sweepViewedPlateauDetected(uie);
      if (removed > 0) {
        totalRemoved += removed;
        this.logging.trackSilentInfo('workout-detail.orphaned-plateau-detected-healed', {
          exercise_id: uie.exercise?._id,
          cleared_count: removed,
        });
      }
    }

    if (totalRemoved > 0) {
      void this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
      this.cdRef.markForCheck();
    }
  }

  /** #6295 self-heal on page load: save the equipment steps `checkEquipment` replaced in memory.
   *  Only a screen that knows the exercise's working weight can tell a 33 kg step on a 30 kg
   *  exercise is not a machine, and the boot migration does not know it, so before this the value
   *  was patched and reported on every app start and never repaired. */
  private async persistHealedCustomIncrementsAsync() {
    // never write docs in view-only or coach-managing mode
    if (!this.isEditableMode() || this.isCoachManaging || !this.exercises) {
      return;
    }
    const pending = this.exercises
      .map(uie => uie.pendingCustomIncrementHeal)
      .filter((heal): heal is PendingCustomIncrementHeal => heal !== undefined);
    if (pending.length === 0) {
      return;
    }

    const written = applyPendingCustomIncrementHeals(this.fitnessState, pending);
    if (written.length === 0) {
      return;
    }
    try {
      await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
    } catch (err) {
      // the steps stay healed in the shared in-memory fitnessState, so the next save of it from
      // anywhere carries them, and if none comes the next app start reads the bad value from disk
      // and heals it again. so an unavailable database (nobody awaits this) is not worth a report;
      // anything else is
      if (isDatabaseUnavailable(err)) {
        return;
      }
      this.logging.trackAbnormalException(err);
      return;
    }
    for (const heal of written) {
      // INFO: the save above proves the repair, the same tier as the boot migration's own report
      this.logging.trackSilentInfo('workout.equipment-custom-increment-repaired', {
        exerciseId: heal.exerciseId,
        unit: heal.unit,
        storedIncrement_kg: heal.storedIncrement_kg,
        repairedIncrement_kg: heal.healedIncrement_kg,
        source: 'workout-page',
      });
    }
  }

  async openExercise(uie: UiExercise) {
    let preSetMessage: string | false = false;
    // read the pointer BEFORE the dialog: answering can wipe it (an exercise
    // switch resets the subslot), and the earlier workout still has to be
    // cleaned afterwards (#4360)
    const pendingPlateauWorkoutLog_id = uie.sets[0].logSet.state === LogRecordState.Scheduled
      ? this.programSubSlotForAnswer(uie)?.plateauWorkoutLog_id
      : undefined;
    if (pendingPlateauWorkoutLog_id) {
      // this site applies the answer inline below rather than through
      // handlePlateauMessage, because it records the answer on the NEW workout
      // instead of on the one the plateau was detected in. The moment the
      // answer ARRIVES is shared with the other site all the same, so the
      // question is asked through the one method that owns it (#4625).
      preSetMessage = await this.askPlateauAnswerAsync(uie, true);
      if (preSetMessage === false) {
        delete this.programSubSlotForAnswer(uie)?.plateauWorkoutLog_id;
        this.writeSubSlotBackToPlan(uie);
      }
    }

    // Check for autoregulation notification
    if (uie.sets[0].logSet.state === LogRecordState.Scheduled && uie.planPath && uie.planPath.autoregulationWorkoutLog_id) {
      const accepted = await this.handleAutoregulationAsync(uie, true);
      await this.handleAutoregulationMessage(uie, accepted);
      if (accepted) {
        delete uie.logExercise.numPlannedSets;
        await this.rebuildSets(uie); // Rebuild sets to reflect any volume changes
      }
    }

    this.rebuildPathsIfNeeded(uie);

    this.setCurrentPath(uie);
    await this.rebuildSets(uie);
    if (preSetMessage) {
      // Set pre status for the first set in this workout exercise
      uie.sets[0].logSet.preStatus.push({ status: PreSetStatus.PlateauResolved, textInserts: { ["message"]: preSetMessage } });
      // Set first set to exercise log to save this status immediately
      uie.logExercise.sets[0] = uie.sets[0].logSet;
      let transaction = DatabaseWrapper.getTransactionId();
      await this.saveWorkoutOrOtherAsync(transaction, this.logWorkout);
      // Remove plateau flag and save subslot to plan
      delete this.programSubSlotForAnswer(uie)?.plateauWorkoutLog_id;
      this.writeSubSlotBackToPlan(uie);
      await this.userDb.savePlanAsync(transaction, undefined, this.planSaveContext(PlanSaveCaller.WorkoutOpenExercisePlateauResolved));
    } if (preSetMessage === false) {
      let transaction = DatabaseWrapper.getTransactionId();
      await this.userDb.savePlanAsync(transaction, undefined, this.planSaveContext(PlanSaveCaller.WorkoutOpenExercisePlateauKept));
    }
    // whichever way the user DECIDED above, the EARLIER workout still carries
    // the status that draws the "Resolve" card, and the plan pointer that made
    // it answerable is now gone. Left alone it becomes a card that can never be
    // resolved and a button that does nothing (#4360).
    //
    // the test is "did the user actually decide", not "is it one particular
    // sentinel" — the two branches above clear the pointer for a message string
    // and for `false`, and for nothing else. An answer that is neither (a
    // dismissal resolves to `null` or `undefined` depending on how the alert was
    // closed) leaves the suggestion pending, so its card must stay. The prompt
    // here is raised with bMustDecide, which offers no "Decide later" button and
    // refuses a backdrop dismiss, so this is not a path a user can take today;
    // the guard is here so a later change to those buttons cannot silently start
    // deleting a live card.
    const userDecided = !!preSetMessage || preSetMessage === false;
    if (pendingPlateauWorkoutLog_id && userDecided) {
      await this.clearPlateauCardInEarlierWorkoutAsync(uie, pendingPlateauWorkoutLog_id);
    }
    // loader populates `reducedTargetReps_delta` and `reducedTargetWeight_pc` as
    // mutually exclusive (its layoff block takes the rep branch OR the weight branch,
    // never both), so checking the rep marker first here is safe.
    if (uie.reducedTargetReps_delta) {
      uie.sets[0].logSet.preStatus.push({ status: PreSetStatus.DidNotPerformDeloadReps, textInserts: undefined });
    } else if (uie.reducedTargetWeight_pc && uie.reducedTargetWeight_pc !== -1) {
      uie.sets[0].logSet.preStatus.push({ status: PreSetStatus.DidNotPerformDeload, textInserts: undefined });
    }
    // #5176: say so when the weight on screen is not the weight the plan wanted. Separate from the
    // layoff messages above rather than chained onto them: a layoff cut and a cap can both apply to
    // the same session, and they explain different things, so the user should read both.
    if (uie.prescriptionCap) {
      uie.sets[0].logSet.preStatus.push({ status: PreSetStatus.PrescribedWeightCapped, textInserts: undefined });
    }
    // #4822: say why the weight is one step lower after last session's deload. Only on a set still
    // to be performed, and once: this status is saved with the workout, and the exercise can be
    // opened again.
    const firstSet = uie.sets[0].logSet;
    if (uie.deloadCarriedOver && firstSet.state === LogRecordState.Scheduled &&
        !firstSet.preStatus.some(p => p.status === PreSetStatus.DeloadedTargetWeight)) {
      firstSet.preStatus.push({ status: PreSetStatus.DeloadedTargetWeight, textInserts: undefined });
    }

    uie.expanded.set(true);
    if (!this.someExerciseHighlighted()) {
      uie.highlighted.set(true);
    }
  }

  // remember which set was focused last
  markFocusedSet(uis: UiSet) {
    this.logWorkout.lastUIState = {
      currentExerciseId: uis.uiExercise.exercise._id, // note: uiExercise.logExercise.exerciseId is not set up until the first set is concluded!
      currentSetIndex: uis.uiExercise.sets.findIndex(s => s === uis),
    }
  }

  restoreUIState(): boolean {
    // if there is a state properly stored
    if (this.logWorkout.lastUIState) {
      let lastSetIndex = this.logWorkout.lastUIState.currentSetIndex;
      let lastExerciseId = this.logWorkout.lastUIState.currentExerciseId;
      if (lastSetIndex >= 0 && lastExerciseId) {
        // find which set it refers to
        // note: uiExercise.logExercise.exerciseId is not set up until the first set is concluded!
        // an entry split off by an exercise change (#5507) is finished and never the one in
        // progress, so it is passed over even when it names the same exercise
        let uie: UiExercise = this.exercises.find(e => !isSplitSubSlotId(e.logExercise?.subSlotId)
          && (e.logExercise.exerciseId === lastExerciseId || e.exercise._id === lastExerciseId));
        // if the remembered exercise is no longer in the workout (it was switched away from, and the
        // stored state predates that), there is nothing to restore — fall back to the default open
        if (!uie) {
          return false;
        }
        let uis: UiSet = uie.sets[lastSetIndex];
        // if the set is valid, and can be opened
        if (uis && ![LogRecordState.Done, LogRecordState.Skipped].includes(uis.logSet.state)) {
          // if set is already open, skip redundant re-initialization
          if (uie.openSetIndex() === lastSetIndex) {
            return true;
          }
          // open the set
          void this.openSet(uie, lastSetIndex, 'focus');
          return true;
        }
      }
    }
    return false;
  }

  // make sure we have the current path valid, if possible
  private setCurrentPath(uie: UiExercise) {
    this.workoutLoader.setCurrentPath(uie);
    this.fixupInvalidPathState(uie);
  }

  private fixupInvalidPathState(uie: UiExercise): void {
    this.workoutLoader.fixupInvalidPathState(uie, this.declaredState, this.fitnessState);
  }

  rebuildPathsIfNeeded(e: UiExercise) {
    this.workoutLoader.rebuildPathsIfNeeded(e, this.declaredState, this.fitnessState);
  }


  getCardElementId(uie: UiExercise) {
    return uie.exercise._id;
  }

  setInstructionsTextInputReps: string;
  setInstructionsTextInputStrength: string;
  setInstructionsTextInputRepsAndStrength: string;
  showSetInstructions = true;
  showBwHelpText = true;

  // if show the last attempt at this exercise, but only if it was a failed RM test
  async prepareLastStatsAsync(s: UiSet) {
    const today = DateTime.fromISO(this.logWorkout.time_utc).toLocal();
    const todayBOD = today.minus({ days: 1 }).endOf('day');
    const prevBOD = today.minus({ days: 14 }).startOf('day');
    const allFutureWorkouts = (await this.userDb.getLogEntriesInRangeAsync(prevBOD, todayBOD, this.docWatcher)).filter(w => w.type === LogRecordType.Workout) as Log_Workout[];
    let lastAttempt: Log_WorkoutExercise = undefined;
    let timestamp_utc: string = undefined;
    for (let iWorkout = allFutureWorkouts.length - 1; iWorkout >= 0; iWorkout--) {
      const logWorkout = allFutureWorkouts[iWorkout];
      const exercises = this.workoutLoader.getEffectiveExercises(logWorkout, this.plan, PlanMerge.AsStored);
      // every entry of this exercise in that workout, not just the first (#5507)
      const workoutExercise = combinedEntryForExercise(exercises, s.uiExercise.exercise._id);
      if (workoutExercise) {
        lastAttempt = workoutExercise;
        timestamp_utc = logWorkout.time_utc;
        break;
      }
    }

    if (!lastAttempt) {
      return;
    }
    let bestFailedStats: SetStats = undefined;
    for (let potentialLastSet of lastAttempt.sets) {
      if (potentialLastSet.setType === SetType.RMTest && potentialLastSet.setSuccess === SetSuccess.Fail) {
        if (!bestFailedStats || bestFailedStats.estimated1rm_net_kg <= potentialLastSet.stats.estimated1rm_net_kg) {
          bestFailedStats = potentialLastSet.stats;
        }
      } else if (potentialLastSet.setType === SetType.NonRMTest && potentialLastSet.setSuccess === SetSuccess.Ok) {
        // if we have a failed set, but not RM test, then we don't show it
        break;
      }
    }

    if (bestFailedStats) {
      let locale = this.settings.locale;
      let days = daysSinceDate(this.debug.getNowLocal(), timestamp_utc);
      s.lastAttemptDays.set(LL.workouts.detail.lastAttempt({ days }));
      s.lastAttemptValue.set(`${formatMeasure(bestFailedStats.weight_kg, 'addedweight_kg', locale, bestFailedStats.weight_unit)} ${Formatting.times} ${bestFailedStats.reps}`);
    }
  }

  // opts.skipSave: remember the focused set in memory only; the caller saves the workout itself
  // afterwards (the bulk "skip remaining" loop, which writes everything with one save at the end)
  async openSet(e: UiExercise, is: number, action: 'focus' | 'open', opts?: { skipSave?: boolean }) {
    if (this.mode === WorkoutLoadMode.Template || this.mode === WorkoutLoadMode.Unknown) {
      return;
    }

    e.loading.set(true);
    this.cdRef.markForCheck();

    try {
    if (is === 0) {
      await this.openExercise(e);
    }

    // if the set is already concluded, refuse to open it. everything below prefills the set
    // for logging — it overwrites the bodyweight share, the weight, the reps and the planned
    // values — so on a Done set it would contradict the strength figures stored beside them
    // (the damage #4556 and #5175 are about). a concluded set is edited through `reopenSet`.
    // re-running the estimate here is not the answer either: bf49fb31e showed that it would
    // work from today's bodyweight rather than the one on the day of the set.
    const setToOpen = e.sets[is];
    if (setToOpen && this.isSetCompleted(setToOpen)) {
      this.logging.trackSilentProblem('openSet was asked to open a concluded set - refused', {
        exerciseId: e.exercise?._id,
        setIndex: is,
        setState: setToOpen.logSet.state,
        action,
      });
      return;
    }

    e.expanded.set(true);
    e.openSetIndex.set(is);
    let s = e.sets[is];
    s.logSet.stats = s.logSet.stats || {}; // guard against uninitialized data

    // initialize weight_unit and bw_pc before any await — otherwise Angular may render
    // cs-input-measure with undefined userUnit during the saveWorkoutAsync yield,
    // causing it to fall back to unit preferences and corrupt stat.weight_unit (#2876)
    s.logSet.stats.weight_unit = e.stat.weight_unit;
    s.logSet.stats.bw_pc = e.exercise.bw_pc;

    // if we are focusing this set, and the workout already has a logged set anywhere
    // (the check is workout-wide, not per exercise: jumping to a later exercise, or moving on to the
    // next one after finishing an exercise, focuses an exercise with nothing logged yet, and that
    // move must still be remembered, or re-entering the page reopens the earlier exercise (#4711).
    // before anything is logged nothing is written, as in 60a18c5db).
    // the list is still unset during the first build, where the max-weight auto-switch opens set 1
    // before the build assigns it (df-d2aa609222fa); nothing is on screen yet, so nothing is remembered
    const workoutHasLoggedSet = (this.exercises ?? []).some(ex => ex.sets?.some(set => this.isSetCompleted(set)));
    if (action === 'focus' && workoutHasLoggedSet) {
      // store the info for later
      this.markFocusedSet(s);
      if (!opts?.skipSave) {
        await this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
      }
    }

    if (s.logSet.state === LogRecordState.Scheduled) {
      s.logSet.state = LogRecordState.Incomplete;
    }

    this.initializeSetType(s);

    // If we should deloading for explosive reps then we should not use myo-sets but regular ones.
    if (is === 1 && s.logSet.setType === SetType.Deload && s.logSet.miniSets && s.logSet.miniSets.length > 0) {

      let numPlannedSets = s.logSet.miniSets.length;
      s.logSet.miniSets = undefined;
      let numSets = numPlannedSets;
      for (let setIndex = 0; setIndex < numSets - 1; setIndex++) {
        let uis = (e.sets[setIndex + 2] = new UiSet());

        let logSet = e.logExercise.sets[setIndex + 2];
        if (!logSet) {
          // note: if it doesn't exist in log, we don't add it now, but only after it is actually performed!
          logSet = { state: LogRecordState.Scheduled, stats: {}, setSuccess: undefined, preStatus: [], postStatus: [] };
        }
        uis.logSet = logSet;
        uis.uiExercise = e;
      }
    }

    let prevSet = is > 0 && e.sets[is - 1];
    let earlierTargetSet = e.sets.slice(0, is - 1).find(s => s.logSet.setSuccess === SetSuccess.TargetReps);
    let internalError = null; // used to handle impossible combinations
    // the rep target a benchmark set was given. The set opens with an empty Reps field (#4142), so this
    // is kept apart from stats.reps and written into plannedReps after the switch
    let benchmarkPlannedReps: number | undefined;
    // #6197: the arms below that work a weight out from an earlier set record it again, so a value
    // from an earlier open never reaches a set the arms (or the fallback below them) did not work out
    s.derivedWeight_kg = undefined;

    if (e.equipment) {
      this.nextAutofocusElementId = this.getCardElementId(e);
      this.nextAutofocusSubSlotId = e.logExercise?.subSlotId;
    }

    switch (s.logSet.setType) {
      case SetType.NonRMTest:
        {
          if (prevSet && prevSet.logSet.setSuccess !== SetSuccess.Ok) {
            s.instructionsText.set(LL.workouts.detail.setInstructions.nonRMTestRetry());
          } else {
            s.instructionsText.set(LL.workouts.detail.setInstructions.nonRMTest());
          }
          s.requireRepsInput.set(false);
          s.requireStrengthInput.set(true);

          s.logSet.plannedWeight_kg = s.logSet.stats.weight_kg = null;
          const intensity_pc = e.planPath && getSlotPathIntensity(e.planSubSlot, e.planPath);
          s.logSet.plannedReps = s.logSet.stats.reps = getRepTargetForIntensity(intensity_pc);
        }
        break;
      case SetType.RMTest:
        {
          if (prevSet && prevSet.logSet.setSuccess !== SetSuccess.Ok) {
            s.instructionsText.set(LL.workouts.detail.setInstructions.rmTestRetry());
          } else {
            s.instructionsText.set(LL.workouts.detail.setInstructions.rmTest());
            await this.prepareLastStatsAsync(s);
          }
          s.requireRepsInput.set(true);
          s.requireStrengthInput.set(true);

          s.logSet.plannedWeight_kg = s.logSet.stats.weight_kg = null;
          s.logSet.plannedReps = s.logSet.stats.reps = null;
        }
        break;
      case SetType.RMTestFollow:
        {
          if (prevSet) {
            s.instructionsText.set(LL.workouts.detail.setInstructions.rmTestFollow());
            s.requireRepsInput.set(true);
            // need to calculate added weight so that the total load is 80% of what it was in the testing set
            // (a follow on a follow repeats the previous weight instead, so it works nothing out)
            const followsTestSet = prevSet.logSet.setType != SetType.RMTestFollow;
            const followFrom_net_kg = prevSet.logSet.stats.weight_net_kg * 0.8;
            // #6197: worked out on every open, also when the field is already filled (a reopened page),
            // so the floor hint still knows the weight this set wanted. Nothing is written here
            if (followsTestSet && Number.isFinite(followFrom_net_kg)) {
              s.derivedWeight_kg = this.wantedAddedWeight_kg(s, followFrom_net_kg);
            }
            if (s.logSet.stats.weight_kg == null) {
              // unless user already edited it
              if (!followsTestSet) {
                // follow, on follow -> same weight
                s.logSet.stats.weight_kg = prevSet.logSet.stats.weight_kg;
                s.logSet.stats.reps = null;
              } else {
                // the figure is ABSENT on a set whose reps rounded to 0 (see the Deload arm below),
                // and NaN would pass straight through setWeightFromNet into the stored plan
                if (Number.isFinite(followFrom_net_kg)) {
                  this.setWeightFromNet(s, followFrom_net_kg);
                  s.logSet.stats.reps = null;
                } else {
                  this.trackPrevSetWithoutNetWeight(e, is, prevSet, SetType.RMTestFollow);
                  internalError = 'Previous set has no strength figures to follow from!';
                }
              }
            }
          } else {
            internalError = 'No previous set!';
          }
        }
        break;
      case SetType.RepTest:
        {
          s.instructionsText.set(LL.workouts.detail.setInstructions.repTest());
          s.requireRepsInput.set(true);
          ({ weight_kg: s.logSet.stats.weight_kg, setup: s.weightSetup } = this.adjustWeight(e.targetWeight_kg, 'round', e.equipment));
          s.logSet.plannedWeight_kg = s.logSet.stats.weight_kg;
          s.logSet.plannedReps = s.logSet.stats.reps = null;
        }
        break;
      case SetType.Regular1st:
        {
          s.instructionsText.set(LL.workouts.detail.setInstructions.regular());
          s.isBasicInstruction.set(true);
          s.requireRepsInput.set(true);
          if (this.isEditableMode()) {
            if (e.isTempSwap) {
              // for temp swaps: always start with empty fields, user enters weight manually
            } else {
              ({ weight_kg: s.logSet.stats.weight_kg, setup: s.weightSetup } = this.adjustWeight(e.targetWeight_kg, 'round', e.equipment));
              s.logSet.plannedWeight_kg = s.logSet.stats.weight_kg;
              // #4142: the Reps field opens empty and shows the range as its placeholder, so the user
              // types what they did instead of overwriting a prefilled target
              s.logSet.stats.reps = null;
              benchmarkPlannedReps = e.planPath.target_reps;
            }
          }
        }
        break;
      case SetType.Followup:
        {
          if (prevSet) {
            s.instructionsText.set(LL.workouts.detail.setInstructions.followup());
            s.isBasicInstruction.set(true);
            s.requireRepsInput.set(true);
            s.logSet.stats.weight_kg = prevSet.logSet.stats.weight_kg;
            s.logSet.stats.reps = null;
            // special case for autoprogression - if two sets in a WO hit the target reps, we must use increased weight immediately
            if (prevSet && prevSet.logSet.setSuccess == SetSuccess.TargetReps && earlierTargetSet && e.planPath) {
              s.logSet.stats.weight_kg = e.planPath.target_weight_kg;
              s.logSet.stats.reps = e.planPath.target_reps;
            }
          } else {
            internalError = 'No previous set!';
          }
        }
        break;
      case SetType.Dropset:
        {
          if (prevSet) {
            // dropsets normally inherit their weight from previous dropset,
            // (except if the previous also failed to do >3 reps!)
            const inheritsPreviousWeight = prevSet.logSet.setType == SetType.Dropset && prevSet.logSet.stats.reps > 3;
            const dropFrom_net_kg = prevSet.logSet.stats.weight_net_kg * 0.9;
            // #6197: worked out on every open, also when the field is already filled (a reopened page),
            // so the floor hint still knows the weight this set wanted. Nothing is written here
            if (!inheritsPreviousWeight && Number.isFinite(dropFrom_net_kg)) {
              s.derivedWeight_kg = this.wantedAddedWeight_kg(s, dropFrom_net_kg);
            }
            if (s.logSet.stats.weight_kg == null) {
              if (inheritsPreviousWeight) {
                s.instructionsText.set(LL.workouts.detail.setInstructions.dropset());
                s.logSet.stats.weight_kg = prevSet.logSet.stats.weight_kg;
              } else {
                // a previous set whose reps rounded to 0 is exactly what triggers a dropset, and it has
                // no net weight at all (see the Deload arm below) - NaN must not become the prescription
                if (Number.isFinite(dropFrom_net_kg)) {
                  this.setWeightFromNet(s, dropFrom_net_kg);
                  if (s.logSet.stats.weight_kg === 0 && prevSet.logSet.stats.weight_net_kg === 0) {
                    // we get here if the user would have to reduce to negative weights - which is not possible (without rubber bands or so)
                    s.instructionsText.set(LL.workouts.detail.setInstructions.dropsetEccentric());
                  } else if (this.isHeldUpByEquipmentFloor(e, s)) {
                    // #6197: the equipment cannot go lighter, so "reduce the weight as shown" would be untrue
                    s.instructionsText.set(LL.workouts.detail.setInstructions.dropset());
                  } else {
                    s.instructionsText.set(LL.workouts.detail.setInstructions.dropsetReduce());
                  }
                } else {
                  this.trackPrevSetWithoutNetWeight(e, is, prevSet, SetType.Dropset);
                  internalError = 'Previous set has no strength figures to drop from!';
                }
              }
              s.requireRepsInput.set(true);
            }

            s.logSet.stats.reps = null;
          } else {
            internalError = 'No previous set!';
          }
        }
        break;
      case SetType.Deload:
        {
          let firstSet = is > 0 && e.sets[0];
          if (prevSet && firstSet) {
            s.instructionsText.set(LL.workouts.detail.setInstructions.deload());
            s.requireRepsInput.set(true);

            // always recalculate the recommendation based on the set that caused the deload (always the first set!)
            // the rule itself, and why it answers "cannot derive" for a first set with no strength
            // figures, live in `deriveDeloadWeight_net_kg` where they can be unit-tested.
            const deloadFrom_net_kg = deriveDeloadWeight_net_kg(firstSet.logSet.stats);

            // an undeducible weight falls back to the same recovery this switch already uses for a
            // first set it cannot work from, and reports the condition rather than absorbing it.
            if (deloadFrom_net_kg != null) {
              s.derivedWeight_kg = this.wantedAddedWeight_kg(s, deloadFrom_net_kg);
              this.setWeightFromNet(s, deloadFrom_net_kg);

              // if user edited _down_ in previous, then use that
              if (prevSet.logSet.setType == SetType.Deload && prevSet.logSet.stats.weight_kg < s.logSet.stats.weight_kg) {
                s.logSet.stats.weight_kg = prevSet.logSet.stats.weight_kg;
              }

              // #6197: the equipment cannot go lighter, so "with the reduced load" would be untrue
              if (this.isHeldUpByEquipmentFloor(e, s)) {
                s.instructionsText.set(LL.workouts.detail.setInstructions.deloadAtFloor());
              }

              s.logSet.stats.reps = 3;
            } else {
              this.logging.trackSilentProblem('Deload weight could not be derived - the first set has no strength figures', {
                exerciseId: e.exercise?._id,
                setIndex: is,
                firstSetState: firstSet.logSet.state,
                firstSetReps: firstSet.logSet.stats.reps,
                firstSetWeight_kg: firstSet.logSet.stats.weight_kg,
                firstSetWeightNet_kg: firstSet.logSet.stats.weight_net_kg,
                firstSetEstimated1rmNet_kg: firstSet.logSet.stats.estimated1rm_net_kg,
              });
              internalError = 'First set has no strength figures to deload from!';
            }
          } else {
            internalError = 'No previous set!';
          }
        }
        break;
      case SetType.Dummy:
        break;
      default: {
        internalError = 'Unexpected type of unfinished set.';
      }
    }
    // df-ba54b87f23c7: the recovery runs BEFORE the two lines below and before `weightChanged`, so
    // the prescription it writes is the one that gets recorded as planned and the one the plate
    // breakdown describes. The other way round, the breakdown was built from the weight the set
    // had before the recovery — absent on every path that reaches here — so the set showed a
    // weight with no "how to load it" line under it and no off-grid warning, until the lifter
    // closed and reopened it (`reopenSet` recomputes).
    if (internalError) {
      console.assert(
        false,
        `Exercise ${e.exercise.name} set #${is} - ${s.logSet.setType} error: ${internalError} . Defaulting to regular set.`,
      );
      // df-f74f6efb268f: an exercise can have NO plan path at all, and the two reads below used to
      // go straight through it and throw a TypeError — so the set never opened and the lifter was
      // stopped, which is the opposite of what a recovery is for. `clearTempSwapProgression` in
      // the loader is the real case: a session-only exercise swap sets BOTH `planPath` and
      // `targetWeight_kg` to undefined, and a following set that cannot be derived (a deload off a
      // first set with no strength figures, or a dropset off a previous set with no net weight)
      // lands here. With no path there is genuinely nothing to prescribe, so say so once: no
      // weight and no rep target, which is the same empty state the `Regular1st` arm gives a temp
      // swap, and the lifter types both in.
      const recoveryPath = e.planPath;

      // the assert above reaches only a dev console. Report every fallback under one group, so
      // production learns how often this "impossible" state happens and on which set types. Three
      // arms already sent a more specific report before getting here; this one still counts them.
      //
      // df-f74f6efb268f: `hasPlanPath` separates the two shapes this fallback comes in. With a path
      // the recovery still has a prescription to offer; without one it hands the lifter empty
      // fields, which is a materially worse outcome and worth being able to count on its own. One
      // report rather than two, so a single occurrence stays a single Sentry event.
      this.logging.trackSilentProblem('openSet fell back to a regular set', {
        exerciseId: e.exercise?._id,
        setIndex: is,
        setType: s.logSet.setType,
        internalError,
        prevSetType: prevSet ? prevSet.logSet.setType : undefined,
        prevSetState: prevSet ? prevSet.logSet.state : undefined,
        hasPlanPath: !!recoveryPath,
      });

      // `??`, not `||`: a prescribed weight of exactly 0 is legitimate on a bodyweight exercise, and
      // `||` discarded it and silently fell back to the path's own (higher) target — precisely where
      // the #5176 cap clamps hardest. The sibling read in `onClickSetDone` already uses `??`.
      const recoveredWeight_kg = e.targetWeight_kg ?? recoveryPath?.target_weight_kg;

      // if there is a weight to fit, put it on the equipment's grid with the same call the `RepTest`
      // and `Regular1st` arms make — this recovery says it is "defaulting to a regular set", and
      // `e.targetWeight_kg` is not pre-rounded, so without this it prescribed a weight the equipment
      // cannot be set to.
      //
      // A prescribed 0 is left exactly as it is, and that is the whole reason this is a branch rather
      // than one unconditional call. Rounding raises 0 to the lightest the equipment can produce — a
      // loadable bar's own weight, or a manual equipment's `weight_min_kg` — and 0 ADDED weight is a
      // real prescription on a bodyweight exercise that happens to be configured with loadable
      // equipment (the case `getDisplayWeightError_kg` names for #3879). Substituting a higher figure
      // for it is exactly what #5176 stopped this path doing.
      if (recoveredWeight_kg) {
        ({ weight_kg: s.logSet.stats.weight_kg, setup: s.weightSetup } = this.adjustWeight(
          recoveredWeight_kg,
          'round',
          e.equipment,
        ));
      } else {
        s.logSet.stats.weight_kg = recoveredWeight_kg;
      }
      s.logSet.stats.reps = recoveryPath?.target_reps;
    }

    s.logSet.plannedWeight_kg = s.logSet.stats.weight_kg;
    s.logSet.plannedReps = s.logSet.stats.reps;
    // if a benchmark set opened with an empty Reps field, still record the rep target it was given:
    // plannedReps is stored, shown on a weight-overridden collapsed row, and read by historical-subslot
    if (benchmarkPlannedReps !== undefined) {
      s.logSet.plannedReps = benchmarkPlannedReps;
    }

    // convert the weight setup into text
    this.weightChanged(s);

    s.showBwHint.set(s.logSet.stats.bw_pc > 0);
    s.showBarbellHint.set(e.equipment && e.equipment.type === EquipmentType.Bar);
    } catch (err) {
      // capture which exercise/set hit the intermittent throw, then re-throw so
      // existing callers' control flow and error propagation are unchanged.
      this.logging.trackAbnormalException(err, untranslatedString(`openSet failed (exercise ${e.exercise?._id}, set #${is})`));
      throw err;
    } finally {
      // always clear the in-card spinner — a throw between set(true) and set(false)
      // would otherwise strand it until the user leaves and re-enters the page
      // (same #720-class "stuck in-card spinner" bug already guarded in rebuildSets)
      e.loading.set(false);
      this.cdRef.markForCheck();
    }
  }

  // used to show again sets that have been closed before
  reopenSet(e: UiExercise, is: number) {
    // cancel any running rest timer as soon as we know this is a reopen —
    // before the early returns below, so the timer doesn't leak if the
    // user reopens a set in a read-only historical workout view
    void this.restTimer.cancel();
    if (this.isCoachManaging) return;
    if (this.mode > WorkoutLoadMode.Current) {
      return;
    }
    e.expanded.set(true);
    e.openSetIndex.set(is);
    let s = e.sets[is];

    // auto-enter edit mode for completed sets
    if (s.logSet && (s.logSet.state === LogRecordState.Done || s.logSet.state === LogRecordState.Skipped)) {
      this.recordEditBaseline(s);
      s.inEditMode.set(true);
    }

    // convert the weight setup into text
    this.incW(e, s, 'manual');
  }

  /**
   * Reports a set whose prescription has to be derived from the previous set's net weight, when
   * that figure is missing. `estimate1RM` deletes it for a set whose reps round to 0, and sets like
   * that were saveable before `b1595f944`. The caller then falls back to the plan's target.
   */
  private trackPrevSetWithoutNetWeight(e: UiExercise, is: number, prevSet: UiSet, setType: SetType) {
    this.logging.trackSilentProblem('Set weight could not be derived - the previous set has no strength figures', {
      setType,
      exerciseId: e.exercise?._id,
      setIndex: is,
      prevSetType: prevSet.logSet.setType,
      prevSetState: prevSet.logSet.state,
      prevSetReps: prevSet.logSet.stats.reps,
      prevSetWeight_kg: prevSet.logSet.stats.weight_kg,
      prevSetWeightNet_kg: prevSet.logSet.stats.weight_net_kg,
    });
  }

  /**
   * The added weight a set should carry for a total (net) load: the bodyweight share taken out, never
   * below 0. Only arithmetic, nothing is written, so #6197 can ask it on every open of a set whose
   * field is already filled.
   */
  private wantedAddedWeight_kg(s: UiSet, netWeight_kg: number): number {
    let addedWeight_kg = netWeight_kg;
    if (s.logSet.stats.bw_pc) {
      addedWeight_kg -= this.fitnessState.weight_kg * (s.logSet.stats.bw_pc / 100);
    }
    if (addedWeight_kg < 0) {
      addedWeight_kg = 0;
    }
    return addedWeight_kg;
  }

  private setWeightFromNet(s: UiSet, netWeight_kg: number) {
    ({ weight_kg: s.logSet.stats.weight_kg, setup: s.weightSetup } = this.adjustWeight(
      this.wantedAddedWeight_kg(s, netWeight_kg),
      'round_down',
      s.uiExercise.equipment,
    ));
  }

  // #4142 compact feedback: true when a finished exercise shows its summary instead of only its header
  isCompactFinishedExercise(e: UiExercise): boolean {
    return this.compactFeedback && e.isCompleted();
  }

  // #4142 compact feedback: the folded summary of a finished exercise; see finished-exercise-display.ts
  getFinishedExerciseSummary(e: UiExercise): FinishedExerciseSummary {
    const setOne = e.sets.find(s => s.logSet?.setType === SetType.Regular1st);
    return getFinishedExerciseSummary({
      setsText: formatDoneSets(e.logExercise, this.settings.locale),
      allSkipped: e.sets.every(s => s.logSet?.state === LogRecordState.Skipped),
      setOne: setOne ? this.getSetProgression(setOne) : undefined,
      hasBenchmarkSet: !!setOne,
      lastWorkoutSetsText: e.lastWorkoutSetsText(),
      isEditableMode: this.isEditableMode(),
      isCurrentWorkout: this.mode === WorkoutLoadMode.Current,
      planPath: e.planPath,
    });
  }

  // #4142 compact feedback: the chip's words, "+2 reps vs last time" or "−4 reps vs last time"
  getRepChangeChipText(chip: RepChangeChip): TranslatedString {
    switch (chip.direction) {
      case RepChangeDirection.Up:
        return LL.workouts.detail.finishedExercise.moreRepsVsLastTime({ count: chip.reps });
      case RepChangeDirection.Same:
        return LL.workouts.detail.finishedExercise.sameAsLastTime();
      default:
        return LL.workouts.detail.finishedExercise.fewerRepsVsLastTime({ count: chip.reps });
    }
  }

  // whether the collapsed template has anything to draw for this exercise: since the #4142 review
  // fixes a finished exercise's result sits in the header line, so only status cards are left
  hasCollapsedContent(e: UiExercise): boolean {
    return !e.expanded() && this.getExerciseResolvableStatusLines(e).length > 0;
  }

  /** #4142 review fixes: the exercise cards whose header line is the done sets and the rep chip, and
   * whose fold arrow points down: finished exercises, with the switch on (cs-exercise-list's input) */
  readonly usesFinishedHeaderLine = (e: UiExercise): boolean => this.isCompactFinishedExercise(e);

  // #4142 compact feedback: draws the workout summary from the stored sets, then looks up the
  // personal records WITHOUT waiting for them: onExerciseDone's callers save after it returns, so an
  // awaited lookup would hold the save. See design-docs/workout-completion-summary.md
  private showWorkoutSummary() {
    if (!this.compactFeedback) {
      return;
    }
    this.summaryRecordExerciseIds = undefined;
    this.refreshWorkoutSummary();
    // a new summary makes every earlier lookup's answer stale, whether or not this one looks anything up
    void this.lookUpSummaryRecordsAsync(++this.summaryLookupGeneration);
  }

  // whether the workout counts as finished for the summary: finished in this visit, or stored as finished
  // when it was opened (#6536). The Close button and the switch-off card still read `complete` alone
  isWorkoutFinished(): boolean {
    return this.complete || this.logWorkout?.state === LogRecordState.Done;
  }

  // rebuilds the summary from what the screen holds now, with the records found so far
  private refreshWorkoutSummary() {
    this.workoutSummary = buildWorkoutSummary(
      (this.exercises ?? []).map(e => ({
        exerciseId: e.exercise?._id,
        name: e.exercise?.name ?? '',
        sets: e.sets.map(s => s.logSet ?? {}),
      })),
      this.summaryRecordExerciseIds,
    );
    this.cdRef.markForCheck();
  }

  // the review prompt's personal-record rule, bounded by its time limit. A timeout is an expected
  // skip (a first build of the view can be slow); a real failure is reported. Both show no records
  private async lookUpSummaryRecordsAsync(generation: number): Promise<void> {
    const exercises = this.screenLogExercises();
    const exerciseIds = exercisesWithRecordCandidates(exercises);
    // if no set could be a record, there is nothing to look up
    if (exerciseIds.length === 0) {
      return;
    }
    const workoutId = this.logWorkout._id;
    let bestElsewhere_net_kg: Map<string, number> | undefined;
    let failure: unknown;
    let gaveUp = false;
    const reportFailure = (err: unknown) => this.logging.trackSilentProblem('workout-summary-pr-lookup-failed',
      { workoutId, ...errorReportFields(err ?? new Error('personal-record lookup rejected without a reason')) });
    const lookup = this.userDb.getBestStrengthElsewhereAsync(exerciseIds, workoutId).then(
      found => { bestElsewhere_net_kg = found; },
      err => {
        // a failure after we stopped waiting is still a failure, and must still be reported
        if (gaveUp) {
          reportFailure(err);
          return;
        }
        failure = err ?? new Error('personal-record lookup rejected without a reason');
      });
    const outcome = await awaitSignalOrTimeoutAsync(lookup, sleep, PERSONAL_RECORD_LOOKUP_TIMEOUT_MS);
    if (outcome === FirstProbeGateOutcome.Timeout) {
      gaveUp = true;
      // a rejection that settled after the timer won but before this line ran is reported here
      if (failure !== undefined) {
        reportFailure(failure);
      }
      this.logging.trackSilentInfo('workout-summary-pr-lookup-slow', { workoutId, timeout_ms: PERSONAL_RECORD_LOOKUP_TIMEOUT_MS });
      return;
    }
    if (failure !== undefined) {
      reportFailure(failure);
      return;
    }
    // if the page went away, the workout was reopened, or a newer lookup started meanwhile, this
    // answer is no longer wanted
    if (this.viewDestroyed || !this.isWorkoutFinished() || this.logWorkout?._id !== workoutId || generation !== this.summaryLookupGeneration) {
      return;
    }
    const beaten = personalRecordReport(exercises, bestElsewhere_net_kg ?? new Map()).filter(row => row.beat);
    this.summaryRecordExerciseIds = new Set(beaten.map(row => row.exerciseId));
    this.refreshWorkoutSummary();
  }

  /** the summary's totals line, e.g. "14 sets · 9,870 kg · 58 min" */
  getWorkoutSummaryTotalsText(summary: WorkoutSummary): string {
    const parts: string[] = [LL.workouts.detail.summary.setsCount({ count: summary.sets })];
    // the volume in the user's unit, whole, with the locale's thousands separator
    const unit = this.firstDoneSetWeightUnit();
    const { factor, unitName } = getMeasureUnitConversion('addedweight_kg', this.settings.locale, unit);
    parts.push(`${formatLocaleFloat(Math.round(summary.volume_kg * factor), this.settings.locale)} ${unitName}`);
    if (summary.durationMinutes !== undefined) {
      parts.push(this.formatSummaryDuration(summary.durationMinutes));
    }
    return parts.join(' · ');
  }

  // "58 min", or "1 h 04 min" from an hour
  private formatSummaryDuration(minutes: number): string {
    if (minutes < 60) {
      return LL.workouts.detail.summary.minutes({ minutes });
    }
    const hours = Math.floor(minutes / 60);
    return LL.workouts.detail.summary.hoursMinutes({ hours, minutes: String(minutes % 60).padStart(2, '0') });
  }

  // the unit the user logs weights in, read from their first done set; the locale's default otherwise
  private firstDoneSetWeightUnit(): string | undefined {
    const doneSets = (this.exercises ?? []).flatMap(e => e.sets).map(s => s.logSet).filter(set => set?.state === LogRecordState.Done);
    const unit = doneSets.find(set => typeof set.stats?.weight_unit === 'string')?.stats.weight_unit;
    return unit;
  }

  /**
   * the highlight line's two halves, "2 personal records" (drawn bold) and "3 exercises progressed",
   * joined by "and" in the template; each half left out at 0, undefined when both are
   */
  getWorkoutSummaryHighlight(summary: WorkoutSummary): { records?: TranslatedString; progressed?: TranslatedString } | undefined {
    const records = summary.personalRecords > 0 ? LL.workouts.detail.summary.recordsPart({ count: summary.personalRecords }) : undefined;
    const progressed = summary.progressedExercises > 0 ? LL.workouts.detail.summary.progressedPart({ count: summary.progressedExercises }) : undefined;
    if (!records && !progressed) {
      return undefined;
    }
    return { records, progressed };
  }

  /** the test-workout message, which the summary keeps as one line; undefined for an ordinary workout */
  getWorkoutSummaryTestMessage(): TranslatedString | undefined {
    return this.completionMessageKind === WorkoutCompletionMessage.Generic ? undefined : this.completionMessage;
  }

  // #4142 compact feedback: whether the exercise has equipment the user chose (not none, not unknown)
  hasChosenEquipment(e: UiExercise): boolean {
    return !!e.equipment && e.equipment.type !== EquipmentType.Unknown;
  }

  // #4142 compact feedback: the equipment box's second line, the setup parts joined with " · ", or
  // today's "(not configured)" when no equipment is chosen; undefined when there is nothing to show
  getEquipmentSetupLine(e: UiExercise, s: UiSet): string | undefined {
    if (s.setupTextParts?.length) {
      return s.setupTextParts.map(part => `${part.label}${part.value}`.trim()).join(' · ');
    }
    if (!this.hasChosenEquipment(e)) {
      return LL.workouts.detail.equipment.notConfigured();
    }
    return undefined;
  }

  private weightSetupToText(e: UiExercise, s: UiSet) {
    s.setupErrorWeight = null;
    // before the early returns below, so a hint never outlives the equipment or weight it described
    this.updateEquipmentFloorHint(e, s);

    // Equipment is always defined now (including automatic equipment)
    if (!e.equipment || e.equipment.type === EquipmentType.Unknown) {
      s.setupTextParts = [];
      return;
    }
    if (!isEquipmentValid(e.equipment)) {
      s.setupErrorWeight = 'Selected equipment has errors. Please correct this.';
      s.setupTextParts = [];
      return;
    }

    if (!s.weightSetup) {
      s.setupTextParts = [];
      return;
    }

    let locale = this.settings.locale;
    let unit = e.equipment.weight_unit;
    let format = (w: number) => formatMeasure(w, 'addedweight_kg', locale, unit);

    let parts: { label: string; value: string }[] = [];
    if (
      e.equipment.type == EquipmentType.Plates ||
      e.equipment.type == EquipmentType.Bar ||
      e.equipment.type == EquipmentType.Stack ||
      e.equipment.type == EquipmentType.Fixed
    ) {
      // Main equipment part
      if (e.equipment.type == EquipmentType.Stack && s.weightSetup.stackIndex >= 0) {
        let i = s.weightSetup.stackIndex;
        parts.push({ label: LL.workouts.detail.equipment.stack(), value: format(e.equipment.stack[i].totalWeight_kg) });
      }
      if (e.equipment.type == EquipmentType.Fixed && s.weightSetup.fixedWeight_kg > 0) {
        parts.push({ label: LL.workouts.detail.equipment.fixed(), value: format(s.weightSetup.fixedWeight_kg) });
      }
      if (e.equipment.type === EquipmentType.Bar && s.weightSetup.bar_kg) {
        parts.push({ label: LL.workouts.detail.equipment.bar(), value: format(s.weightSetup.bar_kg) });
      }

      // Plates part
      if (s.weightSetup.plates && s.weightSetup.plates.length > 0) {
        // Group plates by count of same weight
        let groupedPlates = groupBy(s.weightSetup.plates, "weight_kg");
        let platesTexts: string[] = []
        let platesKeys = Object.keys(groupedPlates);
        platesKeys.sort((a, b) => parseFloat(b) - parseFloat(a));
        for (let key of platesKeys) {
          let platesCount = groupedPlates[key].length;
          if (platesCount > 1) {
            platesTexts.push(`${platesCount}x${format(parseFloat(key))}`);
          } else {
            platesTexts.push(`${format(parseFloat(key))}`);
          }
        }
        if (platesTexts.length > 0) {
          let platesLabel = e.equipment.type === EquipmentType.Bar ? LL.workouts.detail.equipment.platesEachSide()
                          : e.equipment.type === EquipmentType.Stack ? LL.workouts.detail.equipment.micro()
                          : LL.workouts.detail.equipment.plates();
          parts.push({ label: `${platesLabel}: `, value: platesTexts.join(' + ') });
        }
      }
    } else if (e.equipment.type === EquipmentType.Manual) {
      // synthetic auto-equipment built from an inferred single increment (see workout-ui-models.checkEquipment)
      // has an arbitrary N — "35 × 2.3 kg" implies physical plates that don't exist; show the inferred step instead
      const isAutoCustomIncrement = e.equipment.id?.startsWith(AUTO_CUSTOM_INCREMENT_EQUIPMENT_ID_PREFIX);
      if (isAutoCustomIncrement) {
        parts.push({ label: '', value: LL.workouts.detail.equipment.inferredStep({ step: format(s.weightSetup.incrementWeight_kg) }) });
      } else if (s.weightSetup.baseWeight_kg > 0) {
        parts.push({ label: '', value: `${format(s.weightSetup.baseWeight_kg)}${Formatting.plus}${s.weightSetup.increments}${Formatting.times}${format(s.weightSetup.incrementWeight_kg)}` });
      } else {
        parts.push({ label: '', value: `${s.weightSetup.increments}${Formatting.times}${format(s.weightSetup.incrementWeight_kg)}` });
      }
    } else {
      parts.push({ label: '', value: LL.workouts.detail.equipment.notConfigured() });
    }
    s.setupTextParts = parts;

    // Only surface a weight that the equipment could match but lands between increments.
    // A weight below the lightest the equipment can produce is the floor, not an actionable
    // error — e.g. a bodyweight exercise logged with no added weight, or a very light exercise
    // on heavier equipment. getDisplayWeightError_kg returns undefined for those, so we neither
    // show the "-X" nag nor fire the telemetry (nor trigger the equipment-unbrowse cascade,
    // which is gated on setupErrorWeight). (#3879)
    const displayError_kg = getDisplayWeightError_kg(s.logSet.stats.weight_kg, e.equipment);
    if (displayError_kg) {
      s.setupErrorWeight = `${displayError_kg >= 0 ? '+' : ''}${format(displayError_kg * (e.equipment.type === EquipmentType.Bar ? 0.5 : 1))}`;
    }

    if (s.setupErrorWeight) {
      const exerciseStat = this.fitnessState.exerciseStats[e.exercise._id];
      this.telemetry.trackEvent('workout_equipment_setup_error', {
        exercise: e.exercise._id,
        equipmentId: e.equipment.id,
        equipmentType: e.equipment.type,
        equipmentUnit: e.equipment.weight_unit,
        isAutomaticEquipment: e.equipment.id?.startsWith('automatic_') || false,
        error: s.setupErrorWeight,
        targetWeight_kg: s.logSet.stats.weight_kg,
        setWeightUnit: s.logSet.stats.weight_unit,
        statWeightUnit: e.stat?.weight_unit,
        customIncrementByUnit: exerciseStat?.customWeightIncrementByUnit ? JSON.stringify(exerciseStat.customWeightIncrementByUnit) : null,
        equipmentIdByUnit: exerciseStat?.equipmentIdByUnit ? JSON.stringify(exerciseStat.equipmentIdByUnit) : null,
      });
    }
  }

  // whether the log/save button is enabled. The rule itself is `isSetLoggable`, which is pure and
  // unit-tested — it refuses a set whose reps round to 0 whether the set is being edited or logged
  // for the first time, because saving one deletes its strength figures while leaving it Done.
  isSetComplete(s: UiSet) {
    return !!s && isSetLoggable({
      reps: s.logSet.stats.reps,
      weight_kg: s.logSet.stats.weight_kg,
      inEditMode: s.inEditMode(),
    });
  }

  async startFirstSet(e: UiExercise) {
    await this.openSet(e, 0, 'focus');
    if (!this.isEditableMode()) {
      e.openSetIndex.set(-1);
    }
  }

  async saveWorkoutAsync(transaction: TransactionId) {
    // mark workout as modified for return callback
    this.workoutModified = true;

    // the list is still unset when the max-weight auto-switch saves during the first build (df-d2aa609222fa)
    for (let e of this.exercises ?? []) {
      this.logging.checkWorkoutExerciseValidity("saveWorkoutAsync", e.logExercise)
    }

    // ensure name, planReference, and exercises are set before saving (compute if not set).
    // #5640: the plan reference is normally stamped at load, in the same step that builds the
    // exercises from it. This is the fallback for a document that reached a save without
    // going through that step. It asks `resolvePlanReferenceForStampingAsync` rather than the
    // plain accessor, because the accessor answers Workout A whenever the look-back cannot
    // see the previous workout — and this is the exact line that wrote "Workout A" over the
    // reporter's Workout B session. An answer of undefined means the app does not know;
    // nothing is stamped, and the next save that can answer will do it.
    if (this.logWorkout.planReference === undefined) {
      const stampablePlanRef = await this.workoutLoader.resolvePlanReferenceForStampingAsync(this.logWorkout, this.plan);
      if (stampablePlanRef !== undefined) {
        this.workoutLoader.setWorkoutPlanReference(this.logWorkout, stampablePlanRef);
      }
    }
    // the name is derived from the plan reference, so it may only be stamped once that is
    // settled — otherwise it freezes the same guess under a different field
    if (!this.logWorkout.name && this.logWorkout.planReference !== undefined) {
      this.workoutLoader.setWorkoutName(this.logWorkout, this.workoutLoader.getEffectiveWorkoutName(this.logWorkout, this.plan));
    }
    if (!this.logWorkout.exercises || this.logWorkout.exercises.length === 0) {
      this.workoutLoader.setWorkoutExercises(this.logWorkout, this.workoutLoader.getEffectiveExercises(this.logWorkout, this.plan, planMergeForMode(this.mode)));
    }

    // say so when the document has lost a set the user already finished with,
    // BEFORE the re-link below puts it back — the re-link is what makes this
    // safe, so this is the only chance to measure how often it happens (#4832)
    this.reportConcludedSetsMissingBeforeRelink();

    // re-link exercise objects from UiExercise into logWorkout before every save.
    // async backlog regeneration can call ensureCachedReference(diskWorkout) which
    // shallow-copies disk data into the canonical (this.logWorkout), replacing the
    // exercises array with new objects from disk. UiExercise.logExercise still holds
    // the original objects with the latest set data from concludeSet, so re-linking
    // ensures we always save the authoritative exercise state.
    //
    // It MERGES rather than replaces. The screen still wins outright for every
    // exercise it holds — that is the whole point of the re-link — but the screen is
    // not always the whole workout, and writing the short list over the stored one
    // used to delete every set the user had logged in an exercise the page could not
    // display. `mergeScreenExercisesOverStored` owns the rule and explains why
    // keeping an entry cannot resurrect one the user deleted.
    if (this.exercises?.length) {
      const merged = mergeScreenExercisesOverStored(
        this.logWorkout.exercises ?? [],
        this.exercises.map(e => e.logExercise),
        this.exercises.flatMap(e => e.retiredSubSlotIds ?? []),
      );
      this.workoutLoader.setWorkoutExercises(this.logWorkout, merged.exercises);
      this.reportExercisesCarriedOverAtSave(merged.carriedOver);
    }

    // the same defence as the exercises re-link above, for the workout's own
    // state field (#4802). The method's own comment explains what reverts it.
    this.reassertConcludedWorkoutState();

    // if this is an in-memory workout being saved for the first time
    if (this.isInMemoryWorkout) {
      // register our workout as the canonical reference FIRST so any concurrent
      // backlog regeneration sees our data (not an empty stub)
      this.logWorkout = this.userDb.database.ensureCachedReference(this.logWorkout);

      // save workout to PouchDB BEFORE touching the schedule/cache, so that any
      // backlog regeneration triggered by saveScheduleAsync finds it on disk and
      // reuses it instead of creating a stub with exercises: []
      await this.userDb.saveEntryAsync(transaction, this.logWorkout);

      // now add one-time schedule rule for today
      const schedule = await this.userDb.loadScheduleAsync();
      const now = this.debug.getNowLocal();
      const workoutTimeLocal = getEntryLocalTimeInDesiredTimeZone(this.logWorkout, now);
      this.scheduleRuleService.addOneTimeItem({
        rules: schedule.rules,
        itemType: Schedule_ItemType.Workout,
        dateISO: getEntryLocalTimeInDesiredTimeZone(this.logWorkout, now).toISODate(),
        time_hr: isoTimeToHours(workoutTimeLocal.toISOTime()),
      });
      // saveScheduleAsync clears backlog cache internally, which triggers async
      // home page regeneration — but our workout is already on disk so it will be found
      await this.userDb.saveScheduleAsync(transaction);

      this.isInMemoryWorkout = false;
      return;
    }

    return this.userDb.saveEntryAsync(transaction, this.logWorkout);
  }

  /**
   * Save `workout` — through the page's own save when it IS the workout this
   * page is showing, and directly otherwise (#4832).
   *
   * Why a save of this page's own workout comes through here: `saveWorkoutAsync`
   * re-links the exercise objects the SCREEN holds back onto the document before
   * writing, and a save that goes straight to the database does not — so it can
   * write a stored body that has replaced the page's exercise list, losing any set
   * concluded since. `design-docs/sync-integrity.md` explains how the shared object
   * comes to hold that body.
   *
   * A DIFFERENT workout — an earlier one a pending suggestion still points at —
   * is saved directly, and must be: the page holds no on-screen objects for it,
   * so there is nothing to re-link and `saveWorkoutAsync` would write this page's
   * workout instead.
   *
   * `workout-detail-save-sites.test.ts` holds the page to this, with ONE exception
   * it names: `stampCoachReviewedIfApplicableAsync` writes directly and must, for
   * the reasons its own comment gives. Do not "tidy" that site into this helper —
   * doing so is what broke it in #4832.
   */
  private saveWorkoutOrOtherAsync(transaction: TransactionId, workout: Log_Workout): Promise<void> {
    if (this.isWorkoutShownOnThisPage(workout)) {
      return this.saveWorkoutAsync(transaction);
    }
    return this.userDb.saveEntryAsync(transaction, workout);
  }

  /** Is this the very workout the page is showing? Compared by id, not by object
   *  identity, because the shared object for a document can be replaced. */
  private isWorkoutShownOnThisPage(workout: Log_Workout): boolean {
    return !!workout && !!this.logWorkout && workout._id === this.logWorkout._id;
  }

  /**
   * The exercise object to READ and WRITE for `subSlotId` in `workout` (#4832).
   *
   * For the workout this page is showing, the screen is the authority — for the
   * same reason `saveWorkoutAsync` re-links from it. Reading the document's list
   * back instead is how the volume-autoregulation answer came to look for a set
   * that was not there, quietly do nothing, and then save the shorter body it had
   * read. For any other workout the document is all there is, so read it.
   */
  private logExerciseToActOn(workout: Log_Workout, subSlotId: string): Log_WorkoutExercise | undefined {
    if (this.isWorkoutShownOnThisPage(workout)) {
      const onScreen = this.exercises?.find(x => x.logExercise?.subSlotId === subSlotId)?.logExercise;
      if (onScreen) return onScreen;
    }
    return this.workoutLoader.getEffectiveExercises(workout, this.plan, PlanMerge.AsStored).find(x => x.subSlotId === subSlotId);
  }

  /**
   * The workout a pending suggestion points at, and the exercise inside it to act
   * on — or nothing, when either cannot be found.
   *
   * A suggestion (a plateau "Resolve" card, or a volume-autoregulation question)
   * records the id of the workout it was raised in. That is very often the workout
   * this page is showing, and then the page's own `logWorkout` is used rather than
   * re-read: it is the one shared object the whole app holds for that document, so
   * reading a second copy would answer with a body that the screen's own edits have
   * not reached. Any other id is an EARLIER workout, which only exists in the
   * database, so it is read from there.
   *
   * Answering `undefined` covers two different misses, and every caller treats them
   * the same way — by doing nothing further with that workout:
   *
   * - **A dangling pointer**: the plan names a workout this client never logged
   *   (#3291). Each caller clears the pointer itself, above or below its call, so
   *   there is nothing here to repair.
   * - **The exercise is not in that workout**: it was swapped out, or the workout
   *   never held it.
   *
   * This does not catch anything. A read that could not reach the database throws
   * `DatabaseUnavailableError`, and whether that is reported or allowed to travel up
   * is the caller's decision, not this helper's.
   */
  private async loadExerciseInReferencedWorkoutAsync(
    workoutLog_id: string,
    subSlotId: string,
  ): Promise<{ workout: Log_Workout, logExercise: Log_WorkoutExercise } | undefined> {
    const workout = workoutLog_id === this.logWorkout._id
      ? this.logWorkout
      : await this.userDb.getOneLogEntryAsync<Log_Workout>(workoutLog_id, this.docWatcher);
    if (!workout) {
      return undefined;
    }

    const logExercise = this.logExerciseToActOn(workout, subSlotId);
    if (!logExercise) {
      return undefined;
    }

    return { workout, logExercise };
  }

  /**
   * df-148d4d4ced87: called by `concludeSet` just before set `iSet` is written into the stored
   * list. If any earlier position is missing there, report it while the evidence is intact, then
   * fill it from the screen (`fillMissingEarlierSetsFromScreen` holds the rule and its reasons).
   * What is still empty afterwards is reported and healed by the existing end-of-conclude check,
   * as before. See `design-docs/sparse-set-healing.md`, "Filling from the screen at conclude".
   */
  private fillMissingEarlierSetsBeforeConclude(e: UiExercise, iSet: number): void {
    const storedSets = e.logExercise.sets;
    const storedLength = storedSets.length;
    const screenSets = e.sets.map(s => s.logSet);
    const fill = fillMissingEarlierSetsFromScreen(storedSets, iSet, screenSets, e.setsSplitOffBySwitch);
    if (fill.missingIndices.length === 0) return;

    // problem tier: the list losing a set the user finished is a state that should never exist,
    // and its cause is still unknown. This context is what should find it.
    const documentEntries = this.logWorkout.exercises ?? [];
    this.logging.trackSilentProblem('workout-detail.conclude-set-earlier-sets-missing-from-log', {
      docId: this.logWorkout._id,
      rev: this.logWorkout._rev,
      mode: this.mode,
      exerciseId: e.exercise?._id,
      subSlotId: e.logExercise.subSlotId,
      setIndex: iSet,
      setType: e.sets[iSet]?.logSet.setType,
      storedLength,
      earlierScreenStates: screenSets.slice(0, iSet).map(s => s?.state ?? '(none)'),
      entryIsDocumentObject: documentEntries.includes(e.logExercise),
      documentHasSubSlotMatch: documentEntries.some(d => !!d && d.subSlotId === e.logExercise.subSlotId),
      anySplitOffBySwitch: e.setsSplitOffBySwitch.size > 0,
      retiredSubSlotIds: e.retiredSubSlotIds,
      missingIndices: fill.missingIndices,
      filledIndices: fill.filledIndices,
      refused: fill.refused,
    });
  }

  /**
   * Report any set the screen has finished with that the workout document no
   * longer holds (#4832). Called from `saveWorkoutAsync` immediately before the
   * re-link that repairs it, so it measures how often that repair is needed.
   *
   * INFO tier, for the reasons `REVERTED_BEFORE_SAVE` is info: the repair runs
   * on the very next lines, and the condition is a routine consequence of a
   * mechanism we have deliberately not fixed — every canonicalizing read copies
   * the stored body over the shared object this page is holding. What the number
   * is for is deciding whether fixing that mechanism at its source is worth
   * building. Read `design-docs/telemetry.md`, "Two traps when changing a
   * report's tier", before moving it.
   */
  private reportConcludedSetsMissingBeforeRelink(): void {
    if (!this.logWorkout || !this.exercises?.length) return;

    const missing = findConcludedSetsMissingFromDocument(
      this.setStatesOnScreen(),
      this.logWorkout.exercises ?? [],
    );
    if (missing.length === 0) return;

    this.logging.trackSilentInfo('workout-doc-lost-a-concluded-set-before-save', {
      docId: this.logWorkout._id,
      rev: this.logWorkout._rev,
      mode: this.mode,
      missingCount: missing.length,
      missing,
    });
  }

  /**
   * Report the exercises the save had to keep from the document because the
   * screen did not hold them.
   *
   * This is the only signal that an account is holding an exercise the app
   * cannot display. The load side has its own report for the one case it can see
   * (`workout-repair-skipped-exercise-missing-from-screen`, raised when the
   * stuck-workout repair stands down for the same reason) — this one covers
   * every save, including the far commoner route where the load simply threw and
   * the user tapped past the "exercises skipped" alert.
   *
   * The tier depends on what was carried, because the two cases mean different
   * things. A carried entry holding a set the user finished with is PROBLEM tier:
   * the app is failing to show a real person their own training data, and before
   * the merge above that data was being deleted. A carried entry with nothing in
   * it is INFO: usually an unstarted stub that a canonicalizing read put back
   * after the plan merge had dropped it, which costs nobody anything and is
   * cleaned up the next time the workout loads.
   *
   * Reported at most once per document per page instance. `saveWorkoutAsync` runs
   * after every concluded set, so a workout with a permanently undisplayable
   * exercise would otherwise file this dozens of times in one session — which is
   * how a report stops being read at all. Same reason
   * `reportDisplaySubSlotMissingFromPlan` deduplicates (#4614).
   */
  private reportExercisesCarriedOverAtSave(carriedOver: Log_WorkoutExercise[]): void {
    if (carriedOver.length === 0) return;
    const docId = this.logWorkout?._id;
    if (!docId || this.reportedCarriedOverExercisesForDocIds.has(docId)) return;
    this.reportedCarriedOverExercisesForDocIds.add(docId);

    const context = {
      docId,
      rev: this.logWorkout._rev,
      mode: this.mode,
      carriedOverCount: carriedOver.length,
      subSlotIds: carriedOver.map(e => e?.subSlotId ?? '(none)'),
      onScreenCount: this.exercises?.length ?? 0,
    };
    if (carriedOver.some(exerciseHoldsConcludedSet)) {
      // not the user's session going wrong: the person's data is safe and the exercise stays
      // undisplayable for ever, so counting this would stop the app asking exactly these
      // users for a rating on every session, for ever. The transient cause of a short screen
      // (loading one exercise threw) is already counted through the loader's own
      // trackAbnormalException, so nothing a person could act on stops being counted.
      this.logging.trackSilentProblem('workout-save-kept-undisplayable-exercise-with-logged-sets', context,
        undefined, SessionHealth.NotTheUsersSession);
    } else {
      this.logging.trackSilentInfo('workout-save-kept-undisplayable-exercise', context);
    }
  }

  /**
   * Put the workout's own state back to "finished" when every set on screen is
   * finished with but the state field says otherwise. Called from
   * `saveWorkoutAsync`, right after it re-links the exercises. #4802.
   *
   * What reverts it: `this.logWorkout` is the one shared object the whole app
   * uses for this document, so any other code that reads the document from disk
   * — or receives a copy of it from the server — copies its fields over ours in
   * place (`getOrRegisterCanonicalReference` in database-wrapper.ts). When that
   * lands between the moment `onExerciseDone` decides the workout is finished
   * and the moment the save runs, it puts the old "Incomplete" back. The
   * exercises survive it because they are re-linked; the state did not, so a
   * workout whose sets were every one of them Done was stored as Incomplete and
   * Home went on offering "Resume" for ever.
   *
   * The screen is the authority, not the stored document: it holds a set object
   * for every set, including the ones the user has not reached yet, which are
   * written to the document only once they are concluded. Only ever moves an
   * in-progress workout forward to finished — see `resolveConcludedWorkoutState`
   * for why it never writes the other direction.
   *
   * The report is still the only visibility we have into an unsaved workout
   * being overwritten at all: the existing identity-map detector only fires
   * when the arriving copy is provably OLDER, so a same-revision copy reverts
   * the state and is reported nowhere else.
   *
   * `report` exists so the load-time repair can claim its own finding under
   * `workout-found-stuck-in-progress`. That is not only about the numbers: the
   * two mean different things and therefore sit at different severity tiers.
   * `REVERTED_BEFORE_SAVE` means "a workout was reverted while the user had it
   * open, and the line above has already put it back" — routine, repaired,
   * info tier. `FOUND_STUCK_ON_LOAD` means "this document was already stored
   * wrong before the page opened it" — not repaired anywhere until somebody
   * opens that workout, so problem tier. Both constants carry their own
   * reasoning; read those before changing either tier, and read
   * `design-docs/telemetry.md`, "Two traps when changing a report's tier".
   */
  private reassertConcludedWorkoutState(report: ReassertReport = REVERTED_BEFORE_SAVE): boolean {
    const previousState = this.logWorkout.state;
    const resolvedState = resolveConcludedWorkoutState(previousState, this.setStatesOnScreen());
    if (resolvedState === previousState) return false;

    this.logWorkout.state = resolvedState;
    const context = {
      docId: this.logWorkout._id,
      rev: this.logWorkout._rev,
      previousState,
      resolvedState,
      mode: this.mode,
      exerciseCount: this.exercises?.length ?? 0,
    };
    // the repair above has already run, so which tier this goes to is a
    // statement about whether anything is still wrong. Each report constant
    // says why it chose the tier it did.
    if (report.tier === ProblemKind.Info) {
      this.logging.trackSilentInfo(report.name, context);
    } else {
      this.logging.trackSilentProblem(report.name, context);
    }
    return true;
  }

  /**
   * The state of every set the page is showing, one entry per exercise.
   *
   * This is the ONE place the screen's set states are collected, and it is what
   * makes this page the only thing that can produce a `WorkoutSetStatesOnScreen`
   * — the argument the finish repair and the lost-set report both require. The
   * page holds a set object for every set the workout was meant to have,
   * including the ones the user has not reached yet; the stored document holds
   * only the sets the user has concluded, which is why the two must never be
   * confused. `buildWorkoutSetStatesOnScreen` carries the full explanation.
   */
  private setStatesOnScreen(): WorkoutSetStatesOnScreen {
    return buildWorkoutSetStatesOnScreen((this.exercises ?? []).map(e => ({
      subSlotId: e.logExercise?.subSlotId,
      setStates: (e.sets ?? []).map(s => s.logSet?.state),
    })));
  }


  /**
   * #6151: say so when the field shows the equipment's lightest weight because the plan's target is
   * lighter than the equipment can go (10 lb planned on a barbell whose empty bar is 45 lb). The rule
   * is `decideEquipmentFloorHint`; this only supplies its inputs and the words.
   *
   * The floor is the first of the exercise's cached possible weights, which are sorted ascending and
   * rebuilt whenever the equipment changes. `getMinProducibleWeight_kg` would give the same number,
   * but for plate equipment it enumerates every plate combination, and this runs on every weight edit.
   * Both weights are written in the unit of the weight field, so the hint matches the number above it.
   */
  private updateEquipmentFloorHint(e: UiExercise, s: UiSet) {
    const hint = this.equipmentFloorHintFor(e, s);
    // #6197: the drop-set banner follows the hint while the set is open (a weight or equipment change
    // can end the hold), and keeps its last answer once the set is logged and the hint is gone
    if (s.logSet.state === LogRecordState.Scheduled || s.logSet.state === LogRecordState.Incomplete) {
      s.heldAtEquipmentFloor = hint?.reason === EquipmentFloorReason.LighterSetBlocked;
    }
    if (!hint) {
      s.equipmentFloorHint = undefined;
      return;
    }
    const unit = s.logSet.stats.weight_unit;
    const format = (w: number) => formatMeasure(roundWeightForDisplay_kg(w, unit), 'addedweight_kg', this.settings.locale, unit);
    const texts = LL.workouts.detail.equipmentFloor;
    // #6197: a set that should have been lighter names the weight it wanted, not the plan's target
    if (hint.reason === EquipmentFloorReason.LighterSetBlocked) {
      const words = { wanted: format(hint.target_kg), floor: format(hint.floor_kg) };
      const shortLine = {
        [EquipmentFloorKind.EmptyBar]: () => texts.lighterEmptyBar(),
        [EquipmentFloorKind.LightestFixed]: () => texts.lighterLightestFixed(),
        [EquipmentFloorKind.LightestStackPlate]: () => texts.lighterLightestStackPlate(),
        [EquipmentFloorKind.LightestSetting]: () => texts.lighterLightestSetting(),
      }[hint.kind];
      s.equipmentFloorHint = {
        text: shortLine(),
        title: texts.title(words),
        explanation: hint.kind === EquipmentFloorKind.EmptyBar ? texts.lighterBodyEmptyBar(words) : texts.lighterBodyOther(words),
      };
      return;
    }
    const words = { target: format(hint.target_kg), floor: format(hint.floor_kg) };
    const shortLine = {
      [EquipmentFloorKind.EmptyBar]: () => texts.hintEmptyBar(words),
      [EquipmentFloorKind.LightestFixed]: () => texts.hintLightestFixed(words),
      [EquipmentFloorKind.LightestStackPlate]: () => texts.hintLightestStackPlate(words),
      [EquipmentFloorKind.LightestSetting]: () => texts.hintLightestSetting(words),
    }[hint.kind];
    s.equipmentFloorHint = {
      text: shortLine(),
      title: texts.title(words),
      explanation: hint.kind === EquipmentFloorKind.EmptyBar ? texts.bodyEmptyBar(words) : texts.bodyOther(words),
    };
  }

  // the one decision behind the floor hint, also asked by `openSet` to pick a truthful instruction
  private equipmentFloorHintFor(e: UiExercise, s: UiSet): EquipmentFloorHint | undefined {
    return decideEquipmentFloorHint({
      setType: s.logSet.setType,
      setState: s.logSet.state,
      prescribedTarget_kg: e.targetWeight_kg,
      derivedWeight_kg: s.derivedWeight_kg,
      shownWeight_kg: s.logSet.stats.weight_kg,
      floor_kg: e.possibleWeightValues_kg?.[0],
      equipmentType: e.equipment?.type,
    });
  }

  /**
   * #6197: whether a deload, drop or rep-max follow-up set sits on the equipment's lightest weight
   * because the lighter weight it wanted is below it. Asks the same rule as the hint, so the
   * instruction and the hint under the inputs never disagree about the set.
   */
  private isHeldUpByEquipmentFloor(e: UiExercise, s: UiSet): boolean {
    return this.equipmentFloorHintFor(e, s)?.reason === EquipmentFloorReason.LighterSetBlocked;
  }

  weightChanged(s: UiSet) {
    this.incW(s.uiExercise, s, 'manual');
    this.refreshDerivedStatsIfAlreadyConcluded(s);
  }

  repsChanged(s: UiSet) {
    this.refreshDerivedStatsIfAlreadyConcluded(s);
  }

  /**
   * #4556: keep a concluded set's strength figures in step with the weight and reps it records.
   *
   * A set that is not concluded yet has its figures computed by `concludeSet` when the user
   * finishes it, so there is nothing to keep in step. A set that is ALREADY concluded is a
   * different case: the inputs write straight into the stored set (the template binds
   * `[(canonValue)]="s.logSet.stats.weight_kg"` and the same for `reps`), so from the moment the
   * user types, the set records one weight while the three figures derived from it —
   * `weight_net_kg` and the two 1RM estimates — still describe the old one. Any save that happens
   * before the user confirms the edit then writes that disagreement to the database, and every
   * later reader believes the derived figures.
   *
   * The recompute must only ever run on a set whose stored figures no longer match what the set
   * records. `estimate1RM` uses the user's bodyweight TODAY, while the figures already on an old
   * set were computed from their bodyweight on the day of that set, which is recorded nowhere
   * else. So recomputing a set nobody changed silently rewrites history on any exercise where the
   * user's own bodyweight is part of the load, and there is no way back.
   *
   * Two things follow, and both are load-bearing:
   *
   * - This must NOT be hung off `incW`, which `reopenSet` also calls, because merely OPENING an
   *   old set would then recompute it.
   * - `(editDone)` is not a change signal. `cs-input-measure` raises it at the end of an edit
   *   INTERACTION — from a `finally` on desktop blur, and unconditionally when the mobile keyboard
   *   closes — so tapping into a field on an old set and closing it again, typing nothing, reaches
   *   these handlers.
   *
   * So the comparison is against `derivedStatsFrom`: the values the figures now on the set were
   * actually calculated from, which every `estimate1RM` on a real set records. Comparing against the
   * values the EDIT began with is not the same thing and is wrong: a user who types one weight and
   * then puts the original back has already had the figures recalculated for the value in between,
   * and an edit-start comparison would call that "unchanged" and leave the set recording one
   * weight with figures describing another — the exact corruption this fix exists to prevent.
   *
   * Mid-edit values that cannot produce an estimate are left alone rather than recomputed.
   * `estimate1RM` DELETES all three figures for 0 reps, and a save landing in that window would
   * leave a concluded set with no strength figures at all — worse than the stale ones, which at
   * least the next confirmed edit corrects.
   */
  private refreshDerivedStatsIfAlreadyConcluded(s: UiSet) {
    if (s.logSet?.state !== LogRecordState.Done) {
      return;
    }
    const stats = s.logSet.stats;
    // nothing has recalculated this set on this page, so its figures came from what the edit began with
    const computedFrom = s.derivedStatsFrom ?? { weight_kg: s.weightBeforeEdit, reps: s.repsBeforeEdit };
    // if the figures already describe what the set records, recomputing could only rewrite history
    if (stats?.weight_kg === computedFrom.weight_kg && stats?.reps === computedFrom.reps) {
      return;
    }
    if (!(stats?.reps >= 1) || !Number.isFinite(stats.weight_kg)) {
      return;
    }
    estimate1RM(stats, this.fitnessState.weight_kg);
    this.rememberDerivedStatsSource(s);
  }

  /**
   * #4556: record the weight and reps the set's strength figures were just calculated from.
   * Called immediately after every `estimate1RM` on a real (non-clone) set, so
   * `refreshDerivedStatsIfAlreadyConcluded` can tell a set whose figures are stale from one whose
   * figures are already right.
   */
  private rememberDerivedStatsSource(s: UiSet) {
    s.derivedStatsFrom = { weight_kg: s.logSet.stats.weight_kg, reps: s.logSet.stats.reps };
  }

  async onWeightUnitChanged(e: UiExercise, s: UiSet) {
    // Save the current unit to the exercise stat
    if (e.stat) {
      e.stat.weight_unit = s.logSet.stats.weight_unit;
    }

    // #6151: rewrite the floor hint in the new unit now, because two branches below return without
    // reaching weightSetupToText; the branches that do reach it recompute it again for the new equipment
    this.updateEquipmentFloorHint(e, s);

    const exerciseStats = this.fitnessState.exerciseStats[e.exercise._id];
    if (!exerciseStats) {
      return;
    }

    // Update the weight unit in exerciseStats and save to database
    exerciseStats.weight_unit = s.logSet.stats.weight_unit;

    // Initialize equipmentIdByUnit if not present
    ensureEquipmentSlots(exerciseStats);

    const newUnit = s.logSet.stats.weight_unit === 'lb' ? 'lb' : 'kg';

    // Check if we have custom increment (automatic equipment) for the new unit. "!== undefined"
    // (not plain truthiness) so a stored-but-corrupt 0 (#4337, from before that fix existed) still
    // counts as "recorded" and routes to checkEquipment's heal below, instead of into the regular
    // equipment search branch (which can't find anything either, since the real equipment IDs were
    // already cleared by the original unbrowse).
    const hasCustomIncrement = exerciseStats.customWeightIncrementByUnit?.[newUnit] !== undefined;

    if (!hasCustomIncrement) {
      // Only handle equipment if there's no custom increment for this unit
      // #5345: prefer, in order, a machine already recorded for the new unit, a machine the
      // person already owns in that unit, a copy of the machine they configured in the OTHER
      // unit, and only then a fresh copy of the built-in template. Before this, the middle two
      // were missing: with nothing recorded for the new unit and no template on the exercise,
      // the page silently kept the other unit's machine attached and said nothing, so the next
      // weight was measured against a machine configured in a unit the person was no longer
      // looking at. See design-docs/reactive-deload.md, the #5325 section.
      const decision = decideEquipmentForUnitSwitch(this.fitnessState, exerciseStats, newUnit);
      let unitEquipmentId = decision.source === UnitSwitchEquipmentSource.RecordedForUnit ? decision.equipment.id : undefined;
      let copiedEquipment: Equipment;
      // the search list both template steps below work from. Building it reads every custom
      // exercise and makes a UI item per machine and per built-in template, so build it at most
      // once per unit change, and not at all when neither template step runs.
      let sourceEquipment: EquipmentUIItem[];

      if (!unitEquipmentId && e.exercise.equipmentTemplateId) {
        // a machine the person already made for this unit from the same template. `false` stops
        // this call inventing a generic copy of the built-in template — that is the last resort
        // below, after their own machine has been considered.
        // no `viewDestroyed` early return here, deliberately: nothing below this point touches
        // the view until the guard further down, and returning here would skip the save at the
        // end of this method — losing the unit the person just picked
        sourceEquipment = await getSourceEquipments(this.fitnessState, this.exerciseDb, this.userDb, this.settings.locale, false, newUnit, this.docWatcher);
        unitEquipmentId = getBestEquipmentIdBasedOnTemplate(sourceEquipment, this.fitnessState, this.plan, e.exercise.equipmentTemplateId, newUnit, false);
      }

      if (!unitEquipmentId && decision.source === UnitSwitchEquipmentSource.CopiedFromOtherUnit) {
        // same physical machine, shown in the unit the person just switched to
        copiedEquipment = copyEquipmentToOtherUnit(this.fitnessState, decision.equipment, newUnit);
        unitEquipmentId = copiedEquipment.id;
        this.telemetry.trackEvent('workout_equipment_copied_to_other_unit', {
          exercise: e.exercise._id,
          sourceEquipmentId: decision.equipment.id,
          sourceEquipmentType: decision.equipment.type,
          sourceUnit: decision.equipment.weight_unit,
          newUnit: newUnit,
          newEquipmentId: copiedEquipment.id,
        });
      }

      if (!unitEquipmentId && e.exercise.equipmentTemplateId) {
        // last resort, unchanged: build one from the exercise's equipment template. The step above
        // has normally built the list already; build it here too rather than relying on that, so
        // this does not depend on the two steps' conditions staying identical.
        sourceEquipment = sourceEquipment ?? await getSourceEquipments(this.fitnessState, this.exerciseDb, this.userDb, this.settings.locale, false, newUnit, this.docWatcher);
        unitEquipmentId = getBestEquipmentIdBasedOnTemplate(sourceEquipment, this.fitnessState, this.plan, e.exercise.equipmentTemplateId, newUnit);
      }

      if (unitEquipmentId && this.fitnessState.allEquipment[unitEquipmentId]) {
        recordEquipmentForUnit(exerciseStats, newUnit, unitEquipmentId);
        // save here rather than relying on the save at the end of this method: the machine that
        // was just chosen (or copied) must survive even when the page is torn down in the next line
        await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
        if (this.viewDestroyed) return;

        e.equipment = this.fitnessState.allEquipment[unitEquipmentId];

        // Regenerate possible weight values for the new equipment
        e.possibleWeightValues_kg = generatePossibleWeightValues(e.equipment);

        // Update the weight setup display
        this.incW(e, s, 'round');
        this.cdRef.markForCheck();

        if (copiedEquipment) {
          // tell the person what the app just decided for them — a machine being carried over
          // used to happen silently, which is what made the invented step sizes in #5325 so hard
          // to understand
          this.toastCtrl.showToast({
            uniqueId: `equipment-copied-to-unit-${e.exercise._id}-${newUnit}`,
            message: LL.workouts.detail.equipment.copiedFromOtherUnit({ name: copiedEquipment.name }),
            actionButtonText: LL.workouts.detail.equipment.chooseDifferentEquipment(),
            onAction: () => void this.configurePlatesAsync(e, s),
            duration: 8000,
          });
        }
      }
    } else {
      // We have custom increment, so use automatic equipment
      e.checkEquipment(this.fitnessState, this.userDb, this.settings.locale, this.logging);

      // Update the weight setup display  
      this.incW(e, s, 'round');
      this.cdRef.markForCheck();
    }

    // Always save the fitness state when unit changes so equipment search page can detect it
    await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
  }

  /**
   * The workout's logged exercises as the SCREEN holds them — the authoritative copy.
   *
   * Not `this.logWorkout.exercises`, which can be stale at this moment: async backlog regeneration
   * calls `ensureCachedReference`, which shallow-copies disk data over the canonical workout and
   * replaces that array with fresh objects, while `UiExercise.logExercise` keeps the ones carrying
   * what `concludeSet` just wrote. The save path re-links the two for exactly this reason, but the
   * re-link runs at save time, which is AFTER the reads here. `reportConcludedSetsMissingBeforeRelink`
   * measures how often the divergence happens, so it is a real window rather than a theoretical one.
   *
   * The screen is not always the whole workout — an exercise the page cannot display has no row —
   * but for judging what the user has lifted this session that is the right bound anyway: a set the
   * screen cannot show is not one they just logged.
   */
  screenLogExercises(): Log_WorkoutExercise[] {
    return (this.exercises ?? []).map(e => e.logExercise).filter(logExercise => !!logExercise);
  }

  /**
   * What `confirmSetInputAsync`'s "Is this correct?" question judges a typed set against (#5174).
   *
   * Collects the candidates `strengthGuardAnchor_net_kg` ranks; that function decides between them
   * and says why each one is there. This method's only job is to read them off the page.
   *
   * The prescribed weight is the interesting one: `e.targetWeight_kg` is the weight the loader put
   * in front of the user, already carrying any training-break reduction, and a corrupt logged set
   * cannot reach it. The rep-test question a few lines below reads the same field for the same
   * reason. Snapping it onto the equipment grid here is why the arithmetic itself lives in
   * `prescribedImplied1RM_net_kg` and this does not: the snap needs the page's equipment, the
   * arithmetic needs nothing and is unit-tested.
   *
   * **The previous-session candidate can still be missing.** `previousFirstSetStats` is filled by
   * a background query that can take seconds. A first set's Done waits for it, at most
   * `PREVIOUS_SESSION_WAIT_MS` (df-3a4a75620292), so this check usually has it. A later set does
   * not wait, and a read slower than the limit leaves it out. That costs a user returning from a
   * break: their prescription is reduced, so the anchor sits below what they can still lift and a
   * correct set is questioned. Letting the stored value stand in would hand the guard back the
   * inflated number this whole change exists to stop trusting, so the gap errs towards asking:
   * one extra tap on a dialog costs far less than a mistyped set logged in silence.
   */
  strengthGuardAnchorFor(e: UiExercise, typedSetStats: SetStats): number | undefined {
    // a temp swap, or a path the loader left without a target, prescribes nothing to judge against;
    // `prescribedImplied1RM_net_kg` answers `undefined` for those rather than guessing a weight
    const prescribedWeight_kg = e.targetWeight_kg ?? e.planPath?.target_weight_kg;
    const { weight_kg: roundedPrescribedWeight_kg } = this.adjustWeight(prescribedWeight_kg, 'round', e.equipment);

    return strengthGuardAnchor_net_kg({
      prescribedImplied_net_kg: prescribedImplied1RM_net_kg({
        prescribedWeight_kg: roundedPrescribedWeight_kg,
        targetReps: e.planPath?.target_reps,
        bodyWeight_kg: this.fitnessState.weight_kg,
        bw_pc: typedSetStats.bw_pc,
      }),
      // the set being typed is not Done yet, so this is the sets the user already finished today
      sessionBest_net_kg: bestStrengthOfSession_net_kg(sessionSetsForExercise(this.screenLogExercises(), e.exercise._id)),
      previousSessionBest_net_kg: e.previousFirstSetStats?.estimated1rm_net_kg,
      stored_net_kg: e.stat.estimated1RM_net_kg,
    });
  }

  async confirmSetInputAsync(set: UiSet): Promise<boolean> {
    const locale = this.settings.locale;
    const setStatText = `${formatMeasure(set.logSet.stats.weight_kg, 'addedweight_kg', locale, set.logSet.stats.weight_unit)}${Formatting.times}${set.logSet.stats.reps
      }`;

    let message = set.logSet.setType === SetType.RepTest
      ? LL.workouts.alerts.unusualSetRepTest({ setStatText, exerciseName: set.uiExercise.exercise.name })
      : LL.workouts.alerts.unusualSet({ setStatText, exerciseName: set.uiExercise.exercise.name });
    this.duringSetDoneAlert = true;
    try {
      return await this.alertCtrl.showAlertAsync({
        title: LL.workouts.alerts.isThisCorrect(),
        message: message,
        buttons: [
          {
            text: LL.workouts.buttons.yesCorrect(),
            role: AlertButtonRole.Primary,
            result: true,
          },
          {
            text: LL.workouts.buttons.oopsLetMeChange(),
            result: false,
            role: AlertButtonRole.Cancel,
          },
        ],
      });
    } finally {
      // a rejected alert must not leave the Done button hidden for the rest of the workout
      this.duringSetDoneAlert = false;
    }
  }


  isEditableMode(): boolean {
    return this.mode === WorkoutLoadMode.Current || this.mode === WorkoutLoadMode.SoftFuture;
  }

  canChangeWorkout(): boolean {
    // only show for editable modes
    if (!this.isEditableMode()) return false;
    // only if workout hasn't been started yet (still scheduled)
    return this.logWorkout?.state === LogRecordState.Scheduled;
  }

  // whether the workout options menu has any items to show
  get hasWorkoutOptions(): boolean {
    return this.getWorkoutOptionButtons().length > 0;
  }

  private getWorkoutOptionButtons(): PopupMenuButton[] {
    const buttons: PopupMenuButton[] = [];

    // show "Change Workout" for editable workouts that haven't started yet
    if (this.canChangeWorkout()) {
      buttons.push({
        text: LL.training.changeWorkout.menuOption(),
        handler: () => {
          void this.changeWorkoutAsync();
        }
      });
    }

    // show "Skip Remaining" for in-progress workouts
    if (!this.isCoachManaging && this.isEditableMode() && this.logWorkout?.state === LogRecordState.Incomplete) {
      buttons.push({
        text: LL.workouts.detail.skipRemaining(),
        handler: () => {
          void this.skipRemainingAsync();
        }
      });
    }

    return buttons;
  }

  shouldShowSetCompleteButton(e: UiExercise) {
    if (this.isCoachManaging) return false;
    const openIdx = e.openSetIndex();
    return openIdx !== undefined && openIdx !== -1 && e.sets[openIdx] &&
      ((e.sets[openIdx].logSet.state !== LogRecordState.Done && !this.duringSetDoneAlert && this.isEditableMode()) || e.sets[openIdx].inEditMode())
  }

  async onClickSetDone(event: Event, set: UiSet) {
    event.stopPropagation();
    let e = set.uiExercise;
    // df-3a4a75620292: an earlier tap is still waiting for the previous workout below and has
    // concluded nothing yet, so handling this one too would conclude the same set twice
    if (e.isDoneTapAwaitingPreviousSession) return;
    let iSet = e.sets.indexOf(set);
    e.loading.set(true);
    this.cdRef.markForCheck();

    // #4939: what this tap did to the set and to the user's program, filled in by whichever
    // branch below actually concludes something and reported once at the end. It stays
    // undefined on every early return — a dismissed dialog concluded nothing, so there is
    // nothing to report. See the report itself for why each property is there.
    let setRecordOutcome: SetRecordOutcome | undefined;
    // the RM-test reset below rebuilds the sets and then falls through into the fresh-set
    // branch, so without this the report cannot tell that tap apart from an ordinary log
    let rmTestResetRan = false;

    try {

    // both read before anything is concluded, and inside the try so a throw still clears the
    // button's spinner in the finally. The recency answer is computed from two dates that
    // concluding the first set writes; the mode is captured because the SoftFuture branch just
    // below reassigns `this.mode`, and the report runs after it.
    const sessionRecency = this.classifySessionRecencyForExercise(e);
    const workoutModeAtTap = this.mode;
    // whether a conclusion on this tap can reach the program at all. Read here for the same
    // reason as the two above: `writeSubSlotBackToPlan` attaches the page's copy to the plan
    // during the conclusion, so asking afterwards would see the state it just created.
    const programWrite = this.programWriteOutcomeForTap(e);
    // #5276: which of the four things this tap does. Read from the set as it is NOW, because the
    // restart branch below reassigns `set` to a freshly rebuilt one whose `inEditMode` is false —
    // asking afterwards would describe the state that branch just created, exactly as it would for
    // the two answers above. `startedInFuture` is true for a FINISHED workout and nothing else;
    // the input is named for what it means. The rule itself, and why a finished session may or may
    // not move the program, is in `decideSetTapRoute`.
    const setTapRoute = decideSetTapRoute({
      isFirstSet: iSet === 0,
      inEditMode: set.inEditMode(),
      setType: set.logSet?.setType,
      workoutIsFinished: !!e.startedInFuture,
      sessionRecency,
    });

    // if in SoftFuture mode with pending workouts, auto-complete them and switch to Current mode
    if (this.mode === WorkoutLoadMode.SoftFuture && this.previousWorkouts?.length > 0) {
      void this.handleSkipOldClicked();
      this.mode = WorkoutLoadMode.Current;
      this.showPendingWorkoutWarning = false;
      this.previousWorkouts = [];
    }

    // #5276: emptying the exercise and rebuilding it around the corrected strength test. The route
    // is what decides this now — the conditions used to be tested inline with no check that the
    // workout was still being performed, so on a FINISHED session this destroyed every set the user
    // had logged, plus the snapshot of what that session prescribed, with no way back on the page.
    if (setTapRoute === SetTapRoute.RestartExercise) {
      let s = e.sets[iSet];
      this.cdRef.detach();
      const reps = s.logSet.stats.reps;
      const weight_kg = s.logSet.stats.weight_kg;
      await this.handleResetProgressSilent(e);
      rmTestResetRan = true;
      e.sets[0].logSet.stats.reps = reps;
      e.sets[0].logSet.stats.weight_kg = weight_kg;
      set = e.sets[0];
      this.cdRef.reattach();
    }

    // Check if the user is accepting a set with unsupported weight (only for first set)
    if (set.setupErrorWeight && iSet === 0 && !set.inEditMode()) {
      // Unbrowse the current equipment for this exercise
      if (!this.fitnessState.exerciseStats[e.exercise._id]) {
        // Initialize with weight_unit from the current set to ensure correct unit is used
        const currentWeightUnit = set.logSet.stats.weight_unit;
        this.fitnessState.exerciseStats[e.exercise._id] = {
          weight_unit: currentWeightUnit
        } as ExerciseStat;
      }
      const exerciseStat = this.fitnessState.exerciseStats[e.exercise._id];
      const currentWeight = set.logSet.stats.weight_kg;
      // the unit this unbrowse reads/writes throughout the block below (use set's unit, not stat
      // which may be undefined) — computed once so every use of it is guaranteed to agree
      const unbrowseUnit = set.logSet.stats.weight_unit === 'lb' ? 'lb' : 'kg';
      if (!exerciseStat.customWeightIncrementByUnit) {
        exerciseStat.customWeightIncrementByUnit = {};
      }

      // Check if we have lastStats from previous training session to calculate custom increment
      if (e.planPath && e.planPath.lastStats && e.planPath.lastStats.weight_kg) {
        const lastWeight = e.planPath.lastStats.weight_kg;
        const customIncrement = computeUnbrowsedCustomWeightIncrement_kg(currentWeight, lastWeight, unbrowseUnit);
        exerciseStat.customWeightIncrementByUnit[unbrowseUnit] = customIncrement;

        // Log the unbrowsing event for tracking
        const previousEquipmentId = equipmentIdForUnitOrLegacy(exerciseStat, unbrowseUnit);
        this.telemetry.trackEvent('workout_equipment_unbrowsed_custom_increment', {
          exercise: e.exercise._id,
          previousEquipmentId: previousEquipmentId,
          customIncrement_kg: customIncrement,
          currentWeight_kg: currentWeight,
          lastWeight_kg: lastWeight,
          setWeightUnit: set.logSet.stats.weight_unit,
          statWeightUnit: e.stat?.weight_unit,
          incrementUnit: unbrowseUnit,
          customIncrementByUnitAfter: JSON.stringify(exerciseStat.customWeightIncrementByUnit),
        });
      } else {
        // If no lastStats available, use a default increment
        exerciseStat.customWeightIncrementByUnit[unbrowseUnit] = defaultCustomWeightIncrement_kg(unbrowseUnit);

        const previousEquipmentId2 = equipmentIdForUnitOrLegacy(exerciseStat, unbrowseUnit);
        this.telemetry.trackEvent('workout_equipment_unbrowsed_default_increment', {
          exercise: e.exercise._id,
          previousEquipmentId: previousEquipmentId2,
          currentWeight_kg: currentWeight,
          setWeightUnit: set.logSet.stats.weight_unit,
          statWeightUnit: e.stat?.weight_unit,
          defaultIncrementUnit: unbrowseUnit,
          customIncrementByUnitAfter: JSON.stringify(exerciseStat.customWeightIncrementByUnit),
        });
      }

      // Unbrowse the equipment for the current unit only
      clearEquipmentForUnit(exerciseStat, unbrowseUnit);
      // Clear legacy field if it exists
      clearLegacyEquipmentId(exerciseStat);

      // Create automatic equipment for the custom increment
      e.checkEquipment(this.fitnessState, this.userDb, this.settings.locale, this.logging);

      ({ setup: set.weightSetup } = this.adjustWeight(currentWeight, 'round', e.equipment));
      set.logSet.stats.weight_kg = currentWeight;
      this.weightSetupToText(e, set);

      // Save the fitness state changes
      await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
    }

    this.logging.checkWorkoutExerciseValidity("onClickSetDone start", e.logExercise)

    // df-3a4a75620292: a first set is judged against the previous workout, and so is the "Is this
    // correct?" check below. A background read fills that in and can take seconds, so a set logged
    // straight away waits for it, a bounded time, under the card's loading state. Without it the
    // #6309 collapse check is skipped. A temp swap is never judged, so it never waits. Not guarded
    // by `viewDestroyed` afterwards, like the other awaits in this method: the user did tap Done.
    if (set.logSet.setType === SetType.Regular1st && !e.isTempSwap) {
      e.isDoneTapAwaitingPreviousSession = true;
      let waited: PreviousSessionWait;
      try {
        waited = await waitForPreviousSessionReadAsync(e.lastWorkoutRead, PREVIOUS_SESSION_WAIT_MS);
      } finally {
        e.isDoneTapAwaitingPreviousSession = false;
      }
      // the set is judged as it always was before this wait existed, with no previous workout
      if (waited === PreviousSessionWait.TimedOut) {
        this.logging.trackSilentInfo('workout-detail.previous-session-wait-timed-out', {
          exercise: e.exercise?._id,
          limit_ms: PREVIOUS_SESSION_WAIT_MS,
        });
      }
    }

    if (set.inEditMode()) {

      // safety in case button is not properly disabled — the same guard the fresh-set branch
      // below already carries. This branch is the one that saves a set whose reps the user has
      // just edited to 0, which would leave it Done with no strength figures.
      if (!this.isSetComplete(set)) {
        return;
      }

      if (this.hasSetValuesChanged(set)) {
        // #5276: this used to be `!e.startedInFuture` — a blanket "a finished workout never moves
        // the program", for every set type. That is now decided by ownership instead: a finished
        // session that nothing has superseded still owns the exercise, so correcting it moves the
        // program exactly as it would have done had the corrected numbers been logged at the time.
        // See `decideSetTapRoute`, and `util/historical-subslot.ts` for the rule itself.
        let shouldModifyPlanSubslot = setTapRoute === SetTapRoute.ReconcludeWithProgression;
        if (shouldModifyPlanSubslot) {
          // #5049: this used to replace `e.planSubSlot` wholesale with a clone of the workout's own
          // copy (`logExercise.planSubslotBackup`) first. `concludeSet` below writes `e.planSubSlot`
          // into the plan, so that restore put back every sub-slot-level field the user had changed
          // since the copy was taken — the prescribed set count, the exercise after a permanent
          // switch, the intensity. Why it was removed rather than the copy kept fresh, and what
          // `correct-set-rewinds-plan-subslot.spec.ts` proved before it changed:
          // `design-docs/volume-autoregulation.md`, "Correcting an already-finished set".
          this.setCurrentPath(e);
          // df-8d1620a9593c: point at the plan's own entry before the restore below writes, or a
          // correction to a set that is not the first one puts the prescribed targets back
          // everywhere except in the plan. Nothing here replaces the plan's entry — only the fields
          // `restorePrescribedTargets` and the following `concludeSet` write are touched.
          this.refreshLivePlanSubSlot(e);
          // What this set was judged against is restored from the SET's own record, not from the
          // exercise's copy. The copy is the wrong record for the job twice over: the conclusion
          // that created it went on, in the same call, to write its own new targets into it, so it
          // holds what the first set EARNED rather than what it was GIVEN; and it is taken once per
          // exercise, while what matters is what THIS set was prescribed. Without this restore the
          // correction is scored against a target it earned rather than the one it was set — which
          // can offer a deload nobody earned and leaves a raise standing that the correction undid.
          // A set logged before `planPathBackup` existed carries none; then nothing is restored and
          // the behaviour is what it was.
          restorePrescribedTargets(e.livePathsForCurrentIntensity(), set.logSet.planPathBackup);
          // df-8ab3d2fbc794: a plateau is an OUTCOME of concluding this set, so it is rewound here
          // beside the prescription and re-decided by the re-run below — exactly as progression is.
          this.withdrawPlateauRaisedBySet(e, iSet, set);
          // #5276: a strength test's verdict is an outcome of concluding it too, and the RM-test
          // branch of `concludeSet` PUSHES a verdict without removing the old one. Withdrawn here
          // for the same reason as the plateau above, so the re-run writes the one verdict the
          // corrected numbers deserve rather than leaving the set claiming two.
          clearStrengthTestVerdictStatuses([set.logSet]);
          // df-50489fc01ec4: the progression badges (a deload, a weight below the recommendation,
          // maxed-out equipment, a moved rep target) are outcomes of concluding the set as well,
          // and the re-run pushes them afresh. Withdrawn here so a rewound deload does not keep its
          // badge and a repeated one does not show twice.
          clearProgressionOutcomeStatuses([set.logSet]);
          // df-09c86ce422e8: a rep test's success verdict is an outcome of concluding it, and the
          // rep-test branch of `concludeSet` pushes one without removing the old one — the same
          // shape as the strength-test verdict above. Withdrawn here so a correction that still
          // succeeds shows "rep range established" once, and a correction that no longer succeeds
          // does not keep it at all. Only the SUCCESS verdicts: the three statuses recording the
          // user's answer about the weight are never written again by a correction, so they are
          // withdrawn inside `concludeSet` and only when that answer is known to be wrong.
          clearRepTestSuccessStatuses([set.logSet]);
          // df-04d949428155: the set's success mark is an outcome of concluding it too. Most
          // branches of `concludeSet` assign it on every outcome, but the follow-up branch assigns
          // nothing when the set earns nothing — so a follow-up corrected below its target kept
          // the "target hit" its mistyped numbers earned, and that mark then made the corrected
          // set the next session's "Last session" benchmark. Cleared here so the re-run decides it
          // afresh, the way a set being concluded for the first time starts out.
          set.logSet.setSuccess = undefined;
          // #6309: so is the record of a declined deload; the re-run asks again if it still applies
          delete set.logSet.deloadDeclined;
          // #6447: and the record that it was no stronger than last session; the re-run judges it again
          delete set.logSet.noGainOverLastSession;
          // #5276: while this one conclusion runs, let it reach the live program for the
          // intensities this session did NOT perform. A strength test derives a target for every
          // intensity from a single estimate, so without this a corrected test on a finished
          // session moved only the intensity it trained and left the others on targets derived
          // from the result the user has just corrected away. Only ever true here, where the route
          // has already established that this session still owns the exercise's progression.
          e.ownsLiveProgression = true;
          try {
            // df-72de01d6fc68: the retest demand on the OTHER intensities, rewound inside the
            // ownership window so it reaches the live program exactly when the re-run may
            // (see `restoreOtherIntensityRetestDemands`)
            restoreOtherIntensityRetestDemands(
              intensity => e.pageAndPlanPathsForIntensity(intensity),
              set.logSet.otherIntensityRetestDemandBackup);
            await this.concludeSet(e, iSet);
          } finally {
            // cleared whatever happened, so a later tap on a session that does NOT own the
            // exercise can never inherit permission from this one
            e.ownsLiveProgression = false;
          }
          setRecordOutcome = { action: SetRecordAction.Edited, ...programWrite };
        } else {
          e.logExercise.sets[iSet] = set.logSet;
          // hand over what the set's strength figures were last calculated from, so a change that
          // left the weight and reps alone (a mini-rep edit) does not rewrite them (#4556)
          concludeEditedSetWithoutPlanUpdate(
            e.logExercise.sets[iSet],
            this.fitnessState.weight_kg,
            set.derivedStatsFrom ?? { weight_kg: set.weightBeforeEdit, reps: set.repsBeforeEdit },
          );
          this.rememberDerivedStatsSource(set);
          setRecordOutcome = {
            action: SetRecordAction.Edited,
            updatedProgram: false,
            reason: SetRecordUnchangedReason.EditWithoutPlanUpdate,
          };
        }
        if (this.isWorkoutDone()) {
          // mark entire workout as done
          this.logWorkout.state = LogRecordState.Done;
        }
      }
      // reset baseline to current values (keeps edit mode active, hides save button)
      this.recordEditBaseline(set);
      // keep inEditMode = true so inputs remain enabled
      set.requireRepsInput.set(false);
      set.requireStrengthInput.set(false);
    } else {

      // safety in case button is not properly disabled
      if (!this.isSetComplete(set)) {
        return;
      }


      let checkSetInputConfirmation = false;
      switch (set.logSet.setType) {
        case SetType.RepTest:
          const exerciseIntensity = getSlotPathIntensity(e.planSubSlot, e.planPath);
          let repTargetUpperLimit: number = undefined;
          switch (exerciseIntensity) {
            case 60:
            case 65:
              repTargetUpperLimit = 25;
              break;
            case 70:
            case 75:
              repTargetUpperLimit = 20;
              break;
            case 80:
              repTargetUpperLimit = 15;
              break;
            case 85:
              repTargetUpperLimit = 10;
              break;
            case 90:
              repTargetUpperLimit = 6;
              break;
            default:
              break;
          }

          if (repTargetUpperLimit && set.logSet.stats.reps >= repTargetUpperLimit) {
            checkSetInputConfirmation = true;
          }
          break;
        case SetType.RMTest: case SetType.NonRMTest:
          break;
        default:
          let cloneSetStats = deepClone(set.logSet.stats);
          estimate1RM(cloneSetStats, this.fitnessState.weight_kg);
          // #5174: judge the typed set against something that tracks the user, not against their
          // lifetime best. The stored best sits at or above what they can lift today, so it could
          // only ever make this check looser — and one corrupt reading left it stuck high for good.
          if (isSetImplausiblyStrong(cloneSetStats.estimated1rm_net_kg, this.strengthGuardAnchorFor(e, cloneSetStats))) {
            checkSetInputConfirmation = true;
          }
      }
      if (checkSetInputConfirmation && !(await this.confirmSetInputAsync(set))) {
        return;
      }

      if (set.logSet.state !== LogRecordState.Done) {
        // a rep test logged far from the prescribed weight is questioned BEFORE the set is
        // concluded — askRepTestWeightAnswerAsync says why. Temp swaps are excluded because
        // concludeSet skips plan progression for them entirely and so never asked.
        let repTestWeightQuestion: RepTestWeightQuestionOutcome | undefined;
        if (set.logSet.setType === SetType.RepTest && !e.isTempSwap) {
          const prescribedWeight_kg = this.prescribedRepTestWeight_kg(e, set);
          const question = decideRepTestWeightQuestion(set.logSet.stats.weight_kg, prescribedWeight_kg);
          repTestWeightQuestion = { weightWasOffTarget: question.weightWasOffTarget };
          if (question.weightWasOffTarget && question.canAsk) {
            repTestWeightQuestion.answer = await this.askRepTestWeightAnswerAsync(question.direction);
            // if the user dismissed the question instead of answering it
            if (repTestWeightQuestion.answer === undefined) {
              return;
            }
          }
        }

        await this.concludeSet(e, iSet, repTestWeightQuestion);
        setRecordOutcome = { action: SetRecordAction.Logged, ...programWrite };
        // after the set is concluded, if the user chose auto-start, start the
        // rest timer (service no-ops on PWA / desktop). Manual users start it
        // with the Start Rest Timer button on the benchmark set instead.
        if (resolveRestTimerStart(this.declaredState) === RestTimerStart.Auto) {
          const restSeconds = getRestSecondsForExercise(e.exercise, this.declaredState, this.fitnessState);
          void this.restTimer.start(restSeconds, { exerciseName: e.exercise.name, workoutId: this.logWorkout._id });
        }
      }

      // if not last set in this exercise
      if (iSet < e.sets.length - 1) {
        // move to the next one only if it was not done or skipped before, otherwise close the entire exercise
        // (so we don't have to re-close multiple done sets when changing an older one)
        // (future set can get skipped in case of down regulation)
        let nextSet = e.sets[iSet + 1];
        if (nextSet.logSet.state !== LogRecordState.Done && nextSet.logSet.state !== LogRecordState.Skipped) {
          await this.openSet(e, iSet + 1, 'focus');
        } else {
          e.openSetIndex.set(e.sets.length + 1);
        }

        // if last set in this exercise
      } else {
        // increase the counter anyway, to remove focus from active set
        e.openSetIndex.set(iSet + 1);
      }

      // if no more sets left
      if (e.openSetIndex() >= e.sets.length) {
        await this.onExerciseDone(e);
      } else {
        // let nextSetExercise = this.findNextSetExerciseToOpen();
        // if (nextSetExercise != e) {
        //   let unfinishedSetIndex = nextSetExercise.sets.findIndex(set => set.logSet.state === LogRecordState.Incomplete || set.logSet.state === LogRecordState.Scheduled);
        //   if (unfinishedSetIndex !== -1) {
        //     await this.openSet(nextSetExercise, unfinishedSetIndex);
        //     nextSetExercise.expanded = true;
        //   }
        // }
      }
    }


    this.logging.checkWorkoutExerciseValidity("onClickSetDone end", e.logExercise)

    this.reportSetRecorded(
      e, set, iSet, setRecordOutcome, sessionRecency, workoutModeAtTap, rmTestResetRan);

    await this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());

    // force-push completed workout to CouchDB immediately, bypassing live sync checkpoint
    // (live sync checkpoint can drift due to cancelSync race — see #3050)
    if (this.logWorkout.state === LogRecordState.Done) {
      void this.userDb.database.forcePushDocAsync(this.logWorkout._id, 'workout-save-done');
    }

    } finally {
      e.loading.set(false);
      this.cdRef.markForCheck();
    }
  }

  findNextSetExerciseToOpen() {
    const incompletedExercisesSorted = this.exercises.filter(e => e.expanded()).filter(e => e.sets.some(s => s.logSet.state === LogRecordState.Incomplete) && e.sets.some(s => s.logSet.state === LogRecordState.Done)).sort((a, b) => {

      let exerciseALastIncompleteSetTime = findLast(a.sets, s => s.logSet.state === LogRecordState.Done).logSet.completedTime_utc;
      let exerciseBLastIncompleteSetTime = findLast(b.sets, s => s.logSet.state === LogRecordState.Done).logSet.completedTime_utc;
      if (!exerciseALastIncompleteSetTime && exerciseBLastIncompleteSetTime) {
        return -1;
      } else if (!exerciseBLastIncompleteSetTime && exerciseALastIncompleteSetTime) {
        return 1;
      } else if (exerciseALastIncompleteSetTime && exerciseBLastIncompleteSetTime) {
        return exerciseALastIncompleteSetTime > exerciseBLastIncompleteSetTime ? 1 : -1;
      }
      return 0;
    });

    if (incompletedExercisesSorted.length > 0) {
      return incompletedExercisesSorted[0];
    }

    for (let exercise of this.exercises) {
      if (exercise.sets.some(set => set.logSet.state === LogRecordState.Incomplete || set.logSet.state === LogRecordState.Scheduled)) {
        return exercise;
      }
    }
    return undefined;
  }

  // opts.skipSave: passed on to openSet, for a caller that saves the workout itself afterwards
  async onExerciseDone(e: UiExercise, opts?: { skipSave?: boolean }) {
    e.expanded.set(false);
    e.highlighted.set(false);
    e.openSetIndex.set(-1);
    let nextExercise = this.findNextSetExerciseToOpen();
    if (nextExercise) {
      let unfinishedSetIndex = nextExercise.sets.findIndex(set => set.logSet.state === LogRecordState.Incomplete || set.logSet.state === LogRecordState.Scheduled);
      if (unfinishedSetIndex !== -1) {
        await this.openSet(nextExercise, unfinishedSetIndex, 'focus', opts);
        nextExercise.expanded.set(true);
        if (!this.someExerciseHighlighted()) {
          nextExercise.highlighted.set(true);
        }
      }
      this.logging.checkWorkoutExerciseValidity("onExerciseDone-next", nextExercise.logExercise)

    }
    this.telemetry.trackEvent('workout_done_exercise', { exercise: e.exercise._id });
    // if all exercises are done
    if (this.isWorkoutDone()) {
      // #4816: whether this call is what FINISHES the workout. A set edited in a
      // workout that was already finished comes through here too, and must never
      // count as a newly finished workout for the review prompt.
      const finishedJustNow = this.logWorkout.state !== LogRecordState.Done;
      // mark entire workout as done
      this.logWorkout.state = LogRecordState.Done;
      // resolve the completion-card message (#4061) BEFORE flipping `complete`, so the
      // card renders the right text immediately with no generic → payoff flash. The
      // count query yields, so a fast navigate-away could destroy the view here; the
      // field writes below are harmless on a dead view and the side effects
      // (telemetry, coach notify) must still fire, so nothing below is guarded.
      await this.resolveCompletionMessageAsync();
      this.complete = true; // this shows the "close" button at the bottom
      this.showWorkoutSummary();
      this.nextAutofocusElementId = 'workout-complete';
      this.nextAutofocusSubSlotId = undefined;

      this.telemetry.trackEvent('workout_completed', {});

      // track first workout completion for email flow
      this.telemetry.setUserPropertiesOnce({ first_workout_logged_at: DateTime.utc().toISO() });

      // if this person was invited by a friend, tell the server they have started
      // training, so that friend's referral hub can show it (#4963 phase 3). Not
      // awaited: nobody is waiting on it, and the completion card must not sit
      // behind a server call. Everything that decides whether to call at all — and
      // the refusal that matters most, a coach managing somebody else's account —
      // is inside the service, which never throws.
      void this.referral.reportTrainingStartedIfReferredAsync();

      // notify coach if workout has strength test sets
      this.notifyCoachIfStrengthTest();

      // #4816: at phase 2, a finished workout that beat a personal record is a
      // moment to ask for a rating. The service decides everything else and waits
      // for the completion card, so this only says that the workout finished.
      if (finishedJustNow) {
        this.reviewPrompt.scheduleAfterWorkoutPrompt(this.logWorkout._id, this.screenLogExercises());
      }
    }
    this.cdRef.markForCheck();
  }

  /**
   * Is every set on this page finished with?
   *
   * This deliberately answers differently from `isExerciseConcluded`
   * (`shared/src/data-model/log-workout.ts`) for an exercise holding no sets:
   * that one refuses it, this one accepts it. Unifying them either way is worse
   * — see `design-docs/volume-autoregulation.md` for why, and for the guard in
   * `buildSets` that keeps the difference from mattering.
   */
  isWorkoutDone() {
    return this.exercises.every(e => e.sets.every(s => s.logSet.state === LogRecordState.Done || s.logSet.state === LogRecordState.Skipped));
  }

  // Resolve the end-of-workout completion-card message (#4061). The first two
  // strength-test runs OF THIS SPLIT DAY get sequenced payoff copy; everything else
  // keeps the generic message. The count is per split day, not across the user's whole
  // history, because a plan is a split (A/B/C) and each day gets its own strength test
  // the first time it is trained — counting globally made only day A look like a first
  // workout. Runs a cheap local view read. If the read THROWS we keep the generic
  // message and surface the problem via trackSilentProblem. (A silently-empty read —
  // the DB gate momentarily closed — is indistinguishable from a genuine "no prior runs
  // of this day", which is exactly the first-run case, so we deliberately accept it:
  // the count runs right after the user concluded sets against this same DB, so it is
  // available, and the worst case is a one-time friendlier-than-generic message with no
  // data impact. See WONTFIX note in the #4061 review.)
  private async resolveCompletionMessageAsync(): Promise<void> {
    try {
      // which split day (A=0, B=1, ...) this workout is. Read through the loader rather
      // than logWorkout.planReference directly: an in-memory workout may not have been
      // stamped yet, and the loader derives it from the plan in that case.
      const planReference = this.workoutLoader.getEffectivePlanReference(this.logWorkout, this.plan);
      // exclude the current workout's id from the count: at this point it is Done in
      // memory but not yet persisted (the caller saves after onExerciseDone returns),
      // so excluding it and adding one counts the current workout exactly once.
      const priorCount = await this.userDb.getCompletedWorkoutCountForPlanReferenceAsync(planReference, this.logWorkout._id);
      const includingCurrent = priorCount + 1;
      const kind = selectWorkoutCompletionMessage(isStrengthTestWorkout(this.logWorkout), includingCurrent);
      this.completionMessage = this.completionMessageText(kind);
      this.completionMessageKind = kind;
    } catch (err) {
      // Keep the generic message, and surface the failure. This catch existed
      // before and could never fire for the case it most needed to: the view
      // read used to answer an unavailable database with an empty result, so a
      // suppressed read arrived here as a real count of 0 and picked the
      // first-ever-workout wording for someone who had trained for months.
      // getCompletedWorkoutCountForPlanReferenceAsync now throws for that, so
      // this handler finally sees it — the conservative default is unchanged.
      this.completionMessage = LL.workouts.detail.workoutComplete();
      this.completionMessageKind = WorkoutCompletionMessage.Generic;
      this.logging.trackSilentProblem('workout-detail: completion-message count failed', {
        workoutId: this.logWorkout?._id,
        databaseUnavailable: isDatabaseUnavailable(err),
        error: describeError(err),
      });
    }
  }

  private completionMessageText(kind: WorkoutCompletionMessage): TranslatedString {
    switch (kind) {
      case WorkoutCompletionMessage.FirstTest:
        return LL.workouts.detail.workoutCompleteFirstTest();
      case WorkoutCompletionMessage.SecondTest:
        return LL.workouts.detail.workoutCompleteSecondTest();
      default:
        return LL.workouts.detail.workoutComplete();
    }
  }

  /**
   * The one save on this page that writes the document straight to the database,
   * and the only exception `workout-detail-save-sites.test.ts` allows.
   *
   * It runs from `ionViewCanEnter`, which returns early once `this.exercises` is
   * set — so whenever it runs, the page holds no on-screen exercise objects at
   * all. That makes `saveWorkoutAsync` both wrong (it opens by looping over
   * `this.exercises`, and reads a `this.plan` this path has not loaded) and
   * pointless (its re-link has nothing to re-link). Routing this site through it
   * is what stopped the stamp persisting between #4832 and this change.
   *
   * The write site and its direct save are documented in `design-docs/coach-mode.md`.
   */
  private async stampCoachReviewedIfApplicableAsync(): Promise<void> {
    if (!this.isCoachManaging) return;
    if (!this.logWorkout || this.logWorkout.coachReviewedAtUtc) return;
    if (!isStrengthTestWorkout(this.logWorkout)) return;
    // capture the prior value (the guard above ensured it was falsy — undefined
    // or empty) AND the stamp we're writing, so the catch can restore EXACTLY
    // what was there on save failure — but only if our stamp is still the
    // current in-memory value. If a concurrent writer (remote replication,
    // retry-that-succeeded) landed a different value while we were awaiting,
    // leave that alone rather than clobbering it.
    const prior = this.logWorkout.coachReviewedAtUtc;
    const stampedAt = DateTime.utc().toISO();
    this.logWorkout.coachReviewedAtUtc = stampedAt;
    try {
      await this.userDb.saveEntryAsync(DatabaseWrapper.getTransactionId(), this.logWorkout);
    } catch (err) {
      // restore the prior value only when our stamp is still the current in-memory
      // value — a concurrent writer's value wins
      if (this.logWorkout.coachReviewedAtUtc === stampedAt) {
        this.logWorkout.coachReviewedAtUtc = prior;
      }
      // non-blocking — the dashboard will keep the item visible until the
      // next coach visit retries. Surface for telemetry so persistent write
      // denial is visible.
      this.logging.trackSilentProblem('coach-dashboard: stamp coachReviewedAtUtc failed', {
        workoutId: this.logWorkout._id,
        error: err?.message || String(err),
      });
    }
  }

  // #4060: decide whether to show the "test workout" explainer header card.
  //
  // Calibration is PER EXERCISE, so every workout of the split is a calibration
  // workout on its own first outing — the card is shown once for A, once for B,
  // once for C. "Has this split day been done before" is read with the same helper
  // and the same view the sibling completion message uses (#4061,
  // resolveCompletionMessageAsync above); shouldShowTestWorkoutExplainer holds the
  // rules.
  // The whole body sits in a try so that nothing here — not the plan-reference read,
  // not the view query — can fail the page build. Losing the card is not worth
  // popping the user out of their workout over.
  private async updateTestWorkoutExplainerAsync(): Promise<void> {
    // hide the card up front, so that every path that fails to produce an answer —
    // the coach early return, a throw, a torn-down view — leaves it hidden rather
    // than leaves a previous answer standing. That matters on the change-workout
    // call, where the card on screen belongs to the split day we just left.
    this.showTestWorkoutExplainer = false;
    // a coach viewing a client's workout is never shown this card (the rule itself
    // lives in shouldShowTestWorkoutExplainer, which is the single statement of it);
    // returning early here only saves a database read that cannot change the answer.
    if (this.isCoachManaging) return;
    try {
      // both facts about the workout are read here, before the await, so they come
      // from the same moment. Read the reference through the loader rather than
      // logWorkout.planReference: an in-memory workout may not be stamped yet.
      const planReference = this.workoutLoader.getEffectivePlanReference(this.logWorkout, this.plan);
      const workoutState = this.logWorkout?.state;
      const priorCompletionsOfThisWorkout =
        await this.userDb.getCompletedWorkoutCountForPlanReferenceAsync(planReference);
      // the read is awaited, so the page may have been left in the meantime
      if (this.viewDestroyed) return;
      this.showTestWorkoutExplainer = shouldShowTestWorkoutExplainer({
        isCoachManaging: this.isCoachManaging,
        isWorkoutFinished: workoutState === LogRecordState.Done || workoutState === LogRecordState.Skipped,
        priorCompletionsOfThisWorkout,
      });
      this.cdRef.markForCheck();
    } catch (err) {
      // The card stays hidden, which is both the conservative outcome and the
      // whole point: an unavailable read used to arrive here as a count of 0 and
      // put the onboarding explainer in front of an established user (deferred
      // #2). Surface the failure so a future debugger sees it — same handling as
      // the sibling completion-message count read.
      this.logging.trackSilentProblem('workout-detail: test-workout explainer check failed', {
        workoutId: this.logWorkout?._id,
        databaseUnavailable: isDatabaseUnavailable(err),
        error: describeError(err),
      });
    }
  }

  private notifyCoachIfStrengthTest() {
    // if no coach, skip
    const coachRel = this.userDb.loadCoachRelAsClient();
    if (!coachRel?.coachUid) return;

    // filter exercises that have at least one strength test set
    const testExercises = this.exercises.filter(e =>
      e.sets.some(s => isStrengthTestSetType(s.logSet.setType))
    );
    if (testExercises.length === 0) return;

    const params: CoachNotifyWorkoutParams = {
      type: 'workoutComplete',
      clientDisplayName: this.auth.user.name || this.auth.user.uid,
      workoutDateUtc: this.logWorkout.time_utc,
      // travels WITH the instant — the pair is what makes a wall-clock time (#4903)
      workoutDateOffsetMin: this.logWorkout.timeOffset_min,
      workoutLogId: this.logWorkout._id,
      exercises: testExercises.map(e => ({
        exerciseName: e.exercise.name,
        exerciseNotes: this.fitnessState.exerciseStats[e.exercise._id]?.userNotes,
        sets: e.sets
          .filter(s => isStrengthTestSetType(s.logSet.setType))
          .map(s => ({
            setType: s.logSet.setType,
            weight: s.logSet.stats.weight_kg,
            weightUnit: s.logSet.stats.weight_unit || 'kg',
            reps: s.logSet.stats.reps,
            postStatus: s.logSet.postStatus?.map(ps => String(ps.status)) || [],
            setNotes: s.logSet.userNotes,
          })),
      })),
    };

    void this.coachNotify.queueNotificationAsync(params);
  }

  configurePlates(e: UiExercise, s: UiSet, event?: Event) {
    const promise = this.configurePlatesAsync(e, s);
    if (event) lockClick(event, promise);
  }

  private configurePlatesInProgress = false;

  private async configurePlatesAsync(e: UiExercise, s: UiSet) {
    // if already navigating to equipment search, ignore duplicate tap (Sentry 3R8)
    if (this.configurePlatesInProgress) return;
    this.configurePlatesInProgress = true;
    try {
      this.telemetry.trackEvent('workout_select_equipement', {});

      // Save fitness state before navigation to ensure data is not lost
      await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
      if (this.viewDestroyed) return;

      // Find the set index for the return callback context
      const setIndex = e.sets.indexOf(s);

      await this.navCtrl.pushAsync(IEquipmentSearchPage, {
        exercise: e.exercise._id,
        returnCallback: 'onEquipmentSearchResult',
        local: {
          returnContext: {
            exerciseId: e.exercise._id,
            subSlotId: e.logExercise?.subSlotId,
            setIndex: setIndex,
            modifiedWeight: s.logSet.stats.weight_kg,
            previousUnit: s.logSet.stats.weight_unit,
          },
        },
      });
    } finally {
      this.configurePlatesInProgress = false;
    }
  }

  /**
   * Callback invoked by NavController when returning from EquipmentSearchPage.
   * This is called via the returnCallback mechanism in nav-controller.ts.
   */
  async onEquipmentSearchResult(context: { exerciseId: string; subSlotId?: string; setIndex: number; modifiedWeight?: number; previousUnit?: string }, _result: any) {
    // Find the exercise by ID
    const e = this.findExerciseForReturnContext(context);
    if (!e) {
      console.warn('workout-detail: onEquipmentSearchResult - exercise not found:', context.exerciseId);
      return;
    }

    // Find the set by index
    const s = e.sets[context.setIndex];
    if (!s) {
      console.warn('workout-detail: onEquipmentSearchResult - set not found at index:', context.setIndex);
      return;
    }

    const modifiedWeight = context.modifiedWeight;
    const previousUnit = context.previousUnit;

    // Reload fitness state to get the updated unit from equipment search page
    this.fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);

    // Update the local reference to the exercise stat
    e.stat = this.fitnessState.exerciseStats[e.exercise._id];

    // Now check equipment with the updated stats
    e.checkEquipment(this.fitnessState, this.userDb, this.settings.locale, this.logging);

    // auto-dismiss equipment-related status messages on all sets (#2644)
    for (const set of e.sets) {
      if (!set.logSet?.postStatus) continue;
      for (const status of set.logSet.postStatus) {
        const isEquipmentRelated = status.status === PostSetStatus.RepTestBadEquipment
          || status.status === PostSetStatus.MaxedOutEquipment;
        if (isEquipmentRelated) {
          status.dismissed = true;
        }
      }
    }

    // if the unit changed from what we had before
    const unitChanged = e.equipment && e.equipment.weight_unit && e.equipment.weight_unit !== previousUnit;
    if (unitChanged) {
      // update the set's unit to match the equipment
      if (s.logSet && s.logSet.stats) {
        s.logSet.stats.weight_unit = e.equipment.weight_unit;
      }
    }

    // re-round the set's target weight for the newly selected equipment: if the unit changed,
    // recompute from the live value; otherwise re-round the captured pre-navigation weight (#3384)
    if (modifiedWeight != undefined) {
      if (unitChanged || previousUnit !== s.logSet.stats.weight_unit) {
        // units changed, recalculate with the new equipment configuration
        this.incW(e, s, 'round');
      } else {
        // re-round the captured pre-navigation weight to a value the newly selected equipment
        // can actually produce (#3384). the equipment may have changed to one with coarser
        // increments (or a smaller range), so the previously shown weight can be off-increment
        // or out of range. we round modifiedWeight (the weight as it was when the user opened the
        // equipment picker), NOT the live s.logSet.stats.weight_kg — the two are not equal here.
        ({ weight_kg: s.logSet.stats.weight_kg, setup: s.weightSetup } = this.adjustWeight(modifiedWeight, 'round', e.equipment));
        this.weightSetupToText(e, s);
      }

      // df-393f9706fb12: keep the set's RECORD of what it was prescribed in step with the row above.
      // A rep test is judged against `plannedWeight_kg` — see `prescribedRepTestWeight_kg` — and
      // `openSet` snapped that onto whatever equipment was configured when it prefilled the row. If
      // it stayed on the old grid while the row moved to the new one, a user who never touched the
      // weight would be asked why they changed it, and could have the set recorded as a failure.
      //
      // Re-snapped from ITSELF, not re-derived from `e.targetWeight_kg`: the row is rounded from the
      // weight as it was when the picker opened, so rounding the same kind of value the same way is
      // what keeps the two equal. Rounding is idempotent, so this is a no-op when the grid is
      // unchanged or already holds the value.
      //
      // Only an unfinished rep test. A concluded set's prescription is history and must not move —
      // `concludeSet` itself opens this picker after the "closest I can get" answer — and other set
      // types are left alone because nothing has shown their readers want this.
      if (s.logSet.setType === SetType.RepTest
          && s.logSet.state !== LogRecordState.Done
          && typeof s.logSet.plannedWeight_kg === 'number'
          && isFinite(s.logSet.plannedWeight_kg)) {
        ({ weight_kg: s.logSet.plannedWeight_kg } = this.adjustWeight(s.logSet.plannedWeight_kg, 'round', e.equipment));
      }
    }
    this.cdRef.markForCheck();
  }

  getConfigurePlatesCallback(e: UiExercise, s: UiSet): () => void {
    return () => this.configurePlates(e, s);
  }

  incW(e: UiExercise, s: UiSet, mode: 'step_down' | 'round' | 'manual' | 'step_up') {
    // Equipment is always valid now (including automatic equipment)
    ({ weight_kg: s.logSet.stats.weight_kg, setup: s.weightSetup } = this.adjustWeight(s.logSet.stats.weight_kg, mode, e.equipment));
    this.weightSetupToText(e, s);
  }

  incR(e: UiExercise, s: UiSet, delta: number) {
    void e;
    if (!isFinite(s.logSet.stats.reps) || s.logSet.stats.reps < 0) {
      s.logSet.stats.reps = 0;
    }

    s.logSet.stats.reps += delta;
    s.logSet.stats.reps = Math.max(s.logSet.stats.reps, 0);
  }

  async markExerciseSkipped(e: UiExercise, opts?: { skipSave?: boolean }) {
    this.logging.checkWorkoutExerciseValidity("markExerciseSkipped start", e.logExercise)
    if (!e.sets || e.sets.length <= 0) {
      await this.openExercise(e);
      // the save at the end of this method (or the bulk caller's, with opts.skipSave) writes the position
      await this.openSet(e, 0, 'focus', { skipSave: true });
    }

    let numSets = e.sets.length;
    let numSetsDone = 0;

    for (let is = 0; is < numSets; is++) {
      let s = e.sets[is];
      let logSet = s.logSet;

      if (logSet && logSet.state === LogRecordState.Done) {
        numSetsDone++;
        continue;
      }

      if (!logSet) {
        // note: if it doesn't exist in log, we don't add it now, but only after it is actually performed!
        logSet = s.logSet = { state: LogRecordState.Skipped, stats: {}, setSuccess: undefined, preStatus: [], postStatus: [] };
        e.logExercise.sets[is] = logSet;
      } else {
        // this ends the transient "UI-only" detached state that this set and exercise might have had
        e.logExercise.sets[is] = logSet;
        logSet.state = LogRecordState.Skipped;
      }
      // if this skipped set was currently active, deactivate it
      if (e.openSetIndex() === is) {
        e.openSetIndex.set(-1);
      }
    }

    this.logging.checkWorkoutExerciseValidity("markExerciseSkipped ebd", e.logExercise)

    // the focus onExerciseDone moves to the next exercise is saved by the save below, or by the
    // bulk caller's single save when opts.skipSave is set, so it must not write the workout itself
    await this.onExerciseDone(e, { skipSave: true });

    this.telemetry.trackEvent('action_skip_sets', { exercise: e.exercise._id, sets_done: numSetsDone, sets_skipped: numSets - numSetsDone });

    if (!opts?.skipSave) {
      await this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
    }
  }

  /**
   * #5507: before an exercise that already has performed sets is changed to another one, move
   * those sets into an entry of their own, so they stay with the exercise they were performed on.
   *
   * A workout entry names one exercise and its sets carry none of their own, so changing the
   * entry's exercise used to relabel the performed sets as the new exercise: they left the old
   * exercise's history, and the new exercise's next set was prefilled and judged from them.
   * `splitLogExerciseAtSwitch` owns the data rule. This puts the finished entry into the workout
   * directly above the one being changed, and draws it on screen, without reloading the page — a
   * reload builds new objects for every exercise and loses what the screen holds, including the
   * sub-slot ids a switch retired (`mergeScreenExercisesOverStored`).
   *
   * Returns a function that undoes the split, for a switch that then fails, or undefined when
   * there was nothing to split. The finished entry's states are written straight onto its log data, never through `markExerciseSkipped` or
   * `onExerciseDone`: those open the next exercise, can raise dialogs and can mark the whole
   * workout complete.
   */
  private async splitFinishedSetsBeforeSwitchAsync(uie: UiExercise, ctx: WorkoutLoadingContext): Promise<(() => void) | undefined> {
    const entry = uie.logExercise;
    if (!hasSetsToKeepBeforeSwitch(entry)) {
      return undefined;
    }
    // the document's list may hold a copy of this entry rather than the object on screen: a
    // canonicalizing read can replace that array with objects fresh from disk. The save matches
    // them by sub-slot id when identity fails (`mergeScreenExercisesOverStored`), so do the same
    const storedExercises = this.logWorkout.exercises ?? [];
    let entryIndex = storedExercises.indexOf(entry);
    if (entryIndex < 0) {
      entryIndex = storedExercises.findIndex(e => !!e && e.subSlotId === entry.subSlotId);
    }
    // the list itself is still unset during that first build (df-d2aa609222fa)
    const screenIndex = (this.exercises ?? []).indexOf(uie);
    // the split needs both lists to put the finished entry above this one; an exercise that is
    // not on screen yet (the max-weight auto-switch during page load) never has performed sets
    if (entryIndex < 0 || screenIndex < 0) {
      this.logging.trackSilentProblem('workout-detail.split-before-switch-entry-not-found', {
        exercise: uie.exercise?._id, inDocument: entryIndex >= 0, onScreen: screenIndex >= 0,
      });
      return undefined;
    }

    const before = {
      sets: entry.sets,
      planSubslotBackup: entry.planSubslotBackup,
      planSubslotAfterSession: entry.planSubslotAfterSession,
      numPlannedSets: entry.numPlannedSets,
      volumeAutoregulationAnswered: entry.volumeAutoregulationAnswered,
    };
    // the finished entry is drawn from its own copy of the plan entry once the program no longer
    // holds its id; one with no copy yet takes the entry on screen
    const { finished } = splitLogExerciseAtSwitch(
      entry,
      // ids in use on screen as well as in the document: either list can hold one the other lacks
      [...storedExercises.map(e => e?.subSlotId), ...this.exercises.map(e => e.logExercise?.subSlotId)],
      uie.livePlanSubSlot ?? uie.planSubSlot,
      uie.sets?.length ?? 0);
    // an open set-count question on the copy is about the exercise being changed away from; left
    // there it would claim a card is open that nothing can show or answer
    for (const path of finished.planSubslotBackup?.exercisePaths ?? []) {
      clearAutoregulationFields(path);
    }

    this.logWorkout.exercises.splice(entryIndex, 0, finished);
    // df-148d4d4ced87: the finished entry now holds copies of these sets. A switch that fails after
    // the rename leaves them on screen over an empty list, and the conclude step must not copy
    // them back. Both lists are recorded because the screen's objects are the ones it would copy.
    const splitOffSets = [...(before.sets ?? []), ...(uie.sets ?? []).map(s => s.logSet)]
      .filter((s): s is Log_WorkoutSet => !!s);
    for (const s of splitOffSets) uie.setsSplitOffBySwitch.add(s);
    const exerciseIdBefore = entry.exerciseId;
    const subSlotIdBefore = entry.subSlotId;
    const undo = () => {
      // if the switch failed only AFTER renaming the entry to the new exercise, the split is what
      // keeps the performed sets with the exercise they were performed on — putting them back now
      // would relabel them as the new exercise, the very defect the split exists to prevent
      if (entry.exerciseId !== exerciseIdBefore || entry.subSlotId !== subSlotIdBefore) {
        return;
      }
      const at = this.logWorkout.exercises.indexOf(finished);
      if (at >= 0) this.logWorkout.exercises.splice(at, 1);
      // a new array, not a splice: the exercise list rebuilds its cards only when handed one
      this.exercises = this.exercises.filter(e => e.logExercise !== finished);
      // the split is gone, so these sets belong to this entry again
      for (const s of splitOffSets) uie.setsSplitOffBySwitch.delete(s);
      entry.sets = before.sets;
      entry.planSubslotBackup = before.planSubslotBackup;
      entry.planSubslotAfterSession = before.planSubslotAfterSession;
      entry.numPlannedSets = before.numPlannedSets;
      entry.volumeAutoregulationAnswered = before.volumeAutoregulationAnswered;
    };

    try {
      const finishedUie = await this.workoutLoader.loadSingleWorkoutExerciseAsync(
        this.logWorkout, finished, ctx, this.mode as unknown as WorkoutLoadMode);
      // a new array, not a splice: the exercise list rebuilds its cards only when handed one
      const shownAt = this.exercises.indexOf(uie);
      this.exercises = [...this.exercises.slice(0, shownAt), finishedUie, ...this.exercises.slice(shownAt)];
      this.startLastWorkoutRead(finishedUie);
    } catch (err) {
      undo();
      throw err;
    }
    return undo;
  }

  /**
   * Save the workout after a switch that split performed sets off (#5507), so the finished entry
   * does not live only on screen until some later save. Focusing the new exercise's set 1 has
   * normally saved already: the split-off sets are logged, and since #4711 a focus is saved once
   * the workout has any logged set. That focus also remembered the new exercise as the position,
   * which is kept. A remembered position that still names the exercise switched away from is now
   * only on the finished card, so it has nothing left to restore and is forgotten as a progress
   * reset does.
   */
  private async saveSplitWorkoutAsync(oldExerciseId: string) {
    if (this.logWorkout.lastUIState?.currentExerciseId === oldExerciseId) {
      delete this.logWorkout.lastUIState;
    }
    await this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
  }

  async switchExercise(uie: UiExercise, newExerciseId: string, opts: { automatically: boolean }) {
    const oldExerciseId = uie.exercise._id;

    let undoSplit: (() => void) | undefined;
    try {
      const ctx: WorkoutLoadingContext = {
        plan: this.plan,
        fitnessState: this.fitnessState,
        declaredState: this.declaredState,
        nowLocal: this.debug.getNowLocal(),
        locale: this.settings.locale,
        watcher: this.docWatcher,
        currentWorkoutId: this.logWorkout._id,
      };

      // #5507: performed sets stay with the exercise they were performed on
      undoSplit = await this.splitFinishedSetsBeforeSwitchAsync(uie, ctx);

      await this.workoutLoader.performExerciseSwitchAsync(
        uie,
        newExerciseId,
        this.workoutLoader.getEffectivePlanReference(this.logWorkout, this.plan),
        this.plan,
        this.fitnessState,
        this.userDb,
        ctx,
        this.mode as unknown as WorkoutLoadMode,
        // the automatic switch at the maximum weight is the app's choice, not the user's (#6076)
        opts.automatically ? ExerciseChosenBy.App : ExerciseChosenBy.User,
      );
    } catch (err) {
      // put the performed sets back on the entry rather than leave them split off an exercise
      // that was never changed
      undoSplit?.();
      // if the switch failed after the card already took the new exercise, its previous workout is
      // still the old one's: read it for the exercise the card now names (df-f4964c2d2d3c)
      if (uie.exercise?._id !== oldExerciseId) {
        this.startLastWorkoutRead(uie);
      }
      this.cdRef.markForCheck();
      this.logging.trackAbnormalException(err);
      return;
    }

    // the previous session belonged to the exercise switched away from: the attempt to beat (#6447),
    // the baseline set 1 is judged against and the "Last workout" row (df-f4964c2d2d3c). Read them
    // for the new exercise, starting NOW, before set 1 opens, so a Done tap on it waits for this
    // read rather than an earlier, finished one. The read clears all of them as it starts.
    this.startLastWorkoutRead(uie);

    // clear temp swap flags if this was previously a temp swap
    uie.isTempSwap = false;
    delete uie.tempSwapOriginalExerciseId;
    delete uie.logExercise.isTempSwap;
    delete uie.logExercise.tempSwapOriginalExerciseId;

    // page-specific: preselect equipment and start first set
    uie.equipmentPreselected = await this.preselectEquipmentIfNeeded(uie);
    // performed sets were split off above (#5507), so the entry normally starts empty. What can
    // still be left is a first set that was SKIPPED with nothing performed: a permanent switch
    // keeps that, and `openSet` refuses a concluded set, so do not ask it to.
    if (!(uie.sets[0] && this.isSetCompleted(uie.sets[0]))) {
      await this.startFirstSet(uie);
      if (undoSplit) {
        await this.saveSplitWorkoutAsync(oldExerciseId);
      }
    } else {
      // close whichever set was open before the switch: it was prefilled from the OLD exercise, and
      // left open it would be logged with those numbers. tapping it again prefills it afresh.
      uie.openSetIndex.set(-1);
      // the remembered focused set names the exercise switched AWAY from; forget it, the same way
      // a progress reset does, so returning to the page does not try to restore it
      delete this.logWorkout.lastUIState;
      // save the switch into the workout: the renamed exercise, its new sub-slot and its re-taken
      // plan copy. `openSet` used to do this save as a side effect of focusing set 1; without it
      // the workout would keep naming the old exercise while the program names the new one
      await this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
    }
    this.cdRef.markForCheck();

    this.telemetry.trackEvent('workout_switch_exercise', { exercise: oldExerciseId, reason: opts.automatically ? 'auto' : 'manual' });
  }

  async handleExerciseTutorialButton(e: UiExercise) {
    this.telemetry.trackEvent('workout_description', { id: e.exercise._id, mode: 'stats' });
    e.stat.tutorialSeen = true;
    e.tutorialSeen = true;
    await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
    this.cdRef.markForCheck();
    void this.navCtrl.pushAsync(IExerciseStatsPage, { id: e.exercise._id, mode: 'instructions' });
  }

  handleStatsAndNotesButton(e: UiExercise) {
    void this.navCtrl.pushAsync(IExerciseStatsPage, { id: e.exercise._id });
  }

  showSkipTodayButton(e: UiExercise) {
    if (e.sets) {
      let lastSet = e.sets[e.sets.length - 1].logSet;
      if (lastSet.state !== LogRecordState.Done && lastSet.state !== LogRecordState.Skipped && this.isEditableMode()) {
        return true;
      }
    }
    return false;
  }

  handleSkipTodayButton(e: UiExercise) {
    void this.askToSkipExerciseAsync(e);
  }

  async handleSwitchExerciseButton(e: UiExercise) {
    // if sets are already completed, warn that a permanent switch keeps them and a session-only switch resets them
    const someDone = e.sets.some(s => s.logSet && s.logSet.state === LogRecordState.Done);
    if (someDone) {
      await this.alertCtrl.showAlertAsync({
        title: LL.workouts.alerts.switchType(),
        message: LL.workouts.alerts.switchExerciseResetWarning(),
        buttons: [
          { text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: () => void this.handleSwitchExercise(e, { automatically: false }) },
          { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel },
        ],
      });
    } else {
      void this.handleSwitchExercise(e, { automatically: false });
    }
  }

  handleExerciseStatsOptionClick(e: UiExercise) {
    this.telemetry.trackEvent('workout_stats', {});
    void this.navCtrl.pushAsync(IExerciseStatsPage, { id: e.exercise._id, mode: 'log' });
  }

  handleExerciseSettingsOptionClick(e: UiExercise) {
    this.telemetry.trackEvent('workout_settings', {});
    void this.navCtrl.pushAsync(IExerciseStatsPage, { id: e.exercise._id, mode: 'settings' });
  }

  shouldShowSkipButtonOption(e: UiExercise) {
    if (this.isCoachManaging) return false;
    if (!e.sets || !this.isEditableMode()) {
      return false;
    }
    const lastSet = e.sets.length > 0 && e.sets[e.sets.length - 1].logSet;
    return lastSet && lastSet.state !== LogRecordState.Done && lastSet.state !== LogRecordState.Skipped;
  }

  shouldShowSwitchExerciseOption(e: UiExercise) {
    // a card split off by an exercise change (#5507) is a finished record of the exercise changed
    // away from; the program holds no entry for it, so there is nothing to change
    if (isSplitSubSlotId(e.logExercise?.subSlotId)) return false;
    return this.isEditableMode() || this.mode === WorkoutLoadMode.Template;
  }

  shouldShowResetProgressOption(e: UiExercise) {
    if (e.isTempSwap) return false;
    // resetting would delete the performed sets the split exists to keep (#5507)
    if (isSplitSubSlotId(e.logExercise?.subSlotId)) return false;
    return this.isEditableMode();
  }

  handleSkipExercise(e: UiExercise) {
    this.telemetry.trackEvent('workout_skip_exercise', {});
    void this.markExerciseSkipped(e);
  }

  async askToResetProgress(e: UiExercise) {
    await this.alertCtrl.showAlertAsync({
      title: LL.workouts.alerts.resetProgress(),
      message: LL.workouts.alerts.resetProgressConfirm(),
      buttons: [
        { text: LL.common.buttons.yes(), role: AlertButtonRole.Primary, result: () => void this.handleResetProgress(e) },
        { text: LL.common.buttons.no(), role: AlertButtonRole.Cancel },
      ],
    });
  }

  async handleResetProgressSilent(e: UiExercise) {
    e.resetProgress = true;
    this.rebuildPathsIfNeeded(e);
    delete e.resetProgress;
    e.logExercise.sets = [];
    delete this.logWorkout.lastUIState;
    e.logExercise.planSubslotBackup = undefined;
    // the progression this session earned is thrown away too, so its "as left" copy must not
    // bring it back later through the layoff restore (df-5dbef4889250)
    delete e.logExercise.planSubslotAfterSession;
    // the exercise starts over, so an earlier answer no longer describes it — let it
    // be judged again (#4714)
    delete e.logExercise.volumeAutoregulationAnswered;
    // #5617: the layoff weight was worked out for the path just thrown away; left in place, the first
    // set's conclusion would store it onto the rebuilt strength-test path
    delete e.layoffWeight_kg;
    delete e.layoffCut1RM_net_kg;
    this.setCurrentPath(e);
    await this.rebuildSets(e);
    await this.startFirstSet(e);
  }

  async handleResetProgress(e: UiExercise) {
    this.telemetry.trackEvent('workout_reset_progress', {});
    await this.handleResetProgressSilent(e);
    e.sets[0].logSet.preStatus.push({ status: PreSetStatus.CompleteProgressionReset });
  }

  async askToSkipExerciseAsync(e: UiExercise) {
    // check if some sets have been already done
    let someDone = e.sets.some(s => s.logSet && s.logSet.state === LogRecordState.Done);

    await this.alertCtrl.showAlertAsync({
      title: LL.workouts.alerts.skipExercise(),
      message: someDone
        ? LL.workouts.alerts.skipRemainingSets()
        : LL.workouts.alerts.skipThisExercise(),
      buttons: [
        { text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: () => this.handleSkipExercise(e) },
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel },
      ],
    });
  }

  async handleSwitchExercise(e: UiExercise, opts: { automatically: boolean }) {
    // Find the index of the exercise to store in context (can't store e directly as it won't survive serialization)
    const exerciseIndex = this.exercises.indexOf(e);

    void this.navCtrl.pushAsync(IExerciseSwitchPage, {
      exerciseId: e.exercise._id,
      subSlotId: e.logExercise.subSlotId,
      workoutId: String.fromCharCode(this.workoutLoader.getEffectivePlanReference(this.logWorkout, this.plan) + 'A'.charCodeAt(0)),
      returnCallback: 'onExerciseSwitched',
      showTempSwapPrompt: !opts.automatically,
      warnBeforeLinkingWorkouts: true,
      local: {
        fitnessPlan: this.plan,
        fitnessState: this.fitnessState,
        returnContext: { exerciseIndex, opts },
      },
    });
  }

  onExerciseSwitched(context: { exerciseIndex: number; opts: { automatically: boolean } }, result: string | ExerciseSwitchTempResult) {
    // if user cancelled (no exercise selected), do nothing
    if (!result) {
      return;
    }
    const e = this.exercises[context.exerciseIndex];
    if (!e) return;

    // if result is a string, it's a plain exercise ID (auto-switch or no prompt)
    if (typeof result === 'string') {
      void this.switchExercise(e, result, context.opts);
      return;
    }

    // result is { exerciseId, isTemporary } from the temp swap prompt
    if (result.isTemporary) {
      void this.tempSwitchExercise(e, result.exerciseId);
    } else {
      void this.switchExercise(e, result.exerciseId, context.opts);
    }
  }

  private async tempSwitchExercise(uie: UiExercise, newExerciseId: string) {
    const oldExerciseId = uie.exercise._id;

    let undoSplit: (() => void) | undefined;
    try {
      const ctx: WorkoutLoadingContext = {
        plan: this.plan,
        fitnessState: this.fitnessState,
        declaredState: this.declaredState,
        nowLocal: this.debug.getNowLocal(),
        locale: this.settings.locale,
        watcher: this.docWatcher,
        currentWorkoutId: this.logWorkout._id,
      };
      // #5507: performed sets stay with the exercise they were performed on. A session-only
      // switch used to delete them from the workout outright.
      undoSplit = await this.splitFinishedSetsBeforeSwitchAsync(uie, ctx);

      await this.workoutLoader.performTempExerciseSwitchAsync(uie, newExerciseId, this.fitnessState, ctx);
    } catch (err) {
      // put the performed sets back on the entry rather than leave them split off an exercise
      // that was never changed
      undoSplit?.();
      this.cdRef.markForCheck();
      this.logging.trackAbnormalException(err);
      return;
    }

    uie.equipmentPreselected = await this.preselectEquipmentIfNeeded(uie);
    await this.startFirstSet(uie);
    if (undoSplit) {
      await this.saveSplitWorkoutAsync(oldExerciseId);
    }
    // detached lookup off the critical path, as on page load
    this.startLastWorkoutRead(uie);
    this.cdRef.markForCheck();

    this.telemetry.trackEvent('workout_switch_exercise', { exercise: oldExerciseId, reason: 'temp' });
  }

  handleSetNoteClick(s: UiSet) {
    void this.editSetNoteAsync(s);
    this.telemetry.trackEvent('workout_set_note', {});
  }
  async editSetNoteAsync(s: UiSet) {
    let note = await this.alertCtrl.showAlertAsync<string>({
      title: LL.workouts.alerts.notesForSet(),
      message: LL.workouts.alerts.notesForPreviousWorkouts(),
      inputs: [{ type: 'text', name: 'userNotes', value: s.logSet.userNotes }],
      buttons: [
        {
          text: LL.common.buttons.ok(),
          role: AlertButtonRole.Primary,
          result: data => data['userNotes'],
        } as AlertButton<string>,
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: null },
      ],
    });
    if (note !== null) {
      s.logSet.userNotes = note;
      await this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
    }
  }

  handleFakeNextDay() {
    void this.fakeNextDayAsync();
  }

  async fakeNextDayAsync() {
    let thisId = this.logWorkout._id;
    let thisDateISO = DateTime.fromISO(this.logWorkout.time_utc).toISODate();

    let newDate = this.debug.getNowLocal().plus({ days: 1 });
    let newDateISO = newDate.toISODate();

    let newId = thisId.replace(thisDateISO, newDateISO);

    void this.debug.setNowLocalDayAsync(newDate);

    try {
      await this.navCtrl.setPagesAsync(
        [
          {
            page: 'HomePage',
            params: { day: newDateISO },
          },
          {
            page: 'WorkoutDetailPage',
            params: { id: newId },
          },
        ],
        { updateUrl: true },
      );
    } catch (error) {
      // not expected: a page that refuses to open (the next day is not scheduled) does not
      // land here, because cs-nav turns that refusal into a cancelled navigation. Report it,
      // then fall back to the day view.
      this.logging.trackAbnormalException(error);
      void this.navCtrl.setPagesAsync(
        [
          {
            page: 'HomePage',
            params: { day: newDateISO },
          },
        ],
        { updateUrl: true },
      );
    }
  }

  openStatsPage(uie: UiExercise) {
    void this.navCtrl.pushAsync(IExerciseStatsPage, { id: uie.exercise._id });
  }

  async openPinnedNotePopover(_event: Event, uie: UiExercise) {
    await this.navCtrl.pushAsync(INotePage, {
      translationPrefix: 'pinnedNote',
      currentNote: uie.stat.userNotes || '',
      returnCallback: 'onPinnedNoteResult',
      local: {
        returnContext: { exerciseId: uie.exercise._id },
      },
    });
  }

  onPinnedNoteResult(context: { exerciseId: string }, result: NotePageResult) {
    if (!result?.success) return;

    // find the UiExercise by exerciseId and update its note
    const uie = this.exercises.find((e: UiExercise) => e.exercise._id === context.exerciseId);
    if (uie) {
      uie.stat.userNotes = result.note;
      const transaction = DatabaseWrapper.getTransactionId();
      void this.userDb.saveFitnessStateAsync(transaction);
      this.cdRef.markForCheck();
    }
  }

  async openViewNotesPopover(event: Event, uie: UiExercise) {
    const params: CsViewNotesPopoverParams = {
      exerciseId: uie.exercise._id,
      exerciseName: uie.exercise.name,
      note: uie.stat.userNotes || '',
      stat: uie.stat
    };

    const result = await CsViewNotesPopover.present(
      this.popoverCtrl,
      event.target as HTMLElement,
      params
    );

    // if user clicked edit, open the edit popover
    if (result?.action === 'edit') {
      await this.openPinnedNotePopover(event, uie);
    }
  }

  hasNotes(uie: UiExercise): boolean {
    return !!uie.stat.userNotes?.length;
  }

  getNotes(uie: UiExercise): string {
    return uie.stat.userNotes?.trim() || '';
  }

  // Set Notes methods
  hasSetNotes(s: UiSet): boolean {
    return !!s.logSet.userNotes?.length;
  }

  getSetNotes(s: UiSet): string {
    return s.logSet.userNotes?.trim() || '';
  }

  /**
   * The exercise a page returning to this one was opened for. Matched by sub-slot id as well as
   * exercise id when the context carries one: an exercise changed after sets were logged and then
   * changed back (#5507) shows the same exercise on two cards, and the exercise id alone would
   * pick the finished one. A context saved before the sub-slot id was added matches as it did.
   */
  private findExerciseForReturnContext(context: { exerciseId: string; subSlotId?: string }): UiExercise | undefined {
    return this.exercises.find(e => e.exercise._id === context.exerciseId
      && (!context.subSlotId || e.logExercise?.subSlotId === context.subSlotId));
  }

  async openSetNotePopover(_event: Event | null, s: UiSet) {
    const setIndex = s.uiExercise.sets.indexOf(s);
    await this.navCtrl.pushAsync(INotePage, {
      translationPrefix: 'setNote',
      currentNote: s.logSet.userNotes || '',
      returnCallback: 'onSetNoteResult',
      local: {
        returnContext: { exerciseId: s.uiExercise.exercise._id, subSlotId: s.uiExercise.logExercise?.subSlotId, setIndex },
      },
    });
  }

  onSetNoteResult(context: { exerciseId: string; subSlotId?: string; setIndex: number }, result: NotePageResult) {
    if (!result?.success) return;

    // find the UiExercise and set by context
    const uie = this.findExerciseForReturnContext(context);
    if (uie && context.setIndex >= 0 && context.setIndex < uie.sets.length) {
      const s = uie.sets[context.setIndex];
      s.logSet.userNotes = result.note;
      // sync logSet back to logExercise.sets (like concludeSet does)
      uie.logExercise.sets[context.setIndex] = s.logSet;
      void this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
      this.cdRef.markForCheck();
    }
  }

  async openViewSetNotePopover(event: Event, s: UiSet) {
    const params: CsViewNotesPopoverParams = {
      note: s.logSet.userNotes || '',
      titleKey: 'setNote.viewTitle'
    };

    const result = await CsViewNotesPopover.present(
      this.popoverCtrl,
      event.target as HTMLElement,
      params
    );

    // if user clicked edit, open the edit popover
    if (result?.action === 'edit') {
      await this.openSetNotePopover(event, s);
    }
  }

  handleClose(event: Event) {
    lockClick(event, this.navCtrl.popAsync());
  }

  // return the result when navigating away via back button
  async ionViewCanLeave(): Promise<CanLeaveResult<WorkoutDetailPageResult>> {
    return { result: { workoutModified: this.workoutModified } };
  }


  updateViewOnlyInfoMessage() {
    this.isLoggingMessage = false;
    if (this.isCoachManaging) {
      this.viewOnlyInfoMessage = LL.coach.workoutViewOnly();
      this.showViewOnlyInfo = true;
      return;
    }
    if (this.mode === WorkoutLoadMode.HardFuture) {
      this.viewOnlyInfoMessage = LL.workouts.alerts.viewOnly.futureWorkoutMessage();
    } else if (this.mode === WorkoutLoadMode.Past) {
      this.viewOnlyInfoMessage = LL.workouts.alerts.viewOnly.previousWorkoutMessage();
    } else if (this.mode === WorkoutLoadMode.Template) {
      this.viewOnlyInfoMessage = LL.workouts.alerts.viewOnly.templateMessage();
    } else if (this.mode === WorkoutLoadMode.Current || this.mode === WorkoutLoadMode.SoftFuture) {
      // check if user has seen the logging message before
      const hasSeenLoggingMessage = this.hasSeenHint('loggingMessage');

      // show logging message for:
      // 1. first time users (never dismissed this message)
      // 2. past workouts that are editable
      // 3. today's workout if more than 3 hours past scheduled time
      const nowLocal = this.debug.getNowLocal();
      const workoutTime = getEntryLocalTimeInDesiredTimeZone(this.logWorkout, nowLocal);
      const isToday = workoutTime.hasSame(nowLocal, 'day');
      const hoursSinceWorkout = nowLocal.diff(workoutTime, 'hours').hours;

      if (!hasSeenLoggingMessage || !isToday || hoursSinceWorkout > 3) {
        this.viewOnlyInfoMessage = LL.workouts.alerts.viewOnly.loggingMessage();
        this.isLoggingMessage = true;
      } else {
        // today's workout within 3 hours and user has seen message before - no info message
        this.showViewOnlyInfo = false;
        return;
      }
    } else {
      this.showViewOnlyInfo = false;
      return;
    }
    this.showViewOnlyInfo = true;
  }

  // nothing in the app writes a damaged value here, so damage is a problem worth seeing even
  // though the next markHintSeen overwrites it. Once per page: hasSeenHint runs on every
  // refresh of the info message.
  private hintsSeenDamageReported = false;

  private readHintsSeen(): Record<string, boolean> {
    return parseHintsSeen(this.settings.hintsSeenJSON, err => {
      if (this.hintsSeenDamageReported) return;
      this.hintsSeenDamageReported = true;
      this.logging.trackSilentProblem('HintsSeenSettingDamaged', { detail: describeError(err) });
    });
  }

  // whether the person has already dismissed this one-time hint
  private hasSeenHint(hintId: string): boolean {
    return !!this.readHintsSeen()[hintId];
  }

  // record that the person dismissed this one-time hint, and save it
  private markHintSeen(hintId: string): void {
    const hintsSeen = this.readHintsSeen();
    hintsSeen[hintId] = true;
    this.settings.hintsSeenJSON = JSON.stringify(hintsSeen);
    this.settings.saveInBackground('workout hint seen');
  }

  onViewOnlyInfoDismissed() {
    // if this was the logging message, mark it as seen
    if (this.isLoggingMessage) {
      this.markHintSeen('loggingMessage');
    }
    this.showViewOnlyInfo = false;
  }

  onPendingWorkoutWarningDismissed() {
    this.showPendingWorkoutWarning = false;
  }

  navigateToPendingWorkoutDay() {
    void this.navCtrl.setRootAsync(IHomePage, { day: this.pendingWorkoutDateISO });
  }

  onClickSetEditButton(set: UiSet) {
    set.weightBeforeEdit = set.logSet.stats.weight_kg;
    set.repsBeforeEdit = set.logSet.stats.reps;
    set.inEditMode.set(true);
    set.requireStrengthInput.set(true);
    set.requireRepsInput.set(true);
  }

  showSetDoneButton(e: UiExercise) {
    const openIdx = e.openSetIndex();
    return openIdx === e.sets.length - 1 || e.sets.every((s, i) => i <= openIdx || s.logSet.state === LogRecordState.Skipped) || (openIdx !== -1 && e.sets[openIdx].inEditMode());
  }

  shouldShowSetEditButton(e: UiExercise) {
    const openIdx = e.openSetIndex();
    return openIdx !== -1 && e.sets[openIdx].logSet &&
      (e.sets[openIdx].logSet.state === LogRecordState.Done || e.sets[openIdx].logSet.state === LogRecordState.Skipped || this.mode !== WorkoutLoadMode.Current) && (openIdx !== -1 && !e.sets[openIdx].inEditMode());
  }

  showInactiveSet(e: UiExercise, s: UiSet, setIndex: number) {
    if (this.isEditableMode()) {
      if (e.openSetIndex() === -1) {
        return true;
      }

      if (e.openSetIndex() !== setIndex) {
        if (s.logSet.state === LogRecordState.Done || s.logSet.state === LogRecordState.Incomplete || s.logSet.state === LogRecordState.Skipped) {
          return true;
        }
      }
    } else {
      if (e.openSetIndex() == setIndex) {
        return false;
      }
      if (setIndex === 0 || setIndex - 1 >= 0 && e.sets[setIndex - 1].logSet.state === LogRecordState.Done) {
        return true;
      }
    }

    return false;
  }

  isSetEditable(s: UiSet) {
    if (this.isCoachManaging) return false;
    return !s.logSet || (this.isEditableMode() && s.logSet.state !== LogRecordState.Done && s.logSet.state !== LogRecordState.Skipped || s.inEditMode());
  }

  hasSetValuesChanged(s: UiSet): boolean {
    if (!s.inEditMode()) {
      return false;
    }

    // check weight and reps
    if (s.logSet.stats.weight_kg !== s.weightBeforeEdit ||
        s.logSet.stats.reps !== s.repsBeforeEdit) {
      return true;
    }

    // check miniSets
    return !deepCompareEQ(s.logSet.miniSets, s.miniSetsBeforeEdit);
  }

  isSetBW(s: UiSet) {
    return s.logSet && s.logSet.stats && s.logSet.stats.bw_pc > 0;
  }

  // Returns true if set is completed (Done or Skipped)
  isSetCompleted(s: UiSet) {
    return s.logSet && (s.logSet.state === LogRecordState.Done || s.logSet.state === LogRecordState.Skipped);
  }

  // Returns true if set is ready to be worked on (first set OR all previous sets completed)
  isSetReady(e: UiExercise, setIndex: number) {
    // if first set, always ready
    if (setIndex === 0) {
      return true;
    }
    // if all previous sets are completed
    for (let i = 0; i < setIndex; i++) {
      if (!this.isSetCompleted(e.sets[i])) {
        return false;
      }
    }
    return true;
  }

  // Returns true if set is in the future (not ready yet)
  isSetFuture(e: UiExercise, setIndex: number) {
    return !this.isSetReady(e, setIndex) && !this.isSetCompleted(e.sets[setIndex]);
  }

  // Returns true if this is the last set
  isLastSet(e: UiExercise, setIndex: number) {
    return setIndex === e.sets.length - 1;
  }

  /**
   * what a done set's edit starts from: the values `hasSetValuesChanged` compares against, and the
   * snapshot of everything the edit can change, which the collapse arrow puts back (#6694). Recorded
   * when the set is reopened, and again after a confirmed edit.
   */
  private recordEditBaseline(s: UiSet) {
    s.weightBeforeEdit = s.logSet.stats.weight_kg;
    s.repsBeforeEdit = s.logSet.stats.reps;
    s.miniSetsBeforeEdit = s.logSet.miniSets ? deepClone(s.logSet.miniSets) : undefined;
    s.editSnapshot = takeSetEditSnapshot(s.logSet, s.derivedStatsFrom);
  }

  /** whether the open set's header offers the collapse arrow: only a reopened done set, with the compact layout on (#6694) */
  canCollapseOpenSet(s: UiSet): boolean {
    return this.compactFeedback && s.inEditMode() && s.logSet.state === LogRecordState.Done;
  }

  // the collapse arrow on a reopened done set folds it back without saving: what was logged stays,
  // and any change not confirmed with the check is put back (owner, #6694)
  async onCollapseSetButtonClicked(e: Event, s: UiSet) {
    e.stopPropagation();
    // if this set's save is still running, putting the old numbers back now would leave them beside
    // a progression already decided from the new ones
    if (s.uiExercise.loading()) {
      return;
    }
    // the *BeforeEdit baselines are left as they are: they only matter in edit mode, and the next
    // reopen records them again
    const restored = !!s.editSnapshot;
    if (s.editSnapshot) {
      restoreSetEditSnapshot(s.logSet, s.editSnapshot);
      s.derivedStatsFrom = s.editSnapshot.derivedStatsFrom;
    } else {
      // every reopen takes a snapshot, so this is a path that skipped it; fold without restoring
      this.logging.trackSilentProblem('collapse of a reopened set found no edit snapshot', {
        exerciseId: s.uiExercise.exercise?._id,
        setState: s.logSet.state,
      });
    }
    s.editSnapshot = undefined;
    s.inEditMode.set(false);
    s.uiExercise.openSetIndex.set(-1);
    // a save during the edit may already have stored unconfirmed numbers, even ones typed back by hand
    // since, so what was logged is always stored again
    if (restored) {
      await this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
    }
  }

  async handleSkipOldClicked() {
    // for all previous workouts
    for (let w of this.previousWorkouts) {

      let modified = false;
      // if not even started
      if (w.state === LogRecordState.Scheduled) {
        // mark it as completed
        w.state = LogRecordState.Done;
        modified = true;

        // if workout was started but not done
      } else if (w.state === LogRecordState.Incomplete) {

        // for all exercises
        // previousWorkouts run from the current workout up to this one, so they are still trainable
        // and take the plan's edits like the current workout does (#6574). if the workout has a
        // stored list, put the merged one on it here: getEffectiveExercises no longer writes it
        // (#6585), and the sets filled in below must be the ones saved
        const effectiveExercises = this.workoutLoader.getEffectiveExercises(w, this.plan, PlanMerge.WithCurrentPlan);
        if (w.exercises?.length) {
          this.workoutLoader.setWorkoutExercises(w, effectiveExercises);
        }
        for (let e of effectiveExercises) {
          // first make sure all the needed sets are created (because the UI creates sets as you go normally)...

          // determine how many sets were supposed to be be there...
          /* Note that this is not always 100% correct. The user might have autovolume regulation, or for some other reason
          wouldn't have the default number of sets. But since they are skipping the workout, it is not important to be super precise.
          Just make sure we have a reasonable number of sets. */
          let numPlannedSets = e.numPlannedSets;
          // if not already stored
          if (numPlannedSets == undefined || numPlannedSets < 1) {
            // get it from the slot in the plan
            let planSubSlot = this.plan.subSlots[e.subSlotId];
            if (planSubSlot) {
              numPlannedSets = planSubSlot.defaultVolume_sets;
            } else {
              numPlannedSets = 1; // in case something is wrong, keep at least one set
            }
          }

          // if there are not enough sets
          if (e.sets.length < numPlannedSets) {
            // add the missing ones
            for (let i = e.sets.length; i < numPlannedSets; i++) {
              e.sets.push({ state: LogRecordState.Scheduled, stats: {}, preStatus: [], postStatus: [] });
            }
          }

          // for all sets that are not done
          for (let s of e.sets) {
            if (s.state !== LogRecordState.Done) {
              // mark them as skipped
              s.state = LogRecordState.Skipped;
            }
          }
        }
        // mark the workout as done
        w.state = LogRecordState.Done;
        modified = true;
      }

      // save it if needed
      if (modified) {
        await this.saveWorkoutOrOtherAsync(DatabaseWrapper.getTransactionId(), w);
        // force-push skipped/completed workout immediately (see #3050)
        void this.userDb.database.forcePushDocAsync(w._id, 'workout-auto-conclude');
      }
    }
  }

  isResolvableStatus(status: PostSetStatus | PreSetStatus): boolean {
    return status === PostSetStatus.PlateauDetected ||
           status === PostSetStatus.VolumeAutoregulationPending;
  }

  getExerciseResolvableStatusLines(e: UiExercise): StatusLineInfo[] {
    const lines: StatusLineInfo[] = [];
    for (let set of e.sets) {
      // check post-status
      for (let postStatus of set.logSet.postStatus) {
        if (typeof postStatus.status === 'string' && this.isResolvableStatus(postStatus.status)) {
          const textLines = this.getSetPostStatusTextLines(set);
          const matchingLine = textLines.find(l => l.status === postStatus.status);
          if (matchingLine) {
            lines.push(matchingLine);
          }
        }
      }
      // check pre-status
      for (let preStatus of set.logSet.preStatus) {
        if (typeof preStatus.status === 'string' && this.isResolvableStatus(preStatus.status)) {
          const textLines = this.getSetPreStatusTextLines(set);
          const matchingLine = textLines.find(l => l.status === preStatus.status);
          if (matchingLine) {
            lines.push(matchingLine);
          }
        }
      }
    }
    return lines;
  }

  trackByStatus(_index: number, line: StatusLineInfo): string {
    return line.status;
  }

  async handlePlateauResolveButton(event: Event | null, e: UiExercise) {
    event?.stopPropagation();

    // read the pointer BEFORE opening the dialog. Accepting "change the
    // exercise" runs replaceExerciseInSubSlot, which wipes this field, and the
    // clean-up would then have no workout to clean (#4360).
    //
    // #6428: while a coach change made after this session's first set guards the exercise, the
    // program's own entry decides what is still open — the session's copies are out of date
    const guardOn = this.refreshCoachChangeGuard(e);
    const live = this.plan?.subSlots?.[e.logExercise.subSlotId];
    const plateauWorkoutLog_id = this.programSubSlotForAnswer(e)?.plateauWorkoutLog_id;
    const volumeOpen = volumeQuestionOpen(guardOn, live, [e.planPath], this.logWorkout._id);

    if (guardOn && (plateauWorkoutLog_id || volumeOpen)) {
      // #6428 R9 (b): the question is still open, but an answer here would act on a copy the program
      // no longer holds. Clear the card without asking and leave the program's pointer: the question
      // is asked again at the next workout, against the coach's version.
      this.clearCardsUnderCoachGuard(e);
    } else if (plateauWorkoutLog_id) {
      let message = await this.askPlateauAnswerAsync(e, false);
      await this.handlePlateauMessage(e, message, plateauWorkoutLog_id);
    } else if (volumeOpen) {
      const accepted = await this.handleAutoregulationAsync(e, false);
      await this.handleAutoregulationMessage(e, accepted);
    } else {
      // a resolvable card was tapped but there is no plan-side state left to
      // resolve — the card is orphaned (#4052: an earlier resolve cleared the
      // plan fields while a drifted trigger index left the pending status
      // behind). previously this fell through silently, leaving the user with
      // a dead button. self-heal: sweep the orphaned statuses so the card
      // disappears, and surface a canary so we can watch the field rate.
      // `cleared_count` deliberately keeps its original meaning — how many
      // VOLUME autoregulation statuses were swept — so the numbers already in
      // Sentry stay comparable; the plateau half is counted separately (#4360).
      if (guardOn) {
        this.clearVolumeQuestionFromSnapshot(e);
      }
      const removed = this.sweepViewedAutoregulationPending(e);
      const removedPlateau = this.sweepViewedPlateauDetected(e);
      if (removed > 0 || removedPlateau > 0 || guardOn) {
        void this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
      }
      // #6428 R6: a card the coach answered is an expected orphan — but only when a coach change
      // after this workout says it answered exactly the questions the swept cards asked
      const docs = this.exerciseChangesBySubSlot.get(e.logExercise.subSlotId) ?? [];
      const coachAnswered = removed + removedPlateau > 0
        && (removed === 0 || coachAnsweredQuestion(docs, this.logWorkout.time_utc, ExerciseChangeField.VolumeQuestionAnswered))
        && (removedPlateau === 0 || coachAnsweredQuestion(docs, this.logWorkout.time_utc, ExerciseChangeField.PlateauAnswered));
      const report = {
        exercise_id: e.exercise?._id,
        cleared_count: removed,
        cleared_plateau_count: removedPlateau,
      };
      if (coachAnswered) {
        this.logging.trackSilentInfo('workout-detail.coach-answered-card-cleared', report);
      } else {
        this.logging.trackSilentProblem('workout-detail.orphaned-resolve-tap', report);
      }
      this.cdRef.markForCheck();
    }
  }

  /**
   * #6428 R9 (b): clear this exercise's plateau and volume cards without asking, because a coach
   * change made after this session's first set guards the exercise. The program's pointers stay,
   * so a question still open there is asked at the next workout. The active guard is what proves
   * this was designed, so it is reported at info tier.
   */
  private clearCardsUnderCoachGuard(e: UiExercise): void {
    const removed = this.sweepViewedAutoregulationPending(e);
    const removedPlateau = this.sweepViewedPlateauDetected(e);
    if (removed > 0 || removedPlateau > 0) {
      void this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
    }
    this.logging.trackSilentInfo('workout-detail.card-cleared-under-coach-guard', {
      exercise_id: e.exercise?._id,
      cleared_count: removed,
      cleared_plateau_count: removedPlateau,
    });
    this.cdRef.markForCheck();
  }

  /**
   * #6428 R6: a coach answered the volume question in the program, but this workout's own snapshot
   * (`planSubslotBackup`) still holds the question's fields, and the page's current path may point
   * into it. Clear them there so nothing on this workout asks the question again. The caller saves.
   */
  private clearVolumeQuestionFromSnapshot(e: UiExercise): void {
    for (const path of e.logExercise.planSubslotBackup?.exercisePaths ?? []) {
      if (path) {
        clearAutoregulationFields(path);
      }
    }
    if (e.planPath) {
      clearAutoregulationFields(e.planPath);
    }
  }

  /**
   * #6428: read the exercise editor's change documents for every exercise on the page, once per
   * page load. Two things use them: the client's note (R8) and the report of a change some session
   * overwrote (R9). A failed read is reported and costs only the note; nothing else depends on it.
   */
  private async loadExerciseChangesAsync(): Promise<void> {
    const exercises = this.exercises ?? [];
    const workoutTime_utc = this.logWorkout?.time_utc;
    const showNotes = !this.isCoachManaging
      && (this.mode === WorkoutLoadMode.Current || this.mode === WorkoutLoadMode.SoftFuture);
    for (const e of exercises) {
      const subSlotId = e.logExercise?.subSlotId;
      const exerciseId = e.exercise?._id;
      if (!subSlotId || !exerciseId || this.exerciseChangesBySubSlot.has(subSlotId)) {
        continue;
      }
      try {
        await this.loadChangesForExerciseAsync(subSlotId, exerciseId, showNotes ? workoutTime_utc : undefined);
      } catch (err) {
        this.logging.trackSilentProblem('workout-detail.exercise-changes-unreadable', {
          subSlotId,
          error: describeError(err),
        });
      }
      if (this.viewDestroyed) return;
    }
    this.cdRef.markForCheck();
  }

  /**
   * #6428: one exercise's part of `loadExerciseChangesAsync`: remember its change documents, report
   * a change some session overwrote, and, when `noteWorkoutTime_utc` is given (the client's own
   * current or coming workout), work out the coach changes its note lists.
   */
  private async loadChangesForExerciseAsync(subSlotId: string, exerciseId: string, noteWorkoutTime_utc: string | undefined): Promise<void> {
    const docs = await this.userDb.getExerciseChangesForSubSlotAsync(subSlotId, CHANGES_READ_PER_EXERCISE);
    if (this.viewDestroyed) return;
    this.exerciseChangesBySubSlot.set(subSlotId, docs);

    const overwritten = overwrittenChange(docs, this.plan?.subSlots?.[subSlotId]);
    if (overwritten && !reportedOverwrittenChangeIds.has(overwritten._id ?? overwritten.changed_utc)) {
      reportedOverwrittenChangeIds.add(overwritten._id ?? overwritten.changed_utc);
      this.logging.trackSilentProblem('coach-exercise-change-overwritten', { subSlotId, changeId: overwritten._id });
    }

    // if there is no note to show, or no coach change to put in one, nothing more to read
    if (!noteWorkoutTime_utc || !docs.some(doc => doc.author?.role === 'coach')) return;
    const recent = await this.userDb.getRecentWorkoutsForExerciseAsync(exerciseId, WORKOUTS_READ_FOR_NOTE);
    if (this.viewDestroyed) return;
    const previous = previousWorkoutWithSubSlot(recent, subSlotId, noteWorkoutTime_utc);
    const changes = coachNoteChanges(docs, exerciseId, previous?.time_utc);
    if (changes.length > 0) {
      this.coachNoteBySubSlot.set(subSlotId, changes);
    }
  }

  /** #6428 R8: the line under "Your coach changed this exercise", or undefined when there is no note */
  getCoachNoteText(e: UiExercise): string | undefined {
    const changes = this.coachNoteBySubSlot.get(e.logExercise?.subSlotId);
    if (!changes?.length) {
      return undefined;
    }
    // the date of the newest change, every coach who made one, and all of them in the order made
    const newest = changes[changes.length - 1];
    const time = DateTime.fromISO(newest.changed_utc);
    const date = time.isValid ? formatShortDate(time.toLocal(), this.settings) : '';
    const name = [...new Set(changes.map(change => change.author?.name ?? '').filter(n => n))].join(', ');
    const formatWeight = (kg: number) =>
      formatMeasure(kg, 'addedweight_kg', this.settings.locale, this.fitnessState?.exerciseStats?.[e.exercise?._id]?.weight_unit);
    const summary = changes.map(change => describeChangeDoc(change, formatWeight)).filter(text => text).join('; ');
    return LL.workouts.detail.coachChanged.detail({ date, name, summary });
  }

  hasSetOverride(s: UiSet): boolean {
    const plannedWeight = s.logSet.plannedWeight_kg ?? s.logSet.stats.weight_kg;
    return plannedWeight !== s.logSet.stats.weight_kg;
  }

  // format weight without space between number and unit (e.g., "50kg" not "50 kg")
  formatSetWeight(weight_kg: number, unit: string): string {
    const { measurementType, measurementUnitConfig } = findBestUnitConfigForType('addedweight_kg', this.settings.locale, unit);
    const [text1, text2] = canonValueToTexts_global(weight_kg, measurementType, measurementUnitConfig, this.settings.locale);
    let result = `${text1}${measurementUnitConfig.unit1}`;
    if (measurementUnitConfig.unit2) {
      result += ` ${text2}${measurementUnitConfig.unit2}`;
    }
    return result;
  }

  getPlannedSetText(s: UiSet): string {
    const weight = s.logSet.plannedWeight_kg ?? s.logSet.stats.weight_kg;
    const reps = s.logSet.plannedReps ?? s.logSet.stats.reps;
    return `${this.formatSetWeight(weight, s.logSet.stats.weight_unit)} × ${reps}`;
  }

  getActualSetText(s: UiSet): string {
    return `${this.formatSetWeight(s.logSet.stats.weight_kg, s.logSet.stats.weight_unit)} × ${s.logSet.stats.reps}`;
  }

  getSetPostStatusTextLines(s: UiSet): StatusLineInfo[] {
    let lines: StatusLineInfo[] = [];
    if (!s.logSet?.postStatus?.length) {
      return lines;
    }
    for (let statusMessage of messageForPostSetStatus) {
      let foundStatus = s.logSet.postStatus.find(status => {
        // skip dismissed messages
        if (status.dismissed) {
          return false;
        }
        if (typeof status.status === 'number') {
          return remapOldPostSetStatusNumberToEnum[status.status] === statusMessage.status;
        }
        return status.status === statusMessage.status;
      });
      if (foundStatus) {
        const params = foundStatus.textInserts || {};
        lines.push({
          text: statusMessage.getMessage(params),
          status: statusMessage.status,
          statusObject: foundStatus,
          helpId: statusMessage.helpId
        });
      }
    }
    return lines;
  }

  getSetPreStatusTextLines(s: UiSet): StatusLineInfo[] {
    let lines: StatusLineInfo[] = [];
    if (!s.logSet?.preStatus?.length) {
      return lines;
    }
    for (let statusMessage of messageForPreSetStatus) {
      let foundStatus = s.logSet.preStatus.find(status => {
        // skip dismissed messages
        if (status.dismissed) {
          return false;
        }
        if (typeof status.status === 'number') {
          return remapOldPreSetStatusNumberToEnum[status.status] === statusMessage.status;
        }
        return status.status === statusMessage.status;
      });
      if (foundStatus) {
        const params = foundStatus.textInserts || {};
        // #6197: a drop set the equipment could not make lighter must not tell the user to reduce the weight
        const heldAtFloor = statusMessage.status === PreSetStatus.DropSet && s.heldAtEquipmentFloor;
        lines.push({
          text: heldAtFloor ? LL.workouts.status.dropSetAtFloor() : statusMessage.getMessage(params),
          status: statusMessage.status,
          statusObject: foundStatus,
          helpId: statusMessage.helpId
        });
      }
    }
    return lines;
  }

  dismissStatus(statusObj: Log_WorkoutSetPreStatus | Log_WorkoutSetPostStatus): void {
    statusObj.dismissed = true;
    void this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
  }

  // get count of non-dismissed status messages for notification bell
  getStatusMessageCount(s: UiSet): number {
    let count = 0;
    if (s.logSet?.preStatus) {
      count += s.logSet.preStatus.filter(status => !status.dismissed).length;
    }
    if (s.logSet?.postStatus) {
      count += s.logSet.postStatus.filter(status => !status.dismissed).length;
    }
    return count;
  }

  // the status bell's number. #4142 compact feedback: with the switch on it counts only the dismissed
  // (hidden) messages; off, every message, as in v1.34
  getBellBadge(s: UiSet): number {
    return this.compactFeedback ? getHiddenStatusMessageCount(s.logSet) : this.getTotalStatusMessageCount(s);
  }

  // the smallest number the bell draws: with the switch on a single hidden message shows "1"
  getBellBadgeMinimum(): number {
    return this.compactFeedback ? 1 : 2;
  }

  // the bell turns link-blue when every message is dismissed, but never with the switch on
  isBellLinkColored(s: UiSet): boolean {
    return !this.compactFeedback && this.getStatusMessageCount(s) === 0;
  }

  // get total count including dismissed (to know if bell should show)
  getTotalStatusMessageCount(s: UiSet): number {
    let count = 0;
    if (s.logSet?.preStatus) {
      count += s.logSet.preStatus.length;
    }
    if (s.logSet?.postStatus) {
      count += s.logSet.postStatus.length;
    }
    return count;
  }

  // undismiss all status messages
  undismissAllStatusMessages(s: UiSet): void {
    let changed = false;
    if (s.logSet?.preStatus) {
      for (let status of s.logSet.preStatus) {
        if (status.dismissed) changed = true;
        status.dismissed = false;
      }
    }
    if (s.logSet?.postStatus) {
      for (let status of s.logSet.postStatus) {
        if (status.dismissed) changed = true;
        status.dismissed = false;
      }
    }
    if (changed) {
      void this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());
    }
  }

  toggleSetInstructions(): void {
    // if either is showing, hide both; if both hidden, show both
    const newState = !(this.showSetInstructions || this.showBwHelpText);
    this.showSetInstructions = newState;
    this.showBwHelpText = newState;
  }

  // handle notification bell click
  onNotificationBellClick(s: UiSet): void {
    // undismiss all dismissed messages
    this.undismissAllStatusMessages(s);
  }

  showWorkoutOptions(event: Event) {
    event.stopPropagation();
    const targetElement = event.target as HTMLElement;
    const buttons = this.getWorkoutOptionButtons();

    if (buttons.length === 0) return;

    void this.popupCtrl.createMenu({
      targetFromElement: targetElement,
      buttons,
    });
  }

  private async changeWorkoutAsync() {
    // get effective plan reference (computed on-demand if not set)
    const effectivePlanRef = this.workoutLoader.getEffectivePlanReference(this.logWorkout, this.plan);
    // get original plan reference (always computed dynamically, ignoring any saved value)
    const originalPlanReference = this.workoutLoader.getEffectivePlanReference(this.logWorkout, this.plan, true);

    const result = await this.navCtrl.callAsync(IChangeWorkoutPage, {
      currentPlanReference: effectivePlanRef,
      originalPlanReference,
    });

    if (result && result.newPlanReference !== effectivePlanRef) {
      // update workout with new plan reference
      const newPlanWorkout = this.plan.workouts[result.newPlanReference];
      this.workoutLoader.setWorkoutPlanReference(this.logWorkout, result.newPlanReference);
      // store the canonical ENGLISH name on purpose (#4317): the stored value is
      // the stable form. Translation happens at the DISPLAY sites, which wrap
      // getEffectiveWorkoutName() in formatWorkoutName(); the accessor itself
      // deliberately stays English. Storing a translated name here would freeze
      // the user's current language into their data.
      this.workoutLoader.setWorkoutName(this.logWorkout, 'Workout ' + newPlanWorkout.name);

      // rebuild exercises array
      const newExercises: Log_WorkoutExercise[] = [];
      for (let i = 0; i < newPlanWorkout.subSlotIds.length; i++) {
        newExercises[i] = {
          subSlotId: newPlanWorkout.subSlotIds[i],
          sets: [],
        };
      }
      this.workoutLoader.setWorkoutExercises(this.logWorkout, newExercises);

      // reload UI exercise list
      const context: WorkoutLoadingContext = {
        plan: this.plan,
        fitnessState: this.fitnessState,
        declaredState: this.declaredState,
        nowLocal: this.debug.getNowLocal(),
        locale: this.settings.locale,
        watcher: this.docWatcher,
        currentWorkoutId: this.logWorkout._id,
      };
      const { exercises } = await this.workoutLoader.loadWorkoutExercisesAsync(
        this.logWorkout,
        context,
        this.mode
      );
      this.exercises = exercises;
      this.planRevAtExerciseLoad = this.plan?._rev;
      this.pageTitle = formatWorkoutName(this.workoutLoader.getEffectiveWorkoutName(this.logWorkout, this.plan));
      // #4060: the page is now showing a DIFFERENT workout of the split, and the
      // explainer card is decided per split day — so the answer computed on entry
      // belongs to the day we just left. Ask again for the new one.
      void this.updateTestWorkoutExplainerAsync();
      this.showWorkoutChangedInfo = true;
      this.cdRef.markForCheck();
    }
  }

  private async skipRemainingAsync() {
    const result = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.workouts.detail.skipRemaining(),
      message: LL.workouts.detail.skipRemainingConfirm(),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.common.buttons.yes(), role: AlertButtonRole.Primary, result: true },
      ],
    });

    if (!result) return;

    // skip every exercise the user has not finished with. #4802: this used to
    // act only on an exercise holding a set the user had opened and left
    // half-done, which misses two ordinary cases. An exercise the user never
    // started holds Scheduled sets rather than Incomplete ones, so "Skip
    // remaining" walked straight past it and the workout could not finish. And
    // when every set was already done — the state this ticket reports — the loop
    // does nothing at all, which is exactly what the customer saw; that case is
    // closed by the save below, via the re-assert in saveWorkoutAsync.
    for (const e of this.exercises) {
      // an exercise showing no sets has nothing to skip. It is also the one
      // shape markExerciseSkipped handles by OPENING the exercise, which reads
      // sets[0] and would throw here, so leave it alone rather than reaching it
      // from a bulk action the user did not aim at this exercise.
      if (!e.sets?.length) continue;
      if (!isExerciseConcluded(e.sets.map(s => s.logSet?.state))) {
        await this.markExerciseSkipped(e, { skipSave: true });
      }
    }

    await this.saveWorkoutAsync(DatabaseWrapper.getTransactionId());

    // show the workout as finished. When the loop skipped something,
    // markExerciseSkipped has already done this through onExerciseDone; when
    // every set was already done it skipped nothing, and without this the
    // customer taps "Skip remaining exercises" and sees no change at all — the
    // very complaint this ticket is about. The save above is what flips the
    // stored state, so read it rather than deciding again here.
    if (this.logWorkout.state === LogRecordState.Done) {
      this.complete = true;
      this.showWorkoutSummary();
      this.nextAutofocusElementId = 'workout-complete';
      this.nextAutofocusSubSlotId = undefined;
    }
    this.cdRef.markForCheck();
  }

  onWorkoutChangedInfoDismissed() {
    this.showWorkoutChangedInfo = false;
  }

  // reads the compact-feedback switch and follows it while the page lives. watchConfigValue fires
  // on change only, so the one-shot read seeds the first value; the guard keeps one subscription
  // across re-entries. See design-docs/working-set-screen.md, "Compact feedback switch"
  private watchCompactFeedbackSwitch() {
    void this.configDb.getConfigValueAsync<boolean>(ConfigKey.CompactWorkoutFeedback).then(value => {
      if (this.viewDestroyed) return;
      this.applyCompactFeedbackValue(value);
    });
    // if not yet subscribed, subscribe so flipping the switch redraws the page live
    if (!this.unwatchCompactFeedback) {
      this.unwatchCompactFeedback = this.configDb.watchConfigValue<boolean>(ConfigKey.CompactWorkoutFeedback, value => {
        if (this.viewDestroyed) return;
        this.applyCompactFeedbackValue(value);
      });
    }
  }

  // stores the switch's value. #6536: the value can arrive after a finished workout's exercises were
  // loaded, so if the switch is now on and that workout has no summary yet, build it here
  private applyCompactFeedbackValue(value: boolean | undefined) {
    this.compactFeedback = value === true;
    if (this.compactFeedback && !this.workoutSummary && this.exercises?.length && this.isWorkoutFinished()) {
      this.showWorkoutSummary();
    }
    this.cdRef.markForCheck();
  }

  ngOnDestroy() {
    // cancel any in-flight rest timer when leaving the page (matches the
    // "page-leave" requirement in design-docs/rest-timer.md)
    void this.restTimer.cancel();
    this.docWatcher?.destroy();
    this.unwatchCompactFeedback?.();
  }
}

